// Every question here is hand-solved and cross-checked before being added —
// this is a curated bank, not generated content. Each topic is meant to reach
// 25 questions; add new entries the same way (solve twice, on paper, before typing).

export const APTITUDE_TOPICS = [
  { key: 'percentages', label: 'Percentages' },
  { key: 'time-work', label: 'Time & Work' },
  { key: 'profit-loss', label: 'Profit & Loss' },
  { key: 'time-speed-distance', label: 'Time, Speed & Distance' },
  { key: 'si-ci', label: 'Simple & Compound Interest' },
  { key: 'ratio-proportion', label: 'Ratio & Proportion' },
  { key: 'averages', label: 'Averages' },
  { key: 'ages', label: 'Problems on Ages' },
  { key: 'mixtures-alligations', label: 'Mixtures & Alligations' },
  { key: 'numbers', label: 'Numbers (HCF, LCM & Divisibility)' },
  { key: 'permutations-combinations', label: 'Permutations & Combinations' },
  { key: 'probability', label: 'Probability' },
  { key: 'mensuration', label: 'Mensuration' },
  { key: 'number-series', label: 'Number Series' },
  { key: 'simplification', label: 'Simplification' },
  { key: 'data-interpretation', label: 'Data Interpretation' },
];

export const APTITUDE_QUESTIONS = {
  percentages: [
    {
      id: 'pct-01',
      difficulty: 'Medium',
      patternTags: ['TCS', 'Infosys', 'Wipro', 'Zoho'],
      question:
        'In an election between two candidates, the winning candidate secured 60% of the valid votes and won by a margin of 1,600 votes. If 200 votes were declared invalid, find the total number of votes polled (valid + invalid).',
      options: ['7,800', '8,000', '8,200', '8,400'],
      correctIndex: 2,
      steps: [
        {
          label: 'Given',
          text: 'Winner = 60% of the valid votes, so Loser = 40% of the valid votes. Margin = 1,600 votes. Invalid votes = 200.',
        },
        {
          label: 'Concept',
          text: 'When two percentages share the same base, their difference is itself a percentage of that base: Margin % = Winner % − Loser %.',
        },
        {
          label: 'Working',
          text: 'Margin % = 60% − 40% = 20% of the valid votes (V). So 20% of V = 1,600 → V = 1,600 × 100 / 20 = 8,000 valid votes.',
        },
        {
          label: 'Answer',
          text: 'Total votes polled = valid + invalid = 8,000 + 200 = 8,200.',
        },
      ],
      commonTrap:
        'Stopping at 8,000. That figure is only the valid votes — the question asks for total votes polled, so the 200 invalid votes still need to be added back on.',
      alternateMethod: {
        label: 'Ratio shortcut — no equation needed',
        steps: [
          'Winner : Loser = 60 : 40 = 3 : 2.',
          'The gap between the ratio parts (3 − 2 = 1 part) is exactly the margin: 1 part = 1,600 votes.',
          'Total valid votes = (3 + 2) parts = 5 × 1,600 = 8,000.',
          'Add the invalid votes: 8,000 + 200 = 8,200 — same answer, reached without writing a single equation.',
        ],
      },
    },
    {
      id: 'pct-02',
      difficulty: 'Medium',
      patternTags: ['TCS', 'Infosys'],
      question:
        "The price of a commodity increases by 25%. By what percentage must a family reduce its consumption to keep its expenditure on the commodity unchanged?",
      options: ['15%', '20%', '25%', '30%'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: 'Price increases by 25%. Expenditure = Price × Consumption must stay the same as before.' },
        { label: 'Concept', text: 'If price increases by R%, the required decrease in consumption is [R / (100 + R)] × 100% — not R% itself, because the new price is a larger base.' },
        { label: 'Working', text: 'R = 25 → required decrease = 25 / (100 + 25) × 100 = 25/125 × 100 = 20%.' },
        { label: 'Answer', text: 'Consumption must be reduced by 20%.' },
      ],
      commonTrap:
        "Answering 25%, assuming the reduction must match the price increase — the two percentages use different bases (old price vs. new price), so they are never equal except at 0%.",
      alternateMethod: {
        label: 'Assumed-value shortcut',
        steps: [
          'Let original price = 100 and original consumption = 100 units, so original expenditure = 10,000.',
          'New price = 125. To keep expenditure at 10,000, new consumption = 10,000 / 125 = 80 units.',
          'Reduction = 100 − 80 = 20 units, i.e. a 20% reduction — same answer, no formula needed.',
        ],
      },
    },
    {
      id: 'pct-03',
      difficulty: 'Hard',
      patternTags: ['TCS', 'Wipro', 'Zoho'],
      question:
        "A number is first increased by 20% and the result is then decreased by 20%. What is the net percentage change in the number?",
      options: ['5% decrease', '4% decrease', 'No change', '4% increase'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: 'A number is increased by 20%, then the new (larger) value is decreased by 20%.' },
        { label: 'Concept', text: 'For two successive percentage changes a% and b%, the net % change is a + b + (ab/100), where a decrease is entered as a negative value.' },
        { label: 'Working', text: 'a = 20, b = −20 → Net = 20 + (−20) + (20 × −20)/100 = 0 − 4 = −4%.' },
        { label: 'Answer', text: 'The net change is a 4% decrease.' },
      ],
      commonTrap:
        "Assuming a 20% increase followed by a 20% decrease cancels out to 'No change' — it doesn't, because the second percentage is taken on a larger base than the first.",
      alternateMethod: {
        label: 'Assumed-value shortcut',
        steps: [
          'Let the number = 100.',
          'After +20%: 100 × 1.20 = 120.',
          'After −20% of 120: 120 − 24 = 96.',
          '100 → 96 is a change of −4, i.e. a 4% decrease — matching the formula.',
        ],
      },
    },
    {
      id: 'pct-04',
      difficulty: 'Medium',
      patternTags: ['Infosys', 'Wipro'],
      question:
        "A's income is 25% more than B's income. By what percentage is B's income less than A's income?",
      options: ['16.67%', '20%', '25%', '30%'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: "A's income = B's income + 25% of B's income = 1.25 × B." },
        { label: 'Concept', text: '"% less" must be calculated using the LARGER value (A) as the base, not the same base (B) used for the original "% more" statement.' },
        { label: 'Working', text: 'Difference = A − B = 0.25B. % less = (0.25B / 1.25B) × 100 = 20%.' },
        { label: 'Answer', text: "B's income is 20% less than A's income." },
      ],
      commonTrap:
        "Answering 25% by symmetry with the given figure — but '25% more' is measured against B, while '% less' must be measured against A, a different (larger) base.",
      alternateMethod: {
        label: 'Assumed-value shortcut',
        steps: [
          'Let B = 100 → A = 125.',
          '% less = (A − B) / A × 100 = 25 / 125 × 100 = 20%.',
        ],
      },
    },
    {
      id: 'pct-05',
      difficulty: 'Easy',
      patternTags: ['TCS', 'Zoho'],
      question:
        "A student needs 40% marks to pass an exam. He scored 200 marks and failed by 40 marks. What are the maximum marks for the exam?",
      options: ['500', '600', '640', '700'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: 'Passing requirement = 40% of maximum marks. Student scored 200 and fell short of passing by 40 marks.' },
        { label: 'Concept', text: 'Passing marks = marks scored + the shortfall, since "failed by 40" means he needed 40 more marks to reach the passing mark.' },
        { label: 'Working', text: 'Passing marks = 200 + 40 = 240. This equals 40% of the maximum: 0.40 × M = 240.' },
        { label: 'Answer', text: 'M = 240 / 0.40 = 600 maximum marks.' },
      ],
      commonTrap:
        "Subtracting instead of adding (200 − 40 = 160) to find the passing mark — 'failed by 40' means he scored 40 LESS than required, so the shortfall must be added to his score, not subtracted from it.",
      alternateMethod: {
        label: 'Unitary method',
        steps: [
          'Passing marks = 200 + 40 = 240, and this is 40% of the total.',
          '40% → 240, so 1% → 240/40 = 6.',
          '100% → 6 × 100 = 600 maximum marks.',
        ],
      },
    },
    {
      id: 'pct-06',
      difficulty: 'Medium',
      patternTags: ['TCS', 'Infosys', 'Wipro'],
      question:
        "A shopkeeper offers successive discounts of 20% and 10% on an item marked ₹2,000. What is the final selling price?",
      options: ['1,400', '1,440', '1,460', '1,600'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: 'Marked price = ₹2,000. Two successive discounts: 20%, then 10% on the reduced price.' },
        { label: 'Concept', text: 'Successive discounts multiply the remaining fraction each time: Final = MP × (1 − d1) × (1 − d2). They do not simply add together.' },
        { label: 'Working', text: '2,000 × 0.80 = 1,600. Then 1,600 × 0.90 = 1,440.' },
        { label: 'Answer', text: 'Final selling price = ₹1,440.' },
      ],
      commonTrap:
        "Adding the discounts (20% + 10% = 30%) and computing 2,000 × 0.70 = ₹1,400 — successive discounts compound on a shrinking base, they are never equal to one combined flat discount of their sum.",
      alternateMethod: {
        label: 'Single equivalent-discount shortcut',
        steps: [
          'Combined discount % = d1 + d2 − (d1 × d2)/100 = 20 + 10 − (20×10)/100 = 30 − 2 = 28%.',
          'Final price = 2,000 × (1 − 0.28) = 2,000 × 0.72 = ₹1,440 — one multiplication instead of two.',
        ],
      },
    },
    {
      id: 'pct-07',
      difficulty: 'Medium',
      patternTags: ['Infosys', 'Zoho'],
      question:
        "The population of a town grows by 10% every year. If its present population is 60,500, what was the population 2 years ago?",
      options: ['48,400', '50,000', '50,417', '52,000'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: 'Present population = 60,500, after 2 years of 10% annual growth (compounding).' },
        { label: 'Concept', text: 'Population 2 years ago × (1 + r)² = present population, since each year’s 10% growth applies to an already-grown base.' },
        { label: 'Working', text: 'X × (1.10)² = 60,500 → X × 1.21 = 60,500 → X = 60,500 / 1.21 = 50,000.' },
        { label: 'Answer', text: 'Population 2 years ago = 50,000.' },
      ],
      commonTrap:
        "Dividing by 1.20 (treating 2 years of 10% growth as a flat 20%) to get 50,417 — growth compounds year over year, so it is never the same as simply doubling the annual rate.",
      alternateMethod: {
        label: 'Forward verification',
        steps: [
          'Try X = 50,000: after year 1 → 50,000 × 1.1 = 55,000.',
          'After year 2 → 55,000 × 1.1 = 60,500 — matches the given present population exactly, confirming X = 50,000.',
        ],
      },
    },
    {
      id: 'pct-08',
      difficulty: 'Hard',
      patternTags: ['TCS', 'Zoho'],
      question:
        "A man spends 75% of his income. His income increases by 20% and his expenditure increases by 10%. Find the percentage increase in his savings.",
      options: ['12.5%', '30%', '40%', '50%'],
      correctIndex: 3,
      steps: [
        { label: 'Given', text: 'Expenditure = 75% of income, so savings = 25% of income. Income rises 20%, expenditure rises 10%.' },
        { label: 'Concept', text: 'Savings = Income − Expenditure. Compute the new savings from the new income and new expenditure, then compare to the OLD savings — not the old income.' },
        { label: 'Working', text: 'Let income = 100. Old expenditure = 75, old savings = 25. New income = 120, new expenditure = 75 × 1.10 = 82.5, new savings = 120 − 82.5 = 37.5.' },
        { label: 'Answer', text: 'Increase in savings = 37.5 − 25 = 12.5. % increase = 12.5 / 25 × 100 = 50%.' },
      ],
      commonTrap:
        "Reporting the absolute increase (12.5) as if it were already the percentage — the 12.5 is a value out of the old income base (100), and it must still be divided by the OLD SAVINGS (25), not by 100, to get the correct 50%.",
      alternateMethod: {
        label: 'Ratio method',
        steps: [
          'New savings ÷ Old savings = (1.20 × 100 − 1.10 × 75) / 25 = (120 − 82.5) / 25 = 37.5 / 25 = 1.5.',
          'A ratio of 1.5 means savings became 150% of their old value, i.e. a 50% increase.',
        ],
      },
    },
    {
      id: 'pct-09',
      difficulty: 'Medium',
      patternTags: ['TCS', 'Wipro'],
      question:
        "40% of a number is 20 more than 20% of 150. Find the number.",
      options: ['75', '100', '125', '140'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: '40% of N = 20 + (20% of 150).' },
        { label: 'Concept', text: 'Translate the sentence piece by piece: "20 more than X" means X + 20, applied AFTER computing X = 20% of 150.' },
        { label: 'Working', text: '20% of 150 = 30. So 40% of N = 30 + 20 = 50.' },
        { label: 'Answer', text: 'N = 50 × (100/40) = 125.' },
      ],
      commonTrap:
        "Forgetting to add the '+20' after computing 20% of 150, and solving 40% of N = 30 instead — this gives N = 75, missing the extra 20 stated in the question.",
      alternateMethod: {
        label: 'Unitary method',
        steps: [
          '40% of N = 50, so 1% of N = 50/40 = 1.25.',
          '100% of N = 1.25 × 100 = 125.',
        ],
      },
    },
    {
      id: 'pct-10',
      difficulty: 'Medium',
      patternTags: ['Infosys', 'Zoho'],
      question:
        "In a school, 60% of the students are boys and the rest are girls. If there are 480 girls, how many boys are there?",
      options: ['288', '640', '700', '720'],
      correctIndex: 3,
      steps: [
        { label: 'Given', text: 'Boys = 60% of total, Girls = 40% of total. Girls count = 480.' },
        { label: 'Concept', text: 'Use the known percentage-to-count pair (girls) to find the total, then apply the other percentage (boys) to that total.' },
        { label: 'Working', text: '40% of Total = 480 → Total = 480 × 100/40 = 1,200.' },
        { label: 'Answer', text: 'Boys = 60% of 1,200 = 720.' },
      ],
      commonTrap:
        "Treating the given 480 as if it were the TOTAL school strength and computing 60% of 480 = 288 — 480 is only the girls' count, not the whole school.",
      alternateMethod: {
        label: 'Ratio method',
        steps: [
          'Boys : Girls = 60 : 40 = 3 : 2.',
          'Girls (480) correspond to 2 parts, so 1 part = 240.',
          'Boys = 3 parts = 3 × 240 = 720.',
        ],
      },
    },
    {
      id: 'pct-11',
      difficulty: 'Hard',
      patternTags: ['TCS', 'Zoho'],
      question:
        "While measuring the side of a square, an error of 8% in excess is made. Find the percentage error in the calculated area of the square.",
      options: ['8%', '16%', '16.64%', '17%'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: 'Measured side is 8% more than the actual side. Area = side².' },
        { label: 'Concept', text: 'If a linear dimension has a % error of e (in excess), the resulting % error in an area (a squared quantity) is (1 + e/100)² − 1 — not simply 2e.' },
        { label: 'Working', text: '(1.08)² = 1.1664, so the error = 0.1664 = 16.64%.' },
        { label: 'Answer', text: 'The calculated area is in excess by 16.64%.' },
      ],
      commonTrap:
        "Doubling the linear error (8% × 2 = 16%) — close, but it misses the small e² cross-term (0.08 × 0.08 = 0.64%) that gets added because area is a product of two error-carrying lengths.",
      alternateMethod: {
        label: 'Assumed-value shortcut',
        steps: [
          'Let true side = 100 → measured side = 108.',
          'True area = 10,000. Measured area = 108 × 108 = 11,664.',
          'Error % = (11,664 − 10,000)/10,000 × 100 = 16.64%.',
        ],
      },
    },
    {
      id: 'pct-12',
      difficulty: 'Medium',
      patternTags: ['Wipro', 'Zoho'],
      question:
        "Two numbers are respectively 20% and 50% more than a third number. What percentage is the first number of the second?",
      options: ['70%', '75%', '80%', '83.33%'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: 'First = 120% of a third number, Second = 150% of the same third number.' },
        { label: 'Concept', text: 'To compare the First and Second numbers, express both relative to the same common base (the third number), then take their ratio.' },
        { label: 'Working', text: 'Let the third number = 100 → First = 120, Second = 150.' },
        { label: 'Answer', text: 'First as a % of Second = 120/150 × 100 = 80%.' },
      ],
      commonTrap:
        "Subtracting the two given percentages (50% − 20% = 30%) and guessing an answer near 70% — the relationship isn't additive, since both percentages describe a comparison to a common third base, not to each other.",
      alternateMethod: {
        label: 'Ratio method',
        steps: [
          'First : Second = 120 : 150 = 4 : 5.',
          'First is 4/5 of Second = 80%.',
        ],
      },
    },
    {
      id: 'pct-13',
      difficulty: 'Easy',
      patternTags: ['TCS', 'Infosys'],
      question:
        "After deducting a 15% commission, an agent remits ₹5,100 to his company. Find the amount of the original bill (before commission).",
      options: ['5,865', '5,900', '6,000', '6,100'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: 'Remitted amount = Bill − 15% commission = 85% of the bill = ₹5,100.' },
        { label: 'Concept', text: 'This is a reverse-percentage problem: divide the known amount by its percentage (as a decimal) to recover the original value — do not add the percentage back onto the given number.' },
        { label: 'Working', text: '0.85 × Bill = 5,100 → Bill = 5,100 / 0.85 = 6,000.' },
        { label: 'Answer', text: 'The original bill amount is ₹6,000.' },
      ],
      commonTrap:
        "Adding 15% of 5,100 back on (5,100 + 765 = 5,865) — this wrongly treats ₹5,100 as the base for the 15%, when the (larger) original bill is actually the correct base.",
      alternateMethod: {
        label: 'Unitary method',
        steps: [
          '85% of the bill = 5,100, so 1% = 5,100/85 = 60.',
          '100% of the bill = 60 × 100 = ₹6,000.',
        ],
      },
    },
    {
      id: 'pct-14',
      difficulty: 'Medium',
      patternTags: ['TCS', 'Infosys', 'Wipro'],
      question:
        "A's salary is first increased by 10%, and the new salary is then increased by a further 20%. What is the overall percentage increase in A's salary?",
      options: ['26%', '28%', '30%', '32%'],
      correctIndex: 3,
      steps: [
        { label: 'Given', text: 'Two successive increases: 10%, then 20% (on the already-increased amount).' },
        { label: 'Concept', text: 'Net % change for two successive increases a% and b% is a + b + (ab/100) — the cross-term exists because the second increase applies to a larger base.' },
        { label: 'Working', text: '10 + 20 + (10 × 20)/100 = 30 + 2 = 32%.' },
        { label: 'Answer', text: 'Overall increase = 32%.' },
      ],
      commonTrap:
        "Simply adding 10% + 20% = 30% — this ignores the compounding cross-term, since the second 20% increase is calculated on a salary that has already grown by 10%.",
      alternateMethod: {
        label: 'Assumed-value shortcut',
        steps: [
          'Let salary = 100. After +10% → 110. After +20% of 110 → 110 + 22 = 132.',
          'Overall increase = 132 − 100 = 32%.',
        ],
      },
    },
    {
      id: 'pct-15',
      difficulty: 'Easy',
      patternTags: ['TCS', 'Zoho'],
      question:
        "What percentage of 2 hours is 45 minutes?",
      options: ['35%', '37.5%', '40%', '42.5%'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: 'Compare 45 minutes to 2 hours.' },
        { label: 'Concept', text: 'Convert both quantities to the same unit before forming the percentage fraction — minutes and hours cannot be compared directly.' },
        { label: 'Working', text: '2 hours = 120 minutes. Percentage = (45/120) × 100.' },
        { label: 'Answer', text: '(45/120) × 100 = 37.5%.' },
      ],
      commonTrap:
        "Computing 45/2 = 22.5 and mistaking that for the percentage — the units don't match, since 2 was left in hours instead of being converted to 120 minutes first.",
      alternateMethod: {
        label: 'Fraction-simplification method',
        steps: [
          '45/120 simplifies to 3/8.',
          '3/8 as a percentage = 3/8 × 100 = 300/8 = 37.5%.',
        ],
      },
    },
    {
      id: 'pct-16',
      difficulty: 'Medium',
      patternTags: ['Infosys', 'Wipro'],
      question:
        "An item costs ₹1,500. A sales tax of 12% is added to it, and then a discount of 5% is given on the tax-included price. What is the final price?",
      options: ['1,580', '1,596', '1,600', '1,605'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: 'Base price ₹1,500, +12% tax, then −5% discount applied on the NEW (tax-included) price.' },
        { label: 'Concept', text: 'Apply successive percentage changes strictly in the order given, each one acting on the current (already-updated) value.' },
        { label: 'Working', text: '1,500 × 1.12 = 1,680. Then 1,680 × 0.95 = 1,596.' },
        { label: 'Answer', text: 'Final price = ₹1,596.' },
      ],
      commonTrap:
        "Applying the 5% discount to the original ₹1,500 instead of the tax-included ₹1,680 (1,500 × 1.12 − 1,500 × 0.05 = 1,680 − 75 = 1,605) — the order of operations matters here, since the discount is meant to apply to the already-taxed price.",
      alternateMethod: {
        label: 'Combined single multiplier',
        steps: [
          'Net multiplier = 1.12 × 0.95 = 1.064.',
          'Final price = 1,500 × 1.064 = ₹1,596 — one multiplication instead of two.',
        ],
      },
    },
    {
      id: 'pct-17',
      difficulty: 'Medium',
      patternTags: ['TCS', 'Zoho'],
      question:
        "In a village of 8,000 people, 65% are literate. Among the literate people, 40% are women. How many literate men are there?",
      options: ['3,000', '3,080', '3,120', '4,800'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: 'Total population = 8,000. Literate = 65% of total. Literate women = 40% of the LITERATE group.' },
        { label: 'Concept', text: 'Apply the two percentages sequentially: first find the literate subgroup, then split that subgroup (not the whole population) by the second percentage.' },
        { label: 'Working', text: 'Literate = 0.65 × 8,000 = 5,200. Literate women = 0.40 × 5,200 = 2,080. Literate men = 5,200 − 2,080 = 3,120.' },
        { label: 'Answer', text: '3,120 literate men.' },
      ],
      commonTrap:
        "Computing literate men as 60% of the TOTAL population (8,000 × 0.60 = 4,800) instead of 60% of the literate subgroup only — mixing up which base the second percentage applies to.",
      alternateMethod: {
        label: 'Direct combined percentage',
        steps: [
          'Literate men = 65% × 60% of the total population.',
          '0.65 × 0.60 × 8,000 = 0.39 × 8,000 = 3,120.',
        ],
      },
    },
    {
      id: 'pct-18',
      difficulty: 'Hard',
      patternTags: ['TCS', 'Infosys', 'Zoho'],
      question:
        "A retailer allows a discount of 12% on the marked price of an article and still makes a profit of 10%. If the cost price is ₹2,750, find the marked price.",
      options: ['3,355', '3,400', '3,437.5', '3,450'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: 'Cost price = ₹2,750, profit = 10% ⇒ Selling Price = 1.10 × CP. Discount = 12% on Marked Price ⇒ Selling Price = 0.88 × MP.' },
        { label: 'Concept', text: 'Chain the two percentage relations through the Selling Price, which is common to both: first find SP from CP, then reverse from SP to MP.' },
        { label: 'Working', text: 'SP = 2,750 × 1.10 = 3,025. Then MP = SP / 0.88 = 3,025 / 0.88.' },
        { label: 'Answer', text: 'MP = 3,025 / 0.88 = ₹3,437.50.' },
      ],
      commonTrap:
        "Adding the profit % and discount % directly onto the cost price (2,750 × 1.22 = 3,355) — profit is calculated on CP and discount is calculated on MP, two different bases that cannot simply be combined into one multiplier on CP.",
      alternateMethod: {
        label: 'Unitary method on MP',
        steps: [
          '88% of MP = 3,025, so 1% of MP = 3,025/88 = 34.375.',
          '100% of MP = 34.375 × 100 = ₹3,437.50.',
        ],
      },
    },
    {
      id: 'pct-19',
      difficulty: 'Medium',
      patternTags: ['Wipro', 'Zoho'],
      question:
        "If the price of sugar decreases by 20%, by what percentage should a person increase their consumption so as to keep their expenditure on sugar unchanged?",
      options: ['20%', '22%', '25%', '28%'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: 'Price decreases by 20%. Expenditure (Price × Consumption) must stay the same.' },
        { label: 'Concept', text: 'When price DECREASES by R%, the required INCREASE in consumption is [R / (100 − R)] × 100% — the mirror image of the price-increase formula, with (100 − R) in the denominator instead of (100 + R).' },
        { label: 'Working', text: 'R = 20 → required increase = 20 / (100 − 20) × 100 = 20/80 × 100 = 25%.' },
        { label: 'Answer', text: 'Consumption should be increased by 25%.' },
      ],
      commonTrap:
        "Using 20% as the answer directly, assuming the compensating increase equals the price decrease — but the base for the increase is the new, LOWER price, so the required percentage is always larger than the original decrease.",
      alternateMethod: {
        label: 'Assumed-value shortcut',
        steps: [
          'Let price = 100, consumption = 100 → expenditure = 10,000.',
          'New price = 80. To keep expenditure at 10,000: new consumption = 10,000/80 = 125.',
          'Increase = 125 − 100 = 25, i.e. a 25% increase.',
        ],
      },
    },
    {
      id: 'pct-20',
      difficulty: 'Medium',
      patternTags: ['TCS', 'Infosys'],
      question:
        "In an examination, 35% of students failed in Maths, 25% failed in English, and 10% failed in both subjects. What percentage of students passed in both subjects?",
      options: ['40%', '45%', '50%', '55%'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: 'Failed Maths = 35%, Failed English = 25%, Failed BOTH = 10%.' },
        { label: 'Concept', text: 'Use the inclusion-exclusion principle: Failed (at least one subject) = Failed(Maths) + Failed(English) − Failed(both), since students who failed both were counted twice in the simple sum.' },
        { label: 'Working', text: 'Failed at least one = 35 + 25 − 10 = 50%.' },
        { label: 'Answer', text: 'Passed both = 100 − 50 = 50%.' },
      ],
      commonTrap:
        "Adding 35% + 25% = 60% and subtracting from 100 to get 40% — this double-subtracts the 10% who failed both subjects, since they were already included in both the 35% and the 25%.",
      alternateMethod: {
        label: 'Venn-diagram split',
        steps: [
          'Failed ONLY Maths = 35 − 10 = 25%. Failed ONLY English = 25 − 10 = 15%. Failed BOTH = 10%.',
          'Total who failed at least one = 25 + 15 + 10 = 50%.',
          'Passed both = 100 − 50 = 50%.',
        ],
      },
    },
    {
      id: 'pct-21',
      difficulty: 'Hard',
      patternTags: ['TCS', 'Zoho'],
      question:
        "A man's salary was first increased by 25% and then decreased by 20%. If his salary is now ₹19,200, what was his original salary?",
      options: ['18,000', '19,200', '20,000', '20,211'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: 'Original salary → +25% → −20% → final salary of ₹19,200.' },
        { label: 'Concept', text: 'Find the overall net multiplier for the two successive changes: Net multiplier = (1 + 25/100) × (1 − 20/100).' },
        { label: 'Working', text: 'Net multiplier = 1.25 × 0.80 = 1.00 exactly — a net change of 0%.' },
        { label: 'Answer', text: 'Since the two changes exactly cancel out, the original salary equals the final salary: ₹19,200.' },
      ],
      commonTrap:
        "Assuming the two percentages simply subtract (25% − 20% = 5% net increase) and solving X × 1.05 = 19,200 — successive percentage changes multiply, they never just add or subtract.",
      alternateMethod: {
        label: 'Forward verification',
        steps: [
          'Try X = 19,200: after +25% → 19,200 × 1.25 = 24,000.',
          'After −20% → 24,000 × 0.80 = 19,200 — exactly matches the given final salary, confirming the net change really is zero.',
        ],
      },
    },
    {
      id: 'pct-22',
      difficulty: 'Medium',
      patternTags: ['Infosys', 'Wipro'],
      question:
        "A shop sells 60% of its items at a 10% profit and the remaining 40% at a 20% profit. Find the overall percentage profit on the total sales.",
      options: ['12%', '13%', '14%', '15%'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: '60% of items sold at 10% profit, 40% of items sold at 20% profit.' },
        { label: 'Concept', text: 'The overall profit % is a WEIGHTED average of the two rates, weighted by the proportion (60% and 40%) each group represents — not a simple average of 10% and 20%.' },
        { label: 'Working', text: 'Overall % = (0.60 × 10) + (0.40 × 20) = 6 + 8 = 14%.' },
        { label: 'Answer', text: 'Overall profit = 14%.' },
      ],
      commonTrap:
        "Taking a plain average of 10% and 20% to get 15% — this ignores that the two groups are of unequal size (60% vs. 40%), so they can't be weighted equally.",
      alternateMethod: {
        label: 'Assumed-value shortcut',
        steps: [
          'Let total cost = 100 → 60 units of cost sold at 10% profit gives revenue of 66. 40 units of cost sold at 20% profit gives revenue of 48.',
          'Total revenue = 66 + 48 = 114, on a total cost of 100 → overall profit = 14%.',
        ],
      },
    },
    {
      id: 'pct-23',
      difficulty: 'Hard',
      patternTags: ['TCS', 'Wipro', 'Zoho'],
      question:
        "The length of a rectangle is increased by 30% and its breadth is decreased by 20%. What is the percentage change in its area?",
      options: ['2% increase', '4% increase', '6% increase', '10% increase'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: 'Length +30%, Breadth −20%. Area = Length × Breadth.' },
        { label: 'Concept', text: 'Since area is a PRODUCT of two changing quantities, apply the successive-percentage-change formula to the two percentage changes together, just like two successive multipliers.' },
        { label: 'Working', text: 'Net % = 30 + (−20) + (30 × −20)/100 = 10 − 6 = 4%.' },
        { label: 'Answer', text: 'The area increases by 4%.' },
      ],
      commonTrap:
        "Simply subtracting 30% − 20% = 10% and stopping there — this misses the cross-term (−6%) that arises because BOTH dimensions are changing at the same time in a product.",
      alternateMethod: {
        label: 'Assumed-value shortcut',
        steps: [
          'Let length = 100, breadth = 100 → area = 10,000.',
          'New length = 130, new breadth = 80 → new area = 130 × 80 = 10,400.',
          'Change = 400/10,000 × 100 = 4% increase.',
        ],
      },
    },
    {
      id: 'pct-24',
      difficulty: 'Hard',
      patternTags: ['TCS', 'Infosys', 'Zoho'],
      question:
        "A machine depreciates in value by 10% every year. If its present value is ₹72,900, what was its value 3 years ago?",
      options: ['98,000', '1,00,000', '1,04,143', '1,08,000'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: 'Present value = ₹72,900, after 3 years of 10% annual depreciation (compounding).' },
        { label: 'Concept', text: 'Value 3 years ago × (1 − r)³ = present value, because each year’s 10% depreciation applies to the already-reduced value from the year before.' },
        { label: 'Working', text: 'X × (0.90)³ = 72,900 → X × 0.729 = 72,900.' },
        { label: 'Answer', text: 'X = 72,900 / 0.729 = ₹1,00,000.' },
      ],
      commonTrap:
        "Dividing by (1 − 0.30) = 0.70, treating 3 years of 10% depreciation as one flat 30% — compounding means each year's 10% is a smaller absolute amount than the last, so the true combined factor (0.729) is never the same as a flat 30%.",
      alternateMethod: {
        label: 'Forward verification',
        steps: [
          'Try X = 1,00,000: Year 1 → 1,00,000 × 0.9 = 90,000. Year 2 → 90,000 × 0.9 = 81,000. Year 3 → 81,000 × 0.9 = 72,900 — matches exactly.',
        ],
      },
    },
    {
      id: 'pct-25',
      difficulty: 'Easy',
      patternTags: ['TCS', 'Zoho'],
      question:
        "By what percent is 5/6 greater than 3/4?",
      options: ['8.33%', '10%', '11.11%', '12.5%'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: 'Compare the fractions 5/6 and 3/4.' },
        { label: 'Concept', text: '"By what percent is A greater than B" always uses the SMALLER value B as the base: % more = [(A − B)/B] × 100.' },
        { label: 'Working', text: '5/6 − 3/4 = 10/12 − 9/12 = 1/12. Percentage = (1/12) ÷ (3/4) × 100.' },
        { label: 'Answer', text: '(1/12 × 4/3) × 100 = (1/9) × 100 = 11.11%.' },
      ],
      commonTrap:
        "Dividing the difference by 5/6 instead of 3/4 (using the LARGER number as the base): (1/12)/(5/6) × 100 = 10% — a plausible-looking but incorrect base for a '% more than' comparison.",
      alternateMethod: {
        label: 'Decimal method',
        steps: [
          '5/6 = 0.8333, 3/4 = 0.75.',
          '% more = (0.8333 − 0.75)/0.75 × 100 = 0.0833/0.75 × 100 = 11.11%.',
        ],
      },
    },
  ],
  'time-work': [
    {
      id: 'tw-01',
      difficulty: 'Easy',
      patternTags: ['TCS', 'Infosys'],
      question:
        "A can complete a piece of work in 12 days and B can complete the same work in 18 days. In how many days can they complete the work together?",
      options: ['6.5 days', '7 days', '7.2 days', '8 days'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: 'A alone = 12 days, B alone = 18 days.' },
        { label: 'Concept', text: 'Work RATES add when two people work together: combined rate = 1/A + 1/B. Time taken = 1 ÷ combined rate.' },
        { label: 'Working', text: '1/12 + 1/18 = 3/36 + 2/36 = 5/36 of the work per day.' },
        { label: 'Answer', text: 'Time together = 36/5 = 7.2 days.' },
      ],
      commonTrap:
        "Averaging the two given times ((12+18)/2 = 15 days) — work RATES add, not the number of days; averaging days is never mathematically valid here.",
      alternateMethod: {
        label: 'LCM (total-work-units) method',
        steps: [
          'Let total work = LCM(12, 18) = 36 units.',
          "A's rate = 36/12 = 3 units/day. B's rate = 36/18 = 2 units/day.",
          'Combined rate = 5 units/day → Time = 36/5 = 7.2 days.',
        ],
      },
    },
    {
      id: 'tw-02',
      difficulty: 'Medium',
      patternTags: ['TCS', 'Wipro'],
      question:
        "A is twice as efficient as B. If B can complete a work in 24 days, in how many days can A and B together complete the work?",
      options: ['6 days', '8 days', '9 days', '12 days'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: 'A is twice as efficient as B. B alone takes 24 days.' },
        { label: 'Concept', text: 'Efficiency and time are inversely related: if A is twice as efficient as B, A takes HALF the time B does.' },
        { label: 'Working', text: 'A alone = 24/2 = 12 days → rate 1/12. B rate = 1/24. Combined = 1/12 + 1/24 = 3/24 = 1/8 per day.' },
        { label: 'Answer', text: 'Time together = 8 days.' },
      ],
      commonTrap:
        "Confusing 'twice as efficient' with 'takes twice as long' — reversing the relationship would (incorrectly) push A's individual time to 48 days, giving a combined time far off from the correct 8 days.",
      alternateMethod: {
        label: 'Efficiency-ratio method',
        steps: [
          'Efficiency ratio A : B = 2 : 1, so daily-work ratio A : B = 2 : 1.',
          'Total work = 24 units (since B does 1 unit/day for 24 days). A does 2 units/day.',
          'Combined = 3 units/day → Time = 24/3 = 8 days.',
        ],
      },
    },
    {
      id: 'tw-03',
      difficulty: 'Easy',
      patternTags: ['Infosys', 'Zoho'],
      question:
        "A, B and C can complete a work in 10, 15 and 30 days respectively. In how many days can they complete it working all three together?",
      options: ['4 days', '5 days', '6 days', '7.5 days'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: 'A alone = 10 days, B alone = 15 days, C alone = 30 days.' },
        { label: 'Concept', text: 'Add all three individual rates to get the combined rate.' },
        { label: 'Working', text: '1/10 + 1/15 + 1/30 = 3/30 + 2/30 + 1/30 = 6/30 = 1/5 per day.' },
        { label: 'Answer', text: 'Time together = 5 days.' },
      ],
      commonTrap:
        "Averaging the three times ((10+15+30)/3 = 18.33 days) — with three workers involved, this error compounds even further from the correct answer than it would with just two.",
      alternateMethod: {
        label: 'LCM method',
        steps: [
          'Total work = 30 units. A = 3 units/day, B = 2 units/day, C = 1 unit/day.',
          'Combined = 6 units/day → Time = 30/6 = 5 days.',
        ],
      },
    },
    {
      id: 'tw-04',
      difficulty: 'Medium',
      patternTags: ['TCS', 'Zoho'],
      question:
        "A can do a piece of work in 15 days. He works at it for 5 days, and then B finishes the remaining work in 12 days. In how many days can B alone complete the whole work?",
      options: ['15 days', '16 days', '18 days', '20 days'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: 'A alone = 15 days. A works for 5 days, then B finishes the REMAINING work in 12 days.' },
        { label: 'Concept', text: "Find the fraction of work A completed, subtract from 1 to get B's share, then scale B's given time up to what it would take for the WHOLE work." },
        { label: 'Working', text: "A's rate = 1/15/day. In 5 days, A does 5/15 = 1/3 of the work. Remaining = 2/3, done by B in 12 days." },
        { label: 'Answer', text: "B's rate = (2/3)/12 = 1/18/day, so B alone would take 18 days for the whole work." },
      ],
      commonTrap:
        "Assuming B's 12 days is for the WHOLE work and answering 12 days directly — but B's 12 days only covers the remaining 2/3, not the entire job.",
      alternateMethod: {
        label: 'Total-work-units method',
        steps: [
          'Let total work = 45 units (a multiple of 15). A\'s rate = 3 units/day.',
          'In 5 days, A does 15 units. Remaining = 30 units, done by B in 12 days.',
          "B's rate = 30/12 = 2.5 units/day → B's time for 45 units = 45/2.5 = 18 days.",
        ],
      },
    },
    {
      id: 'tw-05',
      difficulty: 'Easy',
      patternTags: ['Wipro', 'Infosys'],
      question:
        "Pipe A can fill a tank in 6 hours and Pipe B can fill it in 8 hours. If both pipes are opened together, how long will it take to fill the tank?",
      options: ['3 hours', '3.43 hours', '4 hours', '4.5 hours'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: 'Pipe A alone fills the tank in 6 hours, Pipe B alone in 8 hours.' },
        { label: 'Concept', text: 'A filling pipe behaves exactly like a worker — add the two filling rates to get the combined rate.' },
        { label: 'Working', text: '1/6 + 1/8 = 4/24 + 3/24 = 7/24 of the tank per hour.' },
        { label: 'Answer', text: 'Time together = 24/7 ≈ 3.43 hours (3 hours 26 minutes).' },
      ],
      commonTrap:
        "Averaging 6 and 8 to guess 7 hours — this is slower than either pipe working alone, which cannot be correct once a second pipe is added to help.",
      alternateMethod: {
        label: 'LCM method',
        steps: [
          'Let tank capacity = 24 units. A fills 4 units/hr, B fills 3 units/hr.',
          'Combined = 7 units/hr → Time = 24/7 ≈ 3.43 hours.',
        ],
      },
    },
    {
      id: 'tw-06',
      difficulty: 'Medium',
      patternTags: ['TCS', 'Zoho'],
      question:
        "An inlet pipe can fill a tank in 10 hours, while an outlet pipe can empty the full tank in 15 hours. If both pipes are opened together, in how many hours will the tank be filled?",
      options: ['20 hours', '25 hours', '30 hours', '35 hours'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: 'Inlet fills in 10 hours, outlet empties in 15 hours, both opened together.' },
        { label: 'Concept', text: 'The outlet works AGAINST the inlet — its rate must be subtracted from the inlet\'s rate, not added.' },
        { label: 'Working', text: 'Net rate = 1/10 − 1/15 = 3/30 − 2/30 = 1/30 of the tank per hour.' },
        { label: 'Answer', text: 'Time to fill = 30 hours.' },
      ],
      commonTrap:
        "Adding the two rates instead of subtracting (1/10 + 1/15 = 1/6 → 6 hours) — this forgets that the outlet drains the tank rather than filling it.",
      alternateMethod: {
        label: 'LCM method',
        steps: [
          'Let tank = 30 units. Inlet fills 3 units/hr, outlet empties 2 units/hr.',
          'Net rate = 3 − 2 = 1 unit/hr → Time = 30/1 = 30 hours.',
        ],
      },
    },
    {
      id: 'tw-07',
      difficulty: 'Hard',
      patternTags: ['TCS', 'Infosys', 'Zoho'],
      question:
        "A and B undertake to do a piece of work for ₹600. A alone can do the work in 6 days and B alone in 8 days. With the help of C, they finish the work in 3 days. How much should C be paid?",
      options: ['₹50', '₹60', '₹75', '₹90'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: 'Total payment = ₹600. A alone = 6 days, B alone = 8 days. A + B + C together finish in 3 days.' },
        { label: 'Concept', text: 'Wages must be split in proportion to the actual WORK each person contributes, found from their work-rate share of the job — not by time spent or an equal split.' },
        { label: 'Working', text: "In 3 days: A does 3/6 = 1/2 of the work, B does 3/8 of the work. C's share = 1 − 1/2 − 3/8 = 1/8 of the work." },
        { label: 'Answer', text: "C's pay = (1/8) × 600 = ₹75." },
      ],
      commonTrap:
        "Splitting ₹600 equally among the three workers (₹200 each) — payment must reflect actual work contributed, and C clearly contributed the smallest share since A and B alone were already fast.",
      alternateMethod: {
        label: 'Daily-rate ratio method',
        steps: [
          "C's daily rate = 1/3 − 1/6 − 1/8 = 8/24 − 4/24 − 3/24 = 1/24.",
          'Ratio of daily rates A : B : C = (1/6) : (1/8) : (1/24) = 4 : 3 : 1 (multiplying through by 24).',
          "Total parts = 8. C's share = 1/8 × 600 = ₹75.",
        ],
      },
    },
    {
      id: 'tw-08',
      difficulty: 'Easy',
      patternTags: ['TCS', 'Wipro'],
      question:
        "12 men can complete a piece of work in 18 days. In how many days can 8 men complete the same work?",
      options: ['24 days', '27 days', '30 days', '36 days'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: '12 men complete a fixed amount of work in 18 days.' },
        { label: 'Concept', text: 'For a fixed amount of work, men and days are inversely proportional: M1 × D1 = M2 × D2.' },
        { label: 'Working', text: '12 × 18 = 216 man-days of work in total.' },
        { label: 'Answer', text: 'With 8 men: Days = 216/8 = 27 days.' },
      ],
      commonTrap:
        "Using a direct (rather than inverse) proportion — e.g. computing 18 × 8/12 = 12 days — but FEWER workers must take MORE days to finish the same fixed job, never fewer.",
      alternateMethod: {
        label: 'Unitary method',
        steps: [
          '1 man alone would take 12 × 18 = 216 days to do the whole job.',
          '8 men together take 216/8 = 27 days.',
        ],
      },
    },
    {
      id: 'tw-09',
      difficulty: 'Medium',
      patternTags: ['Infosys', 'Zoho'],
      question:
        "15 workers can build a wall in 20 days. How many workers are needed to build a similar wall — twice as large — in 25 days?",
      options: ['18', '20', '24', '30'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: '15 workers, 20 days → 1 wall. Need: 25 days → 2 (similar) walls.' },
        { label: 'Concept', text: 'Use the general work-equivalence formula (M1×D1)/W1 = (M2×D2)/W2, where W is the amount of work — here, the number of walls.' },
        { label: 'Working', text: '(15 × 20)/1 = (M2 × 25)/2 → 300 = 12.5 × M2.' },
        { label: 'Answer', text: 'M2 = 300/12.5 = 24 workers.' },
      ],
      commonTrap:
        "Ignoring that the new wall is TWICE as large and solving as if W1 = W2 = 1, giving 15×20/25 = 12 workers — exactly half the correct answer, since it forgets to account for the doubled work.",
      alternateMethod: {
        label: 'Two-step scaling',
        steps: [
          'For ONE wall in 25 days: workers needed = 15 × 20/25 = 12.',
          'Since the new wall is twice as large, double the workers: 12 × 2 = 24 workers.',
        ],
      },
    },
    {
      id: 'tw-10',
      difficulty: 'Hard',
      patternTags: ['TCS', 'Zoho'],
      question:
        "A can do a piece of work in 20 days and B in 30 days. They work on alternate days, starting with A on day 1. In how many days will the work be completed?",
      options: ['22 days', '23 days', '24 days', '25 days'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: 'A alone = 20 days, B alone = 30 days. They alternate days, A going first.' },
        { label: 'Concept', text: 'Track progress in 2-day cycles (A\'s rate + B\'s rate), then switch to day-by-day tracking once the work is close to finishing, since the last cycle is often incomplete.' },
        { label: 'Working', text: 'Per 2-day cycle: 1/20 + 1/30 = 1/12 of the work. After 11 cycles (22 days): 11/12 done. Day 23 (A\'s turn): + 1/20 = 3/60, cumulative = 55/60 + 3/60 = 58/60. Remaining = 1/30.' },
        { label: 'Answer', text: "Day 24 (B's turn): B's rate is exactly 1/30/day, which finishes the remaining work — total time = 24 days." },
      ],
      commonTrap:
        "Using the combined non-alternating rate (1/20 + 1/30 = 1/12 → 12 days) as if both worked EVERY day together — that figure is for simultaneous work, not alternating turns.",
      alternateMethod: {
        label: 'Day-by-day ledger',
        steps: [
          'List cumulative work after each day: Day 1 → 1/20, Day 2 → 1/20+1/30 = 1/12, Day 3 → 1/12+1/20, and so on.',
          'Continuing this running total confirms the work reaches exactly 1 (complete) at the end of Day 24 — a manual check that avoids the cycle shortcut entirely.',
        ],
      },
    },
    {
      id: 'tw-11',
      difficulty: 'Medium',
      patternTags: ['Infosys', 'Wipro'],
      question:
        "A and B together can complete a work in 12 days. They work together for 4 days, after which A leaves. B alone finishes the remaining work in 16 more days. In how many days can B alone complete the entire work?",
      options: ['18 days', '20 days', '24 days', '28 days'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: 'A + B combined = 12 days. They work together for 4 days, then B alone finishes the REST in 16 days.' },
        { label: 'Concept', text: "Use the combined rate to find the work done in the shared period, subtract for the remainder, then derive B's individual rate from that remainder." },
        { label: 'Working', text: 'Work done together in 4 days = 4/12 = 1/3. Remaining = 2/3, completed by B alone in 16 days.' },
        { label: 'Answer', text: "B's rate = (2/3)/16 = 1/24/day, so B alone would take 24 days for the whole work." },
      ],
      commonTrap:
        "Assuming B's 16 days is for the FULL work and answering 16 days directly — it's actually only the time B needed for the remaining 2/3 of the job.",
      alternateMethod: {
        label: 'Total-work-units method',
        steps: [
          'Let total work = 24 units (a multiple of 12). Combined rate = 2 units/day.',
          'In 4 days: 8 units done. Remaining = 16 units, done by B alone in 16 days.',
          "B's rate = 16/16 = 1 unit/day → B's time for all 24 units = 24 days.",
        ],
      },
    },
    {
      id: 'tw-12',
      difficulty: 'Hard',
      patternTags: ['TCS', 'Zoho'],
      question:
        "4 men and 6 women can complete a work in 8 days, while 3 men and 7 women can complete it in 10 days. In how many days will 10 women alone complete the work?",
      options: ['32 days', '36 days', '40 days', '45 days'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: '(4 men + 6 women) finish in 8 days. (3 men + 7 women) finish in 10 days.' },
        { label: 'Concept', text: 'Let a man\'s daily rate = m and a woman\'s = w. Set up two equations using "rate × time = 1 whole job", then eliminate one variable.' },
        { label: 'Working', text: '4m + 6w = 1/8 ...(i); 3m + 7w = 1/10 ...(ii). Multiply (i) by 3 and (ii) by 4: 12m+18w = 3/8, 12m+28w = 2/5. Subtracting: 10w = 2/5 − 3/8 = 16/40 − 15/40 = 1/40.' },
        { label: 'Answer', text: 'w = 1/400. 10 women\'s combined rate = 10 × 1/400 = 1/40/day → Time = 40 days.' },
      ],
      commonTrap:
        "Trying to average or directly substitute between the two given day-counts without properly eliminating a variable first — with two different unknowns (man-rate and woman-rate), a single equation can never isolate either one on its own.",
      alternateMethod: {
        label: 'Eliminate for men first',
        steps: [
          'Multiply (i) by 7 and (ii) by 6: 28m+42w = 7/8, 18m+42w = 3/5.',
          'Subtracting: 10m = 7/8 − 3/5 = 35/40 − 24/40 = 11/40 → m = 11/400.',
          'Substituting back into (i): 44/400 + 6w = 50/400 → w = 1/400 — the same value, confirming 10 women take 40 days.',
        ],
      },
    },
    {
      id: 'tw-13',
      difficulty: 'Medium',
      patternTags: ['TCS', 'Infosys'],
      question:
        "Two pipes A and B can fill a tank in 12 minutes and 15 minutes respectively. A third pipe C can empty the full tank in 20 minutes. If all three pipes are opened together on an empty tank, how long will it take to fill it?",
      options: ['8 minutes', '9 minutes', '10 minutes', '12 minutes'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: 'A fills in 12 min, B fills in 15 min, C empties in 20 min — all opened together.' },
        { label: 'Concept', text: 'Add the filling rates (A, B) and SUBTRACT the emptying rate (C) to get the net rate.' },
        { label: 'Working', text: '1/12 + 1/15 − 1/20 = 5/60 + 4/60 − 3/60 = 6/60 = 1/10 of the tank per minute.' },
        { label: 'Answer', text: 'Time to fill = 10 minutes.' },
      ],
      commonTrap:
        "Adding all three rates as if C also fills the tank (1/12+1/15+1/20 = 1/5 → 5 minutes) — this forgets that the outlet pipe works against the other two.",
      alternateMethod: {
        label: 'LCM method',
        steps: [
          'Let tank = 60 units. A fills 5 units/min, B fills 4 units/min, C empties 3 units/min.',
          'Net rate = 5 + 4 − 3 = 6 units/min → Time = 60/6 = 10 minutes.',
        ],
      },
    },
    {
      id: 'tw-14',
      difficulty: 'Hard',
      patternTags: ['TCS', 'Wipro', 'Zoho'],
      question:
        "A and B together can finish a work in 15 days. B and C together can finish it in 20 days. C and A together can finish it in 12 days. In how many days can A alone finish the work?",
      options: ['15 days', '18 days', '20 days', '24 days'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: '(A+B) = 15 days, (B+C) = 20 days, (C+A) = 12 days, as combined rates.' },
        { label: 'Concept', text: 'Add all three pairwise equations together — since each individual rate then appears exactly twice, dividing the sum by 2 gives the sum of all three individual rates.' },
        { label: 'Working', text: '2(a+b+c) = 1/15 + 1/20 + 1/12 = 4/60 + 3/60 + 5/60 = 12/60 = 1/5, so a+b+c = 1/10.' },
        { label: 'Answer', text: "A's rate = (a+b+c) − (b+c) = 1/10 − 1/20 = 1/20, so A alone takes 20 days." },
      ],
      commonTrap:
        "Trying to isolate A using only the two equations that directly mention A — that still leaves two equations with three unknowns, which alone can never pin down A's individual rate.",
      alternateMethod: {
        label: 'Solve the full system',
        steps: [
          'From a+b+c = 1/10: b = 1/10 − (c+a) = 1/10 − 1/12 = 1/60. c = 1/10 − (a+b) = 1/10 − 1/15 = 1/30.',
          'a = 1/10 − 1/20 = 1/20 (matches). Check: a+b = 1/20+1/60 = 1/15 ✓ matches the given figure.',
          'A alone = 20 days, now confirmed by solving for all three workers, not just A.',
        ],
      },
    },
    {
      id: 'tw-15',
      difficulty: 'Hard',
      patternTags: ['TCS', 'Infosys', 'Zoho'],
      question:
        "A contractor undertakes to complete a work in 40 days and employs 100 men. After 35 days, he finds that only 2/3 of the work is done. How many more men should he employ so the work finishes on time?",
      options: ['200', '225', '250', '300'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: 'Planned: 40 days, 100 men. After 35 days, actual work done = 2/3 (behind the original plan).' },
        { label: 'Concept', text: 'Use the ACTUAL observed progress to find the true man-days required for the whole job (replacing the original estimate), then work out the men needed for the remaining work in the remaining time.' },
        { label: 'Working', text: 'Man-days used = 100 × 35 = 3,500 for 2/3 of the work → man-days for the FULL work = 3,500 × 3/2 = 5,250. Remaining work = 1/3 → remaining man-days = 5,250/3 = 1,750.' },
        { label: 'Answer', text: 'Remaining time = 40 − 35 = 5 days → men required = 1,750/5 = 350. Additional men needed = 350 − 100 = 250.' },
      ],
      commonTrap:
        "Assuming the ORIGINAL plan (100 men finishing in 40 days) is still valid for the remaining work, and using the original total of 100×40 = 4,000 man-days instead of revising the estimate using the ACTUAL rate observed in the first 35 days.",
      alternateMethod: {
        label: 'Direct rate-ratio method',
        steps: [
          'Men required = 100 × [(1/3)/5] ÷ [(2/3)/35].',
          '= 100 × (35/3 ÷ 10/3) = 100 × (35/10) = 350 men in total.',
          'Additional men needed = 350 − 100 = 250.',
        ],
      },
    },
    {
      id: 'tw-16',
      difficulty: 'Medium',
      patternTags: ['Infosys', 'Wipro'],
      question:
        "20 men can complete a work in 20 days. After 5 days, 5 men leave the job. In how many more days will the remaining men complete the remaining work?",
      options: ['15 days', '18 days', '20 days', '24 days'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: '20 men, planned 20 days in total. After 5 days, 5 men leave, so 15 men remain.' },
        { label: 'Concept', text: 'Convert to total man-days (fixed for the whole job), subtract the man-days already completed, then divide the remainder by the new (smaller) workforce.' },
        { label: 'Working', text: 'Total man-days = 20 × 20 = 400. Man-days done in 5 days = 20 × 5 = 100. Remaining = 400 − 100 = 300 man-days, with 15 men left.' },
        { label: 'Answer', text: 'Days needed = 300/15 = 20 more days.' },
      ],
      commonTrap:
        "Just subtracting elapsed days from the original total (20 − 5 = 15 days) without adjusting at all for the reduced workforce — but fewer men working means the remaining work must take LONGER than the original plan allowed, not the same.",
      alternateMethod: {
        label: 'Rate-based method',
        steps: [
          "Original rate with 20 men = 1/20/day. Work done in 5 days = 5/20 = 1/4. Remaining = 3/4.",
          'New rate with 15 men = (15/20) × (1/20) = 3/80/day.',
          'Days = (3/4) ÷ (3/80) = (3/4) × (80/3) = 20 days.',
        ],
      },
    },
    {
      id: 'tw-17',
      difficulty: 'Hard',
      patternTags: ['TCS', 'Zoho'],
      question:
        "If 6 boys and 8 girls can do a piece of work in 10 days, while 26 boys and 48 girls can do the same work in 2 days, how many days will 15 boys and 20 girls take to do the work?",
      options: ['3 days', '4 days', '5 days', '6 days'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: '(6 boys + 8 girls) finish in 10 days. (26 boys + 48 girls) finish in 2 days.' },
        { label: 'Concept', text: "Set up two simultaneous equations in a boy's rate (b) and a girl's rate (g), since boys and girls have different individual efficiencies." },
        { label: 'Working', text: '6b + 8g = 1/10 ...(i); 26b + 48g = 1/2 ...(ii). Multiply (i) by 6: 36b+48g = 3/5. Subtracting (ii): 10b = 3/5 − 1/2 = 1/10 → b = 1/100.' },
        { label: 'Answer', text: 'From (i): 8g = 1/10 − 6/100 = 4/100 → g = 1/200. So 15 boys + 20 girls rate = 15/100 + 20/200 = 0.25/day → Time = 4 days.' },
      ],
      commonTrap:
        "Treating boys and girls as equally efficient workers (adding 6+8=14 total 'workers') and using a plain men-days-style proportion — that erases the entire reason two separate equations were given in the first place.",
      alternateMethod: {
        label: "Eliminate for girl's rate first",
        steps: [
          'Multiply (i) by 26 and (ii) by 6: 156b+208g = 2.6, 156b+288g = 3.0.',
          'Subtracting: 80g = 0.4 → g = 0.005 = 1/200.',
          'Substitute into (i): 6b + 8(0.005) = 0.1 → 6b = 0.06 → b = 0.01 = 1/100 — same rates, confirming 4 days.',
        ],
      },
    },
    {
      id: 'tw-18',
      difficulty: 'Hard',
      patternTags: ['TCS', 'Infosys', 'Wipro'],
      question:
        "A can complete a work in 12 days and B in 15 days. A starts the work alone, and after some days B joins him; together they finish the remaining work. If the entire work took 8 days from start to finish, for how many days did A work alone before B joined?",
      options: ['2 days', '3 days', '4 days', '5 days'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: 'A alone = 12 days, B alone = 15 days. A works alone for x days, then both work together for the rest of the 8-day total.' },
        { label: 'Concept', text: "Split the 8-day timeline into 'A alone' (x days) and 'A+B together' ((8−x) days), and add the work fractions from each phase to equal 1 whole job." },
        { label: 'Working', text: 'Combined rate = 1/12 + 1/15 = 3/20. Equation: x/12 + (8−x)(3/20) = 1. Multiplying by 60: 5x + 9(8−x) = 60 → 5x + 72 − 9x = 60.' },
        { label: 'Answer', text: '−4x = −12 → x = 3. A worked alone for 3 days, then B joined for the remaining 5 days.' },
      ],
      commonTrap:
        "Guessing that A worked alone for exactly half the total time (4 days) without setting up the actual work-fraction equation — a plausible-looking guess, but not the value that actually satisfies the given 8-day total.",
      alternateMethod: {
        label: 'Backward-check method',
        steps: [
          'If A works alone for 3 days: 3/12 = 1/4 of the work is done.',
          'The remaining 5 days at the combined rate of 3/20/day gives 5 × 3/20 = 15/20 = 3/4.',
          'Total = 1/4 + 3/4 = 1 (the whole work) — confirming x = 3 satisfies the 8-day timeline exactly.',
        ],
      },
    },
    {
      id: 'tw-19',
      difficulty: 'Medium',
      patternTags: ['TCS', 'Zoho'],
      question:
        "A pipe can fill a cistern in 12 hours. Because of a leak in the bottom, it takes 4 hours more to fill the cistern. If the cistern is full, in how many hours will the leak alone empty it?",
      options: ['36 hours', '42 hours', '48 hours', '54 hours'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: 'Pipe alone fills in 12 hours. With the leak present, it takes 16 hours (4 more).' },
        { label: 'Concept', text: "The leak's own rate is the DIFFERENCE between the pipe's fill-rate and the reduced effective rate observed with the leak — not simply the extra 4 hours." },
        { label: 'Working', text: "Effective rate with leak = 1/16/hr. Leak's rate = 1/12 − 1/16 = 4/48 − 3/48 = 1/48/hr." },
        { label: 'Answer', text: 'The leak alone would empty the full cistern in 48 hours.' },
      ],
      commonTrap:
        "Simply adding 12 + 4 = 16 and calling that the leak's own emptying time — 16 hours is the PIPE-AND-LEAK combined time, not the leak's solo time, which needs the rate-subtraction step.",
      alternateMethod: {
        label: 'LCM method',
        steps: [
          'Let cistern = 48 units. Pipe fills 4 units/hr (48/12). With the leak, net rate = 48/16 = 3 units/hr.',
          "Leak's draining rate = 4 − 3 = 1 unit/hr → leak alone takes 48/1 = 48 hours.",
        ],
      },
    },
    {
      id: 'tw-20',
      difficulty: 'Hard',
      patternTags: ['Infosys', 'Wipro'],
      question:
        "A can complete a job in 10 days. He works alone for 2 days at his normal efficiency, after which his efficiency increases by 25% for the rest of the job. In how many total days will the job be completed?",
      options: ['7.5 days', '8 days', '8.4 days', '9 days'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: "A's normal rate completes the job in 10 days. He works 2 days normally, then his efficiency rises by 25% for the remainder." },
        { label: 'Concept', text: 'Split into two phases with different rates: find the work remaining after phase 1, then use the NEW (increased) rate for phase 2.' },
        { label: 'Working', text: 'Phase 1 (2 days at 1/10/day): work done = 2/10 = 1/5. Remaining = 4/5. Phase 2 rate = 1.25 × 1/10 = 1/8/day.' },
        { label: 'Answer', text: 'Phase 2 time = (4/5) ÷ (1/8) = 32/5 = 6.4 days. Total = 2 + 6.4 = 8.4 days.' },
      ],
      commonTrap:
        "Applying the 25% efficiency boost to the TOTAL original time (10 ÷ 1.25 = 8 days) and treating that as the final answer — this ignores that the first 2 days already happened at the OLD, slower rate.",
      alternateMethod: {
        label: 'Combined-time method',
        steps: [
          'At the OLD rate, the remaining 4/5 of work would take 8 days (4/5 × 10).',
          'At 25% higher efficiency, that same phase now takes 8/1.25 = 6.4 days.',
          'Total = 2 (already spent) + 6.4 = 8.4 days.',
        ],
      },
    },
    {
      id: 'tw-21',
      difficulty: 'Easy',
      patternTags: ['TCS', 'Infosys'],
      question:
        "If 8 workers can dig a trench in 15 days, how many workers are needed to dig the same trench in 6 days?",
      options: ['12', '16', '20', '24'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: '8 workers complete a fixed trench-digging job in 15 days.' },
        { label: 'Concept', text: 'For a fixed amount of work, workers and days are inversely proportional: M1 × D1 = M2 × D2.' },
        { label: 'Working', text: '8 × 15 = 120 worker-days of work in total.' },
        { label: 'Answer', text: 'For 6 days: workers needed = 120/6 = 20.' },
      ],
      commonTrap:
        "Using a direct (rather than inverse) proportion — e.g. 8 × 6/15 = 3.2 workers — but finishing FASTER requires MORE workers, never fewer.",
      alternateMethod: {
        label: 'Unitary method',
        steps: [
          '1 worker alone would take 8 × 15 = 120 days.',
          'For 6 days, workers needed = 120/6 = 20.',
        ],
      },
    },
    {
      id: 'tw-22',
      difficulty: 'Hard',
      patternTags: ['TCS', 'Zoho'],
      question:
        "A is thrice as fast as B and takes 40 days less than B to complete a piece of work. In how many days can A and B together complete the work?",
      options: ['12 days', '15 days', '18 days', '20 days'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: "A is 3× as fast as B (so A's time = B's time ÷ 3), and A takes 40 fewer days than B." },
        { label: 'Concept', text: 'Translate "thrice as fast" into a time ratio first (A : B = 1 : 3), then use the "40 days less" fact to solve for the actual number of days.' },
        { label: 'Working', text: 'Let B = x days ⇒ A = x/3 days. Given: x − x/3 = 40 → 2x/3 = 40 → x = 60.' },
        { label: 'Answer', text: 'B = 60 days, A = 20 days. Combined rate = 1/20 + 1/60 = 4/60 = 1/15 → together = 15 days.' },
      ],
      commonTrap:
        "Misreading 'thrice as fast' as 'takes 3 more days', or applying the 40-day difference before establishing the 3:1 time ratio — either mistake leads to an inconsistent or unsolvable setup.",
      alternateMethod: {
        label: 'Ratio-parts method',
        steps: [
          'Time ratio A : B = 1 : 3 (since A is thrice as fast).',
          'The difference (3 parts − 1 part = 2 parts) corresponds to the given 40 days → 1 part = 20 days.',
          'So A = 20 days (1 part), B = 60 days (3 parts). Combined rate = 1/20 + 1/60 = 1/15 → 15 days together.',
        ],
      },
    },
    {
      id: 'tw-23',
      difficulty: 'Easy',
      patternTags: ['Infosys', 'Wipro'],
      question:
        "A can do 1/3 of a piece of work in 5 days. In how many days can A complete the entire work?",
      options: ['10 days', '12 days', '15 days', '18 days'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: 'A completes 1/3 of the work in 5 days.' },
        { label: 'Concept', text: "Find A's daily rate from the given fraction and time, then take its reciprocal for the time needed for the full (whole) job." },
        { label: 'Working', text: "A's rate = (1/3) ÷ 5 = 1/15 of the work per day." },
        { label: 'Answer', text: 'Time for the entire work = 15 days.' },
      ],
      commonTrap:
        "Dividing 5 by 3 instead of multiplying (5/3 ≈ 1.67 days) — since 1/3 of the work already took 5 days, completing THREE times as much work must take MORE days, not fewer.",
      alternateMethod: {
        label: 'Direct proportion',
        steps: [
          'Work completed is directly proportional to days, at a constant rate.',
          '1/3 of the work → 5 days, so the full (3/3) work → 5 × 3 = 15 days.',
        ],
      },
    },
    {
      id: 'tw-24',
      difficulty: 'Hard',
      patternTags: ['TCS', 'Zoho'],
      question:
        "A and B together can complete a work in 6 days. A alone would take 5 days more than B alone to complete the work. Find the number of days B alone would take.",
      options: ['8 days', '10 days', '12 days', '15 days'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: 'A + B combined = 6 days. A alone = (B alone) + 5 days.' },
        { label: 'Concept', text: "Let B's time = x. Express A's time as (x + 5), then set up the combined-rate equation — this leads to a quadratic in x." },
        { label: 'Working', text: '1/x + 1/(x+5) = 1/6. Cross-multiplying: 6(x+5) + 6x = x(x+5) → 12x + 30 = x² + 5x → x² − 7x − 30 = 0.' },
        { label: 'Answer', text: 'Factoring: (x−10)(x+3) = 0 → x = 10 (rejecting the negative root x = −3). So B alone takes 10 days (and A alone takes 15 days).' },
      ],
      commonTrap:
        "Assuming the '5 days more' simply splits evenly around the 6-day combined time (e.g. guessing values like 3.5 or 8.5) — the actual relationship is quadratic, not a simple linear split.",
      alternateMethod: {
        label: 'Plug the options back in',
        steps: [
          'Test x = 10 (i.e. B = 10, A = 15) directly in 1/x + 1/(x+5) = 1/6.',
          '1/10 + 1/15 = 3/30 + 2/30 = 5/30 = 1/6 — matches exactly, confirming the answer without solving the quadratic by hand.',
        ],
      },
    },
    {
      id: 'tw-25',
      difficulty: 'Medium',
      patternTags: ['TCS', 'Infosys', 'Zoho'],
      question:
        "12 men working 8 hours a day can complete a work in 10 days. How many days will 8 men working 10 hours a day take to complete the same work?",
      options: ['9 days', '10 days', '12 days', '15 days'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: '12 men, 8 hours/day, 10 days → completes the job. Find days for 8 men, 10 hours/day.' },
        { label: 'Concept', text: 'Extend the men-days formula to include hours per day: total work (in man-hours) = Men × Hours/day × Days, and this total stays constant for the same job.' },
        { label: 'Working', text: 'Total man-hours = 12 × 8 × 10 = 960. New daily capacity = 8 men × 10 hrs = 80 man-hours/day.' },
        { label: 'Answer', text: 'Days = 960/80 = 12 days.' },
      ],
      commonTrap:
        "Only adjusting for the change in men (12 men → 10 days, so 8 men → 10×12/8 = 15 days) — this ignores that the new group also works MORE hours per day (10 vs. 8), which partly offsets having fewer men.",
      alternateMethod: {
        label: 'Two-step scaling',
        steps: [
          'First adjust for men only (same 8 hrs/day): 8 men would take 10 × (12/8) = 15 days.',
          'Then adjust for the longer working day (10 hrs instead of 8): 15 × (8/10) = 12 days.',
        ],
      },
    },
  ],
  'profit-loss': [
    {
      id: 'pl-01',
      difficulty: 'Easy',
      patternTags: ['TCS', 'Infosys'],
      question: "A man buys an article for ₹450 and sells it for ₹540. Find his profit percentage.",
      options: ['15%', '16.67%', '20%', '25%'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: 'Cost Price (CP) = ₹450, Selling Price (SP) = ₹540.' },
        { label: 'Concept', text: 'Profit % is always calculated on the COST PRICE: Profit % = (SP − CP)/CP × 100.' },
        { label: 'Working', text: 'Profit = 540 − 450 = ₹90.' },
        { label: 'Answer', text: 'Profit % = 90/450 × 100 = 20%.' },
      ],
      commonTrap:
        "Calculating the percentage on the selling price instead of the cost price: 90/540 × 100 = 16.67% — profit % must always use CP as the base, never SP.",
      alternateMethod: {
        label: 'Ratio method',
        steps: [
          'CP : SP = 450 : 540 = 5 : 6.',
          'Profit ratio = (6 − 5)/5 = 1/5 = 20%.',
        ],
      },
    },
    {
      id: 'pl-02',
      difficulty: 'Easy',
      patternTags: ['Wipro', 'Zoho'],
      question: "A shopkeeper bought a chair for ₹800 and sold it for ₹680. Find his loss percentage.",
      options: ['12%', '15%', '17.65%', '20%'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: 'CP = ₹800, SP = ₹680 (a loss, since SP < CP).' },
        { label: 'Concept', text: 'Loss % is always calculated on the COST PRICE: Loss % = (CP − SP)/CP × 100.' },
        { label: 'Working', text: 'Loss = 800 − 680 = ₹120.' },
        { label: 'Answer', text: 'Loss % = 120/800 × 100 = 15%.' },
      ],
      commonTrap:
        "Calculating the percentage on the selling price instead of the cost price: 120/680 × 100 = 17.65% — the base must always be CP, never SP.",
      alternateMethod: {
        label: 'Ratio method',
        steps: [
          'CP : SP = 800 : 680 = 20 : 17.',
          'Loss ratio = (20 − 17)/20 = 3/20 = 15%.',
        ],
      },
    },
    {
      id: 'pl-03',
      difficulty: 'Easy',
      patternTags: ['TCS', 'Wipro'],
      question: "A trader bought goods for ₹2,400 and wants to make a profit of 15%. At what price should he sell the goods?",
      options: ['2,650', '2,700', '2,760', '2,800'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: 'CP = ₹2,400, desired profit = 15%.' },
        { label: 'Concept', text: 'SP = CP × (1 + profit%/100) when a profit percentage is targeted on the cost price.' },
        { label: 'Working', text: 'SP = 2,400 × 1.15.' },
        { label: 'Answer', text: 'SP = ₹2,760.' },
      ],
      commonTrap:
        "Adding a rounded or miscalculated percentage of CP instead of the full 15% precisely — small arithmetic slips here (like using 12.5% by mistake) lead to a close but incorrect selling price.",
      alternateMethod: {
        label: 'Unitary method',
        steps: [
          '100% of CP = 2,400, so 1% = 24.',
          '115% of CP = 24 × 115 = ₹2,760.',
        ],
      },
    },
    {
      id: 'pl-04',
      difficulty: 'Easy',
      patternTags: ['Infosys', 'Zoho'],
      question: "By selling a watch for ₹1,020, a man loses 15%. What is the cost price of the watch?",
      options: ['1,100', '1,150', '1,200', '1,250'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: 'SP = ₹1,020, loss = 15% (so SP = 85% of CP).' },
        { label: 'Concept', text: 'This is a reverse-percentage problem: divide the SP by (1 − loss%) to recover the CP — do not add the loss % back onto the SP.' },
        { label: 'Working', text: '0.85 × CP = 1,020.' },
        { label: 'Answer', text: 'CP = 1,020 / 0.85 = ₹1,200.' },
      ],
      commonTrap:
        "Adding 15% of ₹1,020 back on (1,020 + 153 = 1,173) — this wrongly treats the SP as the base for the 15%, when the (larger) original CP is the correct base.",
      alternateMethod: {
        label: 'Unitary method',
        steps: [
          '85% of CP = 1,020, so 1% = 1,020/85 = 12.',
          '100% of CP = 12 × 100 = ₹1,200.',
        ],
      },
    },
    {
      id: 'pl-05',
      difficulty: 'Medium',
      patternTags: ['TCS', 'Infosys', 'Wipro'],
      question: "A shopkeeper marks his goods 40% above cost price and allows a discount of 10% for cash. What is his profit percentage?",
      options: ['24%', '26%', '28%', '30%'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: 'Marked Price (MP) = 140% of CP. Discount = 10% on MP.' },
        { label: 'Concept', text: 'Chain the markup and discount as two successive multipliers on CP — they do not simply subtract from each other.' },
        { label: 'Working', text: 'Let CP = 100 → MP = 140. SP = 140 × 0.90 = 126.' },
        { label: 'Answer', text: 'Profit = 126 − 100 = 26%.' },
      ],
      commonTrap:
        "Naively subtracting the markup and discount (40% − 10% = 30%) — successive percentage changes compound multiplicatively, they never just subtract.",
      alternateMethod: {
        label: 'Combined multiplier',
        steps: [
          'Net multiplier = 1.40 × 0.90 = 1.26.',
          'Profit % = 26%.',
        ],
      },
    },
    {
      id: 'pl-06',
      difficulty: 'Hard',
      patternTags: ['TCS', 'Zoho'],
      question: "A dishonest dealer professes to sell his goods at cost price but uses a weight of 800 grams for every 1,000 grams (1 kg) he actually gives. Find his profit percentage.",
      options: ['20%', '25%', '28%', '33.33%'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: 'Dealer claims no markup (sells "at cost price") but hands over only 800g while charging the price of 1,000g.' },
        { label: 'Concept', text: 'Profit % from a false weight = [(True weight − False weight)/False weight] × 100 — the base is the weight ACTUALLY given (his real cost), not the weight claimed.' },
        { label: 'Working', text: '(1,000 − 800)/800 × 100 = 200/800 × 100.' },
        { label: 'Answer', text: 'Profit = 25%.' },
      ],
      commonTrap:
        "Using the true (full) weight as the base instead of the actual weight given: (1,000−800)/1,000 × 100 = 20% — but the dealer's real cost is only for the 800g he actually hands over, so that must be the base.",
      alternateMethod: {
        label: 'Assumed cost-price method',
        steps: [
          'Let 1,000g cost ₹1,000 (₹1/gram). The dealer gives only 800g, so his real cost = ₹800.',
          'He charges the price of 1,000g = ₹1,000.',
          'Profit = 1,000 − 800 = ₹200, on a real cost of ₹800 → 200/800 × 100 = 25%.',
        ],
      },
    },
    {
      id: 'pl-07',
      difficulty: 'Hard',
      patternTags: ['TCS', 'Infosys', 'Zoho'],
      question: "A man sold two horses for ₹990 each. On one he gained 10% and on the other he lost 10%. Find his overall gain or loss percentage on the whole transaction.",
      options: ['0.5% loss', '1% loss', '2% loss', '5% loss'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: 'Both horses sold for the SAME SP (₹990 each) — one at +10% profit, the other at −10% loss.' },
        { label: 'Concept', text: 'When two items are sold at the same SP with equal % profit and % loss, the result is ALWAYS a net LOSS (never breakeven) — because the two cost prices are actually different, even though the selling prices are equal.' },
        { label: 'Working', text: 'CP1 (profit case) = 990/1.10 = ₹900. CP2 (loss case) = 990/0.90 = ₹1,100. Total CP = ₹2,000. Total SP = ₹1,980.' },
        { label: 'Answer', text: 'Overall loss = 2,000 − 1,980 = ₹20, i.e. 20/2,000 × 100 = 1% loss.' },
      ],
      commonTrap:
        "Assuming the +10% and −10% simply cancel out to 'no profit, no loss' — they never exactly cancel, because the two cost prices work out different once the equal-SP condition is applied.",
      alternateMethod: {
        label: 'Direct shortcut formula',
        steps: [
          'Whenever two items are sold at an equal SP with equal % profit and % loss, the result is always a loss of (common%)² / 100.',
          '(10)² / 100 = 1% loss — matches the detailed calculation exactly.',
        ],
      },
    },
    {
      id: 'pl-08',
      difficulty: 'Medium',
      patternTags: ['Infosys', 'Wipro'],
      question: "The cost price of 20 articles is equal to the selling price of 15 articles. Find the profit percentage.",
      options: ['25%', '30%', '33.33%', '40%'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: 'CP of 20 articles = SP of 15 articles (same total money value).' },
        { label: 'Concept', text: 'Convert both sides to a PER-ARTICLE basis to directly compare the CP and SP of one article.' },
        { label: 'Working', text: 'Let CP of 1 article = ₹1 → CP of 20 = ₹20 = SP of 15 → SP of 1 article = 20/15 = 4/3.' },
        { label: 'Answer', text: 'Profit % = (4/3 − 1)/1 × 100 = 33.33%.' },
      ],
      commonTrap:
        "Comparing the two article-counts (20 and 15) directly as if they described the SAME quantity, missing that the totals match only for DIFFERENT quantities (20 vs. 15) — the comparison must first be converted to a per-article basis.",
      alternateMethod: {
        label: 'Direct shortcut formula',
        steps: [
          'For "CP of x articles = SP of y articles" (with x > y): Profit % = (x − y)/y × 100.',
          '(20 − 15)/15 × 100 = 33.33% — same answer, one step.',
        ],
      },
    },
    {
      id: 'pl-09',
      difficulty: 'Hard',
      patternTags: ['TCS', 'Zoho'],
      question: "A dealer marks up his goods by 20% above cost price and then uses a faulty weight that gives only 900 grams for every 1,000 grams (1 kg) charged. Find his overall profit percentage.",
      options: ['28%', '30%', '33.33%', '35%'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: 'Marks up 20% above CP, and separately shortchanges weight (900g given for every 1,000g charged).' },
        { label: 'Concept', text: 'Two independent profit sources compound MULTIPLICATIVELY, like successive percentage changes — not additively.' },
        { label: 'Working', text: 'Markup multiplier = 1.20. Weight multiplier = 1,000/900 = 10/9. Net multiplier = 1.20 × 10/9 = 4/3.' },
        { label: 'Answer', text: 'Net profit % = (4/3 − 1) × 100 = 33.33%.' },
      ],
      commonTrap:
        "Simply adding the two profit sources (20% markup + 11.11% from the weight ≈ 31.11%) — like successive percentage changes, these two effects compound, they don't just add together.",
      alternateMethod: {
        label: 'Assumed-value method',
        steps: [
          'Let true cost of 1,000g = ₹1,000 (₹1/gram). The dealer gives 900g, costing him ₹900.',
          'He charges the marked-up price for 1kg: MP = 1.20 × 1,000 = ₹1,200.',
          'Actual profit = 1,200 − 900 = ₹300, on a real cost of ₹900 → 300/900 × 100 = 33.33%.',
        ],
      },
    },
    {
      id: 'pl-10',
      difficulty: 'Medium',
      patternTags: ['TCS', 'Infosys'],
      question: "A fruit seller buys 100 kg of apples for ₹5,000. 10 kg of the apples spoil and cannot be sold. At what price per kg should he sell the remaining apples to gain 20% on his total investment?",
      options: ['₹60', '₹63.33', '₹66.67', '₹70'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: 'Total CP = ₹5,000 for 100kg. 10kg spoiled (unsellable). Desired overall profit = 20% on the total investment.' },
        { label: 'Concept', text: 'The desired total SP is based on the ORIGINAL total cost, but that entire amount must now come from the smaller remaining quantity, since the spoiled apples earn nothing.' },
        { label: 'Working', text: 'Desired total SP = 5,000 × 1.20 = ₹6,000. Remaining sellable quantity = 100 − 10 = 90kg.' },
        { label: 'Answer', text: 'Price per kg = 6,000/90 = ₹66.67.' },
      ],
      commonTrap:
        "Dividing the desired SP by the ORIGINAL 100kg instead of the remaining 90kg (6,000/100 = ₹60/kg) — this ignores that the spoiled apples contribute nothing to revenue, so the profit target must be recovered from fewer kilograms.",
      alternateMethod: {
        label: 'Per-kg scaling method',
        steps: [
          'Original CP per kg = 5,000/100 = ₹50. Desired price per kg IF selling all 100kg = 50 × 1.20 = ₹60/kg.',
          'Since only 90kg can actually be sold, scale up: required price = 60 × (100/90) = ₹66.67/kg.',
        ],
      },
    },
    {
      id: 'pl-11',
      difficulty: 'Hard',
      patternTags: ['Wipro', 'Zoho'],
      question: "A man buys two watches for a total of ₹1,200. He sells one at a profit of 20% and the other at a loss of 10%, and on the whole transaction makes neither a profit nor a loss. Find the cost price of the watch sold at a profit.",
      options: ['350', '400', '450', '500'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: 'Total CP = ₹1,200 for two watches. One sold at +20%, other at −10%. Overall result = breakeven (total SP = total CP).' },
        { label: 'Concept', text: 'Let CP of the profit-watch = x, so the other CP = (1,200 − x). Set total SP equal to total CP (₹1,200) for the breakeven condition.' },
        { label: 'Working', text: '1.2x + 0.9(1,200 − x) = 1,200 → 1.2x + 1,080 − 0.9x = 1,200 → 0.3x = 120.' },
        { label: 'Answer', text: 'x = 400. The watch sold at a profit had a cost price of ₹400.' },
      ],
      commonTrap:
        "Splitting ₹1,200 directly using the profit/loss percentages' ratio (20:10 = 2:1) — the correct split comes only from solving the breakeven equation, not from the percentages' ratio directly.",
      alternateMethod: {
        label: 'Verification',
        steps: [
          'CP of profit-watch = 400 → SP = 480. CP of loss-watch = 800 → SP = 720.',
          'Total SP = 480 + 720 = ₹1,200 = Total CP — confirms breakeven exactly.',
        ],
      },
    },
    {
      id: 'pl-12',
      difficulty: 'Medium',
      patternTags: ['TCS', 'Infosys'],
      question: "A shopkeeper allows a discount of 10% on the marked price of an item and still earns a profit of 26%. If the cost price is ₹500, find the marked price.",
      options: ['650', '680', '700', '720'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: 'CP = ₹500, discount = 10% on MP, resulting profit = 26%.' },
        { label: 'Concept', text: 'Chain the profit and discount relations through the Selling Price, common to both: find SP from CP and profit%, then reverse from SP to MP using the discount.' },
        { label: 'Working', text: 'SP = 500 × 1.26 = ₹630. MP × 0.90 = 630.' },
        { label: 'Answer', text: 'MP = 630/0.90 = ₹700.' },
      ],
      commonTrap:
        "Adding the discount % and profit % directly onto the CP (500 × 1.36 = 680) — the two percentages act on different bases (CP for profit, MP for discount) and cannot simply be combined into one multiplier on CP.",
      alternateMethod: {
        label: 'Unitary method on MP',
        steps: [
          '90% of MP = 630, so 1% = 7.',
          '100% of MP = 7 × 100 = ₹700.',
        ],
      },
    },
    {
      id: 'pl-13',
      difficulty: 'Medium',
      patternTags: ['Infosys', 'Wipro'],
      question: "If the profit made by selling an article for ₹384 equals the loss incurred by selling it for ₹256, find the cost price of the article.",
      options: ['300', '310', '320', '330'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: 'Selling at ₹384 gives a profit amount equal to the loss amount from selling at ₹256.' },
        { label: 'Concept', text: 'Profit amount = SP1 − CP, Loss amount = CP − SP2. Setting these two amounts equal gives a direct equation for CP.' },
        { label: 'Working', text: '384 − CP = CP − 256 → 384 + 256 = 2 × CP.' },
        { label: 'Answer', text: 'CP = 640/2 = ₹320.' },
      ],
      commonTrap:
        "Picking one of the two given selling prices (384 or 256) directly as the cost price, instead of recognizing that CP must lie exactly between them.",
      alternateMethod: {
        label: 'Averaging shortcut',
        steps: [
          'Since the profit amount exactly equals the loss amount, CP is simply the arithmetic mean of the two selling prices.',
          'CP = (384 + 256)/2 = ₹320.',
        ],
      },
    },
    {
      id: 'pl-14',
      difficulty: 'Medium',
      patternTags: ['TCS', 'Zoho'],
      question: "A trader purchases a machine for ₹8,000 and spends ₹1,000 on repair and ₹200 on transportation. He sells it for ₹11,000. Find his profit percentage.",
      options: ['15%', '17%', '19.57%', '22%'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: 'Purchase = ₹8,000, repair = ₹1,000, transport = ₹200 (all part of the true cost), SP = ₹11,000.' },
        { label: 'Concept', text: 'ALL expenses incurred before the sale (repair, transport, etc.) must be added to the purchase price to get the TRUE cost price — profit is always measured against this total.' },
        { label: 'Working', text: 'True CP = 8,000 + 1,000 + 200 = ₹9,200. Profit = 11,000 − 9,200 = ₹1,800.' },
        { label: 'Answer', text: 'Profit % = 1,800/9,200 × 100 ≈ 19.57%.' },
      ],
      commonTrap:
        "Calculating profit % using only the purchase price (₹8,000) as CP, ignoring the additional repair and transport costs: 3,000/8,000 × 100 = 37.5% — a much higher (and wrong) profit figure that overstates the trader's real margin.",
      alternateMethod: {
        label: 'Ratio method',
        steps: [
          'True CP : SP = 9,200 : 11,000 = 46 : 55.',
          'Profit ratio = (55 − 46)/46 = 9/46 ≈ 19.57%.',
        ],
      },
    },
    {
      id: 'pl-15',
      difficulty: 'Hard',
      patternTags: ['TCS', 'Infosys', 'Zoho'],
      question: "A shopkeeper marks his goods such that even after allowing a 25% discount, he still makes a profit of 20%. If he had sold without any discount, what would his profit percentage have been?",
      options: ['50%', '55%', '60%', '65%'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: 'After a 25% discount on MP, profit is still 20% on CP.' },
        { label: 'Concept', text: 'First find MP from the discounted-SP relationship, then compute the profit if the FULL marked price (no discount) had been charged instead.' },
        { label: 'Working', text: 'Let CP = 100. SP (discounted) = 120 = 0.75 × MP.' },
        { label: 'Answer', text: 'MP = 120/0.75 = 160. Without discount, SP = MP = 160, so profit = 160 − 100 = 60%.' },
      ],
      commonTrap:
        "Directly combining the discount % and profit % (like adding 25 + 20 = 45%) — the correct approach requires first finding the marked price via the discount relationship, not combining the two percentages arbitrarily.",
      alternateMethod: {
        label: 'Combined multiplier method',
        steps: [
          '0.75 × (MP/CP) = 1.20 → MP/CP = 1.20/0.75 = 1.60, i.e. MP is 160% of CP.',
          'Without any discount, profit = 60%.',
        ],
      },
    },
    {
      id: 'pl-16',
      difficulty: 'Medium',
      patternTags: ['Infosys', 'Wipro'],
      question: "A trader pays for only 18 articles but receives 20 (2 free) as part of a bulk deal, and then sells all 20 articles at the normal per-article price. Find his profit percentage.",
      options: ['10%', '11.11%', '12%', '15%'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: 'Trader pays for 18 articles but receives 20 (2 free). Sells all 20 at the normal (undiscounted) per-article price.' },
        { label: 'Concept', text: "The trader's TRUE cost is only what he actually paid for (18 articles), while revenue comes from selling the FULL quantity received (20) — profit % is calculated on the true (smaller) cost." },
        { label: 'Working', text: 'Let price per article = ₹1. Cost = 18 × 1 = ₹18. Revenue = 20 × 1 = ₹20.' },
        { label: 'Answer', text: 'Profit = ₹2 → Profit % = 2/18 × 100 = 11.11%.' },
      ],
      commonTrap:
        "Calculating profit % on 20 (the quantity SOLD) instead of 18 (the quantity actually paid for): 2/20 × 100 = 10% — using the wrong (revenue-side) base instead of the true cost base.",
      alternateMethod: {
        label: 'Direct shortcut formula',
        steps: [
          'Profit % = (Articles received − Articles paid for)/Articles paid for × 100.',
          '(20 − 18)/18 × 100 = 11.11%.',
        ],
      },
    },
    {
      id: 'pl-17',
      difficulty: 'Medium',
      patternTags: ['TCS', 'Zoho'],
      question: "A shopkeeper marks his goods 50% above cost price but allows a discount of 20%, claiming he is selling at a loss. What is his ACTUAL profit percentage?",
      options: ['15%', '18%', '20%', '25%'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: 'MP = 150% of CP (50% markup). Discount = 20% on MP.' },
        { label: 'Concept', text: 'Find the actual net multiplier from markup and discount together, regardless of what the shopkeeper claims.' },
        { label: 'Working', text: 'Let CP = 100 → MP = 150. SP = 150 × 0.80 = 120.' },
        { label: 'Answer', text: 'Actual profit = 120 − 100 = 20%.' },
      ],
      commonTrap:
        "Taking the 'selling at a loss' claim at face value, or naively subtracting 50% − 20% = 30% — the shopkeeper's marketing claim is irrelevant; only the actual CP-to-SP multiplier determines the real result.",
      alternateMethod: {
        label: 'Combined multiplier method',
        steps: [
          'Net multiplier = 1.50 × 0.80 = 1.20.',
          'Actual profit = 20%, despite the "loss" framing.',
        ],
      },
    },
    {
      id: 'pl-18',
      difficulty: 'Medium',
      patternTags: ['TCS', 'Infosys'],
      question: "A sells an article to B at a profit of 20%, and B sells it to C at a profit of 25%. If C pays ₹450 for it, what did the article cost A originally?",
      options: ['280', '300', '320', '350'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: "A→B at +20% profit, B→C at +25% profit, C's final price = ₹450." },
        { label: 'Concept', text: 'Successive profit percentages along a resale chain compound MULTIPLICATIVELY, just like successive percentage changes — they never simply add.' },
        { label: 'Working', text: "CP_A × 1.20 × 1.25 = 450 → CP_A × 1.5 = 450." },
        { label: 'Answer', text: 'CP_A = 450/1.5 = ₹300.' },
      ],
      commonTrap:
        "Adding the two profit percentages (20% + 25% = 45%) and computing 450/1.45 ≈ ₹310.34 — successive profits along a chain compound multiplicatively, they don't simply add.",
      alternateMethod: {
        label: 'Reverse step-by-step',
        steps: [
          "Before B's 25% profit, the price B paid A was 450/1.25 = ₹360.",
          "Before A's 20% profit, A's original cost was 360/1.20 = ₹300.",
        ],
      },
    },
    {
      id: 'pl-19',
      difficulty: 'Hard',
      patternTags: ['Wipro', 'Zoho'],
      question: "If a man had sold his article for ₹75 more, he would have made a profit of 20% instead of a loss of 5%. Find the cost price of the article.",
      options: ['250', '275', '300', '320'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: 'Actual SP gives a 5% loss; SP + ₹75 would give a 20% profit — on the SAME cost price.' },
        { label: 'Concept', text: 'The ₹75 difference spans the FULL swing from a 5% loss up to a 20% profit — a total percentage swing of 25 points on CP, not just the 20% figure alone.' },
        { label: 'Working', text: '1.20 × CP − 0.95 × CP = 75 → 0.25 × CP = 75.' },
        { label: 'Answer', text: 'CP = 75/0.25 = ₹300.' },
      ],
      commonTrap:
        "Treating the ₹75 as corresponding to only the 20% profit figure and computing 75/0.20 = ₹375 — this misses that the ₹75 spans the full swing from a 5% loss UP to a 20% profit, a total of 25 percentage points, not just 20.",
      alternateMethod: {
        label: 'Percentage-swing method',
        steps: [
          'Total swing from −5% (loss) to +20% (profit) = 20 − (−5) = 25 percentage points, corresponding to ₹75.',
          '1% of CP = 75/25 = ₹3. CP = 3 × 100 = ₹300.',
        ],
      },
    },
    {
      id: 'pl-20',
      difficulty: 'Hard',
      patternTags: ['TCS', 'Infosys', 'Zoho'],
      question: "A trader marks his goods 30% above cost price. He then sells them at a discount of 10%, making a profit of ₹68 on each article. Find the cost price of each article.",
      options: ['380', '400', '420', '450'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: 'MP = 130% of CP, discount = 10% on MP, actual profit = ₹68 per article.' },
        { label: 'Concept', text: 'First find profit as a PERCENTAGE of CP using the markup and discount multipliers together, then use the given rupee profit to back-solve for CP.' },
        { label: 'Working', text: 'Net multiplier = 1.30 × 0.90 = 1.17, so profit % = 17%. Given: 17% of CP = ₹68.' },
        { label: 'Answer', text: 'CP = 68/0.17 = ₹400.' },
      ],
      commonTrap:
        "Assuming the ₹68 profit corresponds to the full 30% markup, ignoring the 10% discount's effect: CP = 68/0.30 ≈ ₹226.67 — well off, since the discount reduces the realized profit percentage from 30% down to just 17%.",
      alternateMethod: {
        label: 'Assumed-value method',
        steps: [
          'Let CP = 100 → SP = 117 → profit (in the same units) = 17.',
          'Since ₹68 corresponds to a profit of "17 units", 1 unit = 68/17 = ₹4.',
          'CP = 4 × 100 = ₹400.',
        ],
      },
    },
    {
      id: 'pl-21',
      difficulty: 'Hard',
      patternTags: ['TCS', 'Zoho'],
      question: "By selling 45 lemons for ₹40, a woman loses 20%. How many lemons should she sell for ₹24 to gain 20% in the transaction?",
      options: ['15', '16', '18', '20'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: '45 lemons sold for ₹40 results in a 20% LOSS. Find how many lemons to sell for ₹24 to instead gain 20%.' },
        { label: 'Concept', text: 'First find the true cost price per lemon from the loss scenario, then use it to find the required selling rate for the desired profit.' },
        { label: 'Working', text: 'CP of 45 lemons = 40/0.80 = ₹50 → CP per lemon = 50/45 = 10/9. Required SP per lemon (for 20% gain) = (10/9) × 1.20 = 4/3.' },
        { label: 'Answer', text: 'Number of lemons for ₹24 = 24 ÷ (4/3) = 24 × 3/4 = 18 lemons.' },
      ],
      commonTrap:
        "Using the original selling rate (40 for 45, i.e. ₹8/9 per lemon) directly to see how many lemons ₹24 buys (24 ÷ 8/9 = 27) — that original rate was a LOSS-making rate, not the profit-making rate the question actually asks for.",
      alternateMethod: {
        label: 'Ratio method',
        steps: [
          'The per-lemon rate must change from a 20% loss to a 20% gain — a multiplier change of 1.20/0.80 = 1.5× on the price per lemon.',
          'At the old rate, ₹24 buys 45 × (24/40) = 27 lemons. Scaling by 1/1.5 = 2/3 for the new (higher) rate: 27 × 2/3 = 18 lemons.',
        ],
      },
    },
    {
      id: 'pl-22',
      difficulty: 'Medium',
      patternTags: ['Infosys', 'Wipro'],
      question: "A man sells an article at a profit of 25% on the selling price. Find his profit percentage on the cost price.",
      options: ['20%', '25%', '30%', '33.33%'],
      correctIndex: 3,
      steps: [
        { label: 'Given', text: 'Profit = 25% of the SELLING PRICE (not the usual CP-based profit%).' },
        { label: 'Concept', text: 'Convert a "profit on SP" figure into the standard "profit on CP" figure using: if profit = p% of SP, then profit % on CP = [p/(100 − p)] × 100.' },
        { label: 'Working', text: 'p = 25 → profit % on CP = 25/(100 − 25) × 100 = 25/75 × 100.' },
        { label: 'Answer', text: 'Profit % on cost price = 33.33%.' },
      ],
      commonTrap:
        "Assuming a 25% profit on SP is the same as a 25% profit on CP and answering 25% directly — percentages measured against different bases (SP vs. CP) are never numerically equal except at 0%.",
      alternateMethod: {
        label: 'Assumed-value method',
        steps: [
          'Let CP = 100. Profit = 0.25 × SP, and SP = CP + Profit = 100 + 0.25 × SP → 0.75 × SP = 100 → SP = 133.33.',
          'Profit = 33.33, i.e. 33.33% of CP.',
        ],
      },
    },
    {
      id: 'pl-23',
      difficulty: 'Hard',
      patternTags: ['TCS', 'Zoho'],
      question: "A trader bought 50 kg of rice at ₹20 per kg and 50 kg of rice at ₹30 per kg. He mixed the two and sold the entire mixture at ₹27.50 per kg. Find his overall profit or loss percentage.",
      options: ['8%', '10%', '12%', '15%'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: '50kg @ ₹20/kg and 50kg @ ₹30/kg, mixed and sold entirely at ₹27.50/kg.' },
        { label: 'Concept', text: 'Find the TOTAL cost price for the whole mixture first, then compare it to the TOTAL selling price — the key skill is working with totals, not naively averaging per-kg rates (which only coincidentally works here because the quantities are equal).' },
        { label: 'Working', text: 'Total CP = 50×20 + 50×30 = 1,000 + 1,500 = ₹2,500. Total SP = 100 × 27.50 = ₹2,750.' },
        { label: 'Answer', text: 'Profit = ₹250 → Profit % = 250/2,500 × 100 = 10%.' },
      ],
      commonTrap:
        "Relying on the simple average of the two buying rates (₹20 and ₹30 → ₹25/kg) — this only coincidentally gives the right overall CP because the two quantities (50kg each) happen to be equal; with UNEQUAL quantities, a plain average of rates would give the wrong overall cost.",
      alternateMethod: {
        label: 'Per-kg method',
        steps: [
          'Average CP/kg = 2,500/100 = ₹25. Profit per kg = 27.50 − 25 = ₹2.50.',
          'Profit % = 2.50/25 × 100 = 10%.',
        ],
      },
    },
    {
      id: 'pl-24',
      difficulty: 'Hard',
      patternTags: ['TCS', 'Infosys', 'Zoho'],
      question: "A dealer sold two-thirds of his stock at a profit of 10% and the remaining one-third at a loss of 5%. If his overall profit on the entire stock was ₹240, find the total cost price of the stock.",
      options: ['4,200', '4,500', '4,800', '5,000'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: '2/3 of stock sold at +10%, 1/3 at −5%. Overall profit (in rupees) on the whole stock = ₹240.' },
        { label: 'Concept', text: 'Find the overall profit PERCENTAGE as a WEIGHTED average (weighted by the fraction of stock in each group), then use the given rupee profit to back-solve for the total CP.' },
        { label: 'Working', text: 'Overall % = (2/3)(10) + (1/3)(−5) = 20/3 − 5/3 = 15/3 = 5%. Given: 5% of total CP = ₹240.' },
        { label: 'Answer', text: 'Total CP = 240/0.05 = ₹4,800.' },
      ],
      commonTrap:
        "Taking a simple (unweighted) average of +10% and −5% to get +2.5%, then computing 240/0.025 = ₹9,600 — this ignores that the two groups are of UNEQUAL size (2/3 vs. 1/3 of the stock), so they can't be weighted equally.",
      alternateMethod: {
        label: 'Assumed-value method',
        steps: [
          'Let total CP = 300 (a multiple of 3). 2/3 = 200 at +10% → profit = 20. 1/3 = 100 at −5% → loss = 5.',
          'Net profit = 20 − 5 = 15, i.e. 5% of the assumed CP of 300.',
          'Since ₹240 corresponds to that same 5%, true CP = 300 × (240/15) = ₹4,800.',
        ],
      },
    },
    {
      id: 'pl-25',
      difficulty: 'Medium',
      patternTags: ['Wipro', 'Zoho'],
      question: "A shopkeeper's marked price is 25% above cost price. What discount percentage would he need to offer to sell exactly at cost price (no profit, no loss)?",
      options: ['15%', '18%', '20%', '25%'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: 'MP = 125% of CP (25% markup). Find the discount % that brings SP down to exactly CP.' },
        { label: 'Concept', text: 'At breakeven, SP = CP. Find the discount % (on MP) needed to bring the marked price back down to that level.' },
        { label: 'Working', text: 'Let CP = 100 → MP = 125. For SP = 100: Discount = (MP − SP)/MP × 100 = (125 − 100)/125 × 100.' },
        { label: 'Answer', text: 'Discount needed = 20%.' },
      ],
      commonTrap:
        "Assuming the breakeven discount % equals the markup % (25%) — but discount % and markup % are computed on DIFFERENT bases (MP vs. CP respectively), so they are never numerically equal except at 0%.",
      alternateMethod: {
        label: 'Unitary method',
        steps: [
          'MP = 125, need SP = 100. Discount amount = 25.',
          'As a % of MP: 25/125 × 100 = 20%.',
        ],
      },
    },
  ],
  'time-speed-distance': [
    {
      id: 'tsd-01',
      difficulty: 'Easy',
      patternTags: ['TCS', 'Infosys'],
      question: "A car travels 180 km in 3 hours. What is its speed?",
      options: ['55 km/h', '60 km/h', '65 km/h', '70 km/h'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: 'Distance = 180 km, Time = 3 hours.' },
        { label: 'Concept', text: 'Speed = Distance ÷ Time.' },
        { label: 'Working', text: '180 ÷ 3.' },
        { label: 'Answer', text: 'Speed = 60 km/h.' },
      ],
      commonTrap:
        "Multiplying distance and time instead of dividing (180 × 3 = 540) — speed is distance PER unit time, found by division, not multiplication.",
      alternateMethod: {
        label: 'Verification via conversion',
        steps: [
          '60 km/h = 60 × 1,000/3,600 = 16.67 m/s.',
          'Over 3 hours (10,800 seconds): 16.67 × 10,800 ≈ 180,000 m = 180 km — confirms the answer.',
        ],
      },
    },
    {
      id: 'tsd-02',
      difficulty: 'Easy',
      patternTags: ['TCS', 'Wipro'],
      question: "Convert a speed of 72 km/h into metres per second.",
      options: ['18 m/s', '20 m/s', '22 m/s', '25 m/s'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: 'Speed = 72 km/h.' },
        { label: 'Concept', text: 'To convert km/h to m/s, multiply by 5/18 (since 1 km/h = 1,000m/3,600s = 5/18 m/s).' },
        { label: 'Working', text: '72 × 5/18.' },
        { label: 'Answer', text: '= 20 m/s.' },
      ],
      commonTrap:
        "Multiplying by 18/5 instead of 5/18 (72 × 18/5 = 259.2) — inverting the conversion factor gives a wildly incorrect, oversized result.",
      alternateMethod: {
        label: 'Direct unit method',
        steps: [
          '72 km/h = 72,000 metres / 3,600 seconds.',
          '= 20 m/s.',
        ],
      },
    },
    {
      id: 'tsd-03',
      difficulty: 'Hard',
      patternTags: ['TCS', 'Zoho'],
      question: "A man travels from A to B at a speed of 40 km/h and returns from B to A at a speed of 60 km/h. Find his average speed for the entire journey.",
      options: ['45 km/h', '48 km/h', '50 km/h', '55 km/h'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: 'Same distance both ways: going at 40 km/h, returning at 60 km/h.' },
        { label: 'Concept', text: 'For EQUAL DISTANCES at two different speeds, average speed = 2×S1×S2/(S1+S2) — the HARMONIC mean, NOT the simple arithmetic mean.' },
        { label: 'Working', text: '2 × 40 × 60/(40+60) = 4,800/100.' },
        { label: 'Answer', text: 'Average speed = 48 km/h.' },
      ],
      commonTrap:
        "Taking the simple arithmetic average (40+60)/2 = 50 km/h — this is only valid for equal TIME durations, not equal distances, which is the case here.",
      alternateMethod: {
        label: 'Assumed-distance method',
        steps: [
          'Let distance each way = 120km (a multiple of 40 and 60). Time going = 120/40 = 3hr. Time returning = 120/60 = 2hr.',
          'Total distance = 240km, total time = 5hr. Average speed = 240/5 = 48 km/h.',
        ],
      },
    },
    {
      id: 'tsd-04',
      difficulty: 'Medium',
      patternTags: ['Infosys', 'Wipro'],
      question: "Two trains start from the same point at the same time, travelling in the SAME direction. One travels at 50 km/h and the other at 70 km/h. After how many hours will they be 60 km apart?",
      options: ['1.5 hours', '2 hours', '2.5 hours', '3 hours'],
      correctIndex: 3,
      steps: [
        { label: 'Given', text: 'Same start point, same direction, speeds 50 km/h and 70 km/h, target gap = 60 km.' },
        { label: 'Concept', text: 'For objects moving in the SAME direction, relative speed = DIFFERENCE of their speeds; the gap between them grows at this relative speed.' },
        { label: 'Working', text: 'Relative speed = 70 − 50 = 20 km/h.' },
        { label: 'Answer', text: 'Time = 60/20 = 3 hours.' },
      ],
      commonTrap:
        "Using the SUM of speeds (50+70=120) instead of the difference, computing 60/120 = 0.5hr — that formula applies to opposite-direction (approaching) motion, not same-direction motion.",
      alternateMethod: {
        label: 'Position-equation method',
        steps: [
          'Distance by faster train − distance by slower train = 60.',
          '70t − 50t = 60 → 20t = 60 → t = 3 hours.',
        ],
      },
    },
    {
      id: 'tsd-05',
      difficulty: 'Easy',
      patternTags: ['TCS', 'Zoho'],
      question: "Two cities A and B are 300 km apart. A car starts from A towards B at 60 km/h, and at the same time another car starts from B towards A at 40 km/h. After how many hours will they meet?",
      options: ['3 hours', '3.5 hours', '4 hours', '4.5 hours'],
      correctIndex: 0,
      steps: [
        { label: 'Given', text: 'Distance = 300 km. Car 1 from A at 60 km/h, Car 2 from B at 40 km/h, moving towards each other.' },
        { label: 'Concept', text: 'For objects moving in OPPOSITE (approaching) directions, relative speed = SUM of their speeds.' },
        { label: 'Working', text: 'Relative speed = 60 + 40 = 100 km/h.' },
        { label: 'Answer', text: 'Time to meet = 300/100 = 3 hours.' },
      ],
      commonTrap:
        "Using the DIFFERENCE of speeds instead of the sum (300/20 = 15 hours) — the difference formula applies to same-direction (catching-up) motion, not two objects approaching each other.",
      alternateMethod: {
        label: 'Position-equation method',
        steps: [
          '60t + 40t = 300 → 100t = 300 → t = 3 hours.',
        ],
      },
    },
    {
      id: 'tsd-06',
      difficulty: 'Medium',
      patternTags: ['TCS', 'Infosys'],
      question: "A train 150 metres long is running at a speed of 54 km/h. How long will it take to cross a stationary pole?",
      options: ['9 seconds', '10 seconds', '11 seconds', '12 seconds'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: 'Train length = 150m, speed = 54 km/h.' },
        { label: 'Concept', text: 'Crossing a POLE (a point object) means the train travels a distance equal to its OWN LENGTH. Convert speed to m/s first for unit consistency.' },
        { label: 'Working', text: '54 km/h = 54 × 5/18 = 15 m/s.' },
        { label: 'Answer', text: 'Time = 150/15 = 10 seconds.' },
      ],
      commonTrap:
        "Forgetting to convert km/h to m/s and dividing 150 by 54 directly (≈2.78) — length is in metres and speed in km/h, so units must be matched before dividing.",
      alternateMethod: {
        label: 'Direct km/h method',
        steps: [
          'Convert train length to km: 150m = 0.15 km.',
          'Time = 0.15/54 hours = 0.15/54 × 3,600 seconds = 10 seconds.',
        ],
      },
    },
    {
      id: 'tsd-07',
      difficulty: 'Medium',
      patternTags: ['Wipro', 'Zoho'],
      question: "A train 200 metres long, running at 72 km/h, crosses a platform in 25 seconds. Find the length of the platform.",
      options: ['280 m', '300 m', '320 m', '350 m'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: 'Train = 200m, speed = 72 km/h, crosses a platform in 25 seconds.' },
        { label: 'Concept', text: 'Crossing a PLATFORM means the train travels a distance equal to (train length + platform length), unlike crossing a pole (train length only).' },
        { label: 'Working', text: '72 km/h = 20 m/s. Distance covered in 25s = 20 × 25 = 500m.' },
        { label: 'Answer', text: 'Platform length = 500 − 200 = 300 metres.' },
      ],
      commonTrap:
        "Treating the full 500m as the platform length alone (forgetting to subtract the train's own length) — part of that distance is just the train clearing itself, not the platform.",
      alternateMethod: {
        label: 'Two-stage check',
        steps: [
          'Time to cross a pole alone = 200/20 = 10s.',
          'Extra time for the platform = 25 − 10 = 15s, covering platform length = 20 × 15 = 300m.',
        ],
      },
    },
    {
      id: 'tsd-08',
      difficulty: 'Medium',
      patternTags: ['TCS', 'Zoho'],
      question: "Two trains, 120 metres and 180 metres long, run at speeds of 42 km/h and 30 km/h respectively, in opposite directions on parallel tracks. How long will they take to cross each other?",
      options: ['12 seconds', '15 seconds', '18 seconds', '20 seconds'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: 'Lengths 120m and 180m, speeds 42 km/h and 30 km/h, opposite directions.' },
        { label: 'Concept', text: 'For two trains crossing each other, the relevant distance is the SUM of their lengths, and (for opposite directions) the relevant speed is the SUM of their speeds.' },
        { label: 'Working', text: 'Relative speed = 42 + 30 = 72 km/h = 20 m/s. Combined length = 120 + 180 = 300m.' },
        { label: 'Answer', text: 'Time = 300/20 = 15 seconds.' },
      ],
      commonTrap:
        "Using only ONE train's length (say 120m or 180m) instead of the combined length — both trains must fully clear each other, requiring the sum of both lengths.",
      alternateMethod: {
        label: 'Unit-consistent direct method',
        steps: [
          'Combined length in km = 0.3 km. Relative speed = 72 km/h.',
          'Time = 0.3/72 hours = 0.3/72 × 3,600 = 15 seconds.',
        ],
      },
    },
    {
      id: 'tsd-09',
      difficulty: 'Medium',
      patternTags: ['TCS', 'Infosys'],
      question: "A train 250 metres long running at 68 km/h overtakes another train 200 metres long running at 32 km/h in the same direction, both on parallel tracks. How long does it take for the faster train to completely pass the slower one?",
      options: ['35 seconds', '40 seconds', '45 seconds', '50 seconds'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: 'Lengths 250m and 200m, speeds 68 km/h and 32 km/h, SAME direction (overtaking).' },
        { label: 'Concept', text: 'For same-direction overtaking, relative speed = DIFFERENCE of speeds, while the relevant distance is still the SUM of both lengths.' },
        { label: 'Working', text: 'Relative speed = 68 − 32 = 36 km/h = 10 m/s. Combined length = 250 + 200 = 450m.' },
        { label: 'Answer', text: 'Time = 450/10 = 45 seconds.' },
      ],
      commonTrap:
        "Using the SUM of speeds (100 km/h) instead of the difference — that gives an unrealistically short time of about 16.2 seconds, since the sum formula applies only to opposite-direction motion, not overtaking.",
      alternateMethod: {
        label: 'Unit-consistent direct method',
        steps: [
          'Combined length in km = 0.45 km. Relative speed = 36 km/h.',
          'Time = 0.45/36 hours = 0.45/36 × 3,600 = 45 seconds.',
        ],
      },
    },
    {
      id: 'tsd-10',
      difficulty: 'Medium',
      patternTags: ['Wipro', 'Zoho'],
      question: "A boat travels 24 km downstream in 2 hours and returns upstream in 3 hours. Find the speed of the stream.",
      options: ['1 km/h', '1.2 km/h', '1.5 km/h', '2 km/h'],
      correctIndex: 3,
      steps: [
        { label: 'Given', text: 'Downstream: 24 km in 2hr. Upstream: 24 km in 3hr.' },
        { label: 'Concept', text: 'Downstream speed = Boat speed + Stream speed; Upstream speed = Boat speed − Stream speed. Stream speed = (Downstream speed − Upstream speed)/2.' },
        { label: 'Working', text: 'Downstream speed = 24/2 = 12 km/h. Upstream speed = 24/3 = 8 km/h.' },
        { label: 'Answer', text: 'Stream speed = (12 − 8)/2 = 2 km/h.' },
      ],
      commonTrap:
        "Mixing up the +/− formulas and reporting the boat's still-water speed (10 km/h) instead of the stream's speed — the two values come from different halves of the same pair of equations.",
      alternateMethod: {
        label: 'Direct formula',
        steps: [
          'Boat speed = (Downstream + Upstream)/2 = (12+8)/2 = 10 km/h.',
          'Stream speed = Downstream − Boat speed = 12 − 10 = 2 km/h.',
        ],
      },
    },
    {
      id: 'tsd-11',
      difficulty: 'Easy',
      patternTags: ['TCS', 'Zoho'],
      question: "A boat's speed in still water is 15 km/h and the speed of the stream is 3 km/h. Find the time taken by the boat to travel 54 km downstream.",
      options: ['2.5 hours', '3 hours', '3.5 hours', '4 hours'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: 'Boat speed = 15 km/h, stream speed = 3 km/h, distance = 54 km downstream.' },
        { label: 'Concept', text: 'Downstream speed = Boat speed + Stream speed (the stream helps the boat along).' },
        { label: 'Working', text: 'Downstream speed = 15 + 3 = 18 km/h.' },
        { label: 'Answer', text: 'Time = 54/18 = 3 hours.' },
      ],
      commonTrap:
        "Using the upstream speed (15 − 3 = 12 km/h) by mistake, giving 54/12 = 4.5 hours — downstream travel adds the stream's speed, it doesn't subtract it.",
      alternateMethod: {
        label: 'Verification',
        steps: [
          'At 18 km/h for 3 hours: 18 × 3 = 54 km — matches the given distance exactly.',
        ],
      },
    },
    {
      id: 'tsd-12',
      difficulty: 'Medium',
      patternTags: ['Infosys', 'Wipro'],
      question: "In a 100-metre race, A can beat B by 20 metres. In a race of 250 metres, by how many metres will A beat B, assuming the same relative speeds?",
      options: ['40 m', '45 m', '50 m', '55 m'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: 'In a 100m race, A beats B by 20m (when A covers 100m, B covers only 80m).' },
        { label: 'Concept', text: 'This establishes a FIXED SPEED RATIO between A and B (same time, different distances): Speed A : Speed B = 100 : 80 = 5 : 4 — this ratio applies to any race distance.' },
        { label: 'Working', text: 'In a 250m race, when A covers 250m, B covers (4/5) × 250 = 200m in the same time.' },
        { label: 'Answer', text: 'A beats B by 250 − 200 = 50 metres.' },
      ],
      commonTrap:
        "Assuming the head-start gap of 20 metres stays the SAME regardless of race distance — but it's the RATIO of speeds (not the absolute gap) that stays constant, so the gap itself grows with a longer race.",
      alternateMethod: {
        label: 'Direct proportion shortcut',
        steps: [
          'The head-start gap scales linearly with race length: 20 × (250/100) = 50 metres — the same answer, reached in one step.',
        ],
      },
    },
    {
      id: 'tsd-13',
      difficulty: 'Hard',
      patternTags: ['TCS', 'Zoho'],
      question: "In a race of 600 metres, A gives B a head start of 100 metres and still beats B by 20 seconds. If A's speed is 15 m/s, find B's speed.",
      options: ['7.5 m/s', '8 m/s', '8.33 m/s', '9 m/s'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: 'Race = 600m, B starts 100m ahead (so B only runs 500m), A\'s speed = 15 m/s, A beats B by 20 seconds.' },
        { label: 'Concept', text: "Find A's actual time for the full race, then B's time is 20 seconds MORE than that; use B's time with B's actual running distance (600 − 100 = 500m) to find B's speed." },
        { label: 'Working', text: "A's time = 600/15 = 40 seconds. B's time = 40 + 20 = 60 seconds, covering 500m." },
        { label: 'Answer', text: "B's speed = 500/60 = 8.33 m/s." },
      ],
      commonTrap:
        "Using the full 600m as B's running distance (forgetting the 100m head start reduces how far B actually runs): 600/60 = 10 m/s — this overstates B's speed by ignoring the head start.",
      alternateMethod: {
        label: 'Verification',
        steps: [
          'If B runs at 8.33 m/s for 60 seconds: 8.33 × 60 = 500m — matches the 500m B actually needed to cover.',
        ],
      },
    },
    {
      id: 'tsd-14',
      difficulty: 'Medium',
      patternTags: ['TCS', 'Infosys'],
      question: "Two runners start at the same point on a circular track of length 400 metres, at the same time, running in OPPOSITE directions. Their speeds are 5 m/s and 3 m/s. After how many seconds will they meet for the first time?",
      options: ['40 seconds', '45 seconds', '50 seconds', '60 seconds'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: 'Circular track = 400m, opposite directions, speeds 5 m/s and 3 m/s.' },
        { label: 'Concept', text: 'Runners moving in OPPOSITE directions on a circular track meet when their combined distance covered equals one full lap; relative speed = sum of speeds.' },
        { label: 'Working', text: 'Relative speed = 5 + 3 = 8 m/s.' },
        { label: 'Answer', text: 'Time = 400/8 = 50 seconds.' },
      ],
      commonTrap:
        "Using the DIFFERENCE of speeds (5 − 3 = 2 m/s) instead of the sum, giving 400/2 = 200 seconds — the difference formula applies to same-direction motion, not opposite directions.",
      alternateMethod: {
        label: 'Position-equation method',
        steps: [
          'Combined distance covered = one full lap: 5t + 3t = 400 → 8t = 400 → t = 50 seconds.',
        ],
      },
    },
    {
      id: 'tsd-15',
      difficulty: 'Medium',
      patternTags: ['Wipro', 'Zoho'],
      question: "Two runners start at the same point on a circular track of length 300 metres, at the same time, running in the SAME direction. Their speeds are 7 m/s and 4 m/s. After how many seconds will the faster runner first be exactly one lap ahead of the slower runner?",
      options: ['75 seconds', '90 seconds', '100 seconds', '120 seconds'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: 'Circular track = 300m, same direction, speeds 7 m/s and 4 m/s.' },
        { label: 'Concept', text: 'Being exactly one lap ahead requires closing a 300m gap at the RELATIVE (difference) speed, since both run in the same direction.' },
        { label: 'Working', text: 'Relative speed = 7 − 4 = 3 m/s.' },
        { label: 'Answer', text: 'Time = 300/3 = 100 seconds.' },
      ],
      commonTrap:
        "Using the SUM of speeds instead of the difference, giving 300/11 ≈ 27.3 seconds — the sum formula applies to opposite-direction motion, not to gaining a lap in the same direction.",
      alternateMethod: {
        label: 'Position-equation method',
        steps: [
          '7t − 4t = 300 → 3t = 300 → t = 100 seconds.',
        ],
      },
    },
    {
      id: 'tsd-16',
      difficulty: 'Hard',
      patternTags: ['TCS', 'Zoho'],
      question: "A man covers a certain distance at a speed of 40 km/h and reaches his destination 15 minutes late. Had he traveled at 60 km/h, he would have reached 15 minutes early. Find the distance.",
      options: ['55 km', '60 km', '65 km', '70 km'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: 'At 40 km/h, arrives 15 min late. At 60 km/h, arrives 15 min early. Same distance, same scheduled time.' },
        { label: 'Concept', text: 'Set up the scheduled time T from BOTH scenarios (D/S = T ± time-difference in hours), then eliminate T to solve for the distance D.' },
        { label: 'Working', text: 'D/40 = T + 0.25 ...(i); D/60 = T − 0.25 ...(ii). Subtracting: D(1/40 − 1/60) = 0.5 → D/120 = 0.5.' },
        { label: 'Answer', text: 'D = 0.5 × 120 = 60 km.' },
      ],
      commonTrap:
        "Trying to use only ONE of the two given scenarios to find D directly — with two unknowns (D and the scheduled time T), a single equation can't be solved on its own; both scenarios must be combined to eliminate T.",
      alternateMethod: {
        label: 'Verification via scheduled time',
        steps: [
          'From the equations, T = 1.25 hours (75 minutes).',
          'Check: D/40 = 60/40 = 1.5hr = 90min = 75+15 ✓ (15 min late). D/60 = 60/60 = 1hr = 60min = 75−15 ✓ (15 min early).',
        ],
      },
    },
    {
      id: 'tsd-17',
      difficulty: 'Medium',
      patternTags: ['Infosys', 'Wipro'],
      question: "A car travels for 2 hours at 50 km/h and for the next 3 hours at 60 km/h. Find its average speed for the entire journey.",
      options: ['52 km/h', '54 km/h', '56 km/h', '58 km/h'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: '2 hours at 50 km/h, then 3 hours at 60 km/h (different TIME durations, not equal distances).' },
        { label: 'Concept', text: 'Average speed is ALWAYS total distance ÷ total time. When time durations (not distances) are given, simply add up the distances and divide by total time — the harmonic-mean shortcut used for equal-DISTANCE problems does NOT apply here.' },
        { label: 'Working', text: 'Distance 1 = 2×50 = 100km. Distance 2 = 3×60 = 180km. Total distance = 280km, total time = 5hr.' },
        { label: 'Answer', text: 'Average speed = 280/5 = 56 km/h.' },
      ],
      commonTrap:
        "Applying the equal-distance harmonic-mean formula (2×50×60/(50+60) = 54.5 km/h) here by habit — that formula only applies when the DISTANCES are equal; here it's the TIME durations that are fixed, calling for the plain total-distance/total-time approach instead.",
      alternateMethod: {
        label: 'Weighted-by-time method',
        steps: [
          'Average speed = (Time1×Speed1 + Time2×Speed2)/(Time1+Time2) = (2×50 + 3×60)/5 = (100+180)/5 = 56 km/h.',
        ],
      },
    },
    {
      id: 'tsd-18',
      difficulty: 'Medium',
      patternTags: ['TCS', 'Zoho'],
      question: "A train 180 metres long is running at 63 km/h. It crosses a man walking in the SAME direction as the train, at a speed of 3 km/h. Find the time taken by the train to cross the man.",
      options: ['9 seconds', '10 seconds', '10.8 seconds', '12 seconds'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: 'Train = 180m, speed = 63 km/h, man walking SAME direction at 3 km/h.' },
        { label: 'Concept', text: 'Crossing a walking person (treated as a point, like a pole) means covering the train\'s own length, but at the RELATIVE speed (difference, since same direction).' },
        { label: 'Working', text: 'Relative speed = 63 − 3 = 60 km/h = 60 × 5/18 = 50/3 m/s.' },
        { label: 'Answer', text: 'Time = 180 ÷ (50/3) = 180×3/50 = 10.8 seconds.' },
      ],
      commonTrap:
        "Using the train's full speed (63 km/h) without adjusting for the man's own motion — ignoring the relative-speed concept entirely gives a shorter, incorrect time.",
      alternateMethod: {
        label: 'Direct m/s conversion',
        steps: [
          '63 km/h = 17.5 m/s, 3 km/h = 0.833 m/s (same direction).',
          'Relative speed = 17.5 − 0.833 = 16.667 m/s. Time = 180/16.667 = 10.8 seconds.',
        ],
      },
    },
    {
      id: 'tsd-19',
      difficulty: 'Medium',
      patternTags: ['Infosys', 'Wipro'],
      question: "A train 250 metres long is running at 54 km/h. It crosses a man walking in the OPPOSITE direction at 6 km/h. Find the time taken by the train to cross the man.",
      options: ['12 seconds', '13.5 seconds', '15 seconds', '18 seconds'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: 'Train = 250m, speed = 54 km/h, man walking OPPOSITE direction at 6 km/h.' },
        { label: 'Concept', text: 'For opposite-direction motion, relative speed = SUM of speeds; crossing the man means covering the train\'s own length at this relative speed.' },
        { label: 'Working', text: 'Relative speed = 54 + 6 = 60 km/h = 60 × 5/18 = 50/3 m/s.' },
        { label: 'Answer', text: 'Time = 250 ÷ (50/3) = 250×3/50 = 15 seconds.' },
      ],
      commonTrap:
        "Using the DIFFERENCE of speeds (54 − 6 = 48 km/h) instead of the sum — that formula applies to same-direction motion, not to two objects approaching each other.",
      alternateMethod: {
        label: 'Direct m/s method',
        steps: [
          '54 km/h = 15 m/s, 6 km/h = 1.667 m/s. Since opposite directions: relative speed = 15+1.667 = 16.667 m/s.',
          'Time = 250/16.667 = 15 seconds.',
        ],
      },
    },
    {
      id: 'tsd-20',
      difficulty: 'Medium',
      patternTags: ['TCS', 'Infosys'],
      question: "A boat can travel a certain distance downstream in 3 hours and the same distance upstream in 6 hours. If the speed of the stream is 5 km/h, find the speed of the boat in still water.",
      options: ['10 km/h', '12 km/h', '15 km/h', '18 km/h'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: 'Downstream time = 3hr, upstream time = 6hr, same distance both ways, stream speed = 5 km/h.' },
        { label: 'Concept', text: 'Same distance D means 3×(downstream speed) = 6×(upstream speed); substitute (b+5) and (b−5) for the boat speed b, and solve.' },
        { label: 'Working', text: '3(b+5) = 6(b−5) → 3b+15 = 6b−30 → 3b = 45.' },
        { label: 'Answer', text: 'Boat speed in still water = 15 km/h.' },
      ],
      commonTrap:
        "Averaging the given time values or misapplying the stream speed directly instead of setting up the distance-equality equation — this skips the algebra step that's actually required to link the two travel times.",
      alternateMethod: {
        label: 'Ratio method',
        steps: [
          'Downstream time : Upstream time = 3:6 = 1:2, so speeds are in the INVERSE ratio 2:1 (downstream:upstream), since distance is fixed.',
          '(b+5):(b−5) = 2:1 → b+5 = 2(b−5) → b+5 = 2b−10 → b = 15.',
        ],
      },
    },
    {
      id: 'tsd-21',
      difficulty: 'Hard',
      patternTags: ['Wipro', 'Zoho'],
      question: "A cyclist starts from a point at 8:00 AM at a speed of 12 km/h. Another cyclist starts from the same point in the same direction at 9:00 AM at a speed of 18 km/h. At what time will the second cyclist catch up with the first?",
      options: ['10:00 AM', '10:30 AM', '11:00 AM', '11:30 AM'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: 'Cyclist 1 starts 8AM at 12 km/h; Cyclist 2 starts 9AM (1 hour later) at 18 km/h, same direction.' },
        { label: 'Concept', text: 'Find the head-start DISTANCE built up during the 1-hour gap, then use relative speed (difference, same direction) to find how long it takes to close that gap.' },
        { label: 'Working', text: 'Head-start distance (by 9AM) = 12×1 = 12km. Relative speed = 18−12 = 6 km/h. Time to catch up = 12/6 = 2 hours (from 9AM).' },
        { label: 'Answer', text: '9:00 AM + 2 hours = 11:00 AM.' },
      ],
      commonTrap:
        "Using the 1-hour head-start TIME directly as the catch-up time — forgetting to convert it into a head-start DISTANCE first and apply the relative-speed calculation.",
      alternateMethod: {
        label: 'Position-equation method',
        steps: [
          'Let t = hours after 9AM. Cyclist 1\'s total distance (from 8AM) = 12×(1+t). Cyclist 2\'s distance (from 9AM) = 18t.',
          '12(1+t) = 18t → 12+12t = 18t → t = 2 hours → catch-up at 11:00 AM.',
        ],
      },
    },
    {
      id: 'tsd-22',
      difficulty: 'Medium',
      patternTags: ['TCS', 'Zoho'],
      question: "Excluding stoppages, a train's speed is 50 km/h, and including stoppages, its speed is 40 km/h. For how many minutes does the train stop per hour?",
      options: ['8 minutes', '10 minutes', '12 minutes', '15 minutes'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: 'Speed excluding stoppages = 50 km/h, speed including stoppages = 40 km/h.' },
        { label: 'Concept', text: 'In a fixed 1-hour (clock) period, find the distance actually covered at the "including stoppages" rate, then find the RUNNING time (at the faster, no-stoppage speed) needed to cover that same distance — the leftover time is the stoppage.' },
        { label: 'Working', text: 'In 1 hour (total), distance covered = 40km. At 50 km/h (no stoppage), running time for 40km = 40/50 hour = 48 minutes.' },
        { label: 'Answer', text: 'Stoppage time per hour = 60 − 48 = 12 minutes.' },
      ],
      commonTrap:
        "Simply subtracting the two speeds (50−40 = 10 km/h) and mistaking that raw difference for the number of minutes — the correct method requires converting through the actual running-time-needed, not just subtracting speeds.",
      alternateMethod: {
        label: 'Direct formula',
        steps: [
          'Stoppage time per hour (minutes) = [(Speed excl. − Speed incl.)/Speed excl.] × 60.',
          '= [(50−40)/50] × 60 = (10/50)×60 = 12 minutes.',
        ],
      },
    },
    {
      id: 'tsd-23',
      difficulty: 'Medium',
      patternTags: ['Infosys', 'Wipro'],
      question: "A train 300 metres long is moving at 45 km/h. It takes 30 seconds to completely cross another train moving in the OPPOSITE direction at 27 km/h. Find the length of the other train.",
      options: ['250 m', '280 m', '300 m', '320 m'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: 'Train 1 = 300m, speed = 45 km/h; Train 2 speed = 27 km/h, opposite direction; crossing time = 30s.' },
        { label: 'Concept', text: 'Combined length = Relative speed × Time (relative speed = sum, since opposite directions); subtract the known train\'s length to find the other train\'s length.' },
        { label: 'Working', text: 'Relative speed = 45+27 = 72 km/h = 20 m/s. Combined length = 20 × 30 = 600m.' },
        { label: 'Answer', text: 'Other train\'s length = 600 − 300 = 300 metres.' },
      ],
      commonTrap:
        "Forgetting to subtract Train 1's own length from the combined 600m, and answering 600m directly as if it were the other train's length alone.",
      alternateMethod: {
        label: 'Unit-consistent check',
        steps: [
          'Combined length in km = 0.6km. Relative speed = 72 km/h.',
          'Time = 0.6/72 hours = 0.6/72×3,600 = 30 seconds — matches the given time, confirming the other train is 300m.',
        ],
      },
    },
    {
      id: 'tsd-24',
      difficulty: 'Easy',
      patternTags: ['TCS', 'Infosys'],
      question: "The ratio of the speeds of two cars is 3:4. If the first car covers a certain distance in 2 hours, find the time taken by the second car to cover the same distance.",
      options: ['1 hour', '1.5 hours', '2 hours', '2.5 hours'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: 'Speed ratio (Car A : Car B) = 3:4. Car A covers the distance in 2 hours.' },
        { label: 'Concept', text: 'For the SAME distance, time is INVERSELY proportional to speed: Time_B = Time_A × (Speed_A/Speed_B).' },
        { label: 'Working', text: 'Time_B = 2 × (3/4).' },
        { label: 'Answer', text: 'Time_B = 1.5 hours.' },
      ],
      commonTrap:
        "Applying the speed ratio directly (rather than inversely) to the time: Time_B = 2×(4/3) ≈ 2.67 hours — this gets the inverse relationship backwards, since a FASTER car must take LESS time, not more.",
      alternateMethod: {
        label: 'Ratio method',
        steps: [
          'Speed A : Speed B = 3:4 ⇒ Time A : Time B = 4:3 (inverse ratio) for the same distance.',
          'Time A = 2hr = 4 parts → 1 part = 0.5hr. Time B = 3 parts = 1.5 hours.',
        ],
      },
    },
    {
      id: 'tsd-25',
      difficulty: 'Medium',
      patternTags: ['Wipro', 'Zoho'],
      question: "Two friends start walking towards each other from two points 45 km apart. One walks at 6 km/h and the other at 9 km/h. How far from the FIRST friend's starting point will they meet?",
      options: ['15 km', '18 km', '20 km', '22.5 km'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: '45km apart, speeds 6 km/h and 9 km/h, walking towards each other.' },
        { label: 'Concept', text: 'Find the meeting TIME using relative (combined) speed, then use that time with the FIRST friend\'s own speed to find how far specifically they walked.' },
        { label: 'Working', text: 'Relative speed = 6+9 = 15 km/h. Time to meet = 45/15 = 3hr.' },
        { label: 'Answer', text: 'Distance covered by first friend = 6×3 = 18 km.' },
      ],
      commonTrap:
        "Splitting the 45km equally between the two friends (45/2 = 22.5km) — that would only be correct if both walked at the SAME speed; here their speeds differ, so the meeting point is NOT the midpoint.",
      alternateMethod: {
        label: 'Ratio method',
        steps: [
          'Speeds are in ratio 6:9 = 2:3, so distances covered (in the same time) are also in ratio 2:3.',
          'Total 45km splits into 5 parts → 1 part = 9km. First friend covers 2 parts = 18km.',
        ],
      },
    },
  ],
  'si-ci': [
    {
      id: 'sici-01',
      difficulty: 'Easy',
      patternTags: ['TCS', 'Infosys'],
      question: "Find the simple interest on ₹8,000 at 9% per annum for 3 years.",
      options: ['2,160', '2,280', '2,340', '2,400'],
      correctIndex: 0,
      steps: [
        { label: 'Given', text: 'Principal (P) = ₹8,000, Rate (R) = 9% per annum, Time (T) = 3 years.' },
        { label: 'Concept', text: 'Simple Interest = P × R × T / 100.' },
        { label: 'Working', text: '8,000 × 9 × 3 / 100.' },
        { label: 'Answer', text: 'SI = ₹2,160.' },
      ],
      commonTrap:
        "Misreading the rate as 9.5% instead of 9% (a common slip when scanning quickly) — this gives ₹2,280 instead of the correct ₹2,160.",
      alternateMethod: {
        label: 'Unit-rate method',
        steps: [
          'SI for 1 year = 8,000 × 9/100 = ₹720.',
          'SI for 3 years = 720 × 3 = ₹2,160.',
        ],
      },
    },
    {
      id: 'sici-02',
      difficulty: 'Easy',
      patternTags: ['TCS', 'Wipro'],
      question: "Find the compound interest on ₹10,000 at 10% per annum for 2 years, compounded annually.",
      options: ['2,000', '2,100', '2,200', '2,300'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: 'P = ₹10,000, R = 10% per annum, T = 2 years, compounded annually.' },
        { label: 'Concept', text: 'CI = P(1+R/100)^T − P — interest earns interest each year, unlike simple interest.' },
        { label: 'Working', text: 'Amount = 10,000 × 1.10 × 1.10 = ₹12,100.' },
        { label: 'Answer', text: 'CI = 12,100 − 10,000 = ₹2,100.' },
      ],
      commonTrap:
        "Computing plain SI instead (10,000×10×2/100 = 2,000) and mistaking it for CI — CI is always greater than SI once T exceeds 1 year, because interest compounds on itself in year 2.",
      alternateMethod: {
        label: 'Year-by-year method',
        steps: [
          'Year 1 interest = 10,000 × 0.10 = 1,000 → balance = 11,000.',
          'Year 2 interest = 11,000 × 0.10 = 1,100 → balance = 12,100.',
          'Total CI = 1,000 + 1,100 = ₹2,100.',
        ],
      },
    },
    {
      id: 'sici-03',
      difficulty: 'Medium',
      patternTags: ['Infosys', 'Zoho'],
      question: "Find the difference between the compound interest and the simple interest on ₹25,000 for 2 years at 8% per annum.",
      options: ['140', '160', '180', '200'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: 'P = ₹25,000, R = 8%, T = 2 years.' },
        { label: 'Concept', text: 'For exactly 2 years, CI − SI = P × (R/100)² — a direct shortcut formula, since the only difference between CI and SI over 2 years is the "interest on the first year\'s interest".' },
        { label: 'Working', text: '25,000 × (0.08)² = 25,000 × 0.0064.' },
        { label: 'Answer', text: 'Difference = ₹160.' },
      ],
      commonTrap:
        "Using the THREE-year difference formula P×(R/100)²×(3+R/100) instead of the 2-year formula P×(R/100)² — the extra (3+r) multiplying term only applies when comparing CI and SI over three years, not two.",
      alternateMethod: {
        label: 'Direct calculation',
        steps: [
          'SI = 25,000 × 8 × 2/100 = ₹4,000.',
          'CI = 25,000×1.08×1.08 − 25,000 = 29,160 − 25,000 = ₹4,160.',
          'Difference = 4,160 − 4,000 = ₹160 — matches the shortcut formula.',
        ],
      },
    },
    {
      id: 'sici-04',
      difficulty: 'Hard',
      patternTags: ['TCS', 'Zoho'],
      question: "Find the difference between the compound interest and the simple interest on ₹15,000 for 3 years at 10% per annum.",
      options: ['430', '465', '480', '510'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: 'P = ₹15,000, R = 10%, T = 3 years.' },
        { label: 'Concept', text: 'For 3 years, CI − SI = P × (R/100)² × (3 + R/100) — an extended version of the 2-year shortcut.' },
        { label: 'Working', text: '15,000 × (0.10)² × (3.10) = 15,000 × 0.01 × 3.10.' },
        { label: 'Answer', text: 'Difference = ₹465.' },
      ],
      commonTrap:
        "Using the 2-year formula P×(R/100)² = 150 instead of the 3-year version — that formula only captures one year's worth of 'interest on interest', not the extra layering that builds up by year 3.",
      alternateMethod: {
        label: 'Direct calculation',
        steps: [
          'SI = 15,000×10×3/100 = ₹4,500.',
          'CI = 15,000×(1.1)³ − 15,000 = 19,965 − 15,000 = ₹4,965.',
          'Difference = 4,965 − 4,500 = ₹465 — matches the shortcut formula.',
        ],
      },
    },
    {
      id: 'sici-05',
      difficulty: 'Easy',
      patternTags: ['TCS', 'Infosys'],
      question: "The simple interest on a sum of money for 4 years at 6% per annum is ₹1,440. Find the sum.",
      options: ['5,500', '5,800', '6,000', '6,200'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: 'SI = ₹1,440, R = 6%, T = 4 years.' },
        { label: 'Concept', text: 'P = SI × 100 / (R × T).' },
        { label: 'Working', text: '1,440 × 100 / (6 × 4) = 144,000 / 24.' },
        { label: 'Answer', text: 'P = ₹6,000.' },
      ],
      commonTrap:
        "Forgetting to divide by T as well as R (e.g. computing 1,440×100/6 = 24,000) — both the rate AND the time must be divided out to isolate the principal.",
      alternateMethod: {
        label: 'Unitary method',
        steps: [
          'SI for 1 year = 1,440/4 = ₹360.',
          'P = 360 × 100/6 = ₹6,000.',
        ],
      },
    },
    {
      id: 'sici-06',
      difficulty: 'Easy',
      patternTags: ['Wipro', 'Zoho'],
      question: "A sum of ₹12,000 earns a simple interest of ₹2,880 in 4 years. Find the rate of interest per annum.",
      options: ['5%', '6%', '7%', '8%'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: 'P = ₹12,000, SI = ₹2,880, T = 4 years.' },
        { label: 'Concept', text: 'R = SI × 100 / (P × T).' },
        { label: 'Working', text: '2,880 × 100 / (12,000 × 4) = 288,000/48,000.' },
        { label: 'Answer', text: 'R = 6% per annum.' },
      ],
      commonTrap:
        "Forgetting to divide by T (2,880×100/12,000 = 24%) — the full formula needs T in the denominator too, not just P.",
      alternateMethod: {
        label: 'Unitary method',
        steps: [
          'SI per year = 2,880/4 = ₹720.',
          'R = 720×100/12,000 = 6%.',
        ],
      },
    },
    {
      id: 'sici-07',
      difficulty: 'Easy',
      patternTags: ['TCS', 'Infosys'],
      question: "In how many years will a sum of ₹9,000 yield a simple interest of ₹2,700 at 10% per annum?",
      options: ['2 years', '3 years', '4 years', '5 years'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: 'P = ₹9,000, SI = ₹2,700, R = 10%.' },
        { label: 'Concept', text: 'T = SI × 100 / (P × R).' },
        { label: 'Working', text: '2,700 × 100 / (9,000 × 10) = 270,000/90,000.' },
        { label: 'Answer', text: 'T = 3 years.' },
      ],
      commonTrap:
        "Forgetting to divide by R as well as P (e.g. computing 2,700×100/9,000 = 30, and mistakenly reading that as 30 years) — the rate must also be divided out to isolate time.",
      alternateMethod: {
        label: 'Unitary method',
        steps: [
          'SI per year at 10% = 9,000×10/100 = ₹900.',
          'T = 2,700/900 = 3 years.',
        ],
      },
    },
    {
      id: 'sici-08',
      difficulty: 'Medium',
      patternTags: ['Infosys', 'Zoho'],
      question: "Find the compound interest on ₹4,000 at 10% per annum for 1 year, compounded half-yearly.",
      options: ['400', '410', '420', '440'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: 'P = ₹4,000, annual R = 10%, T = 1 year, compounded HALF-YEARLY.' },
        { label: 'Concept', text: 'For half-yearly compounding, halve the rate and double the number of periods: rate/period = R/2 = 5%, periods = 2T = 2.' },
        { label: 'Working', text: 'Amount = 4,000 × (1.05)² = 4,000 × 1.1025.' },
        { label: 'Answer', text: 'CI = 4,410 − 4,000 = ₹410.' },
      ],
      commonTrap:
        "Assuming compounding frequency makes no difference within a single year and expecting the same ₹400 as simple interest — half-yearly compounding credits interest twice, so the second half-year also earns interest on the first half-year's interest, giving slightly more.",
      alternateMethod: {
        label: 'Period-by-period method',
        steps: [
          'After 6 months: 4,000 × 1.05 = ₹4,200.',
          'After 12 months: 4,200 × 1.05 = ₹4,410. CI = ₹410.',
        ],
      },
    },
    {
      id: 'sici-09',
      difficulty: 'Medium',
      patternTags: ['TCS', 'Wipro'],
      question: "Find the amount on ₹8,000 at 8% per annum for 6 months, compounded quarterly.",
      options: ['8,160', '8,240', '8,323.20', '8,400'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: 'P = ₹8,000, annual R = 8%, T = 6 months, compounded QUARTERLY.' },
        { label: 'Concept', text: 'Quarterly rate = R/4 = 2%. Number of quarters in 6 months = 2.' },
        { label: 'Working', text: 'Amount = 8,000 × (1.02)² = 8,000 × 1.0404.' },
        { label: 'Answer', text: 'Amount = ₹8,323.20.' },
      ],
      commonTrap:
        "Using the half-yearly rate (4%) for a single period instead of properly splitting 6 months into 2 quarterly periods at 2% each — an easy mix-up between compounding frequencies (this would give ₹8,320, close but subtly wrong).",
      alternateMethod: {
        label: 'Period-by-period method',
        steps: [
          'After Q1 (3 months): 8,000 × 1.02 = ₹8,160.',
          'After Q2 (6 months): 8,160 × 1.02 = ₹8,323.20.',
        ],
      },
    },
    {
      id: 'sici-10',
      difficulty: 'Hard',
      patternTags: ['TCS', 'Zoho'],
      question: "A sum of money doubles itself in 8 years at simple interest. In how many years will it become 4 times itself, at the same rate?",
      options: ['16 years', '20 years', '24 years', '28 years'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: 'Sum doubles in 8 years at SI.' },
        { label: 'Concept', text: '"Doubles" means the SI earned equals the principal itself (Amount = 2P ⇒ Interest = P). Use this to find the rate, then apply the same rate to the "4×" target (Interest = 3P).' },
        { label: 'Working', text: 'P = P×R×8/100 ⇒ R = 12.5%. For 4×: Interest needed = 3P = P×12.5×T/100.' },
        { label: 'Answer', text: 'T = 300/12.5 = 24 years.' },
      ],
      commonTrap:
        "Assuming the time simply scales in proportion to the 'multiple' (doubling in 8 years ⇒ quadrupling in 16 years) — but SI grows LINEARLY from the original principal at a fixed rate, it doesn't repeat the doubling pattern.",
      alternateMethod: {
        label: 'Direct ratio method',
        steps: [
          'Going from 1× to 2× needs 1P of interest, taking 8 years.',
          'Going from 1× to 4× needs 3P of interest — three times as much — so it takes 3×8 = 24 years (since SI accrues at a constant rate per year).',
        ],
      },
    },
    {
      id: 'sici-11',
      difficulty: 'Hard',
      patternTags: ['Infosys', 'Wipro'],
      question: "A sum invested at compound interest becomes 4 times itself in 2 years. In how many years will it become 64 times itself, at the same rate?",
      options: ['4 years', '5 years', '6 years', '8 years'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: 'CI sum becomes 4× itself in 2 years.' },
        { label: 'Concept', text: 'For CI, Amount/Principal = (1+r)^T. Since 4 = 2², the per-year growth factor (1+r) must itself be 2.' },
        { label: 'Working', text: '(1+r)² = 4 ⇒ 1+r = 2. For the target: (1+r)^T = 64 = 2⁶ ⇒ 2^T = 2⁶.' },
        { label: 'Answer', text: 'T = 6 years.' },
      ],
      commonTrap:
        "Assuming the relationship scales linearly (since 64/4=16, guessing T = 2×16 = 32 years) — CI growth is exponential, not linear, so multiples of the growth don't scale time proportionally like that.",
      alternateMethod: {
        label: 'Step-doubling method',
        steps: [
          'Since (1+r)=2, the sum literally DOUBLES every single year.',
          '4× = doubled twice (matches the given 2 years). 64 = 2⁶, i.e. "doubled 6 times" — so it takes 6 years.',
        ],
      },
    },
    {
      id: 'sici-12',
      difficulty: 'Medium',
      patternTags: ['TCS', 'Zoho'],
      question: "Find the simple interest on ₹6,000 at 9% per annum for 2 years 4 months.",
      options: ['1,200', '1,260', '1,320', '1,350'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: 'P = ₹6,000, R = 9%, T = 2 years 4 months.' },
        { label: 'Concept', text: 'Convert the time into years as a fraction before applying SI = PRT/100 (4 months = 4/12 = 1/3 year).' },
        { label: 'Working', text: 'T = 2 + 1/3 = 7/3 years. SI = 6,000 × 9 × (7/3) / 100.' },
        { label: 'Answer', text: 'SI = ₹1,260.' },
      ],
      commonTrap:
        "Rounding 2 years 4 months to '2.5 years', or ignoring the extra 4 months entirely and using T=2 years — either shortcut shifts the answer away from the true fractional time.",
      alternateMethod: {
        label: 'Split-period method',
        steps: [
          'SI for 2 whole years = 6,000×9×2/100 = ₹1,080.',
          'SI for the extra 4 months (1/3 year) = 6,000×9×(1/3)/100 = ₹180.',
          'Total = 1,080 + 180 = ₹1,260.',
        ],
      },
    },
    {
      id: 'sici-13',
      difficulty: 'Medium',
      patternTags: ['TCS', 'Infosys'],
      question: "A sum of ₹15,000 is divided into two parts. One part is lent at 8% per annum and the other at 10% per annum (both simple interest). If the total interest earned in 1 year is ₹1,380, find the amount lent at 10%.",
      options: ['7,500', '8,000', '8,500', '9,000'],
      correctIndex: 3,
      steps: [
        { label: 'Given', text: 'Total = ₹15,000 split into two parts at 8% and 10%; total 1-year interest = ₹1,380.' },
        { label: 'Concept', text: 'Let x = amount at the higher rate (10%), so the other part = (15,000 − x). Set up a total-interest equation and solve for x.' },
        { label: 'Working', text: '0.10x + 0.08(15,000 − x) = 1,380 → 0.10x + 1,200 − 0.08x = 1,380 → 0.02x = 180.' },
        { label: 'Answer', text: 'x = ₹9,000 (amount lent at 10%).' },
      ],
      commonTrap:
        "Splitting the ₹15,000 using the rate ratio directly (8:10) — there's no mathematical basis for this; the actual split comes only from solving the total-interest equation.",
      alternateMethod: {
        label: 'Alligation method',
        steps: [
          'Overall rate = 1,380/15,000 × 100 = 9.2%.',
          'By alligation: Amount(8%) : Amount(10%) = (10−9.2) : (9.2−8) = 0.8 : 1.2 = 2 : 3.',
          'Total 5 parts = ₹15,000 → 1 part = ₹3,000. Amount at 10% = 3×3,000 = ₹9,000.',
        ],
      },
    },
    {
      id: 'sici-14',
      difficulty: 'Medium',
      patternTags: ['Wipro', 'Zoho'],
      question: "Find the compound interest on ₹20,000 for 2 years, if the rate of interest is 5% for the first year and 8% for the second year.",
      options: ['2,600', '2,680', '2,750', '2,850'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: 'P = ₹20,000. Year 1 rate = 5%, Year 2 rate = 8%.' },
        { label: 'Concept', text: 'When rates differ by year, apply each year\'s multiplier successively, rather than averaging the rates.' },
        { label: 'Working', text: 'Amount = 20,000 × 1.05 × 1.08 = 20,000 × 1.134.' },
        { label: 'Answer', text: 'CI = 22,680 − 20,000 = ₹2,680.' },
      ],
      commonTrap:
        "Averaging the two rates ((5+8)/2 = 6.5%) and computing CI at a flat 6.5% for 2 years — this is not equivalent to applying the actual sequence of different yearly rates.",
      alternateMethod: {
        label: 'Year-by-year method',
        steps: [
          'Year 1: 20,000 × 1.05 = ₹21,000 (interest = ₹1,000).',
          'Year 2: 21,000 × 1.08 = ₹22,680 (interest = ₹1,680).',
          'Total CI = 1,000 + 1,680 = ₹2,680.',
        ],
      },
    },
    {
      id: 'sici-15',
      difficulty: 'Medium',
      patternTags: ['TCS', 'Zoho'],
      question: "The compound interest on a certain sum for 2 years at 5% per annum is ₹1,025. Find the sum.",
      options: ['9,500', '10,000', '10,250', '10,500'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: 'CI = ₹1,025, R = 5%, T = 2 years.' },
        { label: 'Concept', text: 'CI = P × [(1+R/100)^T − 1]; solve for P by dividing.' },
        { label: 'Working', text: '(1.05)² − 1 = 0.1025. P = 1,025/0.1025.' },
        { label: 'Answer', text: 'P = ₹10,000.' },
      ],
      commonTrap:
        "Using the SIMPLE interest formula in reverse (P = 1,025×100/(5×2) = ₹10,250) instead of the compound growth factor — close, but subtly wrong since it ignores that the given ₹1,025 is compound interest, not simple.",
      alternateMethod: {
        label: 'Verification',
        steps: [
          'Test P = 10,000: CI = 10,000 × [(1.05)² − 1] = 10,000 × 0.1025 = ₹1,025 — matches exactly.',
        ],
      },
    },
    {
      id: 'sici-16',
      difficulty: 'Hard',
      patternTags: ['Infosys', 'Wipro'],
      question: "The amount on a certain sum after 2 years is ₹4,410, and after 3 years (at the same compound rate) it is ₹4,851. Find the rate of interest per annum.",
      options: ['9%', '10%', '11%', '12%'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: 'Amount after 2 years = ₹4,410, Amount after 3 years = ₹4,851.' },
        { label: 'Concept', text: 'The ratio of the amount after (n+1) years to the amount after n years equals exactly (1+r), since each additional year just multiplies by one more factor of (1+r).' },
        { label: 'Working', text: '4,851 / 4,410 = 1.10.' },
        { label: 'Answer', text: '1+r = 1.10 ⇒ r = 10%.' },
      ],
      commonTrap:
        "Trying to use the DIFFERENCE (4,851−4,410=441) as if it were simple interest on some unknown principal, instead of recognizing that it's the RATIO of the two amounts that directly gives the growth factor.",
      alternateMethod: {
        label: 'Direct compounding check',
        steps: [
          'If r=10%, Amount after 3 years should be exactly 1.10× the Amount after 2 years.',
          '4,410 × 1.10 = 4,851 — matches the given figure exactly, confirming r=10% without needing to compute the principal separately.',
        ],
      },
    },
    {
      id: 'sici-17',
      difficulty: 'Medium',
      patternTags: ['TCS', 'Infosys'],
      question: "A sum of ₹8,000 is invested for 1 year at 10% per annum. Find how much MORE interest is earned if compounded half-yearly compared to compounded annually.",
      options: ['10', '15', '20', '25'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: 'P = ₹8,000, R = 10%, T = 1 year — comparing annual vs. half-yearly compounding.' },
        { label: 'Concept', text: 'For just 1 year, ANNUAL compounding gives the same result as simple interest (only one compounding event); half-yearly compounding credits interest twice, so the second half-year also earns a little interest on the first half-year\'s interest.' },
        { label: 'Working', text: 'Annual CI = 8,000 × 0.10 = ₹800. Half-yearly CI = 8,000 × (1.05)² − 8,000 = 8,820 − 8,000 = ₹820.' },
        { label: 'Answer', text: 'Extra interest = 820 − 800 = ₹20.' },
      ],
      commonTrap:
        "Assuming compounding frequency makes no difference within a single year (since '1 year is 1 year either way') and answering ₹0 — but half-yearly compounding still re-invests interest partway through the year, creating a small but real difference.",
      alternateMethod: {
        label: 'Direct extra-interest formula',
        steps: [
          'Extra interest from half-yearly (vs. annual) compounding over 1 year = P × (R/200)².',
          '8,000 × (0.05)² = 8,000 × 0.0025 = ₹20.',
        ],
      },
    },
    {
      id: 'sici-18',
      difficulty: 'Easy',
      patternTags: ['Wipro', 'Zoho'],
      question: "At what rate of simple interest per annum will a sum of money become 1.5 times itself in 5 years?",
      options: ['9%', '10%', '11%', '12%'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: 'Amount = 1.5 × Principal after 5 years, at simple interest.' },
        { label: 'Concept', text: 'Amount = 1.5P means the INTEREST earned is 0.5P (not 1.5P) — the "1.5" already includes the original principal.' },
        { label: 'Working', text: '0.5P = P × R × 5/100 ⇒ R = 0.5 × 100/5.' },
        { label: 'Answer', text: 'R = 10% per annum.' },
      ],
      commonTrap:
        "Using the full amount multiplier (1.5) instead of the interest fraction (0.5) — computing R = 1.5×100/5 = 30%, which mistakes the total amount ratio for the interest ratio.",
      alternateMethod: {
        label: 'Unitary method',
        steps: [
          'Interest needed per year = 0.5P / 5 = 0.1P, i.e. 10% of P per year.',
          'So R = 10%.',
        ],
      },
    },
    {
      id: 'sici-19',
      difficulty: 'Hard',
      patternTags: ['TCS', 'Zoho'],
      question: "A sum of ₹2,100 is to be repaid in 2 equal annual installments, at 10% per annum compound interest. Find the value of each installment.",
      options: ['1,210', '1,240', '1,270', '1,300'],
      correctIndex: 0,
      steps: [
        { label: 'Given', text: 'Loan = ₹2,100, repaid via 2 equal annual installments, CI rate = 10%.' },
        { label: 'Concept', text: 'Each future installment must be discounted back to its PRESENT VALUE at the compound rate; the sum of both present values must equal the original loan amount.' },
        { label: 'Working', text: 'Let each installment = x. x/1.1 + x/(1.1)² = 2,100. Multiplying by 1.21: 1.1x + x = 2,100×1.21 = 2,541.' },
        { label: 'Answer', text: 'x = 2,541/2.1 = ₹1,210.' },
      ],
      commonTrap:
        "Treating this like a simple-interest repayment and dividing the loan plus one year's flat interest by 2 (e.g. (2,100+210)/2 = ₹1,155) — compound-interest annuity problems require discounting each future payment separately, not a flat interest addition.",
      alternateMethod: {
        label: 'Verification via present values',
        steps: [
          'PV of 1st installment (after 1 year) = 1,210/1.1 = ₹1,100.',
          'PV of 2nd installment (after 2 years) = 1,210/1.21 = ₹1,000.',
          'Total = 1,100+1,000 = ₹2,100 — matches the loan amount exactly.',
        ],
      },
    },
    {
      id: 'sici-20',
      difficulty: 'Medium',
      patternTags: ['Infosys', 'Wipro'],
      question: "The simple interest on a sum of ₹6,000 for 2 years at 7% per annum is equal to the compound interest on another sum for 2 years at 10% per annum. Find the second sum.",
      options: ['3,800', '4,000', '4,200', '4,400'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: 'SI(₹6,000, 7%, 2yr) = CI(unknown sum, 10%, 2yr).' },
        { label: 'Concept', text: 'Compute the known SI first, then set it equal to the CI-on-P2 expression [P2 × ((1+r)² − 1)] and solve for P2.' },
        { label: 'Working', text: 'SI = 6,000×7×2/100 = ₹840. CI factor for 10%, 2yr = (1.1)² − 1 = 0.21. So P2 × 0.21 = 840.' },
        { label: 'Answer', text: 'P2 = 840/0.21 = ₹4,000.' },
      ],
      commonTrap:
        "Assuming the two sums must be equal (since the two INTERESTS are equal) and answering ₹6,000 directly — but equal interest amounts don't imply equal principals, since SI and CI grow differently and use different rates here.",
      alternateMethod: {
        label: 'Verification',
        steps: [
          'CI on ₹4,000 at 10% for 2 years = 4,000 × [(1.1)² − 1] = 4,000 × 0.21 = ₹840 — matches the SI figure exactly.',
        ],
      },
    },
    {
      id: 'sici-21',
      difficulty: 'Hard',
      patternTags: ['TCS', 'Zoho'],
      question: "Find the compound interest on ₹16,000 at 10% per annum for 2 years 6 months, with interest compounded annually and simple interest applied for the final fractional 6 months.",
      options: ['4,200', '4,260', '4,328', '4,400'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: 'P = ₹16,000, R = 10%, T = 2 years 6 months, compounded annually with SI on the final fractional half-year (the standard convention for non-integer CI periods).' },
        { label: 'Concept', text: 'Compound for the WHOLE years first, then apply SIMPLE interest (at the same rate) on the resulting amount for the remaining fractional period.' },
        { label: 'Working', text: 'After 2 years: 16,000×(1.1)² = ₹19,360. For the extra 6 months (0.5yr), SI on 19,360 at 10% = 19,360×0.10×0.5 = ₹968.' },
        { label: 'Answer', text: 'Total amount = 19,360+968 = ₹20,328. CI = 20,328−16,000 = ₹4,328.' },
      ],
      commonTrap:
        "Treating the 6 months as another FULL compounding period instead of applying simple interest for the fractional leftover — the standard convention specifically uses SI for the final partial period, not a fractional compounding step.",
      alternateMethod: {
        label: 'Direct formula',
        steps: [
          'Amount = P × (1+R/100)² × (1 + (R/100)×0.5) = 16,000 × 1.21 × 1.05 = 16,000 × 1.2705 = ₹20,328.',
          'CI = ₹4,328.',
        ],
      },
    },
    {
      id: 'sici-22',
      difficulty: 'Medium',
      patternTags: ['Infosys', 'Wipro'],
      question: "The compound interest on a sum for the SECOND year alone is ₹1,320, at a rate of 10% per annum. Find the compound interest for the FIRST year.",
      options: ['1,100', '1,200', '1,250', '1,300'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: "CI for the 2nd year alone = ₹1,320, rate = 10%." },
        { label: 'Concept', text: "Each year's OWN interest is (1+r) times the previous year's interest, since the base for year 2's interest is the year-1 AMOUNT (principal + year-1 interest)." },
        { label: 'Working', text: 'Interest_year2 / Interest_year1 = (1+r) = 1.10. So Interest_year1 = 1,320/1.10.' },
        { label: 'Answer', text: 'Interest for the 1st year = ₹1,200.' },
      ],
      commonTrap:
        "Assuming both years earn EQUAL interest (since it's 'the same rate') and answering ₹1,320 for year 1 too — but year 2's interest is calculated on a LARGER base, so it's always somewhat more than year 1's interest.",
      alternateMethod: {
        label: 'Direct principal method',
        steps: [
          'Interest_year1 = P×r and Interest_year2 = P×(1+r)×r = 1,320.',
          'Since Interest_year1 × (1+r) = Interest_year2: P×r×1.10 = 1,320 ⇒ P×r = 1,200 — this IS Interest_year1 directly.',
        ],
      },
    },
    {
      id: 'sici-23',
      difficulty: 'Medium',
      patternTags: ['TCS', 'Infosys'],
      question: "A sum of money amounts to ₹6,200 in 2 years and ₹6,800 in 5 years, at simple interest. Find the principal.",
      options: ['5,600', '5,700', '5,800', '5,900'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: 'Amount after 2yr = ₹6,200, Amount after 5yr = ₹6,800 (simple interest).' },
        { label: 'Concept', text: 'The DIFFERENCE in amounts over the extra years gives the SI earned in just that extra period (since SI accrues at a constant rate per year); use this to find annual SI, then back out the principal.' },
        { label: 'Working', text: 'Extra interest over (5−2)=3 years = 6,800−6,200 = ₹600 → SI per year = 600/3 = ₹200. SI for the first 2 years = 200×2 = ₹400.' },
        { label: 'Answer', text: 'Principal = 6,200 − 400 = ₹5,800.' },
      ],
      commonTrap:
        "Assuming the amount after 2 years (₹6,200) is itself the principal — ignoring that it already includes 2 years of accumulated interest.",
      alternateMethod: {
        label: 'Verification',
        steps: [
          'Principal = 5,800, implied rate = 200/5,800×100 ≈ 3.448%.',
          'Check: Amount after 5 years = 5,800 + 5×200 = 5,800+1,000 = ₹6,800 — matches.',
        ],
      },
    },
    {
      id: 'sici-24',
      difficulty: 'Hard',
      patternTags: ['Wipro', 'Zoho'],
      question: "A sum of money invested at compound interest amounts to ₹18,150 after 2 years and ₹19,965 after 3 years. Find the principal.",
      options: ['14,500', '15,000', '15,500', '16,000'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: 'Amount after 2yr = ₹18,150, Amount after 3yr = ₹19,965 (compound interest).' },
        { label: 'Concept', text: 'First find the rate from the ratio of consecutive years\' amounts, then divide the 2-year amount by (1+r)² to recover the principal.' },
        { label: 'Working', text: 'Rate: 19,965/18,150 = 1.10 ⇒ r=10%. Principal = 18,150/(1.10)².' },
        { label: 'Answer', text: 'Principal = 18,150/1.21 = ₹15,000.' },
      ],
      commonTrap:
        "Assuming the amount after 2 years (₹18,150) IS the principal, forgetting that it already reflects 2 years of compounded growth that must be reversed out.",
      alternateMethod: {
        label: 'Verification',
        steps: [
          '15,000×(1.1)² = 15,000×1.21 = ₹18,150 ✓.',
          '15,000×(1.1)³ = 15,000×1.331 = ₹19,965 ✓ — both match, confirming principal = ₹15,000.',
        ],
      },
    },
    {
      id: 'sici-25',
      difficulty: 'Hard',
      patternTags: ['TCS', 'Zoho'],
      question: "Find the effective annual rate of interest corresponding to a nominal rate of 8% per annum, compounded half-yearly.",
      options: ['8%', '8.08%', '8.16%', '8.32%'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: 'Nominal rate = 8% per annum, compounded half-yearly.' },
        { label: 'Concept', text: 'The EFFECTIVE annual rate is the actual growth achieved over 1 year: Effective rate = (1 + nominal/n)^n − 1, where n = compounding periods per year.' },
        { label: 'Working', text: 'Half-yearly rate = 8/2 = 4%. Effective rate = (1.04)² − 1 = 1.0816 − 1.' },
        { label: 'Answer', text: 'Effective annual rate = 8.16%.' },
      ],
      commonTrap:
        "Assuming the effective rate equals the nominal rate (8%) since it's 'the same annual rate either way' — compounding more frequently than once a year always makes the EFFECTIVE rate slightly higher than the stated nominal rate.",
      alternateMethod: {
        label: 'Direct amount comparison',
        steps: [
          'On ₹100, half-yearly compounding for 1 year gives 100×(1.04)² = ₹108.16.',
          'That is an actual (effective) growth of 8.16%, more than the nominal 8%.',
        ],
      },
    },
  ],
  'ratio-proportion': [
    {
      id: 'rp-01',
      difficulty: 'Easy',
      patternTags: ['TCS', 'Infosys'],
      question: "Divide ₹1,200 between A and B in the ratio 5:7. Find A's share.",
      options: ['450', '500', '550', '600'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: 'Total = ₹1,200, ratio A:B = 5:7.' },
        { label: 'Concept', text: "Divide the total into (5+7)=12 equal parts; each person's share = (their ratio number/total parts) × total amount." },
        { label: 'Working', text: '1 part = 1,200/12 = ₹100.' },
        { label: 'Answer', text: "A's share = 5 × 100 = ₹500." },
      ],
      commonTrap:
        "Swapping the ratio (using 7 parts for A instead of 5), giving ₹700 — mixing up which number in the ratio belongs to which person.",
      alternateMethod: {
        label: 'Direct fraction method',
        steps: [
          "A's share = (5/12) × 1,200 = ₹500.",
        ],
      },
    },
    {
      id: 'rp-02',
      difficulty: 'Easy',
      patternTags: ['Wipro', 'Zoho'],
      question: "Divide ₹3,600 among A, B and C in the ratio 2:3:4. Find C's share.",
      options: ['1,500', '1,600', '1,700', '1,800'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: 'Total = ₹3,600, ratio A:B:C = 2:3:4.' },
        { label: 'Concept', text: 'Divide the total into (2+3+4)=9 equal parts.' },
        { label: 'Working', text: '1 part = 3,600/9 = ₹400.' },
        { label: 'Answer', text: "C's share = 4 × 400 = ₹1,600." },
      ],
      commonTrap:
        "Using the number of people (3) instead of the sum of ratio terms (9) as the divisor — confusing 'how many parties share the money' with 'how many equal parts the ratio splits into'.",
      alternateMethod: {
        label: 'Direct fraction method',
        steps: [
          "C's share = (4/9) × 3,600 = ₹1,600.",
        ],
      },
    },
    {
      id: 'rp-03',
      difficulty: 'Medium',
      patternTags: ['TCS', 'Zoho'],
      question: "If A:B = 2:3 and B:C = 4:5, find A:C.",
      options: ['8:15', '5:8', '3:4', '4:5'],
      correctIndex: 0,
      steps: [
        { label: 'Given', text: 'A:B = 2:3 and B:C = 4:5.' },
        { label: 'Concept', text: "To combine two ratios sharing a common term (B), scale each ratio so B has the same value in both (LCM of 3 and 4 = 12)." },
        { label: 'Working', text: 'A:B = 2:3 = 8:12 (×4). B:C = 4:5 = 12:15 (×3). So A:B:C = 8:12:15.' },
        { label: 'Answer', text: 'A:C = 8:15.' },
      ],
      commonTrap:
        "Combining the ratios directly term-by-term without first matching B (e.g. guessing A:C = 2:5) — this skips the essential step of making B's value consistent across both ratios before comparing A and C.",
      alternateMethod: {
        label: 'Assumed-value method',
        steps: [
          'Let B = 12 (the LCM of 3 and 4).',
          'From A:B=2:3, A = 12×2/3 = 8. From B:C=4:5, C = 12×5/4 = 15.',
          'So A:C = 8:15.',
        ],
      },
    },
    {
      id: 'rp-04',
      difficulty: 'Easy',
      patternTags: ['Infosys', 'Wipro'],
      question: "Find the fourth proportional to 4, 6 and 10.",
      options: ['14', '15', '16', '18'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: 'Three numbers: 4, 6, 10.' },
        { label: 'Concept', text: 'In a proportion a:b::c:d, the product of extremes equals the product of means: a×d = b×c.' },
        { label: 'Working', text: '4:6::10:x → 4×x = 6×10 = 60.' },
        { label: 'Answer', text: 'x = 60/4 = 15.' },
      ],
      commonTrap:
        "Setting up the proportion with the terms in the wrong order (e.g. 6:4::10:x), which gives a different, incorrect value for x.",
      alternateMethod: {
        label: 'Ratio method',
        steps: [
          '4:6 simplifies to 2:3.',
          'Since 10 plays the role of the third term, x = 10 × (3/2) = 15 (scaling by the same 2:3 factor).',
        ],
      },
    },
    {
      id: 'rp-05',
      difficulty: 'Medium',
      patternTags: ['TCS', 'Infosys'],
      question: "Find the mean proportional between 9 and 16.",
      options: ['12', '12.5', '13', '14'],
      correctIndex: 0,
      steps: [
        { label: 'Given', text: 'Two numbers: 9 and 16.' },
        { label: 'Concept', text: 'The mean proportional between a and b is √(a×b) — a value m such that a:m::m:b. This is a GEOMETRIC mean, not an arithmetic one.' },
        { label: 'Working', text: '√(9×16) = √144.' },
        { label: 'Answer', text: 'Mean proportional = 12.' },
      ],
      commonTrap:
        "Taking the simple arithmetic mean instead ((9+16)/2 = 12.5) — mean proportional specifically means the square root of the product, not the average of the two numbers.",
      alternateMethod: {
        label: 'Verification',
        steps: [
          '9:12::12:16 → 9/12 = 0.75, 12/16 = 0.75 — equal ratios confirm 12 is correct.',
        ],
      },
    },
    {
      id: 'rp-06',
      difficulty: 'Easy',
      patternTags: ['Wipro', 'Zoho'],
      question: "If the cost of 12 pens is ₹180, find the cost of 18 pens (cost varies directly with quantity).",
      options: ['255', '270', '285', '300'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: '12 pens cost ₹180.' },
        { label: 'Concept', text: 'Cost varies DIRECTLY with quantity — more items cost proportionally more.' },
        { label: 'Working', text: 'Cost per pen = 180/12 = ₹15.' },
        { label: 'Answer', text: 'Cost of 18 pens = 15×18 = ₹270.' },
      ],
      commonTrap:
        "Setting up an INVERSE proportion by mistake (180×12/18 = ₹120) — buying more items should cost MORE, not less, so this is a direct-proportion scenario.",
      alternateMethod: {
        label: 'Ratio method',
        steps: [
          '12:18 = 2:3.',
          'Since cost is direct: 180:x = 2:3 → x = 180×3/2 = ₹270.',
        ],
      },
    },
    {
      id: 'rp-07',
      difficulty: 'Medium',
      patternTags: ['TCS', 'Zoho'],
      question: "If 6 cows can eat a field's grass in 20 days, how many days will 15 cows take to eat the same field's grass?",
      options: ['8', '9', '10', '12'],
      correctIndex: 0,
      steps: [
        { label: 'Given', text: '6 cows eat a fixed amount of grass in 20 days.' },
        { label: 'Concept', text: 'More cows means the grass is eaten FASTER — this is an inverse proportion: N1×D1 = N2×D2.' },
        { label: 'Working', text: '6×20 = 120.' },
        { label: 'Answer', text: 'Days for 15 cows = 120/15 = 8.' },
      ],
      commonTrap:
        "Setting up a DIRECT proportion by mistake (6:20::15:d → d=50) — more cows should mean FEWER days, not more, since this is an inverse-proportion situation.",
      alternateMethod: {
        label: 'Unitary method',
        steps: [
          '1 cow alone would take 6×20 = 120 days.',
          '15 cows together take 120/15 = 8 days.',
        ],
      },
    },
    {
      id: 'rp-08',
      difficulty: 'Medium',
      patternTags: ['Infosys', 'Wipro'],
      question: "The ratio of the present ages of A and B is 3:5. If B is 10 years older than A, find A's present age.",
      options: ['12', '15', '18', '20'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: 'A:B = 3:5, B is 10 years older than A.' },
        { label: 'Concept', text: 'Express ages as 3x and 5x, then use the given age DIFFERENCE to solve for x.' },
        { label: 'Working', text: '5x − 3x = 10 → 2x = 10 → x = 5.' },
        { label: 'Answer', text: "A's age = 3×5 = 15 years." },
      ],
      commonTrap:
        "Treating the given difference (10) as a single ratio 'part' directly (A = 3×10 = 30) — the multiplier x must come from the DIFFERENCE in ratio parts (5−3=2 parts = 10 years), not from treating 10 as one part.",
      alternateMethod: {
        label: 'Ratio-difference method',
        steps: [
          'The ratio parts differ by (5−3)=2, corresponding to 10 years → 1 part = 5 years.',
          "A = 3 parts = 15 years.",
        ],
      },
    },
    {
      id: 'rp-09',
      difficulty: 'Hard',
      patternTags: ['TCS', 'Infosys'],
      question: "Two numbers are in the ratio 3:5. If 10 is added to both numbers, the ratio becomes 5:7. Find the smaller of the two original numbers.",
      options: ['15', '18', '20', '22'],
      correctIndex: 0,
      steps: [
        { label: 'Given', text: 'Numbers in ratio 3:5. Adding 10 to BOTH gives a new ratio of 5:7.' },
        { label: 'Concept', text: 'Express the numbers as 3x and 5x; add 10 to each and set the new ratio equal to 5:7, then cross-multiply to solve for x.' },
        { label: 'Working', text: '(3x+10)/(5x+10) = 5/7 → 7(3x+10) = 5(5x+10) → 21x+70 = 25x+50 → 4x = 20 → x = 5.' },
        { label: 'Answer', text: 'The numbers are 15 and 25 — the smaller number is 15.' },
      ],
      commonTrap:
        "Adding 10 directly to the RATIO terms themselves (3+10 : 5+10 = 13:15) instead of to the actual numbers (3x+10 and 5x+10) — this treats the ratio like real quantities without first scaling by x.",
      alternateMethod: {
        label: 'Verification',
        steps: [
          'Numbers = 15 and 25 (ratio 15:25 = 3:5 ✓).',
          'Adding 10 to each: 25:35 = 5:7 ✓ — matches the given new ratio.',
        ],
      },
    },
    {
      id: 'rp-10',
      difficulty: 'Medium',
      patternTags: ['Wipro', 'Zoho'],
      question: "Find the compound ratio of 2:3, 4:5 and 3:8.",
      options: ['1:5', '1:4', '2:5', '3:10'],
      correctIndex: 0,
      steps: [
        { label: 'Given', text: 'Three ratios: 2:3, 4:5, 3:8.' },
        { label: 'Concept', text: 'The compound ratio of several ratios is found by multiplying all the antecedents together and all the consequents together, then simplifying.' },
        { label: 'Working', text: '(2×4×3) : (3×5×8) = 24 : 120.' },
        { label: 'Answer', text: 'Simplified: 24:120 = 1:5.' },
      ],
      commonTrap:
        "Adding the ratio terms instead of multiplying (antecedents 2+4+3=9, consequents 3+5+8=16, giving 9:16) — compound ratios are formed by MULTIPLICATION, not addition.",
      alternateMethod: {
        label: 'Step-by-step combination',
        steps: [
          'Combine 2:3 and 4:5 first: (2×4):(3×5) = 8:15.',
          'Combine 8:15 with 3:8: (8×3):(15×8) = 24:120 = 1:5.',
        ],
      },
    },
    {
      id: 'rp-11',
      difficulty: 'Medium',
      patternTags: ['TCS', 'Infosys'],
      question: "Find the sub-duplicate ratio of 25:64.",
      options: ['5:8', '5:64', '25:8', '10:16'],
      correctIndex: 0,
      steps: [
        { label: 'Given', text: 'Ratio 25:64.' },
        { label: 'Concept', text: 'The sub-duplicate ratio of a:b is √a : √b — the opposite operation of the "duplicate ratio", which squares each term.' },
        { label: 'Working', text: '√25 = 5, √64 = 8.' },
        { label: 'Answer', text: 'Sub-duplicate ratio = 5:8.' },
      ],
      commonTrap:
        "Confusing 'sub-duplicate' with 'duplicate' and SQUARING instead of taking the square root (25²:64² = 625:4,096) — the 'sub-' prefix specifically means the inverse operation.",
      alternateMethod: {
        label: 'Verification',
        steps: [
          'The duplicate ratio of 5:8 (squaring each term) = 25:64 — confirming 5:8 is indeed the sub-duplicate ratio of 25:64.',
        ],
      },
    },
    {
      id: 'rp-12',
      difficulty: 'Medium',
      patternTags: ['Wipro', 'Zoho'],
      question: "The ratio of a family's income to its expenditure is 7:6. If the family saves ₹2,500 per month, find the monthly income.",
      options: ['16,000', '17,500', '19,000', '20,000'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: 'Income:Expenditure = 7:6. Savings = ₹2,500/month.' },
        { label: 'Concept', text: 'Savings = Income − Expenditure; in ratio terms, this is the DIFFERENCE of the ratio parts (7−6=1 part), which corresponds directly to the actual savings amount.' },
        { label: 'Working', text: '1 part = ₹2,500 (since 7x−6x=x=savings).' },
        { label: 'Answer', text: 'Income = 7 × 2,500 = ₹17,500.' },
      ],
      commonTrap:
        "Assuming the savings amount corresponds to the smaller ratio term (6 parts) instead of the DIFFERENCE between the two terms (7−6=1 part) — confusing 'the smaller quantity' with 'the gap between the two quantities'.",
      alternateMethod: {
        label: 'Direct ratio-part method',
        steps: [
          'Since 1 part = ₹2,500 and Income = 7 parts, Income = 7×2,500 = ₹17,500.',
        ],
      },
    },
    {
      id: 'rp-13',
      difficulty: 'Medium',
      patternTags: ['TCS', 'Zoho'],
      question: "A and B start a business investing ₹5,000 and ₹8,000 respectively, for the same period. At the end of the year they earn a profit of ₹6,500. Find B's share of the profit.",
      options: ['3,250', '3,500', '3,750', '4,000'],
      correctIndex: 3,
      steps: [
        { label: 'Given', text: 'Capitals: A=₹5,000, B=₹8,000, invested for the SAME time period. Total profit = ₹6,500.' },
        { label: 'Concept', text: "When investment TIME is equal, profit is divided in the ratio of CAPITAL invested." },
        { label: 'Working', text: 'Capital ratio A:B = 5,000:8,000 = 5:8.' },
        { label: 'Answer', text: "B's share = (8/13) × 6,500 = ₹4,000." },
      ],
      commonTrap:
        "Splitting the profit EQUALLY between A and B (₹3,250 each) — profit-sharing in a partnership follows the capital ratio, not a straight 50-50 split, unless the capitals happen to be equal.",
      alternateMethod: {
        label: 'Unitary method',
        steps: [
          '1 part = 6,500/13 = ₹500.',
          "B's share = 8 parts = 8×500 = ₹4,000.",
        ],
      },
    },
    {
      id: 'rp-14',
      difficulty: 'Hard',
      patternTags: ['Infosys', 'Wipro'],
      question: "A invests ₹5,000 for 8 months and B invests ₹6,000 for 10 months in a business. If the total profit is ₹5,000, find A's share.",
      options: ['1,900', '2,000', '2,100', '2,200'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: 'A: ₹5,000 for 8 months. B: ₹6,000 for 10 months. Total profit = ₹5,000.' },
        { label: 'Concept', text: 'When BOTH capital and time differ, profit is divided in the ratio of (Capital × Time) for each partner — not capital alone.' },
        { label: 'Working', text: "A's effective capital = 5,000×8 = 40,000. B's effective capital = 6,000×10 = 60,000. Ratio = 40,000:60,000 = 2:3." },
        { label: 'Answer', text: "A's share = (2/5) × 5,000 = ₹2,000." },
      ],
      commonTrap:
        "Dividing the profit using only the raw capital ratio (5,000:6,000 = 5:6), ignoring the different investment durations — when time periods differ, capital alone doesn't determine a fair profit split.",
      alternateMethod: {
        label: 'Unitary method',
        steps: [
          '1 part (of 5 total) = 5,000/5 = ₹1,000.',
          "A's share = 2 parts = ₹2,000.",
        ],
      },
    },
    {
      id: 'rp-15',
      difficulty: 'Hard',
      patternTags: ['TCS', 'Zoho'],
      question: "A starts a business with ₹8,000. After 4 months, B joins with ₹12,000. At the end of the year, they make a profit of ₹7,000. Find A's share.",
      options: ['3,000', '3,250', '3,400', '3,500'],
      correctIndex: 3,
      steps: [
        { label: 'Given', text: "A invests ₹8,000 for the full 12 months. B joins after 4 months with ₹12,000 (invested for 12−4=8 months). Total profit = ₹7,000." },
        { label: 'Concept', text: "Use EFFECTIVE capital = Capital × (months actually invested) for each partner — B's shorter investment period must be accounted for." },
        { label: 'Working', text: "A's effective capital = 8,000×12 = 96,000. B's effective capital = 12,000×8 = 96,000. Ratio = 96,000:96,000 = 1:1." },
        { label: 'Answer', text: "A's share = 7,000/2 = ₹3,500 (an equal split, despite B investing more money, because B invested for less time)." },
      ],
      commonTrap:
        "Using B's full 12 months (ignoring the 4-month late start) — this incorrectly assumes B's capital was invested from day one, giving a wrong, unequal profit ratio.",
      alternateMethod: {
        label: 'Verification',
        steps: [
          'Since both effective capitals equal 96,000, the ratio is exactly 1:1 regardless of the different rupee amounts — a genuine coincidence here (8,000×12 = 12,000×8).',
        ],
      },
    },
    {
      id: 'rp-16',
      difficulty: 'Medium',
      patternTags: ['Infosys', 'Wipro'],
      question: "If a, b, c are in continued proportion (a:b = b:c) and a=4, c=9, find b.",
      options: ['5', '6', '6.5', '7'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: 'a, b, c are in continued proportion, a=4, c=9.' },
        { label: 'Concept', text: 'For a:b::b:c, b is the MEAN PROPORTIONAL, so b² = a×c (cross-multiplying a:b::b:c gives a×c = b×b).' },
        { label: 'Working', text: 'b² = 4×9 = 36.' },
        { label: 'Answer', text: 'b = √36 = 6.' },
      ],
      commonTrap:
        "Taking the arithmetic mean of a and c instead ((4+9)/2 = 6.5) — very close to the correct answer, which is exactly why it's tempting, but continued proportion needs the GEOMETRIC mean, not the arithmetic average.",
      alternateMethod: {
        label: 'Verification',
        steps: [
          '4:6::6:9 → 4/6 = 2/3, 6/9 = 2/3 — equal ratios confirm b=6.',
        ],
      },
    },
    {
      id: 'rp-17',
      difficulty: 'Easy',
      patternTags: ['TCS', 'Zoho'],
      question: "Two numbers are in the ratio 4:7. If their sum is 143, find the larger number.",
      options: ['91', '94', '98', '102'],
      correctIndex: 0,
      steps: [
        { label: 'Given', text: 'Two numbers in ratio 4:7, sum = 143.' },
        { label: 'Concept', text: 'Divide the sum into (4+7)=11 equal parts.' },
        { label: 'Working', text: '1 part = 143/11 = 13.' },
        { label: 'Answer', text: 'Larger number = 7×13 = 91.' },
      ],
      commonTrap:
        "Swapping which number is 'larger' (computing 4×13=52 and reporting that instead) — mixing up which ratio term corresponds to the larger value.",
      alternateMethod: {
        label: 'Direct fraction method',
        steps: [
          'Larger number = (7/11) × 143 = 91.',
        ],
      },
    },
    {
      id: 'rp-18',
      difficulty: 'Medium',
      patternTags: ['Wipro', 'Zoho'],
      question: "Two numbers are in the ratio 5:3. If their difference is 48, find the smaller number.",
      options: ['66', '72', '78', '84'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: 'Two numbers in ratio 5:3, difference = 48.' },
        { label: 'Concept', text: 'The difference in ratio parts (5−3=2) corresponds to the given difference (48), not the full ratio numbers themselves.' },
        { label: 'Working', text: '2 parts = 48 → 1 part = 24.' },
        { label: 'Answer', text: 'Smaller number = 3×24 = 72.' },
      ],
      commonTrap:
        "Treating 48 as if it were 1 part directly instead of the DIFFERENCE of 2 parts (smaller = 3×48 = 144) — a much larger, incorrect value that ignores the actual gap between the ratio terms.",
      alternateMethod: {
        label: 'Unitary method',
        steps: [
          '1 part = 48/2 = 24.',
          'Smaller number (3 parts) = 3×24 = 72.',
        ],
      },
    },
    {
      id: 'rp-19',
      difficulty: 'Hard',
      patternTags: ['TCS', 'Infosys'],
      question: "The present ages of A and B are in the ratio 4:5. Eight years from now, the ratio of their ages will be 6:7. Find A's present age.",
      options: ['12', '14', '16', '20'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: 'Present ratio A:B = 4:5. After 8 years, ratio becomes 6:7.' },
        { label: 'Concept', text: 'Express present ages as 4x, 5x; add 8 to EACH (both age by the same amount), set the new ratio equal to 6:7, and solve for x.' },
        { label: 'Working', text: '(4x+8)/(5x+8) = 6/7 → 28x+56 = 30x+48 → 2x = 8 → x = 4.' },
        { label: 'Answer', text: "A's present age = 4×4 = 16 years." },
      ],
      commonTrap:
        "Applying the future ratio (6:7) directly to the CURRENT ages without introducing x first — ages must be expressed with a common multiplier before the future condition can be set up correctly.",
      alternateMethod: {
        label: 'Verification',
        steps: [
          'A=16, B=20 (ratio 4:5 ✓). After 8 years: A=24, B=28, ratio = 24:28 = 6:7 ✓ — matches.',
        ],
      },
    },
    {
      id: 'rp-20',
      difficulty: 'Medium',
      patternTags: ['Wipro', 'Zoho'],
      question: "A, B and C together have ₹1,620. If A's share is twice B's share and B's share is 3 times C's share, find C's share.",
      options: ['155', '162', '170', '180'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: "Total = ₹1,620. A = 2×B, B = 3×C." },
        { label: 'Concept', text: 'Express everything in terms of the SMALLEST unknown (C=x), building up B and A from the given relationships, then sum to equal the total.' },
        { label: 'Working', text: 'C=x, B=3x, A=6x. Total = x+3x+6x = 10x = 1,620.' },
        { label: 'Answer', text: "x = 162, so C's share = ₹162." },
      ],
      commonTrap:
        "Reversing which multiplier belongs to which relationship (e.g. treating B=2×C or A=3×B) — careful reading of 'A's share is twice B's' vs. 'B's share is 3 times C's' is essential, since they're easy to swap.",
      alternateMethod: {
        label: 'Ratio method',
        steps: [
          'A:B:C = 6:3:1 (from A=6x, B=3x, C=x). Total parts = 10.',
          'C = 1 part = 1,620/10 = ₹162.',
        ],
      },
    },
    {
      id: 'rp-21',
      difficulty: 'Hard',
      patternTags: ['TCS', 'Zoho'],
      question: "What number must be added to each of 6, 10, 14 and 22 to make them proportional (so that the first is to the second as the third is to the fourth)?",
      options: ['1', '2', '3', '4'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: 'Find x such that (6+x):(10+x) :: (14+x):(22+x).' },
        { label: 'Concept', text: 'Use the cross-multiplication rule for proportions (product of extremes = product of means) on all four expressions with x added.' },
        { label: 'Working', text: '(6+x)(22+x) = (10+x)(14+x) → 132+28x+x² = 140+24x+x² → 4x = 8.' },
        { label: 'Answer', text: 'x = 2.' },
      ],
      commonTrap:
        "Trying to spot a pattern from the original ratios (6:10 and 14:22, i.e. 3:5 and 7:11) instead of setting up the actual cross-multiplication equation — since 3:5 ≠ 7:11 to begin with, no amount of pattern-spotting reveals x directly.",
      alternateMethod: {
        label: 'Verification',
        steps: [
          'With x=2: (8):(12) :: (16):(24) → 8/12 = 2/3, 16/24 = 2/3 — equal ratios confirm x=2.',
        ],
      },
    },
    {
      id: 'rp-22',
      difficulty: 'Medium',
      patternTags: ['Infosys', 'Wipro'],
      question: "If a varies directly as b and inversely as c, and a=10 when b=5 and c=2, find a when b=8 and c=4.",
      options: ['7', '8', '9', '10'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: 'a ∝ b/c (joint variation). a=10 when b=5, c=2.' },
        { label: 'Concept', text: 'Combine direct and inverse variation into one formula: a = k×(b/c). Find the constant k from the given values, then apply it to the new values.' },
        { label: 'Working', text: '10 = k×(5/2) → k = 4. New a = 4×(8/4).' },
        { label: 'Answer', text: 'a = 8.' },
      ],
      commonTrap:
        "Treating the variation as purely direct in both b and c (a=k×b×c), ignoring that c is an INVERSE factor — this gives a completely different, incorrect constant and result.",
      alternateMethod: {
        label: 'Ratio method',
        steps: [
          'New a / Old a = (New b/Old b) × (Old c/New c) = (8/5)×(2/4) = 0.8.',
          'New a = 10×0.8 = 8.',
        ],
      },
    },
    {
      id: 'rp-23',
      difficulty: 'Hard',
      patternTags: ['TCS', 'Infosys'],
      question: "A mixture contains milk and water in the ratio 5:1. If 6 litres of water is added to the mixture, the ratio becomes 5:3. Find the initial quantity of milk in the mixture.",
      options: ['13', '15', '17', '18'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: 'Initial ratio milk:water = 5:1. After adding 6L water (milk unchanged), new ratio = 5:3.' },
        { label: 'Concept', text: 'Since only water increases, milk (5x) stays fixed — set up the new ratio using the ORIGINAL milk amount over the NEW (increased) water amount.' },
        { label: 'Working', text: '5x/(x+6) = 5/3 → 15x = 5x+30 → 10x = 30 → x = 3.' },
        { label: 'Answer', text: 'Milk = 5×3 = 15 litres.' },
      ],
      commonTrap:
        "Assuming both milk and water scale up proportionally when water is added, as if the whole mixture got diluted uniformly — but only water was physically added, so milk must stay exactly the same amount throughout.",
      alternateMethod: {
        label: 'Verification',
        steps: [
          'Milk=15L, initial water=3L (ratio 15:3=5:1 ✓).',
          'After adding 6L water: water=9L, new ratio = 15:9 = 5:3 ✓ — matches.',
        ],
      },
    },
    {
      id: 'rp-24',
      difficulty: 'Medium',
      patternTags: ['Wipro', 'Zoho'],
      question: "If x:y = 3:4, find the value of (5x+3y):(5x−3y).",
      options: ['9:1', '7:1', '5:1', '3:1'],
      correctIndex: 0,
      steps: [
        { label: 'Given', text: 'x:y = 3:4.' },
        { label: 'Concept', text: 'Substitute x=3k, y=4k (using the ratio\'s common multiplier) directly into the expression, then simplify — k always cancels out of a pure ratio expression.' },
        { label: 'Working', text: '5x+3y = 15k+12k = 27k. 5x−3y = 15k−12k = 3k.' },
        { label: 'Answer', text: '27k : 3k = 9:1.' },
      ],
      commonTrap:
        "Incorrectly 'distributing' the expression as if (5x+3y) meant (5+3)(x+y) — a plausible-looking algebra slip that gives a completely different, wrong ratio.",
      alternateMethod: {
        label: 'Direct substitution shortcut',
        steps: [
          'Since k cancels out of a homogeneous ratio expression like this one, it\'s valid to just use x=3, y=4 directly (i.e. k=1).',
          '5(3)+3(4)=27, 5(3)−3(4)=3, ratio=27:3=9:1 — same answer, confirming k\'s actual value never mattered here.',
        ],
      },
    },
    {
      id: 'rp-25',
      difficulty: 'Medium',
      patternTags: ['TCS', 'Zoho'],
      question: "A recipe requires flour, sugar and butter in the ratio 8:3:2. If 60 grams of sugar is used, find the combined weight of flour and butter needed.",
      options: ['160', '180', '200', '220'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: 'Flour:Sugar:Butter = 8:3:2. Sugar used = 60g.' },
        { label: 'Concept', text: "Find the value of '1 part' using the known ingredient (sugar's own ratio number), then scale up the other ingredients using their own ratio parts." },
        { label: 'Working', text: 'Sugar = 3 parts = 60g → 1 part = 20g. Flour = 8×20 = 160g. Butter = 2×20 = 40g.' },
        { label: 'Answer', text: 'Combined flour + butter = 160+40 = 200g.' },
      ],
      commonTrap:
        "Using the TOTAL ratio sum (8+3+2=13) to find '1 part' from the sugar amount (60/13≈4.6g) instead of using sugar's OWN ratio number (3) — the part-value must come from matching the known ingredient's specific ratio term, not the sum of all terms.",
      alternateMethod: {
        label: 'Direct ratio method',
        steps: [
          'Flour:Sugar=8:3, so Flour = 60×(8/3) = 160g.',
          'Butter:Sugar=2:3, so Butter = 60×(2/3) = 40g. Combined = 200g.',
        ],
      },
    },
  ],
  averages: [
    {
      id: 'avg-01',
      difficulty: 'Easy',
      patternTags: ['TCS', 'Infosys'],
      question: "Find the average of the numbers 12, 18, 24, 30 and 36.",
      options: ['21', '22', '24', '30'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: 'Five numbers: 12, 18, 24, 30, 36.' },
        { label: 'Concept', text: 'Average = Sum of values ÷ Number of values.' },
        { label: 'Working', text: 'Sum = 12+18+24+30+36 = 120.' },
        { label: 'Answer', text: 'Average = 120/5 = 24.' },
      ],
      commonTrap:
        "Miscounting how many values are in the list — e.g. accidentally leaving one number out of the sum, or dividing by the wrong count (120/4=30 instead of 120/5=24).",
      alternateMethod: {
        label: 'Deviation-from-assumed-mean method',
        steps: [
          'Assume the mean is 24. Deviations: -12, -6, 0, +6, +12.',
          'These deviations sum to exactly 0, confirming 24 is indeed the correct average.',
        ],
      },
    },
    {
      id: 'avg-02',
      difficulty: 'Medium',
      patternTags: ['TCS', 'Wipro'],
      question: "The average of 5 numbers is 18. If one number is replaced by 28, the new average becomes 20. Find the number that was replaced.",
      options: ['16', '18', '26', '30'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: 'Average of 5 numbers = 18. Replacing one number with 28 makes the new average 20.' },
        { label: 'Concept', text: 'Use the CHANGE IN TOTAL SUM (found from the change in average × count) to find the replaced value.' },
        { label: 'Working', text: 'Old sum = 5×18=90. New sum = 5×20=100. Change = 100−90 = 10 = 28 − old value.' },
        { label: 'Answer', text: 'Old value = 28−10 = 18.' },
      ],
      commonTrap:
        "Subtracting the average difference directly from 28 instead of the TOTAL sum difference (28−(20−18)=26) — the average difference must first be scaled up by the count (5) to get the true sum change.",
      alternateMethod: {
        label: 'Direct average-shift method',
        steps: [
          'Average increased by (20−18)=2, over 5 numbers, so the total increased by 2×5=10.',
          'Old value = 28 − 10 = 18.',
        ],
      },
    },
    {
      id: 'avg-03',
      difficulty: 'Medium',
      patternTags: ['Infosys', 'Zoho'],
      question: "A new member joins a family of 4 whose average age is 24 years. This new member's age brings the family's (now 5-member) average age down to 20 years. Find the new member's age.",
      options: ['2', '3', '4', '6'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: 'Family of 4, average age = 24 (sum=96). After the new member joins, 5-member average = 20 (sum=100).' },
        { label: 'Concept', text: "The new member's age equals the new total sum minus the old total sum, since they are the only addition." },
        { label: 'Working', text: 'New sum = 100. Old sum = 96.' },
        { label: 'Answer', text: "New member's age = 100−96 = 4 years." },
      ],
      commonTrap:
        "Averaging 24 and 20 directly ((24+20)/2=22) and guessing the new member's age is close to that — this confuses 'the group's average' with 'the new individual's actual age', which are very different when the group is larger than 1.",
      alternateMethod: {
        label: 'Direct sum-difference method',
        steps: [
          'Old total = 96. New total = 100.',
          "New member's age = 100−96 = 4.",
        ],
      },
    },
    {
      id: 'avg-04',
      difficulty: 'Easy',
      patternTags: ['TCS', 'Zoho'],
      question: "Find the average of the first 15 natural numbers.",
      options: ['7', '7.5', '8', '8.5'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: 'First 15 natural numbers: 1 to 15.' },
        { label: 'Concept', text: 'Average of the first n natural numbers = (n+1)/2, since they are evenly spaced and the average equals the midpoint of the first and last terms.' },
        { label: 'Working', text: '(1+15)/2 = 16/2.' },
        { label: 'Answer', text: 'Average = 8.' },
      ],
      commonTrap:
        "Using n/2 instead of (n+1)/2 (15/2=7.5) — forgetting the '+1' that comes from including both endpoints of an inclusive sequence starting at 1.",
      alternateMethod: {
        label: 'Sum formula method',
        steps: [
          'Sum of first n natural numbers = n(n+1)/2 = 15×16/2 = 120.',
          'Average = 120/15 = 8.',
        ],
      },
    },
    {
      id: 'avg-05',
      difficulty: 'Medium',
      patternTags: ['Wipro', 'Zoho'],
      question: "The average marks of 30 students in a class is 60. If the average marks of 20 of these students is 55, find the average marks of the remaining 10 students.",
      options: ['62', '65', '68', '70'],
      correctIndex: 3,
      steps: [
        { label: 'Given', text: '30 students, average=60 (total=1,800). 20 of them average=55 (total=1,100).' },
        { label: 'Concept', text: 'Find the sum for the remaining group by subtracting the known subgroup\'s sum from the overall total, then divide by the remaining count.' },
        { label: 'Working', text: 'Sum of remaining 10 = 1,800−1,100 = 700.' },
        { label: 'Answer', text: 'Average of remaining 10 = 700/10 = 70.' },
      ],
      commonTrap:
        "Assuming the remaining students' average must also be LOWER (since the known subgroup was below the overall average) — it's actually the OPPOSITE: since that subgroup pulled the overall average down, the remaining group must be HIGHER to compensate.",
      alternateMethod: {
        label: 'Weighted-average balancing',
        steps: [
          'The 20 students average 5 below the overall average (55 vs 60), contributing a "deficit" of 20×5=100.',
          'This deficit is exactly offset by the remaining 10 students: surplus per student = 100/10 = 10 above the overall average.',
          'Remaining average = 60+10 = 70.',
        ],
      },
    },
    {
      id: 'avg-06',
      difficulty: 'Medium',
      patternTags: ['TCS', 'Infosys'],
      question: "A cricketer has an average of 42 runs in 8 innings. How many runs must he score in the 9th innings to raise his average to 45?",
      options: ['69', '72', '75', '78'],
      correctIndex: 0,
      steps: [
        { label: 'Given', text: 'Average = 42 over 8 innings. Target: average = 45 over 9 innings.' },
        { label: 'Concept', text: 'Find the required TOTAL after 9 innings, then subtract the current total to find the runs needed in the 9th innings alone.' },
        { label: 'Working', text: 'Current total = 8×42=336. Required total = 9×45=405.' },
        { label: 'Answer', text: 'Runs needed = 405−336 = 69.' },
      ],
      commonTrap:
        "Adding the desired average increase (3) to the current average and mistaking that for the runs needed (42+3=45) — raising the average across all 9 innings requires far more than a small increment in a single inning.",
      alternateMethod: {
        label: 'Increment-distribution method',
        steps: [
          'The new innings must (a) match the new average itself (45) and (b) additionally cover a gap of 3 runs for each of the 8 previous innings (since their old average of 42 is 3 short of the new target).',
          'Runs needed = 45 + (8×3) = 45+24 = 69.',
        ],
      },
    },
    {
      id: 'avg-07',
      difficulty: 'Medium',
      patternTags: ['Wipro', 'Zoho'],
      question: "The average marks of a class of 30 students was calculated as 72. Later it was found that one student's marks were wrongly entered as 68 instead of the actual 98. Find the correct average.",
      options: ['71', '72', '73', '75'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: '30 students, calculated average=72 (using a wrong mark of 68); actual mark should be 98.' },
        { label: 'Concept', text: 'Adjust the total sum by the DIFFERENCE between the correct and wrong marks (98−68=30), then recompute the average.' },
        { label: 'Working', text: 'Wrong sum = 30×72=2,160. Correct sum = 2,160+30 = 2,190.' },
        { label: 'Answer', text: 'Correct average = 2,190/30 = 73.' },
      ],
      commonTrap:
        "Adding the full new value (98) on top of the old total instead of just the NET difference (2,160+98=2,258, giving a wrong average near 75.3) — the old total already included the wrong value once, so only the difference should be added.",
      alternateMethod: {
        label: 'Direct average-shift method',
        steps: [
          'The correction adds (98−68)=30 extra marks spread over 30 students, raising the average by 30/30=1.',
          'New average = 72+1 = 73.',
        ],
      },
    },
    {
      id: 'avg-08',
      difficulty: 'Medium',
      patternTags: ['TCS', 'Zoho'],
      question: "The average of 5 consecutive odd numbers is 61. Find the largest number.",
      options: ['61', '63', '65', '66'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: '5 consecutive odd numbers, average = 61.' },
        { label: 'Concept', text: 'For any set of evenly-spaced numbers, the average equals the MIDDLE value, since they are symmetric around the center.' },
        { label: 'Working', text: 'Middle (3rd of 5) number = 61. The numbers are 57, 59, 61, 63, 65 (steps of 2 on each side).' },
        { label: 'Answer', text: 'Largest number = 65.' },
      ],
      commonTrap:
        "Stepping outward by 1 unit per position instead of 2 (61+2=63, or even 61+5=66 by miscounting) — these ARE odd numbers, spaced 2 apart, not plain consecutive integers spaced 1 apart.",
      alternateMethod: {
        label: 'Sum method',
        steps: [
          'Sum of 5 numbers = 5×61 = 305.',
          'Let the numbers be x, x+2, x+4, x+6, x+8: sum = 5x+20 = 305 → x=57.',
          'Largest = x+8 = 65.',
        ],
      },
    },
    {
      id: 'avg-09',
      difficulty: 'Hard',
      patternTags: ['Infosys', 'Wipro'],
      question: "The average temperature for Monday, Tuesday, Wednesday and Thursday was 38°C, and for Tuesday, Wednesday, Thursday and Friday it was 40°C. If Monday's temperature was 35°C, find Friday's temperature.",
      options: ['39', '41', '43', '45'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: 'avg(Mon–Thu)=38°C, avg(Tue–Fri)=40°C, Monday=35°C.' },
        { label: 'Concept', text: 'The two 4-day sums share 3 common days (Tue, Wed, Thu); subtracting the sums cancels out the common days, leaving just (Friday − Monday).' },
        { label: 'Working', text: 'Sum(Mon–Thu)=4×38=152. Sum(Tue–Fri)=4×40=160. Friday − Monday = 160−152 = 8.' },
        { label: 'Answer', text: 'Friday = 35+8 = 43°C.' },
      ],
      commonTrap:
        "Averaging 38 and 40 directly (getting 39) and treating that as Friday's temperature — this ignores the actual algebraic relationship linking the two overlapping sums.",
      alternateMethod: {
        label: 'Individual-day method',
        steps: [
          'Let S = Tue+Wed+Thu (common to both sums).',
          'Mon+S=152 → S=152−35=117. S+Fri=160 → Fri=160−117=43.',
        ],
      },
    },
    {
      id: 'avg-10',
      difficulty: 'Medium',
      patternTags: ['TCS', 'Infosys'],
      question: "The average of 10 numbers is 25. If the highest and lowest numbers are excluded, the average of the remaining 8 numbers is 24. If the highest number is 42, find the lowest number.",
      options: ['16', '18', '20', '22'],
      correctIndex: 0,
      steps: [
        { label: 'Given', text: '10 numbers, average=25 (sum=250). Excluding highest & lowest, remaining 8 average=24 (sum=192). Highest=42.' },
        { label: 'Concept', text: 'Sum of (highest+lowest) = Total sum − Sum of remaining 8.' },
        { label: 'Working', text: '250−192 = 58 = highest+lowest.' },
        { label: 'Answer', text: 'Lowest = 58−42 = 16.' },
      ],
      commonTrap:
        "Trying to link the excluded pair's own average ((highest+lowest)/2) directly to the difference between the two given averages (25−24=1) — the real relationship works through the SUMS (scaled by count), not the excluded pair's average.",
      alternateMethod: {
        label: 'Verification',
        steps: [
          'If lowest=16 and highest=42, the remaining 8 numbers must sum to 250−(42+16)=192.',
          '192/8=24 — matches the given remaining average exactly.',
        ],
      },
    },
    {
      id: 'avg-11',
      difficulty: 'Medium',
      patternTags: ['Wipro', 'Zoho'],
      question: "In a class, the average weight of 25 boys is 52 kg and the average weight of 15 girls is 48 kg. Find the average weight of the whole class (40 students).",
      options: ['49', '50', '50.5', '51'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: '25 boys average=52kg, 15 girls average=48kg.' },
        { label: 'Concept', text: 'Combined average = (Sum of both groups)/(Total count) — a WEIGHTED average, weighted by group size, not a simple average of the two given figures.' },
        { label: 'Working', text: 'Total weight = 25×52 + 15×48 = 1,300+720 = 2,020.' },
        { label: 'Answer', text: 'Combined average = 2,020/40 = 50.5 kg.' },
      ],
      commonTrap:
        "Taking the simple average of 52 and 48 ((52+48)/2=50) — this ignores that there are MORE boys (25) than girls (15), so the combined average should lean closer to 52, not sit exactly at the midpoint.",
      alternateMethod: {
        label: 'Weighted-deviation method',
        steps: [
          'Boys are 25/40 of the class, girls are 15/40.',
          'Combined average = 52×(25/40) + 48×(15/40) = 32.5+18 = 50.5.',
        ],
      },
    },
    {
      id: 'avg-12',
      difficulty: 'Medium',
      patternTags: ['TCS', 'Zoho'],
      question: "The average of 12 numbers is 40. If one number, 52, is removed, find the new average of the remaining 11 numbers.",
      options: ['35.67', '38', '38.91', '40'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: '12 numbers, average=40 (sum=480). Remove the number 52.' },
        { label: 'Concept', text: 'Subtract the removed value from the total sum, then divide by the NEW (reduced) count.' },
        { label: 'Working', text: 'New sum = 480−52 = 428.' },
        { label: 'Answer', text: 'New average = 428/11 ≈ 38.91.' },
      ],
      commonTrap:
        "Dividing the new sum by the ORIGINAL count of 12 instead of the reduced count of 11 (428/12≈35.67) — the denominator must shrink along with the removal.",
      alternateMethod: {
        label: 'Deviation logic',
        steps: [
          'The removed number (52) was ABOVE the group\'s old average (40).',
          'Removing an above-average value always pulls the remaining average DOWN — confirming the new average (≈38.91) should indeed be less than 40.',
        ],
      },
    },
    {
      id: 'avg-13',
      difficulty: 'Medium',
      patternTags: ['Infosys', 'Wipro'],
      question: "The average of 9 numbers is 35. A 10th number is added, and the new average becomes 37. Find the 10th number.",
      options: ['37', '50', '55', '58'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: '9 numbers average=35 (sum=315). Adding a 10th number makes the average=37 (over 10 numbers).' },
        { label: 'Concept', text: 'The new number equals the new total sum minus the old total sum.' },
        { label: 'Working', text: 'New sum = 10×37=370.' },
        { label: 'Answer', text: '10th number = 370−315 = 55.' },
      ],
      commonTrap:
        "Assuming the 10th number simply equals the NEW average (37) — but for the average to actually rise when a value joins, that value must be well ABOVE the new average, not merely equal to it.",
      alternateMethod: {
        label: 'Direct formula',
        steps: [
          'New value = New average + (old count)×(New average − Old average).',
          '= 37 + 9×(37−35) = 37+18 = 55.',
        ],
      },
    },
    {
      id: 'avg-14',
      difficulty: 'Easy',
      patternTags: ['TCS', 'Infosys'],
      question: "The average of 6 numbers is 27. Five of the numbers are 22, 25, 28, 30 and 24. Find the sixth number.",
      options: ['27', '29', '31', '33'],
      correctIndex: 3,
      steps: [
        { label: 'Given', text: '6 numbers, average=27. Five known numbers: 22, 25, 28, 30, 24.' },
        { label: 'Concept', text: 'Total sum = average × count; subtract the sum of the known numbers to find the missing one.' },
        { label: 'Working', text: 'Total sum = 6×27 = 162. Sum of 5 known = 22+25+28+30+24 = 129.' },
        { label: 'Answer', text: 'Sixth number = 162−129 = 33.' },
      ],
      commonTrap:
        "Missing one of the five known numbers while adding them up (a simple arithmetic slip) — always recount the terms in the sum before subtracting.",
      alternateMethod: {
        label: 'Deviation method',
        steps: [
          'Deviations of the 5 known numbers from 27: −5, −2, +1, +3, −3 — summing to −6.',
          'For the overall average to be exactly 27, the sixth number\'s deviation must be +6.',
          'Sixth number = 27+6 = 33.',
        ],
      },
    },
    {
      id: 'avg-15',
      difficulty: 'Medium',
      patternTags: ['Wipro', 'Zoho'],
      question: "Find the average of the squares of the first 5 natural numbers.",
      options: ['11', '12', '13', '14'],
      correctIndex: 0,
      steps: [
        { label: 'Given', text: 'First 5 natural numbers, but averaging their SQUARES, not the numbers themselves.' },
        { label: 'Concept', text: 'Average of squares of the first n naturals = (n+1)(2n+1)/6 — a distinct formula from the simple linear average, since squares grow non-linearly.' },
        { label: 'Working', text: '(5+1)(2×5+1)/6 = 6×11/6.' },
        { label: 'Answer', text: 'Average of squares = 11.' },
      ],
      commonTrap:
        "Applying the LINEAR-average formula (n+1)/2=3 to the squares by mistake — squares don't grow at a constant rate, so they need their own averaging formula.",
      alternateMethod: {
        label: 'Direct computation',
        steps: [
          '1²+2²+3²+4²+5² = 1+4+9+16+25 = 55.',
          'Average = 55/5 = 11.',
        ],
      },
    },
    {
      id: 'avg-16',
      difficulty: 'Easy',
      patternTags: ['TCS', 'Zoho'],
      question: "Find the average of the first 20 even natural numbers.",
      options: ['19', '20', '21', '22'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: 'First 20 even natural numbers: 2, 4, ..., 40.' },
        { label: 'Concept', text: 'Average of the first n even numbers = (first + last)/2 = (2+2n)/2 = n+1.' },
        { label: 'Working', text: 'n=20 → average = 20+1.' },
        { label: 'Answer', text: 'Average = 21.' },
      ],
      commonTrap:
        "Using n itself (20) instead of n+1 — forgetting the '+1' adjustment that comes from averaging the first and last terms of the sequence.",
      alternateMethod: {
        label: 'Direct method',
        steps: [
          'Last term = 2×20 = 40.',
          'Average = (2+40)/2 = 42/2 = 21.',
        ],
      },
    },
    {
      id: 'avg-17',
      difficulty: 'Medium',
      patternTags: ['Infosys', 'Wipro'],
      question: "The average weight of 8 men increases by 2.5 kg when a new man replaces one of them weighing 65 kg. Find the weight of the new man.",
      options: ['80', '85', '87', '90'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: '8 men, average increases by 2.5kg when a 65kg man is replaced.' },
        { label: 'Concept', text: 'Total weight increase = (increase in average) × (number of people); the new man\'s weight = old man\'s weight + this TOTAL increase.' },
        { label: 'Working', text: 'Total increase = 8×2.5 = 20kg.' },
        { label: 'Answer', text: "New man's weight = 65+20 = 85 kg." },
      ],
      commonTrap:
        "Adding just the per-person increase (2.5kg) to the old man's weight (65+2.5=67.5) instead of the TOTAL increase spread across all 8 people — the average shift must be scaled up by the group size.",
      alternateMethod: {
        label: 'Verification',
        steps: [
          'Since the ONLY change is swapping the 65kg man for the new man, new man\'s weight − 65 must equal the total increase.',
          'New man\'s weight − 65 = 20 → new man = 85kg.',
        ],
      },
    },
    {
      id: 'avg-18',
      difficulty: 'Medium',
      patternTags: ['TCS', 'Infosys'],
      question: "In a group of 5 friends, one weighs 60 kg. The others weigh 5 kg more, 3 kg less, 8 kg more, and 2 kg less than this friend, respectively. Find the average weight of the group.",
      options: ['60.5', '61', '61.6', '62'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: 'Reference weight = 60kg. Others are +5, −3, +8, −2 relative to it.' },
        { label: 'Concept', text: 'Compute each actual weight from the deviations, then average all 5 (including the reference friend themselves).' },
        { label: 'Working', text: 'Weights: 60, 65, 57, 68, 58. Sum = 308.' },
        { label: 'Answer', text: 'Average = 308/5 = 61.6 kg.' },
      ],
      commonTrap:
        "Averaging just the FOUR deviations (5−3+8−2=8, then 8/4=2) and adding that to 60 (getting 62) — this wrongly excludes the reference person from the divisor; the deviation-sum should be divided by the FULL group size of 5, not 4.",
      alternateMethod: {
        label: 'Deviation-sum shortcut',
        steps: [
          'Sum of the four deviations (the reference person\'s own deviation is 0) = 5−3+8−2 = 8.',
          'Average = reference weight + (sum of deviations)/(total count) = 60 + 8/5 = 61.6.',
        ],
      },
    },
    {
      id: 'avg-19',
      difficulty: 'Medium',
      patternTags: ['Wipro', 'Zoho'],
      question: "The average of 20 numbers is 15. When one number is removed, the average of the remaining numbers becomes 14.5. Find the removed number.",
      options: ['23', '24.5', '26', '28'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: '20 numbers, average=15 (sum=300). After removing one number, remaining 19 average=14.5 (sum=275.5).' },
        { label: 'Concept', text: 'Removed number = Old total sum − New total sum.' },
        { label: 'Working', text: '300 − 275.5 = 24.5.' },
        { label: 'Answer', text: 'Removed number = 24.5.' },
      ],
      commonTrap:
        "Mixing up which count (20 or 19) applies to which average when computing the two sums — always use 20 for the ORIGINAL total and 19 for the REMAINING total.",
      alternateMethod: {
        label: 'Deviation method',
        steps: [
          'Removing a number above the old average pulls the remaining average DOWN (15→14.5 here).',
          'Total "pull down" = (old avg − new avg) × (new count) = 0.5×19 = 9.5.',
          'Removed number = old average + this pull = 15+9.5 = 24.5.',
        ],
      },
    },
    {
      id: 'avg-20',
      difficulty: 'Medium',
      patternTags: ['TCS', 'Zoho'],
      question: "The total weight of 12 boys in a class is 480 kg. If the average weight of the whole class (including 8 girls) is 42 kg, find the average weight of the girls.",
      options: ['40', '42', '44', '45'],
      correctIndex: 3,
      steps: [
        { label: 'Given', text: '12 boys, total weight=480kg. Whole class (20 students) average=42kg.' },
        { label: 'Concept', text: 'Find the whole class\'s total weight, subtract the boys\' known total to get the girls\' total, then divide by the girls\' count.' },
        { label: 'Working', text: 'Class total = 20×42 = 840. Girls\' total = 840−480 = 360.' },
        { label: 'Answer', text: "Girls' average = 360/8 = 45 kg." },
      ],
      commonTrap:
        "Comparing the boys' own average (480/12=40) to the class average (42) and guessing an answer from that gap alone, instead of working through the actual TOTAL-weight subtraction.",
      alternateMethod: {
        label: 'Weighted-average balancing',
        steps: [
          'Boys are 12/20 of the class, girls are 8/20.',
          '42 = (40×12 + G×8)/20 → 840 = 480+8G → 8G=360 → G=45.',
        ],
      },
    },
    {
      id: 'avg-21',
      difficulty: 'Medium',
      patternTags: ['Infosys', 'Wipro'],
      question: "The average of 4 numbers is increased by 3 when one of the numbers is changed from 22 to a new value. Find the new value.",
      options: ['25', '34', '36', '38'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: '4 numbers; changing one from 22 raises the average by 3.' },
        { label: 'Concept', text: 'Total sum increase = (increase in average) × (count); the new value = old value + this TOTAL increase.' },
        { label: 'Working', text: 'Total increase = 4×3 = 12.' },
        { label: 'Answer', text: 'New value = 22+12 = 34.' },
      ],
      commonTrap:
        "Adding just the per-item average increase (3) to the old value (22+3=25) instead of the total increase spread across all 4 numbers — the shift must be scaled up by the count.",
      alternateMethod: {
        label: 'Verification',
        steps: [
          'If the new value is 34 (a rise of 12 from 22), that 12 spread evenly over 4 numbers gives exactly 3 per number.',
          'This matches the given average increase of 3 exactly.',
        ],
      },
    },
    {
      id: 'avg-22',
      difficulty: 'Hard',
      patternTags: ['TCS', 'Zoho'],
      question: "A batsman's average in 15 innings is 32. In one of those innings he scored 92 runs. Excluding that innings, find his average for the remaining 14 innings.",
      options: ['26', '27', '27.71', '29'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: '15 innings, average=32 (total=480); excluding one score of 92.' },
        { label: 'Concept', text: 'Subtract the excluded score from the total, then divide by the REDUCED number of innings.' },
        { label: 'Working', text: 'New total = 480−92 = 388.' },
        { label: 'Answer', text: 'New average = 388/14 ≈ 27.71.' },
      ],
      commonTrap:
        "Assuming excluding a high score only slightly reduces the average (guessing near 29 or 30) — the drop is significant precisely because 92 was so far above the original average of 32.",
      alternateMethod: {
        label: 'Deviation method',
        steps: [
          'The excluded score (92) was 60 runs above the old average (32).',
          'Spreading the removal of that 60-run surplus over the remaining 14 innings: new average = 32 − 60/14 ≈ 27.71.',
        ],
      },
    },
    {
      id: 'avg-23',
      difficulty: 'Medium',
      patternTags: ['Wipro', 'Zoho'],
      question: "The average score of 25 students in a test is 68. Later, 5 more students take the same test with an average score of 80. Find the average score of all 30 students.",
      options: ['68', '69', '70', '74'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: '25 students average=68, 5 more students average=80.' },
        { label: 'Concept', text: 'Combine the two group totals and divide by the combined count — a WEIGHTED average, weighted by group size.' },
        { label: 'Working', text: 'Total = 25×68 + 5×80 = 1,700+400 = 2,100.' },
        { label: 'Answer', text: 'Combined average = 2,100/30 = 70.' },
      ],
      commonTrap:
        "Taking the simple average of 68 and 80 ((68+80)/2=74) — this ignores that the 25-student group is much larger than the 5-student group, so the combined average should lean much closer to 68.",
      alternateMethod: {
        label: 'Weighted-deviation balancing',
        steps: [
          'Let x = combined average. The 25 students are (x−68) below x, and the 5 students are (80−x) above x; these must balance: 25(x−68)=5(80−x).',
          '25x−1,700=400−5x → 30x=2,100 → x=70.',
        ],
      },
    },
    {
      id: 'avg-24',
      difficulty: 'Medium',
      patternTags: ['TCS', 'Infosys'],
      question: "Find the average of the first 8 multiples of 7.",
      options: ['28', '29.5', '31.5', '33'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: 'First 8 multiples of 7: 7, 14, 21, ..., 56.' },
        { label: 'Concept', text: 'For any evenly-spaced sequence, average = (first term + last term)/2.' },
        { label: 'Working', text: 'First = 7, last = 7×8 = 56. Average = (7+56)/2.' },
        { label: 'Answer', text: 'Average = 63/2 = 31.5.' },
      ],
      commonTrap:
        "Multiplying 7 by the 'middle position' rounded down (7×4=28) instead of averaging the first and last terms — with an EVEN count of terms, the true average falls exactly between the two middle terms, not on a single term.",
      alternateMethod: {
        label: 'Formula method',
        steps: [
          'Average of first n multiples of k = k×(n+1)/2.',
          '= 7×(8+1)/2 = 7×4.5 = 31.5.',
        ],
      },
    },
    {
      id: 'avg-25',
      difficulty: 'Easy',
      patternTags: ['Wipro', 'Zoho'],
      question: "Find the average of all numbers from 11 to 30 (both inclusive).",
      options: ['20.5', '21', '21.5', '22'],
      correctIndex: 0,
      steps: [
        { label: 'Given', text: 'Numbers from 11 to 30, inclusive.' },
        { label: 'Concept', text: 'For a consecutive run of integers, average = (first + last)/2, regardless of the starting point.' },
        { label: 'Working', text: '(11+30)/2 = 41/2.' },
        { label: 'Answer', text: 'Average = 20.5.' },
      ],
      commonTrap:
        "Miscounting the number of terms as (30−11)=19 instead of (30−11+1)=20 when cross-checking via the sum method — a common off-by-one error with inclusive ranges.",
      alternateMethod: {
        label: 'Sum-and-count method',
        steps: [
          'Count = 30−11+1 = 20 terms. Sum = (20/2)×(11+30) = 10×41 = 410.',
          'Average = 410/20 = 20.5.',
        ],
      },
    },
  ],
  ages: [
    {
      id: 'age-01',
      difficulty: 'Easy',
      patternTags: ['TCS', 'Infosys'],
      question: "The ages of A and B are in the ratio 5:6. If the sum of their ages is 44 years, find B's age.",
      options: ['24', '26', '28', '30'],
      correctIndex: 0,
      steps: [
        { label: 'Given', text: 'A:B = 5:6, sum of ages = 44.' },
        { label: 'Concept', text: 'Total parts = 5+6=11; each person\'s age = (their ratio number/11) × sum.' },
        { label: 'Working', text: '1 part = 44/11 = 4.' },
        { label: 'Answer', text: "B's age = 6×4 = 24." },
      ],
      commonTrap:
        "Mixing up which ratio number belongs to which person — always match the FIRST-named ratio number to the FIRST-named person (A gets 5, B gets 6).",
      alternateMethod: {
        label: 'Direct fraction method',
        steps: [
          "B's age = (6/11) × 44 = 24.",
        ],
      },
    },
    {
      id: 'age-02',
      difficulty: 'Medium',
      patternTags: ['TCS', 'Wipro'],
      question: "Five years ago, the ratio of the ages of A and B was 3:4. Their present ages are in the ratio 4:5. Find A's present age.",
      options: ['18', '20', '22', '25'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: '5 years ago, A:B = 3:4. Present ratio = 4:5.' },
        { label: 'Concept', text: 'Let ages 5 years ago = 3x, 4x; add 5 to each for present ages, set equal to the given present ratio, and solve for x.' },
        { label: 'Working', text: '(3x+5)/(4x+5) = 4/5 → 15x+25 = 16x+20.' },
        { label: 'Answer', text: 'x = 5. A\'s present age = 3×5+5 = 20.' },
      ],
      commonTrap:
        "Adding 5 to the RATIO NUMBERS directly (3+5 : 4+5 = 8:9) instead of to the actual ages (3x+5 and 4x+5) — the ratio numbers aren't real ages until scaled by x.",
      alternateMethod: {
        label: 'Verification',
        steps: [
          'A 5 years ago=15, B=20 (ratio 3:4 ✓). Present: A=20, B=25 (ratio 4:5 ✓) — matches both conditions.',
        ],
      },
    },
    {
      id: 'age-03',
      difficulty: 'Medium',
      patternTags: ['Infosys', 'Zoho'],
      question: "Six years ago, A's age was twice B's age. A is currently 8 years older than B. Find A's present age.",
      options: ['18', '20', '22', '24'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: '6 years ago, A = 2×B. Currently, A = B+8.' },
        { label: 'Concept', text: "Let B's age 6 years ago = x, A's = 2x; the age GAP between two people never changes over time, so the past gap (2x−x=x) must equal the present gap (8)." },
        { label: 'Working', text: 'x = 8 (directly, from the constant age-gap insight).' },
        { label: 'Answer', text: "A's present age = 2×8+6 = 22." },
      ],
      commonTrap:
        "Assuming the 2:1 RATIO from 6 years ago still holds for their present ages too — ratios change over time even though the actual age DIFFERENCE between two people stays constant forever.",
      alternateMethod: {
        label: 'Constant-gap shortcut',
        steps: [
          'Since the age gap between two people never changes, the "6 years ago" gap (x) must equal the given present gap (8) directly — so x=8 without writing a full equation.',
          'A\'s present age = 2x+6 = 22.',
        ],
      },
    },
    {
      id: 'age-04',
      difficulty: 'Medium',
      patternTags: ['TCS', 'Zoho'],
      question: "The present age of a father is 3 times that of his son. After 10 years, the father's age will be twice that of his son. Find the son's present age.",
      options: ['10', '12', '14', '15'],
      correctIndex: 0,
      steps: [
        { label: 'Given', text: 'Father = 3×son (present). After 10 years, father = 2×son.' },
        { label: 'Concept', text: "Express ages in terms of son's present age x; add 10 to BOTH ages for the future condition, then solve." },
        { label: 'Working', text: '3x+10 = 2(x+10) → 3x+10 = 2x+20.' },
        { label: 'Answer', text: "x = 10. Son's present age = 10." },
      ],
      commonTrap:
        "Applying the 'after 10 years' ratio (2:1) directly to the PRESENT ages instead of the FUTURE (aged-up) ones — the 10-year shift must be added to both ages before the new ratio is applied.",
      alternateMethod: {
        label: 'Verification',
        steps: [
          'Son=10, Father=30 (ratio 3:1 present ✓). After 10 years: son=20, father=40 (ratio 2:1 ✓) — matches.',
        ],
      },
    },
    {
      id: 'age-05',
      difficulty: 'Hard',
      patternTags: ['Infosys', 'Wipro'],
      question: "In 6 years, A will be twice as old as B was 6 years ago. If A is currently 4 years older than B, find A's current age.",
      options: ['24', '26', '28', '30'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: 'A is currently 4 years older than B.' },
        { label: 'Concept', text: "Let B's current age=x, A's=x+4. Compute 'A in 6 years' (x+4+6) and 'B 6 years ago' (x−6) as two DIFFERENT time points — not mirror images of each other." },
        { label: 'Working', text: '(x+4+6) = 2(x−6) → x+10 = 2x−12.' },
        { label: 'Answer', text: 'x = 22. A\'s current age = 22+4 = 26.' },
      ],
      commonTrap:
        "Treating 'A in 6 years' and 'B 6 years ago' as a single shared time shift (as if solving a simple current-age ratio) — this problem deliberately mixes a FUTURE point for A with a PAST point for B, which must be handled as two separate shifts.",
      alternateMethod: {
        label: 'Verification',
        steps: [
          'B=22 now, A=26 now. In 6 years, A=32. B 6 years ago=16.',
          'Is 32 = 2×16? Yes ✓ — matches.',
        ],
      },
    },
    {
      id: 'age-06',
      difficulty: 'Medium',
      patternTags: ['TCS', 'Infosys'],
      question: "The sum of the ages of a father, mother and son is 90 years. The mother is 15 years older than the son, and the father is 15 years older than the mother. Find the son's age.",
      options: ['12', '13', '15', '18'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: 'Sum=90. Mother = Son+15. Father = Mother+15 = Son+30.' },
        { label: 'Concept', text: "Express all three ages in terms of the son's age (the base unknown), then sum and solve." },
        { label: 'Working', text: 'x + (x+15) + (x+30) = 90 → 3x+45 = 90.' },
        { label: 'Answer', text: '3x = 45 → x = 15. Son\'s age = 15.' },
      ],
      commonTrap:
        "Computing the father's age directly from the son's age (Son+15) instead of building through the mother first — the relations CHAIN through the mother, so father's age must come from mother's age, not the son's directly.",
      alternateMethod: {
        label: 'Direct substitution check',
        steps: [
          'With x=15: Son=15, Mother=30, Father=45.',
          '15+30+45 = 90 ✓ — matches.',
        ],
      },
    },
    {
      id: 'age-07',
      difficulty: 'Hard',
      patternTags: ['Wipro', 'Zoho'],
      question: "Ten years ago, A's age was 3 times B's age. Ten years from now, A's age will be twice B's age. Find A's present age.",
      options: ['58', '62', '66', '70'],
      correctIndex: 3,
      steps: [
        { label: 'Given', text: '10 years ago, A=3×B. 10 years from now, A=2×B.' },
        { label: 'Concept', text: "Let B's age 10 years ago=x, A's=3x. To reach '10 years from now', add 20 to these past values (10 to reach present + 10 more to reach the future point), then apply the future ratio." },
        { label: 'Working', text: '(3x+20) = 2(x+20) → 3x+20 = 2x+40.' },
        { label: 'Answer', text: 'x = 20. A\'s present age = 3×20+10 = 70.' },
      ],
      commonTrap:
        "Adding only 10 (not 20) to the 'ten years ago' values when computing 'ten years from now' — the total span from the past reference point to the future one is 20 years, not 10.",
      alternateMethod: {
        label: 'Present-age variables',
        steps: [
          'Let A=a, B=b (present). a−10=3(b−10) → a=3b−20. a+10=2(b+10) → a=2b+10.',
          'Setting equal: 3b−20=2b+10 → b=30. a=2(30)+10=70.',
        ],
      },
    },
    {
      id: 'age-08',
      difficulty: 'Medium',
      patternTags: ['TCS', 'Zoho'],
      question: "A is 6 years older than B. If 4 years ago, the sum of their ages was 40 years, find A's present age.",
      options: ['27', '29', '31', '33'],
      correctIndex: 0,
      steps: [
        { label: 'Given', text: 'A=B+6 (present). 4 years ago, sum of ages=40.' },
        { label: 'Concept', text: "Let B's present age=x, A's=x+6; subtract 4 from EACH person's age for '4 years ago', sum them, and solve." },
        { label: 'Working', text: '(x+6−4)+(x−4) = 40 → 2x−2 = 40.' },
        { label: 'Answer', text: 'x = 21. A\'s present age = 21+6 = 27.' },
      ],
      commonTrap:
        "Forgetting to subtract 4 from BOTH ages individually before summing — each person's age must be reduced by 4 separately, not just the final total.",
      alternateMethod: {
        label: 'Present-sum method',
        steps: [
          'If 4 years ago the sum was 40, presently (4 years later for both people) the sum is 40+4+4=48.',
          'Since A=B+6 and A+B=48: B=(48−6)/2=21, A=21+6=27.',
        ],
      },
    },
    {
      id: 'age-09',
      difficulty: 'Easy',
      patternTags: ['Infosys', 'Wipro'],
      question: "A is elder to B by 12 years. If the ratio of their ages is 5:3, find A's age.",
      options: ['28', '30', '33', '36'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: 'A−B=12. A:B=5:3.' },
        { label: 'Concept', text: "Express ages as 5x, 3x; the DIFFERENCE in ratio parts (5−3=2) corresponds to the given age difference (12)." },
        { label: 'Working', text: '2 parts = 12 → 1 part = 6.' },
        { label: 'Answer', text: 'A\'s age = 5×6 = 30.' },
      ],
      commonTrap:
        "Dividing 12 by the SUM of the ratio parts (8) instead of the DIFFERENCE (2) — the age gap corresponds specifically to the difference between the ratio numbers, not their sum.",
      alternateMethod: {
        label: 'Verification',
        steps: [
          'A=30, B=18. Difference=30−18=12 ✓. Ratio=30:18=5:3 ✓ — matches both conditions.',
        ],
      },
    },
    {
      id: 'age-10',
      difficulty: 'Medium',
      patternTags: ['TCS', 'Zoho'],
      question: "A grandfather is 6 times as old as his granddaughter, and twice as old as his son (the granddaughter's father). If the grandfather is 30 years older than his son, find the granddaughter's age.",
      options: ['8', '9', '10', '12'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: 'Grandfather = 6×granddaughter. Grandfather = 2×son. Grandfather − Son = 30.' },
        { label: 'Concept', text: "Express everyone using the granddaughter's age (x): grandfather=6x, and since grandfather=2×son, son=6x/2=3x." },
        { label: 'Working', text: '6x − 3x = 30 → 3x = 30.' },
        { label: 'Answer', text: 'x = 10. Granddaughter\'s age = 10.' },
      ],
      commonTrap:
        "Applying the granddaughter's multiplier (6) to find the son's age too (son=6x/6=x) — each relation (grandfather:granddaughter and grandfather:son) uses a DIFFERENT multiplier and must be tracked separately.",
      alternateMethod: {
        label: 'Direct ratio method',
        steps: [
          'Grandfather:Son = 2:1, and their difference of 1 part = 30 → Grandfather=60, Son=30.',
          'Since Grandfather = 6×Granddaughter: Granddaughter = 60/6 = 10.',
        ],
      },
    },
    {
      id: 'age-11',
      difficulty: 'Hard',
      patternTags: ['Infosys', 'Wipro'],
      question: "The ratio of the present ages of P and Q is 4:5. Six years ago, the ratio of their ages was 3:4. Find the sum of their present ages.",
      options: ['48', '54', '58', '62'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: 'Present P:Q = 4:5. Six years ago, ratio = 3:4.' },
        { label: 'Concept', text: 'Let present ages = 4x, 5x; subtract 6 from each for "6 years ago", set equal to the given past ratio, and solve for x.' },
        { label: 'Working', text: '(4x−6)/(5x−6) = 3/4 → 16x−24 = 15x−18.' },
        { label: 'Answer', text: 'x = 6. P=24, Q=30, sum = 54.' },
      ],
      commonTrap:
        "Subtracting 6 from the RATIO NUMBERS (4−6, 5−6) instead of from the actual scaled ages (4x−6, 5x−6) — the ratio numbers are placeholders, not real years, until multiplied by x.",
      alternateMethod: {
        label: 'Verification',
        steps: [
          'P=24, Q=30 (ratio 4:5 ✓). 6 years ago: P=18, Q=24 (ratio 18:24=3:4 ✓) — matches.',
        ],
      },
    },
    {
      id: 'age-12',
      difficulty: 'Medium',
      patternTags: ['TCS', 'Infosys'],
      question: "The ages of two friends differ by 20 years. 5 years ago, the elder friend was 6 times as old as the younger friend was then. Find the younger friend's present age.",
      options: ['9', '11', '13', '15'],
      correctIndex: 0,
      steps: [
        { label: 'Given', text: 'Present age difference = 20 years. 5 years ago, elder = 6×younger.' },
        { label: 'Concept', text: "Let younger's age 5 years ago = x, elder's = 6x. The DIFFERENCE between them (6x−x=5x) never changes over time, so it must equal the given present difference of 20." },
        { label: 'Working', text: '5x = 20 → x = 4.' },
        { label: 'Answer', text: "Younger friend's present age = 4+5 = 9." },
      ],
      commonTrap:
        "Assuming the 6:1 RATIO from 5 years ago also applies to their PRESENT ages (e.g. guessing elder's present age = 6×9=54) — only the age DIFFERENCE stays constant over time, never the ratio.",
      alternateMethod: {
        label: 'Verification',
        steps: [
          'Younger 5 yrs ago=4, elder=24 (ratio 6:1 ✓, difference=20 ✓).',
          'Present: younger=9, elder=29, difference=29−9=20 ✓ — matches.',
        ],
      },
    },
    {
      id: 'age-13',
      difficulty: 'Easy',
      patternTags: ['Wipro', 'Zoho'],
      question: "The sum of the ages of A and B is 50 years. A is 8 years older than B. Find A's age.",
      options: ['21', '25', '27', '29'],
      correctIndex: 3,
      steps: [
        { label: 'Given', text: 'A+B=50, A−B=8.' },
        { label: 'Concept', text: 'Adding the sum equation and the difference equation eliminates B, directly giving 2A.' },
        { label: 'Working', text: '(A+B)+(A−B) = 50+8 = 58.' },
        { label: 'Answer', text: '2A = 58 → A = 29.' },
      ],
      commonTrap:
        "Splitting the sum evenly first (25 each) and then subtracting 8 from A instead of adjusting correctly — the sum-and-difference elimination method is direct and needs no intermediate 'average' step.",
      alternateMethod: {
        label: 'Substitution method',
        steps: [
          'B = A−8. Substituting into the sum: A+(A−8) = 50 → 2A = 58 → A = 29.',
        ],
      },
    },
    {
      id: 'age-14',
      difficulty: 'Hard',
      patternTags: ['TCS', 'Zoho'],
      question: "The product of the ages of A and B is 192. If A is 4 years older than B, find B's age.",
      options: ['10', '12', '14', '16'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: 'A×B=192, A=B+4.' },
        { label: 'Concept', text: 'Substitute A=B+4 into the product equation to get a quadratic in B, then solve by factoring.' },
        { label: 'Working', text: 'B(B+4) = 192 → B²+4B−192 = 0 → (B−12)(B+16) = 0.' },
        { label: 'Answer', text: 'B = 12 (rejecting the negative root B=−16).' },
      ],
      commonTrap:
        "Trying a simple division shortcut (192/4=48) that ignores the quadratic nature of a PRODUCT relationship — unlike sum/difference problems, a product between two unknowns needs a quadratic equation.",
      alternateMethod: {
        label: 'Plug the options back in',
        steps: [
          'Test B=12: A=16. Product = 12×16 = 192 ✓ — matches exactly.',
        ],
      },
    },
    {
      id: 'age-15',
      difficulty: 'Medium',
      patternTags: ['Infosys', 'Wipro'],
      question: "The present ages of a father and his son are in the ratio 7:2. Eight years from now, the sum of their ages will be 88 years. Find the son's present age.",
      options: ['12', '14', '16', '18'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: 'Father:Son = 7:2 (present). In 8 years, sum of ages = 88.' },
        { label: 'Concept', text: 'Express ages as 7x, 2x; add 8 to EACH person\'s age for the future ages, sum them, and solve for x.' },
        { label: 'Working', text: '(7x+8)+(2x+8) = 88 → 9x+16 = 88.' },
        { label: 'Answer', text: '9x = 72 → x = 8. Son\'s present age = 2×8 = 16.' },
      ],
      commonTrap:
        "Adding 8 only ONCE to the total (as a single shared adjustment) instead of separately to EACH person's age — since both age individually by 8 years, the total sum rises by 16, not 8.",
      alternateMethod: {
        label: 'Present-sum method',
        steps: [
          'If the sum 8 years from now is 88, the present sum is 88−16=72 (since both ages will each rise by 8, adding 16 total over 8 years).',
          '9x=72 → x=8. Son=16.',
        ],
      },
    },
    {
      id: 'age-16',
      difficulty: 'Easy',
      patternTags: ['TCS', 'Infosys'],
      question: "Three friends' ages are in the ratio 2:3:4. If the sum of their ages is 54, find the age of the youngest friend.",
      options: ['12', '15', '18', '20'],
      correctIndex: 0,
      steps: [
        { label: 'Given', text: 'Ratio 2:3:4, sum=54.' },
        { label: 'Concept', text: 'Total parts = 2+3+4=9.' },
        { label: 'Working', text: '1 part = 54/9 = 6.' },
        { label: 'Answer', text: 'Youngest = 2×6 = 12.' },
      ],
      commonTrap:
        "Picking the wrong ratio term for the 'youngest' person, or dividing by the wrong total (e.g. the number of friends, 3, instead of the sum of ratio parts, 9).",
      alternateMethod: {
        label: 'Direct fraction method',
        steps: [
          'Youngest = (2/9) × 54 = 12.',
        ],
      },
    },
    {
      id: 'age-17',
      difficulty: 'Medium',
      patternTags: ['Wipro', 'Zoho'],
      question: "After 10 years, A will be as old as B is now. The sum of their present ages is 40 years. Find B's present age.",
      options: ['22', '25', '28', '30'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: '(A\'s age in 10 years) = (B\'s present age). Sum of present ages = 40.' },
        { label: 'Concept', text: 'Translate the sentence into an equation: a+10=b, where a and b are present ages. Combine with the sum equation.' },
        { label: 'Working', text: 'a=b−10. (b−10)+b = 40 → 2b−10 = 40.' },
        { label: 'Answer', text: '2b = 50 → b = 25.' },
      ],
      commonTrap:
        "Misreading the sentence as 'B will be as old as A is now' (the reverse relationship) — this exact type of phrasing is a classic source of confusion, so reading carefully which person's FUTURE matches which person's PRESENT is essential.",
      alternateMethod: {
        label: 'Verification',
        steps: [
          'B=25 (present), A=15 (present). In 10 years, A=25, which equals B\'s current age (25) ✓.',
          'Sum = 15+25=40 ✓ — matches.',
        ],
      },
    },
    {
      id: 'age-18',
      difficulty: 'Hard',
      patternTags: ['TCS', 'Zoho'],
      question: "A mother is currently 28 years older than her daughter. In 8 years, the mother's age will be 3 times the daughter's age at that time. Find the mother's age when her daughter was born.",
      options: ['22', '24', '26', '28'],
      correctIndex: 3,
      steps: [
        { label: 'Given', text: 'Mother − Daughter = 28 (a constant gap). In 8 years, Mother = 3×Daughter.' },
        { label: 'Concept', text: "A parent's age at their child's birth always equals their constant age gap, since the child was 0 years old at that moment — this holds regardless of any other clue in the question." },
        { label: 'Working', text: 'The gap is given directly: 28 years.' },
        { label: 'Answer', text: "Mother's age when the daughter was born = 28." },
      ],
      commonTrap:
        "Trying to re-derive the daughter's exact present age from the '3 times in 8 years' clue and then subtracting years elapsed — that clue is only there to verify the given gap is consistent; the age-at-birth is simply the constant gap (28), given directly.",
      alternateMethod: {
        label: 'Consistency check (not required, but confirms it)',
        steps: [
          'Solving the 8-years-hence clue: daughter=6 now, mother=34 now (34−6=28, confirming the gap).',
          'In 8 years: mother=42, daughter=14. Is 42=3×14? Yes ✓ — the given numbers are internally consistent.',
        ],
      },
    },
    {
      id: 'age-19',
      difficulty: 'Hard',
      patternTags: ['Infosys', 'Wipro'],
      question: "Five years ago, A was thrice as old as B. Ten years from now, A will be twice as old as B. Find A's present age.",
      options: ['42', '46', '50', '54'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: '5 years ago, A=3×B. 10 years from now, A=2×B.' },
        { label: 'Concept', text: "Let B's age 5 years ago=x, A's=3x. The span from 'five years ago' to 'ten years from now' is 5+10=15 years total — add 15 to the past values to reach the future point." },
        { label: 'Working', text: '(3x+15) = 2(x+15) → 3x+15 = 2x+30.' },
        { label: 'Answer', text: 'x = 15. A\'s present age = 3×15+5 = 50.' },
      ],
      commonTrap:
        "Adding only 10 years (not the full 15) to the 'five years ago' values when computing 'ten years from now' — the total span between the two reference points is 15 years, not 10.",
      alternateMethod: {
        label: 'Present-age variables',
        steps: [
          'a−5=3(b−5) → a=3b−10. a+10=2(b+10) → a=2b+10.',
          'Setting equal: 3b−10=2b+10 → b=20. a=2(20)+10=50.',
        ],
      },
    },
    {
      id: 'age-20',
      difficulty: 'Hard',
      patternTags: ['TCS', 'Infosys'],
      question: "Ten years ago, the ratio of the ages of A and B was 1:2. Ten years from now, the ratio will be 2:3. Find B's present age.",
      options: ['45', '50', '55', '60'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: '10 years ago, A:B=1:2. 10 years from now, A:B=2:3.' },
        { label: 'Concept', text: "Let A's age 10 years ago=x, B's=2x. To reach '10 years from now', add 20 (10 back to present + 10 forward to the future point)." },
        { label: 'Working', text: '(x+20)/(2x+20) = 2/3 → 3x+60 = 4x+40.' },
        { label: 'Answer', text: 'x = 20. B\'s present age = 2×20+10 = 50.' },
      ],
      commonTrap:
        "Adding only 10 (not 20) to the 'ten years ago' values — the span from the past reference point to the future reference point is 20 years total (10 back + 10 forward).",
      alternateMethod: {
        label: 'Verification',
        steps: [
          'A 10 yrs ago=20, B=40 (ratio 1:2 ✓). Present: A=30, B=50.',
          '10 yrs hence: A=40, B=60, ratio=40:60=2:3 ✓ — matches.',
        ],
      },
    },
    {
      id: 'age-21',
      difficulty: 'Medium',
      patternTags: ['Wipro', 'Zoho'],
      question: "The sum of the ages of A and B is 45. The sum of the ages of B and C is 40. The sum of the ages of C and A is 35. Find A's age.",
      options: ['20', '22', '24', '25'],
      correctIndex: 0,
      steps: [
        { label: 'Given', text: 'A+B=45, B+C=40, C+A=35.' },
        { label: 'Concept', text: 'Add all three equations; each person appears exactly twice in the total, so dividing by 2 gives the sum of all three ages — then subtract the ONE pairwise sum that excludes the desired person.' },
        { label: 'Working', text: '2(A+B+C) = 45+40+35 = 120 → A+B+C = 60.' },
        { label: 'Answer', text: 'A = 60 − (B+C) = 60−40 = 20.' },
      ],
      commonTrap:
        "Trying to solve for A using only the two equations that mention A (A+B=45, C+A=35) without bringing in the third — that leaves 2 equations in 3 unknowns, not enough to isolate A alone.",
      alternateMethod: {
        label: 'Solve for all three',
        steps: [
          'B = 60−35 = 25. C = 60−45 = 15.',
          'Verify: A+B=20+25=45 ✓, B+C=25+15=40 ✓, C+A=15+20=35 ✓ — confirms A=20.',
        ],
      },
    },
    {
      id: 'age-22',
      difficulty: 'Hard',
      patternTags: ['TCS', 'Zoho'],
      question: "In 5 years, A's age will be 3 times what B's age was 5 years ago. If A is currently 2 years older than B, find A's present age.",
      options: ['11', '13', '15', '17'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: 'A is currently 2 years older than B. In 5 years, A = 3×(B\'s age 5 years ago).' },
        { label: 'Concept', text: "Let B's present age=x, A's=x+2. Carefully compute 'A in 5 years' (x+7) and 'B 5 years ago' (x−5) — these are TWO DIFFERENT time points, 10 years apart." },
        { label: 'Working', text: 'x+7 = 3(x−5) → x+7 = 3x−15.' },
        { label: 'Answer', text: '22 = 2x → x = 11. A\'s present age = 11+2 = 13.' },
      ],
      commonTrap:
        "Treating 'in 5 years' and '5 years ago' as if they were the SAME time shift applied to both people — these phrases refer to two different points in time, 10 years apart.",
      alternateMethod: {
        label: 'Verification',
        steps: [
          'B=11 now, A=13 now. In 5 years, A=18. B 5 years ago=6.',
          'Is 18=3×6? Yes ✓ — matches.',
        ],
      },
    },
    {
      id: 'age-23',
      difficulty: 'Medium',
      patternTags: ['Infosys', 'Wipro'],
      question: "The present ages of X and Y are in the ratio 3:5. Four years ago, the sum of their ages was 56. Find X's present age.",
      options: ['20', '22', '24', '28'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: 'X:Y = 3:5 (present). 4 years ago, sum of ages = 56.' },
        { label: 'Concept', text: 'Express ages as 3k, 5k; subtract 4 from each for "4 years ago", sum them, and solve for k.' },
        { label: 'Working', text: '(3k−4)+(5k−4) = 56 → 8k−8 = 56.' },
        { label: 'Answer', text: '8k = 64 → k = 8. X\'s present age = 3×8 = 24.' },
      ],
      commonTrap:
        "Using the CURRENT sum interchangeably with the '4 years ago' sum — 4 years ago, each person's age was smaller by 4, so the past sum is 8 less than the present sum, not equal to it.",
      alternateMethod: {
        label: 'Present-sum method',
        steps: [
          'If 4 years ago the sum was 56, the present sum (4 years later, both aged by 4) is 56+8=64.',
          'Since X:Y=3:5, X=(3/8)×64=24.',
        ],
      },
    },
    {
      id: 'age-24',
      difficulty: 'Easy',
      patternTags: ['TCS', 'Infosys'],
      question: "The present age of A is 25 years. Five years ago, A's age was equal to twice B's age at that time. Find B's present age.",
      options: ['15', '17', '19', '20'],
      correctIndex: 0,
      steps: [
        { label: 'Given', text: 'A\'s present age=25. 5 years ago, A=2×B (at that time).' },
        { label: 'Concept', text: "Find A's age 5 years ago directly, use the given multiple to find B's age 5 years ago, then add 5 back for B's present age." },
        { label: 'Working', text: 'A 5 years ago = 25−5 = 20 = 2×(B 5 years ago) → B 5 years ago = 10.' },
        { label: 'Answer', text: "B's present age = 10+5 = 15." },
      ],
      commonTrap:
        "Applying the 'twice' relationship to the PRESENT ages instead of the 'five years ago' ages (e.g. computing B's present age = 25/2 = 12.5) — the multiple relationship applies specifically to that past time, not now.",
      alternateMethod: {
        label: 'Verification',
        steps: [
          'B\'s present=15, so B 5 years ago=10. A 5 years ago=20=2×10 ✓ — matches.',
        ],
      },
    },
    {
      id: 'age-25',
      difficulty: 'Medium',
      patternTags: ['Wipro', 'Zoho'],
      question: "A is twice as old as B, and B is twice as old as C. If the sum of their ages is 70, find C's age.",
      options: ['8', '10', '12', '14'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: 'A=2×B, B=2×C, sum=70.' },
        { label: 'Concept', text: "Express everything using the smallest unknown (C=x): B=2x, and A=2×B=2×(2x)=4x — chain the two 'twice as old' relationships together." },
        { label: 'Working', text: 'x + 2x + 4x = 7x = 70.' },
        { label: 'Answer', text: 'x = 10. C\'s age = 10.' },
      ],
      commonTrap:
        "Assuming A=2×C directly (combining the two 'twice as old' statements as if they applied to the same pair) instead of correctly chaining them (A=2×B=2×(2×C)=4×C) — the two statements refer to DIFFERENT pairs.",
      alternateMethod: {
        label: 'Ratio method',
        steps: [
          'A:B:C = 4:2:1 (chaining the two doubling relationships). Total parts=7.',
          'C = 1 part = 70/7 = 10.',
        ],
      },
    },
  ],
  'mixtures-alligations': [
    {
      id: 'mix-01',
      difficulty: 'Medium',
      patternTags: ['TCS', 'Infosys'],
      question: "In what ratio must tea worth ₹60 per kg be mixed with tea worth ₹80 per kg so that the mixture is worth ₹65 per kg?",
      options: ['3:1', '1:3', '5:3', '4:1'],
      correctIndex: 0,
      steps: [
        { label: 'Given', text: 'Cheap tea = ₹60/kg, dear tea = ₹80/kg, desired mean = ₹65/kg.' },
        { label: 'Concept', text: 'Alligation rule: ratio of cheaper:dearer = (dearer price − mean price) : (mean price − cheaper price).' },
        { label: 'Working', text: '(80−65) : (65−60) = 15:5.' },
        { label: 'Answer', text: 'Simplified: 15:5 = 3:1.' },
      ],
      commonTrap:
        "Inverting the alligation ratio (computing (mean−cheap):(dear−mean)=5:15=1:3 and assigning it directly) — the LARGER gap (dearer−mean) corresponds to the CHEAPER ingredient's share, not the dearer one's.",
      alternateMethod: {
        label: 'Assumed-value verification',
        steps: [
          'Test cheaper=3, dearer=1: Total cost=3×60+1×80=260, over 4 units.',
          '260/4=65 — matches the target mean exactly.',
        ],
      },
    },
    {
      id: 'mix-02',
      difficulty: 'Easy',
      patternTags: ['Wipro', 'Zoho'],
      question: "Rice worth ₹40 per kg is mixed with rice worth ₹60 per kg in the ratio 3:2. Find the price of the mixture per kg.",
      options: ['48', '50', '52', '54'],
      correctIndex: 0,
      steps: [
        { label: 'Given', text: 'Prices ₹40 and ₹60, mixed in ratio 3:2.' },
        { label: 'Concept', text: 'Mean price = weighted average = (q1×p1+q2×p2)/(q1+q2).' },
        { label: 'Working', text: '(3×40+2×60)/5 = 240/5.' },
        { label: 'Answer', text: 'Mean price = ₹48/kg.' },
      ],
      commonTrap:
        "Taking the simple average of 40 and 60 (₹50) — this ignores the unequal 3:2 weighting between the two quantities.",
      alternateMethod: {
        label: 'Alligation reverse-check',
        steps: [
          '(60−48):(48−40) = 12:8 = 3:2 — matches the given ratio exactly, confirming the mean price.',
        ],
      },
    },
    {
      id: 'mix-03',
      difficulty: 'Easy',
      patternTags: ['TCS', 'Zoho'],
      question: "A mixture of 60 litres contains milk and water in the ratio 2:1. Find the quantity of water in the mixture.",
      options: ['16', '20', '24', '28'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: 'Total=60L, milk:water=2:1.' },
        { label: 'Concept', text: 'Total parts = 2+1=3.' },
        { label: 'Working', text: '1 part = 60/3 = 20.' },
        { label: 'Answer', text: 'Water = 1×20 = 20 litres.' },
      ],
      commonTrap:
        "Swapping the ratio parts (using 2 parts for water instead of 1), giving 40 litres — mixing up which term in the ratio belongs to water.",
      alternateMethod: {
        label: 'Direct fraction method',
        steps: [
          'Water = (1/3) × 60 = 20 litres.',
        ],
      },
    },
    {
      id: 'mix-04',
      difficulty: 'Easy',
      patternTags: ['Infosys', 'Wipro'],
      question: "A container has 40 litres of pure milk. 8 litres of milk is removed and replaced with water (one operation). Find the quantity of milk left after this ONE operation.",
      options: ['26', '28', '30', '32'],
      correctIndex: 3,
      steps: [
        { label: 'Given', text: 'V=40L pure milk. Remove 8L, replace with water (first operation).' },
        { label: 'Concept', text: 'For a SINGLE operation starting from pure milk, the removed portion is entirely milk — no dilution has happened yet.' },
        { label: 'Working', text: 'Milk left = 40 − 8.' },
        { label: 'Answer', text: '= 32 litres.' },
      ],
      commonTrap:
        "Applying the repeated-dilution formula V×(1−x/V)^n unnecessarily for a single operation — for just ONE step from pure milk, it's simple subtraction; the formula matters starting from the SECOND operation, once the mixture is no longer pure.",
      alternateMethod: {
        label: 'Direct percentage method',
        steps: [
          'Removing 8 out of 40 litres removes 20% of the milk.',
          'Remaining = 80% of 40 = 32 litres.',
        ],
      },
    },
    {
      id: 'mix-05',
      difficulty: 'Medium',
      patternTags: ['TCS', 'Infosys'],
      question: "A container has 50 litres of pure milk. 10 litres is removed and replaced with water; this process is repeated once more (2 operations total). Find the quantity of pure milk left.",
      options: ['32', '34', '36', '38'],
      correctIndex: 0,
      steps: [
        { label: 'Given', text: 'V=50L, x=10L removed & replaced with water, repeated n=2 times.' },
        { label: 'Concept', text: 'After each operation the removed portion is a proportional mix (not pure milk after the first time), so use Remaining = V×(1−x/V)^n.' },
        { label: 'Working', text: '50×(1−10/50)² = 50×(0.8)².' },
        { label: 'Answer', text: '= 50×0.64 = 32 litres.' },
      ],
      commonTrap:
        "Treating each removal as taking out a full 10L of PURE milk both times (50−10−10=30) — after the first operation the vessel already contains water, so the second removal takes a proportional blend, not pure milk.",
      alternateMethod: {
        label: 'Step-by-step method',
        steps: [
          'After operation 1: milk = 50×0.8 = 40L.',
          'After operation 2: milk = 40×0.8 = 32L.',
        ],
      },
    },
    {
      id: 'mix-06',
      difficulty: 'Hard',
      patternTags: ['Wipro', 'Zoho'],
      question: "In what ratio should a mixture of milk and water in the ratio 3:1 be mixed with another mixture in the ratio 5:2, so that the resulting mixture has milk and water in the ratio 11:4?",
      options: ['7:8', '8:7', '5:4', '4:5'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: 'Mixture A (3:1, milk fraction 3/4). Mixture B (5:2, milk fraction 5/7). Target: 11:4 (milk fraction 11/15).' },
        { label: 'Concept', text: 'Apply alligation on the MILK FRACTIONS of the two mixtures (not the raw ratios), using the target fraction as the mean.' },
        { label: 'Working', text: 'Ratio(B:A) = (f_A−target):(target−f_B) = (3/4−11/15):(11/15−5/7) = (1/60):(2/105) = 7:8.' },
        { label: 'Answer', text: 'So A:B = 8:7.' },
      ],
      commonTrap:
        "Combining the raw ratios directly (like averaging 3:1 and 5:2 term-by-term) instead of first converting each mixture to its milk FRACTION — alligation needs comparable fractions, not raw ratio pairs.",
      alternateMethod: {
        label: 'Verification',
        steps: [
          'Take 8 parts of A (3:1): milk=6, water=2. Take 7 parts of B (5:2): milk=5, water=2.',
          'Total milk=11, total water=4 → ratio 11:4 ✓ matches exactly.',
        ],
      },
    },
    {
      id: 'mix-07',
      difficulty: 'Medium',
      patternTags: ['TCS', 'Zoho'],
      question: "A mixture contains three liquids A, B and C in the ratio 2:3:5. If 30 litres of liquid B is present, find the total quantity of the mixture.",
      options: ['80', '90', '100', '110'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: 'A:B:C = 2:3:5. B=30L.' },
        { label: 'Concept', text: "Find the value of '1 part' using B's OWN ratio number (3), then find the total using the sum of ALL ratio parts (10)." },
        { label: 'Working', text: '3 parts = 30 → 1 part = 10.' },
        { label: 'Answer', text: 'Total = 10 parts × 10 = 100 litres.' },
      ],
      commonTrap:
        "Using a different ingredient's ratio number (e.g. C's '5' instead of B's '3') to find the value of '1 part' — each ingredient's own specific ratio number must be matched to its own known quantity.",
      alternateMethod: {
        label: 'Direct ratio method',
        steps: [
          'B:Total = 3:10. Since B=30 is 3 parts, Total = 30 × (10/3) = 100.',
        ],
      },
    },
    {
      id: 'mix-08',
      difficulty: 'Hard',
      patternTags: ['Infosys', 'Wipro'],
      question: "A can contains a mixture of two liquids A and B in the ratio 7:5. When 9 litres of the mixture is drawn off and replaced with liquid B, the ratio of A to B becomes 7:9. Find the total quantity of the mixture in the can initially.",
      options: ['30', '32', '34', '36'],
      correctIndex: 3,
      steps: [
        { label: 'Given', text: 'Initial ratio A:B=7:5. 9L of the mixture (in that same ratio) is drawn off and replaced with pure B. New ratio=7:9.' },
        { label: 'Concept', text: 'Let initial total = 12x (matching 7+5=12 parts); the 9L removed contains A and B in the SAME 7:5 proportion as the whole.' },
        { label: 'Working', text: 'A removed=9×7/12=5.25, B removed=9×5/12=3.75. New ratio: (7x−5.25)/(5x−3.75+9)=7/9 → 63x−47.25=35x+36.75 → 28x=84.' },
        { label: 'Answer', text: 'x=3. Total initial = 12×3 = 36 litres.' },
      ],
      commonTrap:
        "Assuming the 9L removed is PURE A (or pure B) instead of a proportional mix of both — any portion drawn from a MIXTURE contains both liquids in the container's current ratio.",
      alternateMethod: {
        label: 'Verification',
        steps: [
          'Total=36L → A=21, B=15. Remove 9L (in 7:5 ratio): A removed=5.25, B removed=3.75.',
          'Remaining: A=15.75, B=11.25. Add 9L B: B=20.25. Ratio=15.75:20.25=7:9 ✓ matches.',
        ],
      },
    },
    {
      id: 'mix-09',
      difficulty: 'Medium',
      patternTags: ['TCS', 'Zoho'],
      question: "A vessel contains 81 litres of pure wine. 27 litres of wine is drawn off and replaced with water. This process is repeated two more times (3 operations total). Find the quantity of pure wine left.",
      options: ['20', '24', '27', '30'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: 'V=81L wine, x=27L drawn & replaced with water, repeated n=3 times total.' },
        { label: 'Concept', text: 'Remaining pure liquid = V×(1−x/V)^n.' },
        { label: 'Working', text: '(1−27/81)=2/3. 81×(2/3)³ = 81×8/27.' },
        { label: 'Answer', text: '= 24 litres.' },
      ],
      commonTrap:
        "Using simple repeated subtraction (81−27−27−27=0) — after the first operation the vessel holds a wine-water mix, so later removals only take out a PROPORTIONAL share of wine, not a full 27L of pure wine each time.",
      alternateMethod: {
        label: 'Step-by-step method',
        steps: [
          'After op 1: wine=81×2/3=54L. After op 2: wine=54×2/3=36L. After op 3: wine=36×2/3=24L.',
        ],
      },
    },
    {
      id: 'mix-10',
      difficulty: 'Medium',
      patternTags: ['Wipro', 'Zoho'],
      question: "A shopkeeper has 20 litres of a sugar solution which is 20% sugar. How many litres of pure sugar must be added to make it a 36% sugar solution?",
      options: ['5', '6', '7', '8'],
      correctIndex: 0,
      steps: [
        { label: 'Given', text: '20L solution at 20% sugar; add pure sugar (100%) to reach 36%.' },
        { label: 'Concept', text: 'Sugar before + sugar added = Sugar required in the final (LARGER) total volume.' },
        { label: 'Working', text: '0.20×20 + x = 0.36×(20+x) → 4+x = 7.2+0.36x.' },
        { label: 'Answer', text: '0.64x = 3.2 → x = 5 litres.' },
      ],
      commonTrap:
        "Computing the target based on the ORIGINAL volume (20L) instead of the NEW, larger total (20+x) — the final percentage applies to the total volume AFTER adding the sugar.",
      alternateMethod: {
        label: 'Alligation method',
        steps: [
          'Pure sugar=100%, original solution=20%, target=36%.',
          'Ratio(original:pure) = (100−36):(36−20) = 64:16 = 4:1.',
          'Since original=20L=4 parts, 1 part (pure sugar needed) = 5L.',
        ],
      },
    },
    {
      id: 'mix-11',
      difficulty: 'Hard',
      patternTags: ['TCS', 'Infosys'],
      question: "A milkman mixes water with milk and sells the mixture at the cost price of pure milk. If the ratio of milk to water in the mixture is 4:1, find his profit percentage.",
      options: ['20%', '25%', '30%', '33.33%'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: 'Milk:water=4:1 in the mixture; sold at the per-unit cost price of pure milk.' },
        { label: 'Concept', text: "The milkman's real COST is only for the milk (4 parts); the water is free but sold as if it were milk too." },
        { label: 'Working', text: 'Cost = 4 units (of milk-price). Revenue = 5 units (all sold at milk-price).' },
        { label: 'Answer', text: 'Profit = 1 unit → Profit% = (1/4)×100 = 25% (on the actual cost of 4 units).' },
      ],
      commonTrap:
        "Computing profit % on the total mixture sold (5 units) instead of the ACTUAL cost (4 units of real milk): (1/5)×100=20% — profit % must always be measured against what was actually SPENT.",
      alternateMethod: {
        label: 'Assumed-price method',
        steps: [
          'Let milk cost ₹1/unit. Cost of mixture (4 units milk + 1 free unit water) = ₹4.',
          'Selling 5 units at ₹1/unit = ₹5. Profit = ₹1 on a cost of ₹4 → 25%.',
        ],
      },
    },
    {
      id: 'mix-12',
      difficulty: 'Medium',
      patternTags: ['Infosys', 'Wipro'],
      question: "A grocer mixes two varieties of pulses costing ₹50/kg and ₹70/kg to get 30 kg of a mixture costing ₹58/kg. Find the quantity of the cheaper pulse used.",
      options: ['12', '15', '18', '20'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: 'Cheaper=₹50/kg, dearer=₹70/kg, mean=₹58/kg, total=30kg.' },
        { label: 'Concept', text: 'Find the alligation ratio first, then apply it to the known TOTAL quantity.' },
        { label: 'Working', text: 'Ratio=(70−58):(58−50)=12:8=3:2. Total parts=5.' },
        { label: 'Answer', text: 'Cheaper quantity = 30×(3/5) = 18kg.' },
      ],
      commonTrap:
        "Assigning the SMALLER gap (8) to the cheaper ingredient instead of the LARGER gap (12) — the alligation cross-rule pairs each ingredient's share with the OPPOSITE gap.",
      alternateMethod: {
        label: 'Verification',
        steps: [
          'Cheaper=18kg, dearer=12kg (total 30kg). Cost=18×50+12×70=900+840=1,740.',
          'Average=1,740/30=58 ✓ matches.',
        ],
      },
    },
    {
      id: 'mix-13',
      difficulty: 'Medium',
      patternTags: ['TCS', 'Zoho'],
      question: "How many litres of a 40% acid solution must be mixed with a 60% acid solution to get 10 litres of a 48% acid solution?",
      options: ['3', '4', '5', '6'],
      correctIndex: 3,
      steps: [
        { label: 'Given', text: '40% solution and 60% solution mixed to give 10L of a 48% solution.' },
        { label: 'Concept', text: 'Alligation ratio(40%-soln : 60%-soln) = (60−48):(48−40).' },
        { label: 'Working', text: '(60−48):(48−40) = 12:8 = 3:2. Total parts=5.' },
        { label: 'Answer', text: '40% solution needed = 10×(3/5) = 6L.' },
      ],
      commonTrap:
        "Swapping the alligation cross-terms (assigning the smaller gap to the 40% solution instead of the larger one) — the ratio pairs each ingredient with the OPPOSITE concentration gap.",
      alternateMethod: {
        label: 'Verification via acid content',
        steps: [
          '6L×0.40 + 4L×0.60 = 2.4+2.4 = 4.8, which is 48% of the 10L total ✓ matches.',
        ],
      },
    },
    {
      id: 'mix-14',
      difficulty: 'Hard',
      patternTags: ['Wipro', 'Zoho'],
      question: "A vessel full of wine has 3/4 of its wine replaced by water, in each of 2 successive operations. Find the fraction of wine left in the vessel.",
      options: ['1/16', '1/8', '3/16', '1/4'],
      correctIndex: 0,
      steps: [
        { label: 'Given', text: 'In each operation, 3/4 of the CURRENT wine is replaced with water (a fixed fraction, not a fixed volume).' },
        { label: 'Concept', text: 'When a constant FRACTION is replaced each time, remaining fraction after n operations = (1 − fraction replaced)^n.' },
        { label: 'Working', text: 'Fraction remaining per operation = 1−3/4 = 1/4. After 2 operations: (1/4)².' },
        { label: 'Answer', text: '= 1/16 of the original wine remains.' },
      ],
      commonTrap:
        "Adding the replaced fractions across operations (3/4+3/4, an impossible 'more than whole' amount) instead of multiplying the REMAINING fraction across operations — repeated proportional dilution always compounds multiplicatively.",
      alternateMethod: {
        label: 'Step-by-step method',
        steps: [
          'Start with 1 (whole). After operation 1: remaining wine = 1×1/4 = 1/4.',
          'After operation 2: remaining wine = 1/4×1/4 = 1/16.',
        ],
      },
    },
    {
      id: 'mix-15',
      difficulty: 'Hard',
      patternTags: ['TCS', 'Infosys'],
      question: "A shopkeeper mixes two varieties of tea costing ₹120/kg and ₹180/kg in some ratio, and sells the mixture at ₹176/kg, making a profit of 10%. Find the ratio in which the two varieties were mixed.",
      options: ['1:2', '2:1', '1:3', '3:1'],
      correctIndex: 0,
      steps: [
        { label: 'Given', text: 'Cheaper=₹120/kg, dearer=₹180/kg, SP=₹176/kg, profit=10%.' },
        { label: 'Concept', text: "First find the mixture's COST price by reversing the profit% from the selling price, then apply alligation using this mean cost." },
        { label: 'Working', text: 'Mean CP = 176/1.10 = 160. Ratio = (180−160):(160−120) = 20:40.' },
        { label: 'Answer', text: 'Simplified: cheaper:dearer = 1:2.' },
      ],
      commonTrap:
        "Applying alligation directly to the SELLING price (176) instead of first converting it back to the mixture's COST price — alligation always compares ingredient cost prices to the mixture's mean COST, not its selling price.",
      alternateMethod: {
        label: 'Verification',
        steps: [
          '1kg cheaper (₹120) + 2kg dearer (₹360) = ₹480 for 3kg → CP/kg=160.',
          'SP at 10% profit = 160×1.1 = 176 ✓ matches.',
        ],
      },
    },
    {
      id: 'mix-16',
      difficulty: 'Medium',
      patternTags: ['Wipro', 'Zoho'],
      question: "A mixture of 40 litres contains milk and water in the ratio 7:3. How much pure milk must be added to make the ratio of milk to water 9:2?",
      options: ['22', '26', '28', '30'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: '40L mixture, milk:water=7:3 → milk=28L, water=12L. Adding PURE MILK only.' },
        { label: 'Concept', text: 'Since only milk is added, water (12L) stays FIXED; use the target ratio to find the required new milk amount.' },
        { label: 'Working', text: 'milk/water = 9/2 → milk = 12×9/2.' },
        { label: 'Answer', text: '= 54L. Milk to add = 54−28 = 26 litres.' },
      ],
      commonTrap:
        "Assuming both milk and water scale up together (as if the whole mixture just got more concentrated proportionally) — but only milk was physically added, so water must remain exactly 12L throughout.",
      alternateMethod: {
        label: 'Verification',
        steps: [
          'New milk=54L, water=12L (unchanged). Ratio=54:12=9:2 ✓.',
          'Milk added = 54−28 = 26L.',
        ],
      },
    },
    {
      id: 'mix-17',
      difficulty: 'Medium',
      patternTags: ['TCS', 'Zoho'],
      question: "Three types of rice costing ₹30/kg, ₹36/kg and ₹42/kg are mixed in the ratio 2:3:1. Find the average price of the mixture per kg.",
      options: ['33', '34', '35', '36'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: 'Prices ₹30, ₹36, ₹42, mixed in ratio 2:3:1.' },
        { label: 'Concept', text: 'Weighted average = (Σ quantity×price)/(Σ quantity).' },
        { label: 'Working', text: '(2×30+3×36+1×42)/6 = (60+108+42)/6.' },
        { label: 'Answer', text: '= 210/6 = ₹35/kg.' },
      ],
      commonTrap:
        "Taking the simple average of the three prices ((30+36+42)/3=36) — this ignores the unequal 2:3:1 weighting between the three quantities.",
      alternateMethod: {
        label: 'Deviation-from-assumed-mean check',
        steps: [
          'Try assumed mean=35: deviations are −5(×2=−10), +1(×3=+3), +7(×1=+7).',
          'Sum = −10+3+7 = 0 — confirms 35 is exact.',
        ],
      },
    },
    {
      id: 'mix-18',
      difficulty: 'Hard',
      patternTags: ['Infosys', 'Wipro'],
      question: "Two types of sugar, one costing ₹42/kg, are mixed in the ratio 3:2 to get a mixture costing ₹48/kg. Find the price of the second type of sugar.",
      options: ['54', '57', '60', '63'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: 'Type1=₹42/kg (3 parts), Type2=₹y/kg (2 parts), mean=₹48/kg.' },
        { label: 'Concept', text: 'Since the mean (48) is ABOVE Type1\'s price (42), Type2 must be the more expensive one; apply the alligation cross-rule.' },
        { label: 'Working', text: '(y−48)/(48−42) = 3/2 → (y−48)/6 = 3/2 → y−48 = 9.' },
        { label: 'Answer', text: 'y = ₹57/kg.' },
      ],
      commonTrap:
        "Assuming the given ratio (3:2) multiplies the price gaps in the same order without checking which ingredient is cheaper vs. dearer relative to the mean — since 42<48, Type1's ratio number pairs with the DEARER ingredient's gap, not its own.",
      alternateMethod: {
        label: 'Verification',
        steps: [
          'Mix 3kg@₹42 + 2kg@₹57 = 126+114 = 240, over 5kg → 240/5 = ₹48/kg ✓ matches.',
        ],
      },
    },
    {
      id: 'mix-19',
      difficulty: 'Medium',
      patternTags: ['TCS', 'Infosys'],
      question: "How much water must be added to 15 litres of a solution containing 40% alcohol, to reduce the alcohol concentration to 25%?",
      options: ['9', '10', '11', '12'],
      correctIndex: 0,
      steps: [
        { label: 'Given', text: '15L solution at 40% alcohol; dilute to 25% by adding water (0% alcohol).' },
        { label: 'Concept', text: 'The actual ALCOHOL AMOUNT stays fixed when only water is added; find the total volume needed for the target %, then subtract the original volume.' },
        { label: 'Working', text: 'Alcohol = 15×0.40 = 6L. Required total volume = 6/0.25.' },
        { label: 'Answer', text: '= 24L. Water to add = 24−15 = 9 litres.' },
      ],
      commonTrap:
        "Working directly from the concentration DIFFERENCE (40%−25%=15%) instead of tracking the fixed ALCOHOL AMOUNT — the key insight is that the alcohol quantity never changes, only the total volume does.",
      alternateMethod: {
        label: 'Alligation method',
        steps: [
          'Water=0%, original=40%, target=25%. Ratio(water:original)=(40−25):(25−0)=15:25=3:5.',
          'Since original=15L=5 parts, 1 part (water)=3L, so water=3 parts×3=9L.',
        ],
      },
    },
    {
      id: 'mix-20',
      difficulty: 'Hard',
      patternTags: ['Wipro', 'Zoho'],
      question: "A container has 20 litres of pure milk. 4 litres is replaced with water. Then, 4 litres of the resulting mixture is sold, and the container is topped up with 4 litres of pure milk. Find the final quantity of pure milk in the container.",
      options: ['15.2', '16', '16.8', '17.6'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: '20L pure milk → remove 4L, add 4L water → remove 4L of the resulting mixture (sold) → add 4L pure milk.' },
        { label: 'Concept', text: 'Track milk through EACH step: dilution removes pure milk; selling removes a proportional share of the now-diluted mixture; topping up adds pure milk back.' },
        { label: 'Working', text: 'After dilution: milk=16L (in 20L, 80% milk). Selling 4L removes 4×0.8=3.2L milk → milk=12.8L (in 16L).' },
        { label: 'Answer', text: 'Adding 4L pure milk: milk = 12.8+4 = 16.8L.' },
      ],
      commonTrap:
        "Assuming the 4L 'sold' step removes PURE milk (like the first dilution step) instead of a proportional mix — by this point, the container is no longer pure milk, so any portion removed reflects the CURRENT diluted composition.",
      alternateMethod: {
        label: 'Fraction-tracking method',
        steps: [
          'After step 1, milk fraction=16/20=0.8. Selling 4L removes 0.8×4=3.2L milk, leaving 12.8L in 16L.',
          'Adding 4L pure milk: total milk=16.8L, total volume=20L (back to full).',
        ],
      },
    },
    {
      id: 'mix-21',
      difficulty: 'Medium',
      patternTags: ['TCS', 'Zoho'],
      question: "Two vessels contain mixtures of milk and water in the ratios 3:2 and 5:3 respectively. If 25 litres from the first vessel and 24 litres from the second are mixed together, find the ratio of milk to water in the resultant mixture.",
      options: ['30:19', '29:19', '31:20', '3:2'],
      correctIndex: 0,
      steps: [
        { label: 'Given', text: 'Vessel1=25L (3:2). Vessel2=24L (5:3).' },
        { label: 'Concept', text: 'Compute the actual milk and water quantities from each vessel separately using their own ratios and volumes, then combine the totals.' },
        { label: 'Working', text: 'Vessel1: milk=25×3/5=15, water=10. Vessel2: milk=24×5/8=15, water=9.' },
        { label: 'Answer', text: 'Total milk=30, total water=19 → ratio = 30:19.' },
      ],
      commonTrap:
        "Combining the two RATIOS directly (like averaging 3:2 and 5:3 term-by-term) without first converting each to actual quantities — the two mixtures have DIFFERENT volumes, so their ratios can't be combined as abstract fractions.",
      alternateMethod: {
        label: 'Fraction method',
        steps: [
          'Vessel1 milk fraction=3/5=0.6 → milk=25×0.6=15. Vessel2 milk fraction=5/8=0.625 → milk=24×0.625=15.',
          'Total milk=30, total volume=49, water=19. Ratio=30:19.',
        ],
      },
    },
    {
      id: 'mix-22',
      difficulty: 'Hard',
      patternTags: ['Infosys', 'Wipro'],
      question: "A vessel contains 100 litres of pure milk. The same quantity is drawn off and replaced with water, twice in a row. If the final mixture contains 64 litres of pure milk, find the quantity drawn off in each operation.",
      options: ['14', '16', '18', '20'],
      correctIndex: 3,
      steps: [
        { label: 'Given', text: 'V=100L, same quantity x drawn off & replaced with water TWICE, final pure milk=64L.' },
        { label: 'Concept', text: 'Use the repeated-dilution formula Remaining=V×(1−x/V)^n and solve backward for x.' },
        { label: 'Working', text: '100×(1−x/100)² = 64 → (1−x/100)² = 0.64 → (1−x/100) = √0.64 = 0.8.' },
        { label: 'Answer', text: 'x/100 = 0.2 → x = 20 litres.' },
      ],
      commonTrap:
        "Dividing 0.64 by 2 instead of taking a square root (mistaking the exponent for multiplication) — the fraction remaining COMPOUNDS by squaring across 2 operations, so recovering it requires a square root.",
      alternateMethod: {
        label: 'Step-by-step verification',
        steps: [
          'With x=20: After operation 1: milk=100×0.8=80L. After operation 2: milk=80×0.8=64L ✓ matches.',
        ],
      },
    },
    {
      id: 'mix-23',
      difficulty: 'Hard',
      patternTags: ['TCS', 'Zoho'],
      question: "A merchant mixes 30 kg of rice at ₹20/kg with some kg of rice at ₹36/kg, and sells the mixture at ₹30/kg, making a profit of 25%. Find the quantity of the second variety used.",
      options: ['10', '11', '13', '15'],
      correctIndex: 0,
      steps: [
        { label: 'Given', text: 'Cheaper=₹20/kg (30kg), dearer=₹36/kg (unknown qty), SP=₹30/kg, profit=25%.' },
        { label: 'Concept', text: 'First find the mean COST price (reversing the profit% from SP), then use alligation, then apply the known cheaper quantity.' },
        { label: 'Working', text: 'Mean CP = 30/1.25 = 24. Ratio(cheaper:dearer) = (36−24):(24−20) = 12:4 = 3:1.' },
        { label: 'Answer', text: 'Since cheaper (3 parts)=30kg → 1 part=10kg. Dearer quantity = 10kg.' },
      ],
      commonTrap:
        "Applying alligation to the SELLING price (30) directly instead of first converting to the mixture's COST price (24) — the profit% must be reversed out first.",
      alternateMethod: {
        label: 'Verification',
        steps: [
          '30kg@₹20 + 10kg@₹36 = 600+360 = 960, over 40kg → CP/kg=24.',
          'SP at 25% profit = 24×1.25 = 30 ✓ matches.',
        ],
      },
    },
    {
      id: 'mix-24',
      difficulty: 'Medium',
      patternTags: ['Wipro', 'Zoho'],
      question: "A jeweller has gold that is 90% pure and gold that is 70% pure. In what ratio should the 90% pure gold be mixed with the 70% pure gold to obtain gold that is 84% pure?",
      options: ['3:7', '7:3', '2:5', '5:2'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: 'Two gold purities: 90% and 70%; target=84%.' },
        { label: 'Concept', text: 'Alligation rule: ratio(lower purity : higher purity) = (higher% − mean%) : (mean% − lower%).' },
        { label: 'Working', text: '(90−84):(84−70) = 6:14 = 3:7 — this is the ratio of 70%-gold : 90%-gold.' },
        { label: 'Answer', text: 'So 90%-gold : 70%-gold = 7:3.' },
      ],
      commonTrap:
        "Reporting the ratio in the wrong order (stating 90%:70%=3:7 instead of 7:3) — always double-check which quantity the alligation cross-terms actually refer to before stating the final ratio.",
      alternateMethod: {
        label: 'Verification',
        steps: [
          'Mix 7 parts of 90% gold + 3 parts of 70% gold (10 parts total).',
          'Pure gold content = 7×0.9+3×0.7 = 6.3+2.1 = 8.4, over 10 parts = 84% ✓ matches.',
        ],
      },
    },
    {
      id: 'mix-25',
      difficulty: 'Hard',
      patternTags: ['TCS', 'Infosys'],
      question: "A container has 60 litres of a mixture of milk and water in the ratio 2:1. 15 litres of this mixture is removed and replaced with 15 litres of pure water. Find the new ratio of milk to water.",
      options: ['1:1', '3:2', '4:3', '2:1'],
      correctIndex: 0,
      steps: [
        { label: 'Given', text: '60L mixture, milk:water=2:1 → milk=40L, water=20L. Remove 15L of the mixture, replace with 15L pure water.' },
        { label: 'Concept', text: 'The removed 15L contains milk and water in the SAME 2:1 ratio as the whole container; subtract these proportional amounts, then add the pure water.' },
        { label: 'Working', text: 'Milk removed = 15×2/3 = 10L, water removed = 15×1/3 = 5L. Remaining: milk=30L, water=15L. Adding 15L water: water=30L.' },
        { label: 'Answer', text: 'New ratio = 30:30 = 1:1.' },
      ],
      commonTrap:
        "Assuming the 15L removed is entirely water (or entirely milk) instead of a proportional mix of both — any portion drawn from a well-mixed container reflects the container's overall current ratio.",
      alternateMethod: {
        label: 'Fraction method',
        steps: [
          'Milk fraction=2/3. Removing 15L removes 15×2/3=10L milk, leaving 30L milk in 45L remaining mixture.',
          'Adding 15L water brings total to 60L, with water = 45−30+15 = 30L. Ratio = 30:30 = 1:1.',
        ],
      },
    },
  ],
  numbers: [
    {
      id: 'num-01',
      difficulty: 'Easy',
      patternTags: ['TCS', 'Infosys'],
      question: "Find the HCF of 36 and 84.",
      options: ['8', '12', '16', '18'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: '36 and 84.' },
        { label: 'Concept', text: 'HCF = product of the LOWEST powers of all common prime factors.' },
        { label: 'Working', text: '36 = 2²×3². 84 = 2²×3×7.' },
        { label: 'Answer', text: 'Common: 2¹×3¹ = 12.' },
      ],
      commonTrap:
        "Using the HIGHEST power seen in either number instead of the LOWEST (e.g. using 3² since it appears in 36, even though 84 only has 3¹) — HCF always takes the minimum shared power.",
      alternateMethod: {
        label: 'Euclidean algorithm',
        steps: [
          '84 = 2×36 + 12. 36 = 3×12 + 0.',
          'HCF = 12 (the last non-zero remainder).',
        ],
      },
    },
    {
      id: 'num-02',
      difficulty: 'Easy',
      patternTags: ['TCS', 'Wipro'],
      question: "Find the LCM of 12, 15 and 20.",
      options: ['45', '50', '60', '72'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: '12, 15, 20.' },
        { label: 'Concept', text: 'LCM = product of the HIGHEST powers of all prime factors appearing in any of the numbers.' },
        { label: 'Working', text: '12=2²×3, 15=3×5, 20=2²×5. Take 2², 3¹, 5¹.' },
        { label: 'Answer', text: '4×3×5 = 60.' },
      ],
      commonTrap:
        "Multiplying all three numbers directly (12×15×20=3,600) without removing shared factors, or using the LOWEST powers instead of highest (confusing HCF and LCM's opposite rules).",
      alternateMethod: {
        label: 'Verification',
        steps: [
          '60/12=5, 60/15=4, 60/20=3 — all divide evenly, and 60 is the smallest such number.',
        ],
      },
    },
    {
      id: 'num-03',
      difficulty: 'Easy',
      patternTags: ['Infosys', 'Zoho'],
      question: "The product of two numbers is 1,536. If their HCF is 8, find their LCM.",
      options: ['192', '204', '216', '224'],
      correctIndex: 0,
      steps: [
        { label: 'Given', text: 'Product=1,536, HCF=8.' },
        { label: 'Concept', text: 'For any two numbers, HCF × LCM = Product of the two numbers.' },
        { label: 'Working', text: 'LCM = 1,536/8.' },
        { label: 'Answer', text: '= 192.' },
      ],
      commonTrap:
        "Multiplying instead of dividing (1,536×8=12,288), or confusing which value divides which — the identity is a straightforward product-equals-product relationship.",
      alternateMethod: {
        label: 'Verification via co-prime factors',
        steps: [
          'Write the numbers as 8a, 8b (a,b co-prime). Product=64ab=1,536 → ab=24.',
          'LCM = 8ab = 8×24 = 192.',
        ],
      },
    },
    {
      id: 'num-04',
      difficulty: 'Medium',
      patternTags: ['TCS', 'Zoho'],
      question: "Two numbers have HCF 12 and LCM 144. If the sum of the numbers is 84, find the larger number.",
      options: ['42', '48', '54', '60'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: 'HCF=12, LCM=144, sum=84.' },
        { label: 'Concept', text: 'Write the numbers as 12a, 12b where a,b are co-prime; use LCM=12ab and the sum condition to find a,b.' },
        { label: 'Working', text: 'ab = 144/12 = 12. a+b = 84/12 = 7. Solving t²−7t+12=0 → (t−3)(t−4)=0.' },
        { label: 'Answer', text: 'a=3, b=4 (co-prime ✓). Numbers=36, 48 → larger = 48.' },
      ],
      commonTrap:
        "Assuming the two numbers are simply the HCF and LCM themselves, or trying to split the sum using the HCF-LCM ratio directly — the numbers must be built as co-prime multiples of the HCF.",
      alternateMethod: {
        label: 'Verification',
        steps: [
          'HCF(36,48)=12 ✓ (36=2²×3², 48=2⁴×3, common=2²×3=12).',
          'LCM(36,48)=2⁴×3²=144 ✓. Sum=36+48=84 ✓ — all match.',
        ],
      },
    },
    {
      id: 'num-05',
      difficulty: 'Medium',
      patternTags: ['Wipro', 'Zoho'],
      question: "Find the greatest number that divides 285 and 1,249, leaving remainders 9 and 7 respectively.",
      options: ['92', '115', '138', '161'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: '285 leaves remainder 9, 1,249 leaves remainder 7, when divided by the same unknown number.' },
        { label: 'Concept', text: 'Subtract each remainder from its number first (the divisor evenly divides what remains); the answer is the HCF of the two REDUCED numbers.' },
        { label: 'Working', text: '285−9=276. 1,249−7=1,242.' },
        { label: 'Answer', text: 'HCF(276, 1,242) = 138.' },
      ],
      commonTrap:
        "Finding the HCF of the ORIGINAL numbers (285, 1,249) directly without subtracting the remainders first — the divisor doesn't evenly divide 285 or 1,249 themselves, only the reduced values.",
      alternateMethod: {
        label: 'Verification',
        steps: [
          '285÷138=2 remainder 9 ✓. 1,249÷138=9 remainder 7 ✓ — both match.',
        ],
      },
    },
    {
      id: 'num-06',
      difficulty: 'Medium',
      patternTags: ['TCS', 'Infosys'],
      question: "Find the greatest number that divides 43, 69 and 108, leaving the same remainder in each case.",
      options: ['1', '7', '13', '17'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: 'Three numbers: 43, 69, 108. Want the greatest divisor giving the SAME remainder for each.' },
        { label: 'Concept', text: 'The divisor must exactly divide every PAIRWISE DIFFERENCE between the numbers (equal remainders cancel out when subtracting two such numbers).' },
        { label: 'Working', text: 'Differences: 69−43=26, 108−69=39, 108−43=65.' },
        { label: 'Answer', text: 'HCF(26, 39, 65) = 13.' },
      ],
      commonTrap:
        "Finding the HCF of the ORIGINAL numbers directly (HCF(43,69,108)=1, since 43 is prime and shares no factors with the others) — this is NOT the same as the HCF of the pairwise differences.",
      alternateMethod: {
        label: 'Verification',
        steps: [
          '43÷13=3 remainder 4. 69÷13=5 remainder 4. 108÷13=8 remainder 4 — all leave the same remainder, confirming 13.',
        ],
      },
    },
    {
      id: 'num-07',
      difficulty: 'Medium',
      patternTags: ['Infosys', 'Wipro'],
      question: "Find the smallest number which, when divided by 6, 9 and 15, leaves a remainder of 4 in each case.",
      options: ['90', '94', '98', '104'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: 'Divided by 6, 9, 15, remainder = 4 each time.' },
        { label: 'Concept', text: 'Find LCM(6,9,15) first (the smallest number divisible by all three with NO remainder), then add the common remainder.' },
        { label: 'Working', text: 'LCM(6,9,15) = 90.' },
        { label: 'Answer', text: '90+4 = 94.' },
      ],
      commonTrap:
        "Forgetting to add the remainder back at the end and just answering the LCM itself (90) — the LCM only covers the 'zero remainder' case.",
      alternateMethod: {
        label: 'Verification',
        steps: [
          '94÷6=15 r4 ✓. 94÷9=10 r4 ✓. 94÷15=6 r4 ✓ — all confirm.',
        ],
      },
    },
    {
      id: 'num-08',
      difficulty: 'Medium',
      patternTags: ['TCS', 'Zoho'],
      question: "Find the smallest number which, when increased by 5, is divisible by 18, 24 and 30.",
      options: ['345', '350', '355', '360'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: 'N+5 must be divisible by 18, 24, 30.' },
        { label: 'Concept', text: 'Find LCM(18,24,30) — the smallest number exactly divisible by all three — then subtract the given adjustment (5) to find N.' },
        { label: 'Working', text: 'LCM(18,24,30) = 360.' },
        { label: 'Answer', text: 'N = 360−5 = 355.' },
      ],
      commonTrap:
        "Adding 5 to the LCM instead of subtracting (365) — since N+5 must equal a multiple of the LCM, N itself must be LESS than that multiple by 5.",
      alternateMethod: {
        label: 'Verification',
        steps: [
          '355+5=360, which is exactly divisible by 18 (20), 24 (15), and 30 (12) — confirms.',
        ],
      },
    },
    {
      id: 'num-09',
      difficulty: 'Medium',
      patternTags: ['Wipro', 'Zoho'],
      question: "Find the LCM of 2/3, 4/9 and 6/5.",
      options: ['4', '6', '8', '12'],
      correctIndex: 3,
      steps: [
        { label: 'Given', text: 'Fractions 2/3, 4/9, 6/5.' },
        { label: 'Concept', text: 'LCM of fractions = LCM(numerators) / HCF(denominators).' },
        { label: 'Working', text: 'LCM(2,4,6)=12. HCF(3,9,5)=1.' },
        { label: 'Answer', text: '12/1 = 12.' },
      ],
      commonTrap:
        "Using HCF of numerators and LCM of denominators instead — the EXACT REVERSE of the correct rule; LCM and HCF of fractions use opposite combination rules for numerators vs. denominators.",
      alternateMethod: {
        label: 'Verification',
        steps: [
          '12÷(2/3)=18 (integer ✓), 12÷(4/9)=27 (integer ✓), 12÷(6/5)=10 (integer ✓) — dividing 12 by each fraction gives a whole number, confirming it as the smallest common multiple.',
        ],
      },
    },
    {
      id: 'num-10',
      difficulty: 'Medium',
      patternTags: ['TCS', 'Infosys'],
      question: "Find the HCF of 4/9, 10/21 and 20/63.",
      options: ['1/63', '2/63', '2/21', '4/63'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: 'Fractions 4/9, 10/21, 20/63.' },
        { label: 'Concept', text: 'HCF of fractions = HCF(numerators) / LCM(denominators).' },
        { label: 'Working', text: 'HCF(4,10,20)=2. LCM(9,21,63)=63.' },
        { label: 'Answer', text: '2/63.' },
      ],
      commonTrap:
        "Using LCM of numerators and HCF of denominators instead — the exact reverse of the correct rule for HCF of fractions.",
      alternateMethod: {
        label: 'Verification',
        steps: [
          '(4/9)÷(2/63)=14 (integer ✓). (10/21)÷(2/63)=15 (integer ✓). (20/63)÷(2/63)=10 (integer ✓).',
          '2/63 divides each fraction evenly, and it\'s the largest such common divisor.',
        ],
      },
    },
    {
      id: 'num-11',
      difficulty: 'Medium',
      patternTags: ['Wipro', 'Zoho'],
      question: "Find the number of factors of 360.",
      options: ['20', '24', '28', '30'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: 'N=360.' },
        { label: 'Concept', text: 'If N = p1^a × p2^b × p3^c (prime factorization), the number of factors = (a+1)(b+1)(c+1).' },
        { label: 'Working', text: '360 = 2³×3²×5¹. Number of factors = (3+1)(2+1)(1+1).' },
        { label: 'Answer', text: '4×3×2 = 24.' },
      ],
      commonTrap:
        "Forgetting to add 1 to each exponent before multiplying (computing 3×2×1=6 instead) — each exponent contributes (exponent+1) choices, since 'not using that prime at all' also counts as an option.",
      alternateMethod: {
        label: 'Verification with a smaller example',
        steps: [
          'For 12=2²×3¹: factors=(2+1)(1+1)=6, and indeed 12\'s factors are 1,2,3,4,6,12 — exactly 6, confirming the formula before applying it to 360.',
        ],
      },
    },
    {
      id: 'num-12',
      difficulty: 'Hard',
      patternTags: ['TCS', 'Zoho'],
      question: "Find the sum of all factors of 60.",
      options: ['144', '156', '168', '180'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: '60 = 2²×3×5.' },
        { label: 'Concept', text: 'Sum of factors of N = [(p1^(a+1)−1)/(p1−1)] × [(p2^(b+1)−1)/(p2−1)] × ... — a geometric-series product for each prime.' },
        { label: 'Working', text: 'For 2²: 1+2+4=7. For 3¹: 1+3=4. For 5¹: 1+5=6.' },
        { label: 'Answer', text: '7×4×6 = 168.' },
      ],
      commonTrap:
        "Simply adding up the prime factors themselves (2+3+5=10) instead of using the full geometric-series-product formula, which accounts for EVERY actual factor of 60, not just the primes.",
      alternateMethod: {
        label: 'Direct listing check',
        steps: [
          'Factors of 60: 1,2,3,4,5,6,10,12,15,20,30,60.',
          'Sum = 168 — matches the formula.',
        ],
      },
    },
    {
      id: 'num-13',
      difficulty: 'Medium',
      patternTags: ['Infosys', 'Wipro'],
      question: "Find the value of the digit 'x' such that the number 42x35 is divisible by 9.",
      options: ['4', '5', '6', '7'],
      correctIndex: 0,
      steps: [
        { label: 'Given', text: 'Number 42x35, must be divisible by 9.' },
        { label: 'Concept', text: 'A number is divisible by 9 if and only if the SUM OF ITS DIGITS is divisible by 9.' },
        { label: 'Working', text: 'Sum = 4+2+x+3+5 = 14+x. Need 14+x to be a multiple of 9.' },
        { label: 'Answer', text: '14+4=18 (divisible by 9) → x=4.' },
      ],
      commonTrap:
        "Using the divisibility-by-11 rule (alternating sum) instead of the divisibility-by-9 rule (straight digit sum) — these are two different tests and must not be mixed up.",
      alternateMethod: {
        label: 'Verification',
        steps: [
          'With x=4, the number is 42435. Digit sum=4+2+4+3+5=18, and 18/9=2 exactly — confirms divisibility.',
        ],
      },
    },
    {
      id: 'num-14',
      difficulty: 'Medium',
      patternTags: ['TCS', 'Infosys'],
      question: "Find the value of the digit 'y' such that the number 3y6547 is divisible by 11.",
      options: ['1', '2', '3', '4'],
      correctIndex: 0,
      steps: [
        { label: 'Given', text: 'Number 3y6547, must be divisible by 11.' },
        { label: 'Concept', text: 'A number is divisible by 11 if the difference between the sums of digits at odd and even positions (counted from either end, consistently) is a multiple of 11 (including 0).' },
        { label: 'Working', text: 'Counting from the right: odd positions (7,5,y) sum to 12+y; even positions (4,6,3) sum to 13. Difference = (12+y)−13 = y−1.' },
        { label: 'Answer', text: 'Set y−1=0 (the only valid multiple of 11 reachable with a single digit) → y=1.' },
      ],
      commonTrap:
        "Using the divisibility-by-9 rule (straight digit sum) instead of the alternating-sum rule required specifically for 11.",
      alternateMethod: {
        label: 'Verification',
        steps: [
          'With y=1, number=316547. From the right: 7−4+5−6+1−3=0 ✓ — confirms divisibility by 11.',
        ],
      },
    },
    {
      id: 'num-15',
      difficulty: 'Medium',
      patternTags: ['Wipro', 'Zoho'],
      question: "How many numbers between 1 and 100 (inclusive) are divisible by 3 or 5?",
      options: ['45', '47', '49', '51'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: 'Numbers 1–100, divisible by 3 OR 5.' },
        { label: 'Concept', text: 'Inclusion-exclusion: Count(A or B) = Count(A) + Count(B) − Count(A and B), where "A and B" means divisible by their LCM.' },
        { label: 'Working', text: 'Divisible by 3: 33. Divisible by 5: 20. Divisible by 15 (LCM): 6.' },
        { label: 'Answer', text: '33+20−6 = 47.' },
      ],
      commonTrap:
        "Simply adding the two counts (33+20=53) without subtracting the overlap — numbers divisible by BOTH 3 and 5 get counted twice unless corrected for.",
      alternateMethod: {
        label: 'Direct exclusive-count method',
        steps: [
          'Divisible by 3 only + by 5 only + by both = (33−6)+(20−6)+6 = 27+14+6 = 47.',
        ],
      },
    },
    {
      id: 'num-16',
      difficulty: 'Easy',
      patternTags: ['TCS', 'Zoho'],
      question: "Four bells ring at intervals of 6, 8, 12 and 18 seconds respectively. If they all ring together at 12:00:00, after how many seconds will they next ring together?",
      options: ['64', '68', '72', '78'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: 'Bells ring every 6, 8, 12, 18 seconds.' },
        { label: 'Concept', text: 'All bells ring together again after a time equal to the LCM of their individual intervals.' },
        { label: 'Working', text: '6=2×3, 8=2³, 12=2²×3, 18=2×3². LCM=2³×3².' },
        { label: 'Answer', text: '= 72 seconds.' },
      ],
      commonTrap:
        "Finding the HCF instead of the LCM — a common mix-up in 'ringing together' problems; aligning multiple cycles simultaneously always needs the LCM.",
      alternateMethod: {
        label: 'Verification',
        steps: [
          '72/6=12, 72/8=9, 72/12=6, 72/18=4 — all divide evenly, and 72 is the smallest such time.',
        ],
      },
    },
    {
      id: 'num-17',
      difficulty: 'Medium',
      patternTags: ['Infosys', 'Wipro'],
      question: "Three traffic lights at a junction change every 45, 60 and 75 seconds respectively. If they all change simultaneously at 8:00:00 AM, at what time will they next change simultaneously?",
      options: ['8:10:00 AM', '8:12:00 AM', '8:15:00 AM', '8:20:00 AM'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: 'Intervals 45, 60, 75 seconds; simultaneous at 8:00:00 AM.' },
        { label: 'Concept', text: 'Find the LCM of the three intervals to determine when they next align.' },
        { label: 'Working', text: '45=3²×5, 60=2²×3×5, 75=3×5². LCM=2²×3²×5²=900 seconds.' },
        { label: 'Answer', text: '900 seconds = 15 minutes → 8:00:00 + 15 min = 8:15:00 AM.' },
      ],
      commonTrap:
        "Forgetting to convert the LCM (in seconds) into minutes before adding it to the clock time — 900 seconds is easy to mistake for 900 minutes or leave unconverted.",
      alternateMethod: {
        label: 'Verification',
        steps: [
          '900/45=20, 900/60=15, 900/75=12 — all divide evenly, confirming 900 seconds as the alignment interval.',
        ],
      },
    },
    {
      id: 'num-18',
      difficulty: 'Hard',
      patternTags: ['TCS', 'Zoho'],
      question: "Three athletes A, B and C run around a circular track. A completes one round in 252 seconds, B in 308 seconds, and C in 198 seconds. If they all start together from the same point, after how many seconds will they next meet at the starting point together?",
      options: ['2,640', '2,772', '2,860', '2,940'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: 'Lap times 252, 308, 198 seconds.' },
        { label: 'Concept', text: 'They meet again at the start after a time that is a common multiple of ALL their lap times — the LCM.' },
        { label: 'Working', text: '252=2²×3²×7, 308=2²×7×11, 198=2×3²×11. LCM=2²×3²×7×11.' },
        { label: 'Answer', text: '= 2,772 seconds.' },
      ],
      commonTrap:
        "Finding the LCM of only two of the three lap times and forgetting the third runner entirely — all three periods must be included in a single combined LCM.",
      alternateMethod: {
        label: 'Verification',
        steps: [
          '2,772/252=11, 2,772/308=9, 2,772/198=14 — all divide evenly.',
        ],
      },
    },
    {
      id: 'num-19',
      difficulty: 'Easy',
      patternTags: ['Wipro', 'Zoho'],
      question: "The HCF and LCM of two numbers are 6 and 210 respectively. If one of the numbers is 30, find the other number.",
      options: ['42', '45', '48', '52'],
      correctIndex: 0,
      steps: [
        { label: 'Given', text: 'HCF=6, LCM=210, one number=30.' },
        { label: 'Concept', text: 'HCF × LCM = Product of the two numbers.' },
        { label: 'Working', text: 'Product = 6×210 = 1,260.' },
        { label: 'Answer', text: 'Other number = 1,260/30 = 42.' },
      ],
      commonTrap:
        "Trying to find the other number from a direct ratio of HCF and LCM (e.g. 210/6=35) instead of using the actual product relationship together with the KNOWN number.",
      alternateMethod: {
        label: 'Verification',
        steps: [
          'HCF(30,42): 30=2×3×5, 42=2×3×7 → common=2×3=6 ✓.',
          'LCM(30,42)=2×3×5×7=210 ✓ — both match.',
        ],
      },
    },
    {
      id: 'num-20',
      difficulty: 'Medium',
      patternTags: ['TCS', 'Infosys'],
      question: "Find the largest 4-digit number that is exactly divisible by 27.",
      options: ['9,963', '9,972', '9,981', '9,990'],
      correctIndex: 3,
      steps: [
        { label: 'Given', text: 'Largest 4-digit number = 9,999. Must be divisible by 27.' },
        { label: 'Concept', text: 'Divide 9,999 by 27, find the remainder, and subtract that remainder to get the largest multiple ≤9,999.' },
        { label: 'Working', text: '9,999÷27 = 370 remainder 9 (since 27×370=9,990).' },
        { label: 'Answer', text: 'Largest 4-digit multiple = 9,999−9 = 9,990.' },
      ],
      commonTrap:
        "Rounding UP to the next multiple instead of down (9,990+27=10,017, a 5-digit number) — since we want the largest number that still fits within 4 digits, we must round down, not up.",
      alternateMethod: {
        label: 'Verification',
        steps: [
          '9,990/27=370 exactly, and 9,990+27=10,017 exceeds 4 digits — confirming 9,990 is the largest valid 4-digit multiple.',
        ],
      },
    },
    {
      id: 'num-21',
      difficulty: 'Medium',
      patternTags: ['Infosys', 'Wipro'],
      question: "Find the smallest 5-digit number that is exactly divisible by 43.",
      options: ['10,000', '10,019', '10,036', '10,043'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: 'Smallest 5-digit number = 10,000. Must be divisible by 43.' },
        { label: 'Concept', text: 'Divide 10,000 by 43, find the remainder, then ADD (43 − remainder) to round UP to the next multiple.' },
        { label: 'Working', text: '10,000÷43 = 232 remainder 24 (since 43×232=9,976). Shortfall to next multiple = 43−24 = 19.' },
        { label: 'Answer', text: '9,976+43 = 10,019.' },
      ],
      commonTrap:
        "Subtracting the remainder instead of adding the shortfall (10,000−24=9,976, only a 4-digit number) — for the SMALLEST valid multiple meeting the digit-count requirement, we must round UP.",
      alternateMethod: {
        label: 'Verification',
        steps: [
          '10,019/43=233 exactly, and 10,019−43=9,976 is only 4 digits — confirming 10,019 is the smallest valid 5-digit multiple.',
        ],
      },
    },
    {
      id: 'num-22',
      difficulty: 'Hard',
      patternTags: ['TCS', 'Zoho'],
      question: "Two numbers are co-prime and their sum is 25. If the sum of their reciprocals is 25/144, find the two numbers.",
      options: ['9 and 16', '8 and 17', '7 and 18', '12 and 13'],
      correctIndex: 0,
      steps: [
        { label: 'Given', text: 'Two co-prime numbers, sum=25, sum of reciprocals=25/144.' },
        { label: 'Concept', text: 'Sum of reciprocals = (a+b)/(ab); use the given sum to solve directly for the product ab, then find a,b via a quadratic.' },
        { label: 'Working', text: '25/(ab) = 25/144 → ab = 144. With a+b=25: t²−25t+144=0 → (t−9)(t−16)=0.' },
        { label: 'Answer', text: 'The numbers are 9 and 16.' },
      ],
      commonTrap:
        "Getting stuck trying to find a and b individually first, instead of recognizing that (a+b)/(ab) can be rearranged to find ab directly from the GIVEN sum and reciprocal-sum values.",
      alternateMethod: {
        label: 'Verification',
        steps: [
          '9+16=25 ✓. 1/9+1/16=16/144+9/144=25/144 ✓ — matches both conditions, and HCF(9,16)=1 confirms they are co-prime.',
        ],
      },
    },
    {
      id: 'num-23',
      difficulty: 'Hard',
      patternTags: ['Wipro', 'Zoho'],
      question: "Find the greatest number that divides 73, 103 and 149, leaving remainders 3, 5 and 9 respectively.",
      options: ['7', '14', '21', '28'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: '73 leaves remainder 3, 103 leaves remainder 5, 149 leaves remainder 9 — all when divided by the same unknown number.' },
        { label: 'Concept', text: 'Subtract each remainder from its corresponding number, then find the HCF of the three REDUCED values.' },
        { label: 'Working', text: '73−3=70, 103−5=98, 149−9=140.' },
        { label: 'Answer', text: 'HCF(70, 98, 140) = 14.' },
      ],
      commonTrap:
        "Mismatching which remainder belongs to which number when subtracting — each number must be paired with its OWN stated remainder.",
      alternateMethod: {
        label: 'Verification',
        steps: [
          '73÷14=5 r3 ✓. 103÷14=7 r5 ✓. 149÷14=10 r9 ✓ — all match.',
        ],
      },
    },
    {
      id: 'num-24',
      difficulty: 'Hard',
      patternTags: ['TCS', 'Infosys'],
      question: "Find the remainder when 2¹⁰⁰ is divided by 7.",
      options: ['2', '3', '4', '6'],
      correctIndex: 0,
      steps: [
        { label: 'Given', text: 'Find 2¹⁰⁰ mod 7.' },
        { label: 'Concept', text: 'Powers of 2 modulo 7 repeat in a CYCLE (2, 4, 1, 2, 4, 1, ...) with period 3; find where the exponent 100 falls within this cycle.' },
        { label: 'Working', text: '100÷3 = 33 remainder 1 → 2¹⁰⁰ has the same remainder as 2¹ (mod 7).' },
        { label: 'Answer', text: '2¹ mod 7 = 2.' },
      ],
      commonTrap:
        "Assuming the wrong cycle length (e.g. guessing period 4 without actually computing it) — always verify the ACTUAL repeating pattern by computing the first several powers directly.",
      alternateMethod: {
        label: 'Direct cycle verification',
        steps: [
          '2¹=2, 2²=4, 2³=8≡1(mod7), 2⁴=16≡2(mod7) — confirms the cycle truly repeats every 3 powers.',
          'Since 100≡1(mod3), the answer matches 2¹≡2.',
        ],
      },
    },
    {
      id: 'num-25',
      difficulty: 'Hard',
      patternTags: ['Wipro', 'Zoho'],
      question: "Find the number of trailing zeros in 100!.",
      options: ['20', '22', '24', '26'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: '100! (100 factorial).' },
        { label: 'Concept', text: 'Trailing zeros come from factors of 10=2×5; since factors of 2 are always more abundant than 5 in a factorial, count how many times 5 appears as a factor from 1 to 100.' },
        { label: 'Working', text: 'floor(100/5)=20 (multiples of 5). floor(100/25)=4 (multiples of 25 contribute an EXTRA factor of 5 each). floor(100/125)=0.' },
        { label: 'Answer', text: '20+4 = 24 trailing zeros.' },
      ],
      commonTrap:
        "Only counting floor(100/5)=20 and forgetting the EXTRA factor of 5 contributed by multiples of 25 (like 25, 50, 75, 100), each of which contributes TWO factors of 5, not just one.",
      alternateMethod: {
        label: 'Direct verification logic',
        steps: [
          'Multiples of 5 up to 100: 20 numbers, each contributing at least one 5.',
          'Among these, multiples of 25 (25,50,75,100) contribute an additional 5 each → +4. Total=20+4=24.',
        ],
      },
    },
  ],
  'permutations-combinations': [
    {
      id: 'pnc-01',
      difficulty: 'Easy',
      patternTags: ['TCS', 'Infosys'],
      question: "Find the value of ⁷P₃ (permutations of 7 things taken 3 at a time).",
      options: ['180', '210', '240', '270'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: 'n=7, r=3.' },
        { label: 'Concept', text: 'nPr = n×(n−1)×(n−2)×...×(n−r+1) — r consecutive descending factors starting from n, equivalent to n!/(n−r)!.' },
        { label: 'Working', text: '7×6×5.' },
        { label: 'Answer', text: '= 210.' },
      ],
      commonTrap:
        "Computing nCr instead (7!/(3!4!)=35) and confusing it with nPr — permutations count ORDER, combinations don't, so nPr is always ≥ nCr for the same n,r.",
      alternateMethod: {
        label: 'Factorial method',
        steps: [
          '7!/4! = (7×6×5×4×3×2×1)/(4×3×2×1) = 210 (the 4! in the denominator cancels the tail).',
        ],
      },
    },
    {
      id: 'pnc-02',
      difficulty: 'Easy',
      patternTags: ['TCS', 'Wipro'],
      question: "Find the value of ¹⁰C₄ (combinations of 10 things taken 4 at a time).",
      options: ['180', '195', '210', '225'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: 'n=10, r=4.' },
        { label: 'Concept', text: 'nCr = n!/(r!(n−r)!), equivalently the product of r descending terms from n, divided by r!.' },
        { label: 'Working', text: '(10×9×8×7)/(4×3×2×1) = 5,040/24.' },
        { label: 'Answer', text: '= 210.' },
      ],
      commonTrap:
        "Forgetting to divide by r! (just computing 10×9×8×7=5,040 and stopping) — that's ¹⁰P₄, not ¹⁰C₄; combinations require dividing out the r! internal orderings since order doesn't matter.",
      alternateMethod: {
        label: 'Symmetry check',
        steps: [
          '¹⁰C₄ = ¹⁰C₆ (since nCr=nC(n−r)). Computing 10!/(6!4!) gives the same 210, confirming consistency.',
        ],
      },
    },
    {
      id: 'pnc-03',
      difficulty: 'Easy',
      patternTags: ['Infosys', 'Zoho'],
      question: "In how many ways can the letters of the word 'MOBILE' be arranged?",
      options: ['600', '650', '700', '720'],
      correctIndex: 3,
      steps: [
        { label: 'Given', text: 'MOBILE has 6 distinct letters.' },
        { label: 'Concept', text: 'Number of arrangements of n distinct items = n!.' },
        { label: 'Working', text: '6! = 6×5×4×3×2×1.' },
        { label: 'Answer', text: '= 720.' },
      ],
      commonTrap:
        "Using nPr with a wrong, smaller r (e.g. computing ⁶P₅) instead of the full 6! — when ALL letters are used and are distinct, it's simply n! (equivalent to nPn).",
      alternateMethod: {
        label: 'Step-by-step counting',
        steps: [
          '6 choices for the 1st letter, 5 for the 2nd, 4 for the 3rd, down to 1 for the last: 6×5×4×3×2×1=720.',
        ],
      },
    },
    {
      id: 'pnc-04',
      difficulty: 'Medium',
      patternTags: ['TCS', 'Zoho'],
      question: "In how many ways can the letters of the word 'STATISTICS' be arranged?",
      options: ['50,400', '52,000', '54,000', '56,000'],
      correctIndex: 0,
      steps: [
        { label: 'Given', text: "STATISTICS (10 letters: S×3, T×3, A×1, I×2, C×1)." },
        { label: 'Concept', text: 'When letters repeat, arrangements = n!/(repetition factorials product), since swapping identical letters gives no NEW arrangement.' },
        { label: 'Working', text: '10!/(3!×3!×2!) = 3,628,800/72.' },
        { label: 'Answer', text: '= 50,400.' },
      ],
      commonTrap:
        "Using plain 10! (3,628,800) without dividing by the repeated-letter factorials — this overcounts arrangements that look identical because some letters repeat.",
      alternateMethod: {
        label: 'Step-by-step division',
        steps: [
          'Treat all 10 as distinct first: 10!=3,628,800.',
          'Divide by 3! for the S\'s (÷6), by 3! for the T\'s (÷6), by 2! for the I\'s (÷2): 3,628,800/6/6/2=50,400.',
        ],
      },
    },
    {
      id: 'pnc-05',
      difficulty: 'Medium',
      patternTags: ['Infosys', 'Wipro'],
      question: "In how many ways can the letters of the word 'LEADER' be arranged so that the two E's are always together?",
      options: ['100', '120', '140', '160'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: 'LEADER (L,E,A,D,E,R) — the two E\'s must stay adjacent.' },
        { label: 'Concept', text: "Treat the two E's as a single combined block; arrange this block along with the other 4 distinct letters as 5 total units." },
        { label: 'Working', text: '5 units (L,A,D,R,[EE]): 5!.' },
        { label: 'Answer', text: '= 120.' },
      ],
      commonTrap:
        "Multiplying by 2! for 'arranging the two E's within the block' (120×2=240) — but since both letters in the block are IDENTICAL, swapping them internally creates no distinguishable new arrangement.",
      alternateMethod: {
        label: 'Direct insight',
        steps: [
          "Placing 5 'items' (4 distinct letters + 1 EE-block) in a row: 5! = 120, with no extra internal factor needed since the block's contents are identical.",
        ],
      },
    },
    {
      id: 'pnc-06',
      difficulty: 'Hard',
      patternTags: ['TCS', 'Infosys'],
      question: "In how many ways can the letters of the word 'FRIEND' be arranged so that the vowels (I and E) never come together?",
      options: ['400', '440', '480', '520'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: 'FRIEND (6 distinct letters), vowels I,E must NEVER be adjacent.' },
        { label: 'Concept', text: 'Find total arrangements, subtract the arrangements where the vowels ARE together (complementary counting).' },
        { label: 'Working', text: 'Total=6!=720. Together: treat IE as one unit (2! internal arrangements since I≠E) → 5!×2!=240.' },
        { label: 'Answer', text: 'Never together = 720−240 = 480.' },
      ],
      commonTrap:
        "Forgetting the ×2! for the internal arrangement of the vowel-block when computing the 'together' case (using 5!=120 instead of 240) — since I and E are DIFFERENT letters, the block itself has 2 internal orderings.",
      alternateMethod: {
        label: 'Direct placement method',
        steps: [
          'Arrange the 4 consonants first: 4!=24 ways, creating 5 gaps (including ends).',
          'Choose 2 of these 5 gaps for the vowels, in order: ⁵P₂=20 ways.',
          'Total = 24×20 = 480.',
        ],
      },
    },
    {
      id: 'pnc-07',
      difficulty: 'Easy',
      patternTags: ['Wipro', 'Zoho'],
      question: "In how many ways can 6 people be seated around a circular table?",
      options: ['120', '144', '180', '720'],
      correctIndex: 0,
      steps: [
        { label: 'Given', text: '6 people, circular table.' },
        { label: 'Concept', text: 'Circular permutations of n distinct objects = (n−1)!, since rotations of the same arrangement are considered identical.' },
        { label: 'Working', text: '(6−1)! = 5!.' },
        { label: 'Answer', text: '= 120.' },
      ],
      commonTrap:
        "Using the LINEAR permutation formula (6!=720) instead — a circular table has no fixed 'start', so arrangements that are just rotations of each other count only ONCE.",
      alternateMethod: {
        label: 'Fixed-reference method',
        steps: [
          "Fix one person's seat as a reference (since rotations are equivalent). The remaining 5 people fill the remaining 5 seats in 5!=120 ways.",
        ],
      },
    },
    {
      id: 'pnc-08',
      difficulty: 'Hard',
      patternTags: ['TCS', 'Zoho'],
      question: "In how many ways can a committee of 5 people be formed from 6 men and 4 women, if the committee must include AT LEAST 3 men?",
      options: ['156', '168', '180', '186'],
      correctIndex: 3,
      steps: [
        { label: 'Given', text: '6 men, 4 women; form a 5-person committee with AT LEAST 3 men.' },
        { label: 'Concept', text: 'Break into mutually exclusive cases (exactly 3, 4, or 5 men), compute each via combinations, and sum.' },
        { label: 'Working', text: '(3M,2W): ⁶C₃×⁴C₂=20×6=120. (4M,1W): ⁶C₄×⁴C₁=15×4=60. (5M,0W): ⁶C₅×⁴C₀=6×1=6.' },
        { label: 'Answer', text: '120+60+6 = 186.' },
      ],
      commonTrap:
        "Only computing the 'exactly 3 men' case and forgetting that 'at least 3' also includes the 4-men and 5-men cases — 'at least' requires summing ALL qualifying cases.",
      alternateMethod: {
        label: 'Complementary counting',
        steps: [
          'Total ways to pick any 5 from 10 = ¹⁰C₅=252.',
          'Subtract fewer-than-3-men cases: (1M,4W)=6×1=6, (2M,3W)=15×4=60. (0M,5W)=0, since only 4 women exist.',
          '252−(6+60) = 186.',
        ],
      },
    },
    {
      id: 'pnc-09',
      difficulty: 'Easy',
      patternTags: ['Infosys', 'Wipro'],
      question: "A bag contains 5 red balls and 6 blue balls. In how many ways can 3 red balls and 2 blue balls be selected together?",
      options: ['120', '150', '180', '210'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: '5 red, 6 blue. Select 3 red and 2 blue.' },
        { label: 'Concept', text: 'Selections from independent groups are MULTIPLIED together, since each choice from group 1 pairs with each choice from group 2.' },
        { label: 'Working', text: '⁵C₃ × ⁶C₂ = 10 × 15.' },
        { label: 'Answer', text: '= 150.' },
      ],
      commonTrap:
        "Adding the two combination counts instead of multiplying (10+15=25) — independent selections combine multiplicatively, not additively.",
      alternateMethod: {
        label: 'Sequential reasoning',
        steps: [
          'Choose 3 red first (10 ways); for EACH of those, choose 2 blue (15 ways).',
          '10×15 = 150 total combined outcomes.',
        ],
      },
    },
    {
      id: 'pnc-10',
      difficulty: 'Medium',
      patternTags: ['TCS', 'Infosys'],
      question: "Find the number of diagonals in a polygon with 12 sides (a dodecagon).",
      options: ['48', '50', '54', '60'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: '12-sided polygon.' },
        { label: 'Concept', text: 'Total segments between any 2 vertices = ⁿC₂; subtract the n SIDES (which are not diagonals): Diagonals = n(n−3)/2.' },
        { label: 'Working', text: '12×9/2.' },
        { label: 'Answer', text: '= 54.' },
      ],
      commonTrap:
        "Using ⁿC₂ alone (12×11/2=66) without subtracting the 12 sides — ⁿC₂ counts ALL vertex-pairs, including the polygon's own sides, which aren't diagonals.",
      alternateMethod: {
        label: 'Direct combination method',
        steps: [
          'Total vertex-pairs = ¹²C₂ = 66. Subtract the 12 sides (each joining two adjacent vertices): 66−12=54.',
        ],
      },
    },
    {
      id: 'pnc-11',
      difficulty: 'Easy',
      patternTags: ['Wipro', 'Zoho'],
      question: "In a party, every person shakes hands with every other person exactly once. If there are 15 people, find the total number of handshakes.",
      options: ['105', '110', '120', '130'],
      correctIndex: 0,
      steps: [
        { label: 'Given', text: '15 people, each shakes hands with every other person once.' },
        { label: 'Concept', text: "A handshake is an UNORDERED pair (A shaking B's hand is the same event as B shaking A's) — this is a combination, ⁿC₂, not a permutation." },
        { label: 'Working', text: '¹⁵C₂ = 15×14/2.' },
        { label: 'Answer', text: '= 105.' },
      ],
      commonTrap:
        "Using ⁿP₂ instead of ⁿC₂ (15×14=210), double-counting each handshake once as 'A-then-B' and once as 'B-then-A' — but a handshake between two people is a single event.",
      alternateMethod: {
        label: 'Direct reasoning',
        steps: [
          'Person 1 shakes with 14 others. Person 2 shakes with 13 NEW people (already counted with person 1). ... down to 1.',
          '14+13+...+1 = (14×15)/2 = 105.',
        ],
      },
    },
    {
      id: 'pnc-12',
      difficulty: 'Easy',
      patternTags: ['TCS', 'Zoho'],
      question: "How many 3-digit numbers can be formed using the digits 1, 2, 3, 4, 5 (repetition of digits allowed)?",
      options: ['100', '125', '150', '175'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: 'Digits 1–5, repetition ALLOWED, form 3-digit numbers.' },
        { label: 'Concept', text: 'With repetition allowed, EACH digit position independently has the full set of 5 choices — no digit gets "used up".' },
        { label: 'Working', text: '5×5×5.' },
        { label: 'Answer', text: '= 125.' },
      ],
      commonTrap:
        "Treating it as a no-repetition permutation (5×4×3=60) — that formula only applies when repetition is NOT allowed; here every position independently has all 5 choices.",
      alternateMethod: {
        label: 'Fundamental counting principle',
        steps: [
          '3 independent positions, each with 5 options: 5³ = 125.',
        ],
      },
    },
    {
      id: 'pnc-13',
      difficulty: 'Easy',
      patternTags: ['Infosys', 'Wipro'],
      question: "How many 3-digit numbers can be formed using the digits 1, 2, 3, 4, 5 (no digit repeated)?",
      options: ['60', '80', '100', '120'],
      correctIndex: 0,
      steps: [
        { label: 'Given', text: 'Digits 1–5, NO repetition, form 3-digit numbers.' },
        { label: 'Concept', text: 'Without repetition, each chosen digit REDUCES the choices for the next position.' },
        { label: 'Working', text: '5 choices for 1st digit, 4 for 2nd, 3 for 3rd.' },
        { label: 'Answer', text: '5×4×3 = 60 (equivalently ⁵P₃).' },
      ],
      commonTrap:
        "Using 5³=125 (the WITH-repetition formula) by mistake — since repetition is explicitly disallowed here, the choices shrink at each step instead of staying constant.",
      alternateMethod: {
        label: 'Permutation formula',
        steps: [
          '⁵P₃ = 5!/(5−3)! = 120/2 = 60.',
        ],
      },
    },
    {
      id: 'pnc-14',
      difficulty: 'Medium',
      patternTags: ['TCS', 'Infosys'],
      question: "How many 3-digit EVEN numbers can be formed using the digits 1, 2, 3, 4, 5 (no digit repeated)?",
      options: ['20', '24', '28', '32'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: 'Digits 1–5, no repetition, form 3-digit EVEN numbers.' },
        { label: 'Concept', text: 'Fill the RESTRICTED position (the units digit, which determines even/odd) FIRST, since it has the fewest valid choices.' },
        { label: 'Working', text: 'Last digit (must be even): 2 choices (2 or 4). Remaining 2 positions from the remaining 4 digits: 4×3=12.' },
        { label: 'Answer', text: '2×12 = 24.' },
      ],
      commonTrap:
        "Filling the digits in the natural left-to-right order (hundreds, tens, units) without special handling for the units digit's restriction — restricted positions should always be filled FIRST.",
      alternateMethod: {
        label: 'Case-by-case method',
        steps: [
          'If last digit=2: remaining {1,3,4,5} → 4×3=12 ways for the first two positions.',
          'If last digit=4: remaining {1,2,3,5} → 4×3=12 ways.',
          'Total = 12+12 = 24.',
        ],
      },
    },
    {
      id: 'pnc-15',
      difficulty: 'Medium',
      patternTags: ['Wipro', 'Zoho'],
      question: "In how many ways can a team of 4 players be selected from 10 players, if a particular player must always be included?",
      options: ['72', '78', '84', '90'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: '10 players, 4-player team, one SPECIFIC player must always be in it.' },
        { label: 'Concept', text: "Since that player's inclusion is fixed, only the remaining (4−1=3) spots need to be filled from the remaining (10−1=9) players." },
        { label: 'Working', text: '⁹C₃ = 9×8×7/(3×2×1) = 504/6.' },
        { label: 'Answer', text: '= 84.' },
      ],
      commonTrap:
        "Computing ¹⁰C₄ (the unrestricted total, =210) and then dividing by something arbitrary, instead of directly reducing BOTH the team size and player pool by 1 for the fixed inclusion.",
      alternateMethod: {
        label: 'Fraction-of-total method',
        steps: [
          'By symmetry, the fraction of all ¹⁰C₄=210 combinations that include one specific player = (spots)/(players) = 4/10.',
          '210 × 4/10 = 84.',
        ],
      },
    },
    {
      id: 'pnc-16',
      difficulty: 'Medium',
      patternTags: ['TCS', 'Zoho'],
      question: "In how many ways can a team of 4 players be selected from 10 players, if a particular player must NEVER be included?",
      options: ['84', '98', '112', '126'],
      correctIndex: 3,
      steps: [
        { label: 'Given', text: '10 players, 4-player team, one SPECIFIC player must be EXCLUDED.' },
        { label: 'Concept', text: 'Remove that player from the pool entirely, then choose the full team size from the remaining players.' },
        { label: 'Working', text: '⁹C₄ = 9×8×7×6/(4×3×2×1) = 3,024/24.' },
        { label: 'Answer', text: '= 126.' },
      ],
      commonTrap:
        "Confusing this with the 'always included' scenario and computing ⁹C₃ instead of ⁹C₄ — exclusion reduces only the PLAYER POOL, not the team size.",
      alternateMethod: {
        label: 'Complementary counting',
        steps: [
          'Total (unrestricted) = ¹⁰C₄=210. "Always included" cases = ⁹C₃=84.',
          'Since every combination either includes or excludes this player: Excluded cases = 210−84 = 126.',
        ],
      },
    },
    {
      id: 'pnc-17',
      difficulty: 'Medium',
      patternTags: ['Infosys', 'Wipro'],
      question: "In how many ways can 6 different beads be arranged to form a necklace?",
      options: ['60', '72', '90', '120'],
      correctIndex: 0,
      steps: [
        { label: 'Given', text: '6 different beads, forming a NECKLACE (not just a seating arrangement).' },
        { label: 'Concept', text: 'A necklace can be flipped over as well as rotated, and a flipped arrangement looks physically identical — so divide the circular-permutation count by an EXTRA factor of 2.' },
        { label: 'Working', text: 'Circular arrangements = (6−1)! = 120.' },
        { label: 'Answer', text: 'Necklace arrangements = 120/2 = 60.' },
      ],
      commonTrap:
        "Using the plain circular-table formula (n−1)!=120 without the extra ÷2 for reflections — a table seating has a fixed left/right, but a necklace can be turned over.",
      alternateMethod: {
        label: 'Conceptual check',
        steps: [
          'At a table, clockwise order A-B-C-D-E-F differs from its mirror image A-F-E-D-C-B — 2 distinct seatings.',
          'On a necklace, flipping it over turns one into the other, so they count as the SAME necklace — hence dividing by 2.',
        ],
      },
    },
    {
      id: 'pnc-18',
      difficulty: 'Hard',
      patternTags: ['TCS', 'Zoho'],
      question: "If nPr = 60 and nCr = 10, find the value of r.",
      options: ['2', '3', '4', '5'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: 'nPr = 60, nCr = 10.' },
        { label: 'Concept', text: 'The relationship nPr = nCr × r! always holds, since permutations account for the r! internal orderings that combinations ignore.' },
        { label: 'Working', text: '60 = 10 × r! → r! = 6.' },
        { label: 'Answer', text: 'r = 3 (since 3! = 6).' },
      ],
      commonTrap:
        "Trying to guess n and r simultaneously from scratch instead of using the direct nPr/nCr=r! relationship, which isolates r immediately without needing n at all.",
      alternateMethod: {
        label: 'Verification',
        steps: [
          'If r=3 and nCr=10: n(n−1)(n−2)/6=10 → n(n−1)(n−2)=60 → n=5 (since 5×4×3=60).',
          'Check nPr = 5P3 = 5×4×3 = 60 ✓ matches.',
        ],
      },
    },
    {
      id: 'pnc-19',
      difficulty: 'Medium',
      patternTags: ['Infosys', 'Wipro'],
      question: "If ⁿC₂ = 45, find the value of n.",
      options: ['8', '10', '12', '15'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: 'ⁿC₂ = 45.' },
        { label: 'Concept', text: 'ⁿC₂ = n(n−1)/2 — set up and solve this quadratic for n.' },
        { label: 'Working', text: 'n(n−1) = 90 → n²−n−90=0 → (n−10)(n+9)=0.' },
        { label: 'Answer', text: 'n = 10 (rejecting the negative root n=−9).' },
      ],
      commonTrap:
        "Trying trial values without solving properly, or making a factoring slip — since ⁿC₂ always involves two CONSECUTIVE integers multiplying to 2×(the given value), finding the right factor pair is key.",
      alternateMethod: {
        label: 'Verification',
        steps: [
          '¹⁰C₂ = 10×9/2 = 45 — matches exactly.',
        ],
      },
    },
    {
      id: 'pnc-20',
      difficulty: 'Hard',
      patternTags: ['TCS', 'Zoho'],
      question: "In how many ways can 10 identical chocolates be distributed among 4 children, such that each child gets at least one chocolate?",
      options: ['56', '70', '84', '120'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: '10 identical chocolates, 4 children, each gets AT LEAST 1.' },
        { label: 'Concept', text: "Give each child 1 chocolate first (satisfying the minimum), leaving 10−4=6 to distribute FREELY (allowing zero) among 4 children — the classic 'stars and bars' formula: C(remaining+groups−1, groups−1)." },
        { label: 'Working', text: 'C(6+4−1, 4−1) = C(9,3) = 9×8×7/(3×2×1) = 504/6.' },
        { label: 'Answer', text: '= 84.' },
      ],
      commonTrap:
        "Applying stars-and-bars directly to the original 10 chocolates without first setting aside the mandatory 1-per-child (computing C(13,3)=286 instead) — the 'at least 1 each' condition must be handled by pre-allocating first.",
      alternateMethod: {
        label: 'Direct formula',
        steps: [
          'Distributing n identical items among r people with each getting ≥1 = C(n−1, r−1).',
          'C(10−1, 4−1) = C(9,3) = 84 — the same formula, skipping the "give 1 first" narration.',
        ],
      },
    },
    {
      id: 'pnc-21',
      difficulty: 'Medium',
      patternTags: ['Wipro', 'Zoho'],
      question: "In how many ways can 3 boys and 3 girls be seated in a row such that boys and girls sit in alternate positions?",
      options: ['36', '48', '60', '72'],
      correctIndex: 3,
      steps: [
        { label: 'Given', text: '3 boys, 3 girls, alternating seats in a row of 6.' },
        { label: 'Concept', text: 'There are TWO possible alternating patterns (starting with a boy or a girl); for each, arrange boys among boy-spots and girls among girl-spots independently.' },
        { label: 'Working', text: 'Each pattern: 3!×3! = 6×6 = 36.' },
        { label: 'Answer', text: 'Two patterns (B-start or G-start): 36×2 = 72.' },
      ],
      commonTrap:
        "Considering only ONE starting pattern (e.g. only Boy-Girl-Boy...) and forgetting the girl-starting arrangement is equally valid — this gives exactly half the correct answer.",
      alternateMethod: {
        label: 'Direct multiplication',
        steps: [
          'Choose the starting gender (2 ways), then arrange 3 boys in their fixed positions (3!=6) and 3 girls in theirs (3!=6).',
          '2×6×6 = 72.',
        ],
      },
    },
    {
      id: 'pnc-22',
      difficulty: 'Medium',
      patternTags: ['TCS', 'Infosys'],
      question: "In how many ways can a committee of 4 be selected from 8 people, and then a chairperson chosen from among those 4 selected?",
      options: ['252', '280', '308', '336'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: 'Select a 4-person committee from 8, then choose 1 chairperson from those 4.' },
        { label: 'Concept', text: 'This is a two-stage process — first a COMBINATION (the committee, order doesn\'t matter), then a simple selection (the chair) — multiply the two stage-counts.' },
        { label: 'Working', text: '⁸C₄ = 70. Chairperson choices = 4.' },
        { label: 'Answer', text: '70×4 = 280.' },
      ],
      commonTrap:
        "Treating the whole problem as a single permutation (computing ⁸P₄=1,680 directly) — this overcounts by treating all 4 committee positions as distinctly ordered, when only ONE role is actually distinguished.",
      alternateMethod: {
        label: 'Alternative sequencing',
        steps: [
          'Choose the chairperson first from all 8 people (8 ways), then choose the remaining 3 members from the remaining 7 (⁷C₃=35 ways).',
          '8×35 = 280 — same answer via a different order of the same two decisions.',
        ],
      },
    },
    {
      id: 'pnc-23',
      difficulty: 'Hard',
      patternTags: ['Wipro', 'Zoho'],
      question: "There are 12 points in a plane, out of which 4 are collinear. Find the number of triangles that can be formed using these points.",
      options: ['216', '224', '232', '240'],
      correctIndex: 0,
      steps: [
        { label: 'Given', text: '12 points, 4 of which are collinear.' },
        { label: 'Concept', text: 'Any 3 non-collinear points form a triangle; subtract the combinations of 3 points that are ALL from the collinear set (which form a straight line, not a triangle).' },
        { label: 'Working', text: 'Total 3-point selections = ¹²C₃=220. Collinear-only selections = ⁴C₃=4.' },
        { label: 'Answer', text: '220−4 = 216.' },
      ],
      commonTrap:
        "Forgetting to subtract the collinear cases entirely (answering 220 directly) — not every combination of 3 points from a set with collinear points forms a valid triangle.",
      alternateMethod: {
        label: 'Direct construction',
        steps: [
          'Non-collinear points=8. Triangles = (3 from the 8)=⁸C₃=56, + (2 from 8, 1 from 4 collinear)=⁸C₂×⁴C₁=112, + (1 from 8, 2 from 4 collinear)=⁸C₁×⁴C₂=48.',
          '56+112+48 = 216 — matches.',
        ],
      },
    },
    {
      id: 'pnc-24',
      difficulty: 'Hard',
      patternTags: ['TCS', 'Zoho'],
      question: "In how many ways can the letters of the word 'COMPUTER' be arranged so that all the vowels always come together?",
      options: ['3,600', '4,080', '4,320', '4,600'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: 'COMPUTER (8 distinct letters: 5 consonants, 3 vowels O,U,E). Vowels must always be together.' },
        { label: 'Concept', text: 'Treat the vowel group as ONE block; arrange it with the 5 consonants as 6 total units, then separately account for internal arrangements WITHIN the vowel block (since the vowels are all different).' },
        { label: 'Working', text: '6 units arranged: 6!=720. Internal vowel arrangements: 3!=6.' },
        { label: 'Answer', text: '720×6 = 4,320.' },
      ],
      commonTrap:
        "Forgetting to multiply by 3! for the internal vowel arrangements (stopping at 720) — since the vowels are all DIFFERENT letters, their internal order matters and must be counted separately.",
      alternateMethod: {
        label: 'Two-stage counting',
        steps: [
          'Stage 1: arrange the 6 items (5 consonants + 1 vowel-block): 6! ways.',
          'Stage 2: permute the 3 vowels within their block: 3! ways.',
          '6!×3! = 4,320.',
        ],
      },
    },
    {
      id: 'pnc-25',
      difficulty: 'Medium',
      patternTags: ['Infosys', 'Wipro'],
      question: "In how many ways can 3 letters be selected from the word 'NUMBER' and then arranged to form a 3-letter code (no repetition)?",
      options: ['120', '150', '180', '216'],
      correctIndex: 0,
      steps: [
        { label: 'Given', text: 'NUMBER (6 distinct letters), select 3 and arrange into an ORDERED code.' },
        { label: 'Concept', text: '"Select AND arrange" for r items out of n, where order matters, is precisely the definition of nPr — no need to separately compute nCr and multiply by r!, since nPr already IS that.' },
        { label: 'Working', text: '⁶P₃ = 6×5×4.' },
        { label: 'Answer', text: '= 120.' },
      ],
      commonTrap:
        "Computing ⁶C₃ (=20) alone as if it were the final answer, and forgetting the ARRANGEMENT step entirely — selection without ordering the 3 chosen letters isn't enough for a 'code'.",
      alternateMethod: {
        label: 'Two-stage equivalent',
        steps: [
          'First SELECT 3 letters from 6 (order doesn\'t matter yet): ⁶C₃=20 ways.',
          'Then ARRANGE those 3 selected letters in order: 3!=6 ways.',
          'Total = 20×6 = 120 — confirms the direct nPr calculation.',
        ],
      },
    },
  ],
  probability: [
    {
      id: 'prob-01',
      difficulty: 'Easy',
      patternTags: ['TCS', 'Infosys'],
      question: "A die is rolled once. Find the probability of getting a number greater than 4.",
      options: ['1/3', '1/6', '1/4', '1/2'],
      correctIndex: 0,
      steps: [
        { label: 'Given', text: 'A die (numbers 1–6) is rolled once.' },
        { label: 'Concept', text: 'P(event) = favorable outcomes / total outcomes.' },
        { label: 'Working', text: 'Numbers greater than 4: {5, 6} → 2 favorable outcomes. Total outcomes = 6.' },
        { label: 'Answer', text: 'P = 2/6 = 1/3.' },
      ],
      commonTrap:
        "Counting only '6' as greater than 4 and forgetting '5' — always list out the qualifying outcomes explicitly to avoid missing one.",
      alternateMethod: {
        label: 'Complement method',
        steps: [
          'P(≤4) = 4/6 = 2/3.',
          'P(>4) = 1 − 2/3 = 1/3.',
        ],
      },
    },
    {
      id: 'prob-02',
      difficulty: 'Easy',
      patternTags: ['TCS', 'Wipro'],
      question: "A card is drawn at random from a well-shuffled pack of 52 playing cards. Find the probability that it is a king.",
      options: ['1/12', '1/13', '1/26', '1/4'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: '52 cards, 4 of which are kings.' },
        { label: 'Concept', text: 'P = favorable / total.' },
        { label: 'Working', text: '4/52.' },
        { label: 'Answer', text: 'Simplified: 1/13.' },
      ],
      commonTrap:
        "Confusing 'king' with 'face card' (12 face cards total: J, Q, K across 4 suits) and computing 12/52=3/13 — face cards include kings, queens, AND jacks, not just kings.",
      alternateMethod: {
        label: 'Verification',
        steps: [
          'Exactly 1 king per suit × 4 suits = 4 kings. 4/52 simplifies to 1/13.',
        ],
      },
    },
    {
      id: 'prob-03',
      difficulty: 'Medium',
      patternTags: ['Infosys', 'Zoho'],
      question: "Two dice are rolled together. Find the probability of getting at least one 6.",
      options: ['1/6', '25/36', '11/36', '1/3'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: 'Two dice rolled together (36 equally likely outcomes).' },
        { label: 'Concept', text: "'At least one' is best solved via the complement: P(at least one 6) = 1 − P(no 6 at all)." },
        { label: 'Working', text: 'P(no 6 on a single die) = 5/6. P(no 6 on both) = (5/6)² = 25/36.' },
        { label: 'Answer', text: 'P(at least one 6) = 1 − 25/36 = 11/36.' },
      ],
      commonTrap:
        "Adding P(6 on die1)+P(6 on die2)=1/6+1/6=1/3 — this DOUBLE-COUNTS the case where BOTH dice show 6, giving the wrong answer.",
      alternateMethod: {
        label: 'Direct counting',
        steps: [
          'Outcomes with at least one 6: (6,1)...(6,6) and (1,6)...(5,6), avoiding double-counting (6,6) = 11 outcomes.',
          'P = 11/36.',
        ],
      },
    },
    {
      id: 'prob-04',
      difficulty: 'Medium',
      patternTags: ['TCS', 'Zoho'],
      question: "A bag contains 5 red and 3 blue balls. A ball is drawn, its color noted, and put back (with replacement). A second ball is then drawn. Find the probability that both balls are red.",
      options: ['15/64', '5/8', '25/56', '25/64'],
      correctIndex: 3,
      steps: [
        { label: 'Given', text: '5 red, 3 blue, WITH replacement, drawn twice.' },
        { label: 'Concept', text: 'For independent events (replacement restores the original odds), multiply the individual probabilities.' },
        { label: 'Working', text: 'P(red) = 5/8 each time (unchanged since the ball is replaced).' },
        { label: 'Answer', text: '(5/8) × (5/8) = 25/64.' },
      ],
      commonTrap:
        "Treating this like a WITHOUT-replacement problem (5/8 then 4/7): (5/8)×(4/7)=5/14 — that only applies when balls are NOT replaced; here replacement keeps the probability identical each draw.",
      alternateMethod: {
        label: 'Sample-space verification',
        steps: [
          'With replacement, total possible outcome pairs = 8×8=64. Favorable (both red) = 5×5=25.',
          'P = 25/64.',
        ],
      },
    },
    {
      id: 'prob-05',
      difficulty: 'Easy',
      patternTags: ['Wipro', 'Zoho'],
      question: "A card is drawn from a pack of 52 cards. Find the probability that it is either a king or a queen.",
      options: ['2/13', '1/13', '4/13', '1/26'],
      correctIndex: 0,
      steps: [
        { label: 'Given', text: 'Event A=king (4), Event B=queen (4) — mutually exclusive (a card can\'t be both).' },
        { label: 'Concept', text: 'For MUTUALLY EXCLUSIVE events, P(A or B) = P(A) + P(B).' },
        { label: 'Working', text: '4/52 + 4/52 = 8/52.' },
        { label: 'Answer', text: 'Simplified: 2/13.' },
      ],
      commonTrap:
        "Multiplying instead of adding (4/52×4/52) — 'or' for mutually exclusive events means ADDING probabilities; multiplication is for 'and'/independent joint events.",
      alternateMethod: {
        label: 'Direct counting',
        steps: [
          'Favorable cards = 4 kings + 4 queens = 8 cards (no overlap possible).',
          'P = 8/52 = 2/13.',
        ],
      },
    },
    {
      id: 'prob-06',
      difficulty: 'Medium',
      patternTags: ['TCS', 'Infosys'],
      question: "A card is drawn from a pack of 52 cards. Find the probability that it is either a king or a heart.",
      options: ['17/52', '4/13', '1/13', '3/13'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: 'Event A=king (4), Event B=heart (13) — these OVERLAP (the king of hearts is both).' },
        { label: 'Concept', text: 'P(A or B) = P(A) + P(B) − P(A and B) — subtract the overlap once so it isn\'t double-counted.' },
        { label: 'Working', text: '4/52 + 13/52 − 1/52 = 16/52.' },
        { label: 'Answer', text: 'Simplified: 4/13.' },
      ],
      commonTrap:
        "Simply adding 4/52+13/52=17/52 without subtracting the overlap — this double-counts the king of hearts, which satisfies BOTH conditions.",
      alternateMethod: {
        label: 'Direct counting',
        steps: [
          'Favorable cards = 4 kings + 13 hearts − 1 (king of hearts, counted in both) = 16 cards.',
          'P = 16/52 = 4/13.',
        ],
      },
    },
    {
      id: 'prob-07',
      difficulty: 'Hard',
      patternTags: ['Wipro', 'Zoho'],
      question: "A bag contains 4 red and 6 black balls. Two balls are drawn one after another WITHOUT replacement. Find the probability that both are black.",
      options: ['3/5', '1/2', '1/3', '5/9'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: '4 red, 6 black, drawn WITHOUT replacement.' },
        { label: 'Concept', text: 'For dependent events (without replacement), the second probability changes based on what was removed — multiply the sequential conditional probabilities.' },
        { label: 'Working', text: 'P(1st black)=6/10. P(2nd black | 1st black)=5/9 (one black and one total ball already removed).' },
        { label: 'Answer', text: '6/10 × 5/9 = 30/90 = 1/3.' },
      ],
      commonTrap:
        "Using 6/10 for BOTH draws (as if with replacement): (6/10)×(6/10)=9/25 — without replacement, the second draw's probability must account for the ball already removed.",
      alternateMethod: {
        label: 'Combination method',
        steps: [
          'P(both black) = ⁶C₂/¹⁰C₂ = 15/45 = 1/3 (choosing 2 black out of 6, over choosing any 2 out of 10).',
        ],
      },
    },
    {
      id: 'prob-08',
      difficulty: 'Easy',
      patternTags: ['TCS', 'Zoho'],
      question: "Three coins are tossed simultaneously. Find the probability of getting exactly two heads.",
      options: ['1/8', '1/2', '3/4', '3/8'],
      correctIndex: 3,
      steps: [
        { label: 'Given', text: '3 coins tossed together.' },
        { label: 'Concept', text: 'Total outcomes = 2ⁿ for n coins; count outcomes with EXACTLY 2 heads (not "at least 2").' },
        { label: 'Working', text: 'Total = 2³ = 8. Favorable (exactly 2 heads): HHT, HTH, THH = 3.' },
        { label: 'Answer', text: 'P = 3/8.' },
      ],
      commonTrap:
        "Confusing 'exactly 2 heads' with 'at least 2 heads' (which also includes HHH, giving 4/8=1/2) — 'exactly' excludes the all-heads case.",
      alternateMethod: {
        label: 'Combination method',
        steps: [
          'Ways to choose which 2 of the 3 coins show heads = ³C₂ = 3. Total outcomes = 2³=8.',
          'P = 3/8.',
        ],
      },
    },
    {
      id: 'prob-09',
      difficulty: 'Medium',
      patternTags: ['Infosys', 'Wipro'],
      question: "Two dice are rolled together. Find the probability that the sum of the numbers is 8.",
      options: ['5/36', '1/6', '1/9', '7/36'],
      correctIndex: 0,
      steps: [
        { label: 'Given', text: 'Two dice, find P(sum = 8).' },
        { label: 'Concept', text: 'List all ordered pairs (die1, die2) that sum to the target, out of 36 total equally likely outcomes.' },
        { label: 'Working', text: 'Pairs: (2,6), (3,5), (4,4), (5,3), (6,2) = 5 pairs.' },
        { label: 'Answer', text: 'P = 5/36.' },
      ],
      commonTrap:
        "Forgetting that (4,4) counts only ONCE (same number on both dice) while both orderings of different-number pairs like (2,6) and (6,2) must each be counted — a common off-by-one error in dice-sum counting.",
      alternateMethod: {
        label: 'Systematic table',
        steps: [
          'For sum=8, die1 can be 2,3,4,5,6 (die2 is then 6,5,4,3,2 respectively) = 5 valid combinations.',
          'P = 5/36.',
        ],
      },
    },
    {
      id: 'prob-10',
      difficulty: 'Medium',
      patternTags: ['TCS', 'Zoho'],
      question: "A bag contains 6 white and 4 black balls. If 3 balls are drawn at random, find the probability that all 3 are white.",
      options: ['3/10', '1/6', '1/5', '2/15'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: '6 white, 4 black. Draw 3 balls together (no replacement/order).' },
        { label: 'Concept', text: 'Use combinations for both favorable and total outcomes, since the balls are drawn together.' },
        { label: 'Working', text: 'Total = ¹⁰C₃ = 120. Favorable (all white) = ⁶C₃ = 20.' },
        { label: 'Answer', text: 'P = 20/120 = 1/6.' },
      ],
      commonTrap:
        "Answering just 6/10 (the probability the FIRST ball alone is white) and stopping there — this ignores that all THREE balls drawn must be white, not just one.",
      alternateMethod: {
        label: 'Sequential method',
        steps: [
          'P(1st white)=6/10, P(2nd white | 1st white)=5/9, P(3rd white | first two white)=4/8.',
          '6/10 × 5/9 × 4/8 = 120/720 = 1/6 — same answer via step-by-step conditional probability.',
        ],
      },
    },
    {
      id: 'prob-11',
      difficulty: 'Easy',
      patternTags: ['Infosys', 'Wipro'],
      question: "A fair coin is tossed 4 times. Find the probability of getting exactly 3 heads.",
      options: ['3/8', '1/2', '1/4', '1/16'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: '4 coin tosses, exactly 3 heads.' },
        { label: 'Concept', text: 'Total outcomes = 2⁴=16. Favorable ways to choose WHICH 3 of the 4 tosses are heads = ⁴C₃.' },
        { label: 'Working', text: '⁴C₃ = 4.' },
        { label: 'Answer', text: 'P = 4/16 = 1/4.' },
      ],
      commonTrap:
        "Using ⁴C₄ (=1, all four heads) or otherwise miscounting which combination applies — always confirm you're counting 'choose 3 heads out of 4 positions'.",
      alternateMethod: {
        label: 'Direct listing',
        steps: [
          'Exactly 3 heads out of 4 tosses: HHHT, HHTH, HTHH, THHH = 4 outcomes.',
          'P = 4/16 = 1/4.',
        ],
      },
    },
    {
      id: 'prob-12',
      difficulty: 'Easy',
      patternTags: ['TCS', 'Infosys'],
      question: "A number is selected at random from the numbers 1 to 25. Find the probability that it is NOT a multiple of 4.",
      options: ['6/25', '7/25', '18/25', '19/25'],
      correctIndex: 3,
      steps: [
        { label: 'Given', text: 'Numbers 1–25, find P(NOT a multiple of 4).' },
        { label: 'Concept', text: 'Find P(multiple of 4) first, then subtract from 1 for the complement.' },
        { label: 'Working', text: 'Multiples of 4 in range: 4,8,12,16,20,24 = 6. P(multiple) = 6/25.' },
        { label: 'Answer', text: 'P(not) = 1 − 6/25 = 19/25.' },
      ],
      commonTrap:
        "Miscounting the multiples of 4 (e.g. including 28, which is out of range) — always verify by listing: 4,8,12,16,20,24 (exactly 6, since 28>25).",
      alternateMethod: {
        label: 'Direct count',
        steps: [
          'Numbers from 1–25 that are NOT multiples of 4 = 25 − 6 = 19.',
          'P = 19/25.',
        ],
      },
    },
    {
      id: 'prob-13',
      difficulty: 'Medium',
      patternTags: ['Wipro', 'Zoho'],
      question: "A card is drawn from a pack of 52 cards. Find the probability that it is neither a face card nor an ace.",
      options: ['9/13', '10/13', '3/4', '11/13'],
      correctIndex: 0,
      steps: [
        { label: 'Given', text: '52 cards; exclude face cards (12) AND aces (4).' },
        { label: 'Concept', text: 'Find the total EXCLUDED cards (face cards and aces are separate, non-overlapping categories), subtract from 52.' },
        { label: 'Working', text: 'Excluded = 12 + 4 = 16. Remaining = 52 − 16 = 36.' },
        { label: 'Answer', text: 'P = 36/52 = 9/13.' },
      ],
      commonTrap:
        "Assuming aces overlap with face cards and subtracting a nonexistent overlap — aces are a SEPARATE category from J/Q/K face cards, with no shared cards.",
      alternateMethod: {
        label: 'Direct count',
        steps: [
          'Non-face, non-ace cards per suit = 13 − 3(face) − 1(ace) = 9 per suit × 4 suits = 36.',
          'P = 36/52 = 9/13.',
        ],
      },
    },
    {
      id: 'prob-14',
      difficulty: 'Medium',
      patternTags: ['TCS', 'Zoho'],
      question: "A box contains 12 items, of which 3 are defective. If 2 items are selected at random, find the probability that both are defective.",
      options: ['1/12', '1/22', '1/44', '3/22'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: '12 items, 3 defective. Select 2 without replacement.' },
        { label: 'Concept', text: 'Use combinations: favorable/total, since the 2 items are selected together.' },
        { label: 'Working', text: 'Total = ¹²C₂ = 66. Favorable = ³C₂ = 3.' },
        { label: 'Answer', text: 'P = 3/66 = 1/22.' },
      ],
      commonTrap:
        "Treating this as WITH replacement: (3/12)×(3/12)=1/16 — without replacement, the second draw's pool shrinks by one defective and one total item.",
      alternateMethod: {
        label: 'Sequential method',
        steps: [
          'P(1st defective)=3/12, P(2nd defective | 1st defective)=2/11.',
          '3/12 × 2/11 = 6/132 = 1/22 — matches.',
        ],
      },
    },
    {
      id: 'prob-15',
      difficulty: 'Hard',
      patternTags: ['Infosys', 'Wipro'],
      question: "A box contains 12 items, of which 3 are defective. If 2 items are selected at random, find the probability that AT LEAST ONE is defective.",
      options: ['6/11', '1/22', '5/11', '3/11'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: 'Same setup as before, but "at least one" defective.' },
        { label: 'Concept', text: 'Use the complement: P(at least 1 defective) = 1 − P(NO defectives at all).' },
        { label: 'Working', text: 'P(none defective) = ⁹C₂/¹²C₂ = 36/66 = 6/11.' },
        { label: 'Answer', text: 'P(at least one) = 1 − 6/11 = 5/11.' },
      ],
      commonTrap:
        "Computing 1 − P(both defective) = 1 − 1/22 = 21/22 instead of 1 − P(no defectives) — 'at least one' is the complement of 'zero', not the complement of 'all'.",
      alternateMethod: {
        label: 'Direct sum method',
        steps: [
          'P(exactly 1 defective) = (³C₁×⁹C₁)/¹²C₂ = 27/66. P(exactly 2 defective) = ³C₂/¹²C₂ = 3/66.',
          'Sum = 30/66 = 5/11 — confirms via direct addition of the two qualifying cases.',
        ],
      },
    },
    {
      id: 'prob-16',
      difficulty: 'Hard',
      patternTags: ['TCS', 'Zoho'],
      question: "A family has two children. Given that at least one child is a boy, find the probability that both children are boys.",
      options: ['1/2', '1/4', '2/3', '1/3'],
      correctIndex: 3,
      steps: [
        { label: 'Given', text: 'Two children; at least one is a boy.' },
        { label: 'Concept', text: 'This is CONDITIONAL probability — restrict the sample space to outcomes consistent with the given information, then find the qualifying fraction.' },
        { label: 'Working', text: 'Sample space (birth order matters): {BB,BG,GB,GG}. "At least one boy" excludes GG, leaving {BB,BG,GB}.' },
        { label: 'Answer', text: 'P(both boys) = 1/3 (only BB out of these 3 remaining outcomes).' },
      ],
      commonTrap:
        "Assuming the answer is 1/2 (as if the 'other' child's gender were an independent 50-50 question) — the classic trap; BG and GB are two distinct equally-likely outcomes since birth order matters.",
      alternateMethod: {
        label: 'Formula method',
        steps: [
          'P(both boys | at least one boy) = P(BB) / P(not GG) = (1/4) / (3/4) = 1/3.',
        ],
      },
    },
    {
      id: 'prob-17',
      difficulty: 'Hard',
      patternTags: ['Wipro', 'Zoho'],
      question: "Find the probability that a leap year selected at random will have 53 Sundays.",
      options: ['2/7', '1/7', '3/7', '1/2'],
      correctIndex: 0,
      steps: [
        { label: 'Given', text: 'A leap year = 366 days = 52 complete weeks + 2 extra days.' },
        { label: 'Concept', text: 'The 2 extra days determine which weekday(s) occur 53 times; there are 7 equally likely consecutive-day pairs these extra 2 days could be.' },
        { label: 'Working', text: 'Pairs including a Sunday: (Saturday,Sunday) and (Sunday,Monday) = 2 out of 7 pairs.' },
        { label: 'Answer', text: 'P = 2/7.' },
      ],
      commonTrap:
        "Using the NON-leap-year formula (365 days=52 weeks+1 day, giving 1/7) — a leap year has 2 extra days, not 1, doubling the favorable outcomes.",
      alternateMethod: {
        label: 'Direct listing',
        steps: [
          'The 7 extra-day pairs: (Mon,Tue), (Tue,Wed), (Wed,Thu), (Thu,Fri), (Fri,Sat), (Sat,Sun), (Sun,Mon).',
          'Exactly 2 of these 7 contain "Sun". P = 2/7.',
        ],
      },
    },
    {
      id: 'prob-18',
      difficulty: 'Medium',
      patternTags: ['TCS', 'Infosys'],
      question: "The letters of the word 'TABLE' are arranged at random. Find the probability that the vowels (A and E) always come together.",
      options: ['1/5', '2/5', '3/5', '1/10'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: 'TABLE (5 distinct letters, vowels A, E).' },
        { label: 'Concept', text: 'P = favorable arrangements / total arrangements; use the "treat as one block" technique to count the favorable case.' },
        { label: 'Working', text: 'Total = 5! = 120. Favorable (vowels together) = 4! × 2! = 48.' },
        { label: 'Answer', text: 'P = 48/120 = 2/5.' },
      ],
      commonTrap:
        "Forgetting the ×2! for the internal arrangement of the vowel block (using 4!=24 instead of 48), which would incorrectly give 1/5.",
      alternateMethod: {
        label: 'Position-choice method',
        steps: [
          'Choosing 2 of the 5 positions for the vowels: ⁵C₂=10 ways, of which 4 are adjacent pairs (1-2,2-3,3-4,4-5).',
          'P(positions adjacent) = 4/10 = 2/5 — matches.',
        ],
      },
    },
    {
      id: 'prob-19',
      difficulty: 'Medium',
      patternTags: ['Wipro', 'Zoho'],
      question: "Two dice are thrown together. Find the probability that the numbers shown differ by 2.",
      options: ['1/6', '1/9', '2/9', '5/18'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: 'Two dice, find P(|die1 − die2| = 2).' },
        { label: 'Concept', text: 'List all ordered pairs where the difference equals 2, out of 36 total outcomes.' },
        { label: 'Working', text: 'Pairs: (1,3),(3,1),(2,4),(4,2),(3,5),(5,3),(4,6),(6,4) = 8 pairs.' },
        { label: 'Answer', text: 'P = 8/36 = 2/9.' },
      ],
      commonTrap:
        "Counting each unordered pair only ONCE (forgetting (1,3) and (3,1) are different outcomes on two distinguishable dice), giving 4/36=1/9 instead of the correct 8/36.",
      alternateMethod: {
        label: 'Systematic method',
        steps: [
          'die1 ∈ {1,2,3,4} paired with die1+2 gives 4 pairs, PLUS die1 ∈ {3,4,5,6} paired with die1−2 gives 4 more = 8 ordered pairs.',
          'P = 8/36 = 2/9.',
        ],
      },
    },
    {
      id: 'prob-20',
      difficulty: 'Hard',
      patternTags: ['TCS', 'Zoho'],
      question: "A committee of 3 is to be formed from 5 men and 4 women. Find the probability that the committee consists of exactly 2 men and 1 woman.",
      options: ['5/12', '2/7', '1/3', '10/21'],
      correctIndex: 3,
      steps: [
        { label: 'Given', text: '5 men, 4 women (9 total); form a 3-person committee, exactly 2 men + 1 woman.' },
        { label: 'Concept', text: 'Total ways = ⁹C₃ (from all 9 people). Favorable = ⁵C₂ × ⁴C₁ (choose the men and women groups separately).' },
        { label: 'Working', text: 'Total = 84. Favorable = 10×4 = 40.' },
        { label: 'Answer', text: 'P = 40/84 = 10/21.' },
      ],
      commonTrap:
        "Computing the total as if selecting from only one gender group, or mixing up which combination count represents 'total' versus 'favorable' — the total must always come from ALL 9 people.",
      alternateMethod: {
        label: 'Simplification check',
        steps: [
          '40/84 shares a common factor of 4 with both terms: 40÷4=10, 84÷4=21.',
          'P = 10/21.',
        ],
      },
    },
    {
      id: 'prob-21',
      difficulty: 'Easy',
      patternTags: ['Infosys', 'Wipro'],
      question: "If P(A) = 0.4, P(B) = 0.5, and A and B are independent events, find P(A and B).",
      options: ['0.90', '0.20', '0.10', '0.45'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: 'P(A)=0.4, P(B)=0.5, A and B independent.' },
        { label: 'Concept', text: 'For INDEPENDENT events, P(A and B) = P(A) × P(B) — never add, and never apply a dependence adjustment.' },
        { label: 'Working', text: '0.4 × 0.5.' },
        { label: 'Answer', text: '= 0.20.' },
      ],
      commonTrap:
        "Adding the probabilities instead of multiplying (0.4+0.5=0.9) — that's 'or' (union) territory, not the 'and' (intersection) formula for independent events.",
      alternateMethod: {
        label: 'Percentage framing',
        steps: [
          '40% chance of A, and independently 50% chance of B — of that 40%, exactly 50% would also have B: 40%×50%=20%=0.20.',
        ],
      },
    },
    {
      id: 'prob-22',
      difficulty: 'Hard',
      patternTags: ['TCS', 'Infosys'],
      question: "Urn A contains 3 red and 2 blue balls. Urn B contains 2 red and 3 blue balls. An urn is chosen at random, and then a ball is drawn from it. Find the probability that the ball drawn is red.",
      options: ['3/5', '1/2', '2/5', '1/4'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: 'Urn A (3R,2B), Urn B (2R,3B); pick an urn at random (50-50), then a ball.' },
        { label: 'Concept', text: 'Use the LAW OF TOTAL PROBABILITY: P(red) = P(A)×P(red|A) + P(B)×P(red|B) — sum over each possible path, weighted by its own probability.' },
        { label: 'Working', text: '1/2 × 3/5 + 1/2 × 2/5 = 3/10 + 2/10.' },
        { label: 'Answer', text: '= 5/10 = 1/2.' },
      ],
      commonTrap:
        "Pooling the two urns directly ((3+2 red)/(5+5 total)=1/2) — this happens to match here only because both urns are equal-sized and equally likely to be chosen; with unequal urn sizes or selection odds, this shortcut would give the wrong answer, unlike the properly weighted total-probability formula.",
      alternateMethod: {
        label: 'Probability tree',
        steps: [
          'Branch 1 (choose A, prob 1/2) → red with prob 3/5 → combined 3/10.',
          'Branch 2 (choose B, prob 1/2) → red with prob 2/5 → combined 2/10.',
          'Sum = 5/10 = 1/2.',
        ],
      },
    },
    {
      id: 'prob-23',
      difficulty: 'Medium',
      patternTags: ['Wipro', 'Zoho'],
      question: "Four coins are tossed together. Find the probability of getting at most 1 head.",
      options: ['1/16', '4/16', '5/16', '6/16'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: '4 coins tossed together; "at most 1 head" means 0 or 1 heads.' },
        { label: 'Concept', text: '"At most 1" sums the mutually exclusive cases for 0 heads AND 1 head.' },
        { label: 'Working', text: '0 heads = ⁴C₀=1 (TTTT). 1 head = ⁴C₁=4. Total favorable = 5.' },
        { label: 'Answer', text: 'P = 5/16.' },
      ],
      commonTrap:
        "Interpreting 'at most 1' as ONLY 'exactly 1' and forgetting the '0 heads' case, giving 4/16=1/4 instead of the correct 5/16.",
      alternateMethod: {
        label: 'Complement method',
        steps: [
          'P(2+ heads): 2 heads=⁴C₂=6, 3 heads=⁴C₃=4, 4 heads=⁴C₄=1. Sum=11. P(2+)=11/16.',
          'P(at most 1) = 1 − 11/16 = 5/16.',
        ],
      },
    },
    {
      id: 'prob-24',
      difficulty: 'Easy',
      patternTags: ['TCS', 'Zoho'],
      question: "A number is selected at random from the first 30 natural numbers. Find the probability that it is a prime number.",
      options: ['3/10', '2/5', '1/2', '1/3'],
      correctIndex: 3,
      steps: [
        { label: 'Given', text: 'Numbers 1–30, find P(prime).' },
        { label: 'Concept', text: 'Primes must be counted directly by identification — there is no shortcut formula.' },
        { label: 'Working', text: 'Primes: 2,3,5,7,11,13,17,19,23,29 = 10 numbers.' },
        { label: 'Answer', text: 'P = 10/30 = 1/3.' },
      ],
      commonTrap:
        "Mistakenly including 1 as a prime number — 1 is NOT prime by definition (a prime must have exactly two distinct positive divisors), a very common error.",
      alternateMethod: {
        label: 'Verification by elimination',
        steps: [
          '30 numbers total, minus 1 (neither prime nor composite), minus non-prime evens (4,6,...,30 = 14 numbers, since 2 is the only even prime), minus remaining odd composites (9,15,21,25,27 = 5).',
          '30−1−14−5=10 primes — confirms the count.',
        ],
      },
    },
    {
      id: 'prob-25',
      difficulty: 'Hard',
      patternTags: ['Infosys', 'Wipro'],
      question: "The probability that A can solve a problem is 1/3, and the probability that B can solve it is 1/4. If both attempt the problem independently, find the probability that the problem gets solved (at least one of them solves it).",
      options: ['7/12', '1/12', '5/12', '1/2'],
      correctIndex: 3,
      steps: [
        { label: 'Given', text: 'P(A solves)=1/3, P(B solves)=1/4, independent attempts.' },
        { label: 'Concept', text: '"At least one solves" is best found via the complement: 1 − P(neither solves).' },
        { label: 'Working', text: 'P(A fails)=2/3. P(B fails)=3/4. P(both fail)=2/3×3/4=6/12=1/2.' },
        { label: 'Answer', text: 'P(at least one solves) = 1 − 1/2 = 1/2.' },
      ],
      commonTrap:
        "Simply adding the two probabilities (1/3+1/4=7/12) — this double-counts the scenario where BOTH solve it, and isn't the correct way to combine independent 'at least one' probabilities.",
      alternateMethod: {
        label: 'Direct inclusion-exclusion',
        steps: [
          'P(A or B) = P(A)+P(B)−P(A and B) = 1/3+1/4−(1/3×1/4) = 4/12+3/12−1/12 = 6/12 = 1/2.',
        ],
      },
    },
  ],
  mensuration: [
    {
      id: 'mens-01',
      difficulty: 'Easy',
      patternTags: ['TCS', 'Infosys'],
      question: "Find the area of a square whose side is 15 cm.",
      options: ['225', '240', '255', '270'],
      correctIndex: 0,
      steps: [
        { label: 'Given', text: 'Side = 15 cm.' },
        { label: 'Concept', text: 'Area of a square = side².' },
        { label: 'Working', text: '15².' },
        { label: 'Answer', text: '= 225 cm².' },
      ],
      commonTrap:
        "Computing the perimeter instead (4×15=60) and confusing it with area — perimeter is a boundary measure, area is a surface measure.",
      alternateMethod: {
        label: 'Grid verification',
        steps: [
          'A 15×15 grid of unit squares contains exactly 225 unit squares — matches.',
        ],
      },
    },
    {
      id: 'mens-02',
      difficulty: 'Easy',
      patternTags: ['TCS', 'Wipro'],
      question: "Find the area of a rectangle with length 18 cm and breadth 12 cm.",
      options: ['206', '216', '226', '236'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: 'Length = 18 cm, Breadth = 12 cm.' },
        { label: 'Concept', text: 'Area of a rectangle = length × breadth.' },
        { label: 'Working', text: '18 × 12.' },
        { label: 'Answer', text: '= 216 cm².' },
      ],
      commonTrap:
        "Using the perimeter formula 2(l+b)=60 instead of l×b — perimeter and area answer different questions entirely.",
      alternateMethod: {
        label: 'Split multiplication',
        steps: [
          '18×12 = 18×10 + 18×2 = 180+36 = 216.',
        ],
      },
    },
    {
      id: 'mens-03',
      difficulty: 'Easy',
      patternTags: ['Infosys', 'Zoho'],
      question: "Find the area of a triangle with base 24 cm and height 16 cm.",
      options: ['168', '180', '192', '204'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: 'Base = 24 cm, Height = 16 cm.' },
        { label: 'Concept', text: 'Area of a triangle = (1/2) × base × height.' },
        { label: 'Working', text: '(1/2) × 24 × 16.' },
        { label: 'Answer', text: '= 192 cm².' },
      ],
      commonTrap:
        "Forgetting the 1/2 factor (24×16=384) — a triangle is exactly HALF of the rectangle formed by its base and height.",
      alternateMethod: {
        label: 'Rectangle-half method',
        steps: [
          'A 24×16 rectangle has area 384. The triangle is exactly half of it: 384/2=192.',
        ],
      },
    },
    {
      id: 'mens-04',
      difficulty: 'Hard',
      patternTags: ['TCS', 'Zoho'],
      question: "Find the area of a triangle whose sides are 13 cm, 14 cm and 15 cm.",
      options: ['72', '76', '80', '84'],
      correctIndex: 3,
      steps: [
        { label: 'Given', text: 'Sides a=13, b=14, c=15.' },
        { label: 'Concept', text: "Heron's formula: s = (a+b+c)/2, Area = √(s(s−a)(s−b)(s−c))." },
        { label: 'Working', text: 's = 21. Area = √(21×8×7×6) = √7,056.' },
        { label: 'Answer', text: '= 84 cm².' },
      ],
      commonTrap:
        "Using s = a+b+c (=42) instead of the HALF-sum (=21) — 's' in Heron's formula is the semi-perimeter, not the full perimeter.",
      alternateMethod: {
        label: 'Altitude-split method',
        steps: [
          'Dropping the altitude to the side of length 14 splits it into segments 5 and 9, with height 12 (forming a 5-12-13 and a 9-12-15 right triangle pair).',
          'Area = (1/2)×14×12 = 84 — matches.',
        ],
      },
    },
    {
      id: 'mens-05',
      difficulty: 'Easy',
      patternTags: ['Wipro', 'Zoho'],
      question: "Find the area of a circle with radius 14 cm. (Use π = 22/7)",
      options: ['616', '630', '644', '658'],
      correctIndex: 0,
      steps: [
        { label: 'Given', text: 'Radius = 14 cm.' },
        { label: 'Concept', text: 'Area of a circle = πr².' },
        { label: 'Working', text: '(22/7) × 14².' },
        { label: 'Answer', text: '= (22/7)×196 = 616 cm².' },
      ],
      commonTrap:
        "Mistaking the given value for the diameter (using radius=7 instead of 14) — always confirm whether the given length is a radius or a diameter before squaring.",
      alternateMethod: {
        label: 'Step-by-step division',
        steps: [
          '196/7 = 28. 28×22 = 616.',
        ],
      },
    },
    {
      id: 'mens-06',
      difficulty: 'Easy',
      patternTags: ['TCS', 'Infosys'],
      question: "Find the circumference of a circle with diameter 21 cm. (Use π = 22/7)",
      options: ['60', '66', '70', '74'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: 'Diameter = 21 cm.' },
        { label: 'Concept', text: 'Circumference = πd (or equivalently 2πr, using r = d/2).' },
        { label: 'Working', text: '(22/7) × 21.' },
        { label: 'Answer', text: '= 66 cm.' },
      ],
      commonTrap:
        "Plugging the diameter directly into 2πr without halving it first (2×22/7×21=132) — the 2πr formula needs the RADIUS, not the diameter.",
      alternateMethod: {
        label: 'Radius-based check',
        steps: [
          'r = 10.5. C = 2×(22/7)×10.5 = 2×33 = 66 — matches.',
        ],
      },
    },
    {
      id: 'mens-07',
      difficulty: 'Easy',
      patternTags: ['Wipro', 'Zoho'],
      question: "Find the area of a parallelogram with base 20 cm and height 9 cm.",
      options: ['160', '170', '180', '190'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: 'Base = 20 cm, Height = 9 cm.' },
        { label: 'Concept', text: 'Area of a parallelogram = base × height (no 1/2 factor, unlike a triangle).' },
        { label: 'Working', text: '20 × 9.' },
        { label: 'Answer', text: '= 180 cm².' },
      ],
      commonTrap:
        "Applying the triangle formula (halving to get 90) by confusing a parallelogram with a triangle — a parallelogram is a full base×height area, not half of it.",
      alternateMethod: {
        label: 'Two-triangle method',
        steps: [
          'A parallelogram splits into 2 congruent triangles, each with area (1/2)×20×9=90.',
          'Total = 2×90 = 180.',
        ],
      },
    },
    {
      id: 'mens-08',
      difficulty: 'Medium',
      patternTags: ['TCS', 'Zoho'],
      question: "Find the area of a trapezium with parallel sides 15 cm and 25 cm, and height 12 cm.",
      options: ['210', '220', '230', '240'],
      correctIndex: 3,
      steps: [
        { label: 'Given', text: 'Parallel sides = 15cm, 25cm. Height = 12cm.' },
        { label: 'Concept', text: 'Area of a trapezium = (1/2) × (sum of parallel sides) × height.' },
        { label: 'Working', text: '(1/2) × (15+25) × 12 = (1/2)×40×12.' },
        { label: 'Answer', text: '= 240 cm².' },
      ],
      commonTrap:
        "Forgetting the 1/2 factor ((15+25)×12=480) — exactly double the correct area.",
      alternateMethod: {
        label: 'Rectangle + triangle split',
        steps: [
          'Split into a 15×12 rectangle (area 180) plus a triangle with base 10 and height 12 (area 60).',
          'Total = 180+60 = 240.',
        ],
      },
    },
    {
      id: 'mens-09',
      difficulty: 'Medium',
      patternTags: ['Infosys', 'Wipro'],
      question: "Find the area of a rhombus whose diagonals are 16 cm and 24 cm.",
      options: ['192', '204', '216', '228'],
      correctIndex: 0,
      steps: [
        { label: 'Given', text: 'Diagonals d1=16cm, d2=24cm.' },
        { label: 'Concept', text: 'Area of a rhombus = (1/2) × d1 × d2.' },
        { label: 'Working', text: '(1/2) × 16 × 24.' },
        { label: 'Answer', text: '= 192 cm².' },
      ],
      commonTrap:
        "Multiplying the diagonals directly without the 1/2 factor (16×24=384) — exactly double the correct area.",
      alternateMethod: {
        label: 'Four-triangle method',
        steps: [
          "A rhombus's diagonals split it into 4 congruent right triangles, each with legs 8 and 12: area = (1/2)(8)(12)=48.",
          'Total = 4×48 = 192.',
        ],
      },
    },
    {
      id: 'mens-10',
      difficulty: 'Medium',
      patternTags: ['TCS', 'Infosys'],
      question: "The perimeter of a rectangle is 46 cm and its area is 120 cm². Find the length of the rectangle (the longer side).",
      options: ['12', '15', '18', '20'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: 'Perimeter=46cm, Area=120cm².' },
        { label: 'Concept', text: 'l+b=23 (half the perimeter), l×b=120; the two dimensions are the roots of t²−23t+120=0.' },
        { label: 'Working', text: '(t−15)(t−8) = 0.' },
        { label: 'Answer', text: 'Length (longer side) = 15cm, Breadth = 8cm.' },
      ],
      commonTrap:
        "Guessing a dimension pair that multiplies to 120 without checking it also satisfies the perimeter condition — both conditions (sum AND product) must hold simultaneously.",
      alternateMethod: {
        label: 'Verification',
        steps: [
          '15×8=120 ✓. 2(15+8)=46 ✓ — matches both given conditions.',
        ],
      },
    },
    {
      id: 'mens-11',
      difficulty: 'Easy',
      patternTags: ['Wipro', 'Zoho'],
      question: "Find the volume of a cube whose edge is 9 cm.",
      options: ['639', '689', '729', '769'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: 'Edge = 9 cm.' },
        { label: 'Concept', text: 'Volume of a cube = edge³.' },
        { label: 'Working', text: '9³.' },
        { label: 'Answer', text: '= 729 cm³.' },
      ],
      commonTrap:
        "Squaring instead of cubing (9²=81) or computing the surface area formula (6×9²=486) instead of volume.",
      alternateMethod: {
        label: 'Step-by-step',
        steps: [
          '9×9=81. 81×9=729.',
        ],
      },
    },
    {
      id: 'mens-12',
      difficulty: 'Medium',
      patternTags: ['TCS', 'Zoho'],
      question: "Find the volume of a cuboid with length 12 cm, breadth 8 cm and height 6 cm.",
      options: ['516', '536', '556', '576'],
      correctIndex: 3,
      steps: [
        { label: 'Given', text: 'Length=12, Breadth=8, Height=6.' },
        { label: 'Concept', text: 'Volume of a cuboid = length × breadth × height.' },
        { label: 'Working', text: '12 × 8 × 6.' },
        { label: 'Answer', text: '= 576 cm³.' },
      ],
      commonTrap:
        "Computing the surface area formula (2(lb+bh+hl)=432) instead of volume — surface area and volume measure fundamentally different things.",
      alternateMethod: {
        label: 'Step-by-step',
        steps: [
          '12×8=96. 96×6=576.',
        ],
      },
    },
    {
      id: 'mens-13',
      difficulty: 'Medium',
      patternTags: ['Infosys', 'Wipro'],
      question: "Find the volume of a cylinder with radius 7 cm and height 10 cm. (Use π = 22/7)",
      options: ['1,540', '1,580', '1,620', '1,660'],
      correctIndex: 0,
      steps: [
        { label: 'Given', text: 'Radius = 7cm, Height = 10cm.' },
        { label: 'Concept', text: 'Volume of a cylinder = πr²h.' },
        { label: 'Working', text: '(22/7) × 49 × 10.' },
        { label: 'Answer', text: '= 1,540 cm³.' },
      ],
      commonTrap:
        "Forgetting to square the radius (mistakenly computing πrh instead of πr²h), which gives a much smaller, incorrect volume.",
      alternateMethod: {
        label: 'Step-by-step division',
        steps: [
          '49/7=7. 7×22=154. 154×10=1,540.',
        ],
      },
    },
    {
      id: 'mens-14',
      difficulty: 'Hard',
      patternTags: ['TCS', 'Zoho'],
      question: "Find the volume of a sphere with radius 21 cm. (Use π = 22/7)",
      options: ['37,000', '38,808', '40,000', '41,500'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: 'Radius = 21cm.' },
        { label: 'Concept', text: 'Volume of a sphere = (4/3)πr³.' },
        { label: 'Working', text: 'r³ = 9,261. (4/3) × (22/7) × 9,261.' },
        { label: 'Answer', text: '= 38,808 cm³.' },
      ],
      commonTrap:
        "Using r² instead of r³, or misplacing the (4/3) factor — sphere volume needs the CUBE of the radius, unlike circle area which uses the square.",
      alternateMethod: {
        label: 'Step-by-step division',
        steps: [
          '9,261/7=1,323 (dividing by 7 first, since 21 is divisible by 7).',
          '1,323×22=29,106. 29,106×4=116,424. 116,424/3=38,808.',
        ],
      },
    },
    {
      id: 'mens-15',
      difficulty: 'Medium',
      patternTags: ['Wipro', 'Zoho'],
      question: "Find the volume of a cone with radius 9 cm and height 14 cm. (Use π = 22/7)",
      options: ['1,088', '1,148', '1,188', '1,220'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: 'Radius=9cm, Height=14cm.' },
        { label: 'Concept', text: 'Volume of a cone = (1/3)πr²h.' },
        { label: 'Working', text: '(1/3) × (22/7) × 81 × 14.' },
        { label: 'Answer', text: '= 1,188 cm³.' },
      ],
      commonTrap:
        "Forgetting the 1/3 factor and using the CYLINDER formula instead — a cone's volume is always exactly 1/3 of a cylinder with the same radius and height.",
      alternateMethod: {
        label: 'Cylinder-fraction method',
        steps: [
          'Cylinder volume with same r,h = πr²h = (22/7)×81×14 = 3,564.',
          'Cone volume = 3,564/3 = 1,188.',
        ],
      },
    },
    {
      id: 'mens-16',
      difficulty: 'Easy',
      patternTags: ['TCS', 'Infosys'],
      question: "Find the total surface area of a cube whose edge is 11 cm.",
      options: ['666', '696', '716', '726'],
      correctIndex: 3,
      steps: [
        { label: 'Given', text: 'Edge = 11 cm.' },
        { label: 'Concept', text: 'Total surface area of a cube = 6 × (edge)² — 6 identical square faces.' },
        { label: 'Working', text: '6 × 11².' },
        { label: 'Answer', text: '= 6×121 = 726 cm².' },
      ],
      commonTrap:
        "Computing the volume instead (11³=1,331) or forgetting to multiply by 6 (leaving just 121, the area of ONE face).",
      alternateMethod: {
        label: 'Per-face method',
        steps: [
          'Each face = 121 cm². 6 faces = 6×121 = 726 cm².',
        ],
      },
    },
    {
      id: 'mens-17',
      difficulty: 'Medium',
      patternTags: ['Infosys', 'Wipro'],
      question: "Find the total surface area of a cylinder with radius 7 cm and height 10 cm. (Use π = 22/7)",
      options: ['748', '764', '780', '796'],
      correctIndex: 0,
      steps: [
        { label: 'Given', text: 'Radius=7cm, Height=10cm.' },
        { label: 'Concept', text: 'Total surface area of a cylinder = 2πr(h+r) — the curved surface PLUS both circular end caps.' },
        { label: 'Working', text: '2 × (22/7) × 7 × (10+7) = 2×22×17.' },
        { label: 'Answer', text: '= 748 cm².' },
      ],
      commonTrap:
        "Using only the CURVED surface area formula (2πrh=440) and forgetting to add the two circular end caps (2πr²=308) — 440 alone is not the TOTAL surface area.",
      alternateMethod: {
        label: 'Separate components',
        steps: [
          'Curved surface = 2πrh = 440. Two end caps = 2πr² = 308.',
          'Total = 440+308 = 748.',
        ],
      },
    },
    {
      id: 'mens-18',
      difficulty: 'Medium',
      patternTags: ['TCS', 'Zoho'],
      question: "Find the curved surface area of a cone with radius 7 cm and slant height 25 cm. (Use π = 22/7)",
      options: ['520', '550', '565', '580'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: 'Radius=7cm, Slant height=25cm.' },
        { label: 'Concept', text: 'Curved surface area of a cone = πrl (radius × SLANT height, not vertical height).' },
        { label: 'Working', text: '(22/7) × 7 × 25.' },
        { label: 'Answer', text: '= 22×25 = 550 cm².' },
      ],
      commonTrap:
        "Confusing slant height with vertical height, or mistakenly squaring the radius — the CSA formula uses r×l directly, not r².",
      alternateMethod: {
        label: 'Total-surface-area check',
        steps: [
          'TSA = πr(l+r) = (22/7)×7×32 = 704. Base area = πr² = (22/7)×49 = 154.',
          'CSA = TSA − base = 704−154 = 550 — matches.',
        ],
      },
    },
    {
      id: 'mens-19',
      difficulty: 'Medium',
      patternTags: ['Infosys', 'Wipro'],
      question: "Find the area of a circular ring whose outer and inner radii are 14 cm and 7 cm respectively. (Use π = 22/7)",
      options: ['432', '448', '462', '478'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: 'Outer radius R=14cm, Inner radius r=7cm.' },
        { label: 'Concept', text: 'Area of a ring = π(R² − r²) — the difference of the SQUARES, not the square of the difference.' },
        { label: 'Working', text: '(22/7) × (196 − 49) = (22/7) × 147.' },
        { label: 'Answer', text: '= 22×21 = 462 cm².' },
      ],
      commonTrap:
        "Computing π(R−r)² instead of π(R²−r²) — squaring the difference of radii instead of taking the difference of the squares, a common algebra slip.",
      alternateMethod: {
        label: 'Separate circles method',
        steps: [
          'Outer circle area = (22/7)×196 = 616. Inner circle area = (22/7)×49 = 154.',
          'Ring area = 616−154 = 462.',
        ],
      },
    },
    {
      id: 'mens-20',
      difficulty: 'Easy',
      patternTags: ['TCS', 'Infosys'],
      question: "Find the length of the diagonal of a rectangle with length 16 cm and breadth 12 cm.",
      options: ['14', '16', '18', '20'],
      correctIndex: 3,
      steps: [
        { label: 'Given', text: 'Length=16cm, Breadth=12cm.' },
        { label: 'Concept', text: "The diagonal, length and breadth form a right triangle — use the Pythagorean theorem." },
        { label: 'Working', text: '√(16²+12²) = √(256+144) = √400.' },
        { label: 'Answer', text: '= 20 cm.' },
      ],
      commonTrap:
        "Adding length and breadth directly (16+12=28) instead of applying the Pythagorean theorem — the diagonal is shorter than the sum of the two sides.",
      alternateMethod: {
        label: 'Scaled triple recognition',
        steps: [
          '12-16-20 is a 3-4-5 right triangle scaled by 4 (3×4=12, 4×4=16, 5×4=20) — confirms the diagonal is 20 without computing the square root directly.',
        ],
      },
    },
    {
      id: 'mens-21',
      difficulty: 'Medium',
      patternTags: ['Wipro', 'Zoho'],
      question: "Find the area of a sector of a circle with radius 21 cm and a central angle of 60°. (Use π = 22/7)",
      options: ['231', '242', '253', '264'],
      correctIndex: 0,
      steps: [
        { label: 'Given', text: 'Radius=21cm, central angle=60°.' },
        { label: 'Concept', text: 'Sector area = (θ/360°) × πr² — the sector is a fraction of the full circle, proportional to its angle.' },
        { label: 'Working', text: 'θ/360 = 60/360 = 1/6. πr² = (22/7)×441 = 1,386.' },
        { label: 'Answer', text: '1,386 × (1/6) = 231 cm².' },
      ],
      commonTrap:
        "Forgetting to take the (θ/360) fraction and computing the FULL circle's area (1,386) instead of just the sector's share.",
      alternateMethod: {
        label: 'Simplify first',
        steps: [
          '60/360 = 1/6. r²=441, 441/7=63, 63×22=1,386, 1,386×(1/6)=231.',
        ],
      },
    },
    {
      id: 'mens-22',
      difficulty: 'Medium',
      patternTags: ['TCS', 'Zoho'],
      question: "Find the length of an arc of a circle with radius 14 cm subtending a central angle of 90°. (Use π = 22/7)",
      options: ['18', '22', '24', '26'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: 'Radius=14cm, central angle=90°.' },
        { label: 'Concept', text: 'Arc length = (θ/360°) × 2πr — a fraction of the full CIRCUMFERENCE, not the area.' },
        { label: 'Working', text: 'θ/360 = 90/360 = 1/4. 2πr = 2×(22/7)×14 = 88.' },
        { label: 'Answer', text: '88 × (1/4) = 22 cm.' },
      ],
      commonTrap:
        "Using the sector-AREA formula (πr²) instead of the arc-LENGTH formula (2πr) — these are related but distinct formulas for different quantities.",
      alternateMethod: {
        label: 'Fraction-of-circumference method',
        steps: [
          'Full circumference = 2πr = 88. A 90° arc is 1/4 of the full circle (since 90/360=1/4).',
          'Arc = 88/4 = 22.',
        ],
      },
    },
    {
      id: 'mens-23',
      difficulty: 'Hard',
      patternTags: ['Infosys', 'Wipro'],
      question: "A metallic sphere of radius 6 cm is melted and recast into a cylinder of radius 4 cm. Find the height of the cylinder.",
      options: ['18', '21', '24', '27'],
      correctIndex: 0,
      steps: [
        { label: 'Given', text: 'Sphere radius=6cm, melted and recast into a cylinder of radius=4cm.' },
        { label: 'Concept', text: 'Melting and recasting conserves VOLUME (the amount of material) — set the two volume formulas equal and solve.' },
        { label: 'Working', text: '(4/3)π(6)³ = π(4)²h → (4/3)(216) = 16h → 288 = 16h.' },
        { label: 'Answer', text: 'h = 288/16 = 18 cm.' },
      ],
      commonTrap:
        "Assuming some other dimension (like diameter) stays the same between the two shapes, or equating SURFACE AREAS instead of volumes — melting conserves the material's volume, not its shape-dependent surface area.",
      alternateMethod: {
        label: 'Direct computation',
        steps: [
          'Sphere volume = (4/3)π(216) = 288π. Cylinder volume = π(16)h = 16πh.',
          'Setting equal: 288π=16πh → h=288/16=18.',
        ],
      },
    },
    {
      id: 'mens-24',
      difficulty: 'Easy',
      patternTags: ['TCS', 'Infosys'],
      question: "Find the cost of fencing a rectangular field of length 60 m and breadth 40 m at ₹15 per metre.",
      options: ['2,700', '2,850', '2,950', '3,000'],
      correctIndex: 3,
      steps: [
        { label: 'Given', text: 'Field 60m × 40m, fencing rate = ₹15/metre.' },
        { label: 'Concept', text: 'Fencing covers the PERIMETER (the boundary), not the area — a length-based cost, not a surface-based one.' },
        { label: 'Working', text: 'Perimeter = 2(60+40) = 200 m.' },
        { label: 'Answer', text: 'Cost = 200 × 15 = ₹3,000.' },
      ],
      commonTrap:
        "Using the AREA (60×40=2,400 m²) instead of the perimeter for a fencing cost — area would be relevant for a FLOORING cost problem instead, not a boundary/fencing one.",
      alternateMethod: {
        label: 'Side-by-side method',
        steps: [
          '2 lengths = 2×60=120m. 2 breadths = 2×40=80m. Total = 200m.',
          'Cost = 200×15 = ₹3,000.',
        ],
      },
    },
    {
      id: 'mens-25',
      difficulty: 'Medium',
      patternTags: ['Wipro', 'Zoho'],
      question: "If the radius of a sphere is doubled, find the ratio of the volume of the new sphere to the volume of the original sphere.",
      options: ['2:1', '4:1', '8:1', '16:1'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: 'Radius doubled (scale factor k=2).' },
        { label: 'Concept', text: 'Volume of a sphere is proportional to r³ — scaling a linear dimension by k scales the VOLUME by k³.' },
        { label: 'Working', text: 'k=2 → volume ratio = 2³.' },
        { label: 'Answer', text: '= 8:1.' },
      ],
      commonTrap:
        "Assuming volume scales the same way as the radius (giving 2:1), or confusing it with how AREA scales (k²=4:1, which applies to surface area, not volume) — length, area, and volume each scale differently with a linear scale factor.",
      alternateMethod: {
        label: 'Assumed-value method',
        steps: [
          'Let original radius=1: volume=(4/3)π(1)³=(4/3)π.',
          'New radius=2: volume=(4/3)π(2)³=(4/3)π×8.',
          'Ratio = 8:1.',
        ],
      },
    },
  ],
  'number-series': [
    {
      id: 'ns-01',
      difficulty: 'Easy',
      patternTags: ['TCS', 'Infosys'],
      question: "Find the next number in the series: 5, 11, 17, 23, 29, ?",
      options: ['35', '36', '37', '39'],
      correctIndex: 0,
      steps: [
        { label: 'Given', text: '5, 11, 17, 23, 29.' },
        { label: 'Concept', text: 'Check consecutive differences: 11−5=6, 17−11=6, 23−17=6, 29−23=6 — a constant difference of 6 (arithmetic progression).' },
        { label: 'Working', text: 'Next = 29 + 6.' },
        { label: 'Answer', text: '= 35.' },
      ],
      commonTrap:
        "Checking only ONE pair of consecutive terms and assuming that difference holds throughout — always confirm the difference is consistent across ALL given terms before extending it.",
      alternateMethod: {
        label: 'Formula method',
        steps: [
          'nth term = a + (n−1)d = 5 + (n−1)(6). For n=6: 5+5×6=35.',
        ],
      },
    },
    {
      id: 'ns-02',
      difficulty: 'Easy',
      patternTags: ['TCS', 'Wipro'],
      question: "Find the next number in the series: 3, 6, 12, 24, 48, ?",
      options: ['90', '96', '102', '108'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: '3, 6, 12, 24, 48.' },
        { label: 'Concept', text: 'Check consecutive ratios: 6/3=2, 12/6=2, 24/12=2, 48/24=2 — a constant ratio of 2 (geometric progression).' },
        { label: 'Working', text: 'Next = 48 × 2.' },
        { label: 'Answer', text: '= 96.' },
      ],
      commonTrap:
        "Treating this as an arithmetic series and adding the last difference (48−24=24) instead of multiplying by the constant ratio — always check ratios first when differences keep growing.",
      alternateMethod: {
        label: 'Formula method',
        steps: [
          'nth term = a × r^(n−1) = 3 × 2⁵ = 3×32 = 96.',
        ],
      },
    },
    {
      id: 'ns-03',
      difficulty: 'Medium',
      patternTags: ['Infosys', 'Zoho'],
      question: "Find the next number in the series: 2, 3, 5, 8, 12, 17, ?",
      options: ['21', '22', '23', '24'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: '2, 3, 5, 8, 12, 17.' },
        { label: 'Concept', text: "The differences themselves form a pattern: 1,2,3,4,5 (increasing by 1 each time) — a second-order arithmetic series." },
        { label: 'Working', text: 'Next difference = 6.' },
        { label: 'Answer', text: '17 + 6 = 23.' },
      ],
      commonTrap:
        "Assuming a CONSTANT difference (like the most recent gap, 5) instead of recognizing the differences themselves are increasing by 1 each step.",
      alternateMethod: {
        label: 'Second-difference method',
        steps: [
          'Differences 1,2,3,4,5 are themselves an AP with common difference 1. The 6th difference = 6.',
          '17+6 = 23.',
        ],
      },
    },
    {
      id: 'ns-04',
      difficulty: 'Medium',
      patternTags: ['TCS', 'Zoho'],
      question: "Find the next number in the series: 100, 95, 85, 70, 50, ?",
      options: ['19', '21', '23', '25'],
      correctIndex: 3,
      steps: [
        { label: 'Given', text: '100, 95, 85, 70, 50.' },
        { label: 'Concept', text: 'The amount SUBTRACTED increases by 5 each time (5,10,15,20) — a second-order pattern.' },
        { label: 'Working', text: 'Next subtraction = 25.' },
        { label: 'Answer', text: '50 − 25 = 25.' },
      ],
      commonTrap:
        "Assuming the subtraction stays constant at the last-seen value (20), giving 50−20=30 — the subtraction itself is growing by 5 each step, not staying fixed.",
      alternateMethod: {
        label: 'Difference verification',
        steps: [
          '100−95=5, 95−85=10, 85−70=15, 70−50=20 — differences increase by 5 each time.',
          'Next difference=25. 50−25=25.',
        ],
      },
    },
    {
      id: 'ns-05',
      difficulty: 'Easy',
      patternTags: ['Wipro', 'Zoho'],
      question: "Find the next number in the series: 1, 4, 9, 16, 25, ?",
      options: ['36', '38', '40', '42'],
      correctIndex: 0,
      steps: [
        { label: 'Given', text: '1, 4, 9, 16, 25.' },
        { label: 'Concept', text: 'These are perfect squares: 1², 2², 3², 4², 5².' },
        { label: 'Working', text: 'Next = 6².' },
        { label: 'Answer', text: '= 36.' },
      ],
      commonTrap:
        "Extending the difference pattern (3,5,7,9 — consecutive odd numbers) mechanically without recognizing WHY it increases by 2 each time — spotting the underlying squares pattern directly is faster and less error-prone.",
      alternateMethod: {
        label: 'Direct pattern',
        steps: [
          'Terms are 1²,2²,3²,4²,5² → next = 6² = 36.',
        ],
      },
    },
    {
      id: 'ns-06',
      difficulty: 'Easy',
      patternTags: ['TCS', 'Infosys'],
      question: "Find the next number in the series: 1, 8, 27, 64, 125, ?",
      options: ['206', '216', '226', '236'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: '1, 8, 27, 64, 125.' },
        { label: 'Concept', text: 'These are perfect cubes: 1³, 2³, 3³, 4³, 5³.' },
        { label: 'Working', text: 'Next = 6³.' },
        { label: 'Answer', text: '= 216.' },
      ],
      commonTrap:
        "Trying to extend the pattern via differences (7,19,37,61 — which need a third level of differencing to resolve) instead of simply recognizing the cubes pattern directly.",
      alternateMethod: {
        label: 'Direct pattern',
        steps: [
          '1³,2³,3³,4³,5³ → next = 6³ = 216.',
        ],
      },
    },
    {
      id: 'ns-07',
      difficulty: 'Medium',
      patternTags: ['Infosys', 'Wipro'],
      question: "Find the next number in the series: 2, 3, 5, 7, 11, 13, ?",
      options: ['15', '16', '17', '19'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: '2, 3, 5, 7, 11, 13 — prime numbers in order.' },
        { label: 'Concept', text: 'Find the next PRIME number after 13, not just the next odd number.' },
        { label: 'Working', text: 'Check 14 (even), 15 (=3×5, composite), 16 (even), 17 (no divisors other than 1 and itself).' },
        { label: 'Answer', text: '17 is the next prime.' },
      ],
      commonTrap:
        "Assuming 15 fits since it 'looks like' the next odd number — 15=3×5 is composite, NOT prime; primality must be verified, not assumed from parity alone.",
      alternateMethod: {
        label: 'Verification',
        steps: [
          '14 is even, 15 is divisible by 3 and 5, 16 is even — 17 is the first number after 13 with no divisors besides 1 and itself.',
        ],
      },
    },
    {
      id: 'ns-08',
      difficulty: 'Medium',
      patternTags: ['TCS', 'Zoho'],
      question: "Find the next number in the series: 2, 3, 5, 8, 13, 21, ?",
      options: ['28', '30', '32', '34'],
      correctIndex: 3,
      steps: [
        { label: 'Given', text: '2, 3, 5, 8, 13, 21.' },
        { label: 'Concept', text: 'Each term equals the SUM of the previous two terms (Fibonacci-style growth).' },
        { label: 'Working', text: 'Verify throughout: 2+3=5, 3+5=8, 5+8=13, 8+13=21 — all confirmed.' },
        { label: 'Answer', text: '13+21 = 34.' },
      ],
      commonTrap:
        "Confusing this with an 'increasing difference' pattern (a different series that also starts 2,3,5,8 but then diverges) — always verify a pattern using ALL given terms, since two different rules can start identically.",
      alternateMethod: {
        label: 'Full verification',
        steps: [
          'Every consecutive triple confirms term(n)=term(n-1)+term(n-2) throughout the given series.',
          'Next = 13+21 = 34.',
        ],
      },
    },
    {
      id: 'ns-09',
      difficulty: 'Hard',
      patternTags: ['Wipro', 'Zoho'],
      question: "Find the next number in the series: 2, 6, 24, 120, 720, ?",
      options: ['5,040', '5,320', '5,600', '5,880'],
      correctIndex: 0,
      steps: [
        { label: 'Given', text: '2, 6, 24, 120, 720.' },
        { label: 'Concept', text: 'Each term is multiplied by an increasing integer (×3, ×4, ×5, ×6, ...) — this is factorial growth (n!).' },
        { label: 'Working', text: 'Ratios: 6/2=3, 24/6=4, 120/24=5, 720/120=6. Next multiplier = 7.' },
        { label: 'Answer', text: '720 × 7 = 5,040.' },
      ],
      commonTrap:
        "Assuming a CONSTANT ratio (like the most recent one, ×6) instead of recognizing the multiplier itself increases by 1 each time.",
      alternateMethod: {
        label: 'Factorial recognition',
        steps: [
          '2=2!, 6=3!, 24=4!, 120=5!, 720=6!. Next = 7! = 5,040.',
        ],
      },
    },
    {
      id: 'ns-10',
      difficulty: 'Hard',
      patternTags: ['TCS', 'Infosys'],
      question: "Find the next number in the series: 3, 8, 6, 11, 9, 14, 12, ?",
      options: ['15', '17', '19', '21'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: '3, 8, 6, 11, 9, 14, 12 (7 terms — find the 8th).' },
        { label: 'Concept', text: 'This is actually TWO interleaved arithmetic series — one at odd positions (3,6,9,12,...), one at even positions (8,11,14,...), each increasing by 3.' },
        { label: 'Working', text: 'Position 8 is EVEN, continuing the even-position series: 8, 11, 14, ?' },
        { label: 'Answer', text: '14 + 3 = 17.' },
      ],
      commonTrap:
        "Trying to find a single consistent difference across ALL consecutive terms (3→8 is +5, 8→6 is −2, inconsistent) — this signals TWO separate interleaved patterns, not one single series.",
      alternateMethod: {
        label: 'Separate the subsequences',
        steps: [
          'Series A (odd positions): 3,6,9,12 (common difference 3). Series B (even positions): 8,11,14 (common difference 3).',
          'The 8th term belongs to Series B: 14+3=17.',
        ],
      },
    },
    {
      id: 'ns-11',
      difficulty: 'Medium',
      patternTags: ['Wipro', 'Zoho'],
      question: "Find the wrong number in the series: 3, 6, 12, 24, 46, 96",
      options: ['12', '24', '46', '96'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: '3, 6, 12, 24, 46, 96.' },
        { label: 'Concept', text: 'Check the pattern (each term should double the previous) and find exactly where it breaks.' },
        { label: 'Working', text: '3×2=6✓, 6×2=12✓, 12×2=24✓, 24×2=48 (given: 46, MISMATCH), 48×2=96 (matches the given last term).' },
        { label: 'Answer', text: '46 is the wrong number — it should be 48.' },
      ],
      commonTrap:
        "Assuming the LAST number (96) is wrong just because it looks large, without verifying each step of the doubling pattern systematically.",
      alternateMethod: {
        label: 'Reverse-check from the end',
        steps: [
          '96/2=48 (expected 5th term), which doesn\'t match the given 46 — confirms 46 is the error, and everything else fits the ×2 pattern.',
        ],
      },
    },
    {
      id: 'ns-12',
      difficulty: 'Medium',
      patternTags: ['TCS', 'Zoho'],
      question: "Find the next number in the series: 2, 5, 10, 17, 26, ?",
      options: ['31', '33', '35', '37'],
      correctIndex: 3,
      steps: [
        { label: 'Given', text: '2, 5, 10, 17, 26.' },
        { label: 'Concept', text: 'Each term equals n²+1 (a perfect square plus a constant): 1+1=2, 4+1=5, 9+1=10, 16+1=17, 25+1=26.' },
        { label: 'Working', text: 'Next: 6²+1.' },
        { label: 'Answer', text: '= 37.' },
      ],
      commonTrap:
        "Guessing a simple constant addition from just the first gap (2 to 5 is +3) and extending it flatly (+3 repeatedly) — the actual differences (3,5,7,9) are increasing by 2, not staying constant.",
      alternateMethod: {
        label: 'Difference-based method',
        steps: [
          'Differences are 3,5,7,9 (each increasing by 2). Next difference=11.',
          '26+11=37 — matches the n²+1 formula.',
        ],
      },
    },
    {
      id: 'ns-13',
      difficulty: 'Hard',
      patternTags: ['Infosys', 'Wipro'],
      question: "Find the wrong number in the series: 5, 6, 14, 44, 184, 925",
      options: ['6', '14', '44', '184'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: '5, 6, 14, 44, 184, 925.' },
        { label: 'Concept', text: 'Each term = (previous term × n) + n, where n increases by 1 each step (×1+1, ×2+2, ×3+3, ...).' },
        { label: 'Working', text: '5×1+1=6✓. 6×2+2=14✓. 14×3+3=45 (given: 44, MISMATCH). 45×4+4=184✓ (matches, confirming the pattern resumes correctly after the error).' },
        { label: 'Answer', text: '44 should be 45 — it is the wrong number.' },
      ],
      commonTrap:
        "Assuming 925 (the largest, most complex-looking number) must be the error just because of its size — always verify the actual multiplication rule step by step rather than guessing from appearance.",
      alternateMethod: {
        label: 'Working backward',
        steps: [
          '925 = 184×5+5 ✓ (matches). 184 = 45×4+4, meaning the term before 184 should be 45, not the given 44 — confirms 44 is the error.',
        ],
      },
    },
    {
      id: 'ns-14',
      difficulty: 'Medium',
      patternTags: ['TCS', 'Infosys'],
      question: "Find the next number in the series: 4, 6, 10, 16, 24, ?",
      options: ['32', '34', '36', '38'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: '4, 6, 10, 16, 24.' },
        { label: 'Concept', text: 'The differences (2,4,6,8) themselves increase by 2 each time — a second-order pattern.' },
        { label: 'Working', text: 'Next difference = 10.' },
        { label: 'Answer', text: '24 + 10 = 34.' },
      ],
      commonTrap:
        "Assuming a constant difference of 8 (the last-seen gap) instead of recognizing it increases by 2 each time.",
      alternateMethod: {
        label: 'Direct formula',
        steps: [
          'term(n) = n²−n+4. Check: n=5→24 ✓. For n=6: 36−6+4=34.',
        ],
      },
    },
    {
      id: 'ns-15',
      difficulty: 'Easy',
      patternTags: ['Wipro', 'Zoho'],
      question: "Find the next number in the series: 960, 480, 240, 120, 60, ?",
      options: ['20', '25', '30', '35'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: '960, 480, 240, 120, 60.' },
        { label: 'Concept', text: 'Each term is divided by 2 (halved) — a geometric, not arithmetic, pattern.' },
        { label: 'Working', text: 'Next = 60 ÷ 2.' },
        { label: 'Answer', text: '= 30.' },
      ],
      commonTrap:
        "Trying a subtraction-based pattern instead of division — the differences (480,240,120,60) aren't a constant AMOUNT, they're halving, signaling a ratio-based (geometric) pattern.",
      alternateMethod: {
        label: 'Ratio check',
        steps: [
          '480/960=0.5, 240/480=0.5 — a consistent ×0.5 (halving) each time.',
          '60×0.5=30.',
        ],
      },
    },
    {
      id: 'ns-16',
      difficulty: 'Hard',
      patternTags: ['TCS', 'Zoho'],
      question: "Find the next number in the series: 4, 9, 19, 39, 79, ?",
      options: ['149', '153', '156', '159'],
      correctIndex: 3,
      steps: [
        { label: 'Given', text: '4, 9, 19, 39, 79.' },
        { label: 'Concept', text: 'Each term = (previous term × 2) + 1.' },
        { label: 'Working', text: 'Verify: 4×2+1=9✓, 9×2+1=19✓, 19×2+1=39✓, 39×2+1=79✓.' },
        { label: 'Answer', text: '79×2+1 = 159.' },
      ],
      commonTrap:
        "Assuming a constant additive difference from just the first gap (9−4=5) and extending it flatly — the actual gaps (5,10,20,40) are DOUBLING each time, not staying constant.",
      alternateMethod: {
        label: 'Doubling-difference check',
        steps: [
          'Differences are 5,10,20,40 (each double the previous). Next difference=80.',
          '79+80=159 — matches the ×2+1 rule.',
        ],
      },
    },
    {
      id: 'ns-17',
      difficulty: 'Medium',
      patternTags: ['Infosys', 'Wipro'],
      question: "Find the next number in the series: 0, 2, 6, 12, 20, 30, ?",
      options: ['42', '44', '46', '48'],
      correctIndex: 0,
      steps: [
        { label: 'Given', text: '0, 2, 6, 12, 20, 30.' },
        { label: 'Concept', text: 'term(n) = n(n−1) — a product-of-consecutive-integers pattern; differences (2,4,6,8,10) increase by 2 each time.' },
        { label: 'Working', text: 'Next difference = 12.' },
        { label: 'Answer', text: '30 + 12 = 42.' },
      ],
      commonTrap:
        "Guessing the difference increases by a different amount than actually observed, instead of confirming the differences themselves increase by exactly 2 each time.",
      alternateMethod: {
        label: 'Direct formula',
        steps: [
          'term(n) = n(n−1). For the 7th term: 7×6=42.',
        ],
      },
    },
    {
      id: 'ns-18',
      difficulty: 'Hard',
      patternTags: ['TCS', 'Zoho'],
      question: "Find the next number in the series: 200, 197, 191, 179, 155, ?",
      options: ['103', '107', '111', '115'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: '200, 197, 191, 179, 155.' },
        { label: 'Concept', text: 'The SUBTRACTED amount doubles each time (3, 6, 12, 24, ...) — a geometric pattern in the differences.' },
        { label: 'Working', text: 'Next subtraction = 48.' },
        { label: 'Answer', text: '155 − 48 = 107.' },
      ],
      commonTrap:
        "Assuming the subtracted amount grows by a constant ADDITION instead of DOUBLING — verify whether the differences themselves follow an arithmetic or geometric pattern before extending.",
      alternateMethod: {
        label: 'Verification',
        steps: [
          '200−197=3, 197−191=6, 191−179=12, 179−155=24 — each difference exactly doubles the previous.',
          'Next=48. 155−48=107.',
        ],
      },
    },
    {
      id: 'ns-19',
      difficulty: 'Medium',
      patternTags: ['Infosys', 'Wipro'],
      question: "Find the wrong number in the series: 1, 4, 9, 16, 24, 36, 49",
      options: ['9', '16', '24', '36'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: '1, 4, 9, 16, 24, 36, 49 — should be consecutive perfect squares (1² through 7²).' },
        { label: 'Concept', text: 'Check every term against n²: 1²=1, 2²=4, 3²=9, 4²=16, 5²=25, 6²=36, 7²=49.' },
        { label: 'Working', text: 'Given 5th term is 24, but 5²=25 — a MISMATCH.' },
        { label: 'Answer', text: '24 should be 25 — it is the wrong number.' },
      ],
      commonTrap:
        "Assuming the error must be near the very start or end of the series, rather than systematically checking EVERY term against its expected square value.",
      alternateMethod: {
        label: 'Difference check',
        steps: [
          'Expected square differences are consecutive odd numbers: 3,5,7,9,11,13. Given differences show a break right at the 16→24 step (8 instead of 9) — confirms the error sits at "24".',
        ],
      },
    },
    {
      id: 'ns-20',
      difficulty: 'Medium',
      patternTags: ['TCS', 'Infosys'],
      question: "Find the wrong number in the series: 2, 6, 18, 54, 160, 486",
      options: ['6', '18', '54', '160'],
      correctIndex: 3,
      steps: [
        { label: 'Given', text: '2, 6, 18, 54, 160, 486.' },
        { label: 'Concept', text: 'Check the pattern (each term should be ×3 of the previous) and find where it breaks.' },
        { label: 'Working', text: '2×3=6✓, 6×3=18✓, 18×3=54✓, 54×3=162 (given: 160, MISMATCH), 162×3=486 (matches the given last term).' },
        { label: 'Answer', text: '160 should be 162 — it is the wrong number.' },
      ],
      commonTrap:
        "Assuming the pattern breaks at 486 (the final, largest number) rather than checking each ratio systematically from the start.",
      alternateMethod: {
        label: 'Reverse-check from the end',
        steps: [
          '486/3=162 (expected 5th term), which doesn\'t match the given 160 — confirms 160 is the error.',
        ],
      },
    },
    {
      id: 'ns-21',
      difficulty: 'Hard',
      patternTags: ['Wipro', 'Zoho'],
      question: "Find the next number in the series: 2, 5, 14, 41, 122, ?",
      options: ['365', '370', '375', '380'],
      correctIndex: 0,
      steps: [
        { label: 'Given', text: '2, 5, 14, 41, 122.' },
        { label: 'Concept', text: 'Each term = (previous term × 3) − 1 — a combined multiply-then-subtract rule.' },
        { label: 'Working', text: 'Verify: 2×3−1=5✓, 5×3−1=14✓, 14×3−1=41✓, 41×3−1=122✓.' },
        { label: 'Answer', text: '122×3−1 = 365.' },
      ],
      commonTrap:
        "Trying a pure multiplication-only pattern (×3 exactly, ignoring the '−1' adjustment): 2×3=6≠5 — confirms the '−1' step is essential and must be checked at every step.",
      alternateMethod: {
        label: 'Difference check',
        steps: [
          'Differences are 3,9,27,81 (each ×3 — geometric, consistent with the recursive rule). Next difference=243.',
          '122+243=365 — matches.',
        ],
      },
    },
    {
      id: 'ns-22',
      difficulty: 'Hard',
      patternTags: ['TCS', 'Zoho'],
      question: "Find the next number in the series: 10, 16, 13, 19, 16, 22, ?",
      options: ['17', '19', '21', '23'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: '10, 16, 13, 19, 16, 22.' },
        { label: 'Concept', text: 'The series alternates between two fixed operations: +6, then −3, repeating.' },
        { label: 'Working', text: 'Verify: 10+6=16✓, 16−3=13✓, 13+6=19✓, 19−3=16✓, 16+6=22✓.' },
        { label: 'Answer', text: 'Next operation (following +6) is −3: 22−3=19.' },
      ],
      commonTrap:
        "Treating this as a single overall trend and trying to average the net change, instead of recognizing the alternating +6/−3 two-step pattern.",
      alternateMethod: {
        label: 'Interleaved-AP view',
        steps: [
          'Odd positions (10,13,16) and even positions (16,19,22) each independently increase by 3.',
          'Position 7 (odd) continues 10,13,16,? → 19 — matches.',
        ],
      },
    },
    {
      id: 'ns-23',
      difficulty: 'Medium',
      patternTags: ['Infosys', 'Wipro'],
      question: "Find the wrong number in the series: 1, 8, 28, 64, 125, 216",
      options: ['8', '28', '64', '125'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: '1, 8, 28, 64, 125, 216 — should be consecutive cubes (1³ through 6³).' },
        { label: 'Concept', text: 'Check every term against n³: 1³=1, 2³=8, 3³=27, 4³=64, 5³=125, 6³=216.' },
        { label: 'Working', text: 'Given 3rd term is 28, but 3³=27 — a MISMATCH.' },
        { label: 'Answer', text: '28 should be 27 — it is the wrong number.' },
      ],
      commonTrap:
        "Assuming the discrepancy must be a large arithmetic error rather than checking for a small ±1 mismatch — the wrong number here is off by just 1 from the correct value.",
      alternateMethod: {
        label: 'Difference check',
        steps: [
          'Expected cube differences are 7,19,37,61,91. Given differences show small ±1 discrepancies right around the "28" term — confirms it as the error.',
        ],
      },
    },
    {
      id: 'ns-24',
      difficulty: 'Medium',
      patternTags: ['TCS', 'Infosys'],
      question: "Find the next number in the series: 1, 3, 6, 10, 15, 21, ?",
      options: ['22', '24', '26', '28'],
      correctIndex: 3,
      steps: [
        { label: 'Given', text: '1, 3, 6, 10, 15, 21 — triangular numbers.' },
        { label: 'Concept', text: 'term(n) = n(n+1)/2; differences increase by 1 each time (2,3,4,5,6).' },
        { label: 'Working', text: 'Next difference = 7.' },
        { label: 'Answer', text: '21 + 7 = 28.' },
      ],
      commonTrap:
        "Assuming a constant difference (like the most recent gap of 6) instead of recognizing the differences themselves increase by exactly 1 each time.",
      alternateMethod: {
        label: 'Direct formula',
        steps: [
          'term(7) = 7×8/2 = 28.',
        ],
      },
    },
    {
      id: 'ns-25',
      difficulty: 'Hard',
      patternTags: ['Wipro', 'Zoho'],
      question: "Find the next number in the series: 5, 10, 7, 14, 11, 22, ?",
      options: ['19', '22', '25', '28'],
      correctIndex: 0,
      steps: [
        { label: 'Given', text: '5, 10, 7, 14, 11, 22.' },
        { label: 'Concept', text: 'The series alternates between two operations: ×2, then −3, repeating.' },
        { label: 'Working', text: 'Verify: 5×2=10✓, 10−3=7✓, 7×2=14✓, 14−3=11✓, 11×2=22✓.' },
        { label: 'Answer', text: 'Next operation (following ×2) is −3: 22−3=19.' },
      ],
      commonTrap:
        "Trying to find a single constant ratio or difference across the WHOLE series — since it alternates between two different operations, no single consistent value will fit.",
      alternateMethod: {
        label: 'Step-by-step verification',
        steps: [
          'Confirming the ×2, −3 pattern holds at every single step (as shown in Working) is the most reliable check for this alternating-operation type.',
        ],
      },
    },
  ],
  simplification: [
    {
      id: 'simp-01',
      difficulty: 'Easy',
      patternTags: ['TCS', 'Infosys'],
      question: "Simplify: 18 + 6 × 3 − 12 ÷ 4",
      options: ['33', '36', '39', '42'],
      correctIndex: 0,
      steps: [
        { label: 'Given', text: '18 + 6 × 3 − 12 ÷ 4.' },
        { label: 'Concept', text: 'BODMAS: perform multiplication/division BEFORE addition/subtraction, regardless of left-right position in the expression.' },
        { label: 'Working', text: '6×3=18. 12÷4=3.' },
        { label: 'Answer', text: '18+18−3 = 33.' },
      ],
      commonTrap:
        "Solving strictly left to right, ignoring operator precedence (18+6=24, ×3=72, −12=60, ÷4=15) — this violates BODMAS entirely.",
      alternateMethod: {
        label: 'Rewrite resolved',
        steps: [
          'Rewrite with multiplication/division already done: 18+18−3.',
          'Left-to-right: 36−3=33.',
        ],
      },
    },
    {
      id: 'simp-02',
      difficulty: 'Medium',
      patternTags: ['TCS', 'Wipro'],
      question: "Simplify: 24 − [16 − {8 − (6 − 3)}]",
      options: ['11', '13', '15', '17'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: '24 − [16 − {8 − (6 − 3)}].' },
        { label: 'Concept', text: 'Resolve brackets from the INNERMOST outward: parentheses (), then braces {}, then square brackets [].' },
        { label: 'Working', text: '(6−3)=3 → {8−3}=5 → [16−5]=11.' },
        { label: 'Answer', text: '24−11 = 13.' },
      ],
      commonTrap:
        "Resolving brackets from the outermost inward instead of innermost-out — you can't simplify the outer bracket before knowing what's inside the inner ones.",
      alternateMethod: {
        label: 'Step-by-step substitution',
        steps: [
          'Start from the center: (6−3)=3.',
          'Substitute: 24−[16−{8−3}] = 24−[16−5] = 24−11 = 13.',
        ],
      },
    },
    {
      id: 'simp-03',
      difficulty: 'Easy',
      patternTags: ['Infosys', 'Zoho'],
      question: "Simplify: 3/4 + 2/3 − 5/6",
      options: ['1/2', '2/3', '7/12', '5/6'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: '3/4 + 2/3 − 5/6.' },
        { label: 'Concept', text: 'Convert all fractions to a COMMON DENOMINATOR (LCM of 4,3,6 = 12) before combining.' },
        { label: 'Working', text: '9/12 + 8/12 − 10/12.' },
        { label: 'Answer', text: '= 7/12.' },
      ],
      commonTrap:
        "Combining numerators and denominators directly without a common denominator (e.g. (3+2−5)/(4+3−6)=0) — fractions can only be added/subtracted once they share the same denominator.",
      alternateMethod: {
        label: 'Decimal check',
        steps: [
          '3/4=0.75, 2/3≈0.667, 5/6≈0.833.',
          '0.75+0.667−0.833≈0.583 ≈ 7/12 (0.5833...) — confirms.',
        ],
      },
    },
    {
      id: 'simp-04',
      difficulty: 'Medium',
      patternTags: ['TCS', 'Zoho'],
      question: "Simplify: 12.5 × 4.4 − 8.2 × 2.5",
      options: ['28.5', '31', '32.5', '34.5'],
      correctIndex: 3,
      steps: [
        { label: 'Given', text: '12.5 × 4.4 − 8.2 × 2.5.' },
        { label: 'Concept', text: 'BODMAS: perform both multiplications first, then subtract.' },
        { label: 'Working', text: '12.5×4.4=55. 8.2×2.5=20.5.' },
        { label: 'Answer', text: '55−20.5 = 34.5.' },
      ],
      commonTrap:
        "Misplacing the decimal point during multiplication (e.g. computing 12.5×4.4 as 5.5 instead of 55) — a common error with decimal arithmetic under time pressure.",
      alternateMethod: {
        label: 'Whole-number scaling',
        steps: [
          '125×44=5,500 → adjust for 2 decimal places → 55.00. Similarly 82×25=2,050 → 20.50.',
          '55−20.5=34.5.',
        ],
      },
    },
    {
      id: 'simp-05',
      difficulty: 'Easy',
      patternTags: ['Wipro', 'Zoho'],
      question: "Simplify: √(0.0625) × √(1600)",
      options: ['10', '12', '14', '16'],
      correctIndex: 0,
      steps: [
        { label: 'Given', text: '√0.0625 × √1600.' },
        { label: 'Concept', text: 'Find each square root by recognizing the underlying perfect square (0.0625=0.25², 1600=40²).' },
        { label: 'Working', text: '√0.0625=0.25. √1600=40.' },
        { label: 'Answer', text: '0.25×40 = 10.' },
      ],
      commonTrap:
        "Misjudging the decimal placement in √0.0625 (guessing 0.025 or 2.5 instead of 0.25) — always verify by squaring back: 0.25²=0.0625.",
      alternateMethod: {
        label: 'Combine under one root',
        steps: [
          '√a×√b=√(ab) for non-negative a,b: √(0.0625×1600)=√100=10.',
        ],
      },
    },
    {
      id: 'simp-06',
      difficulty: 'Easy',
      patternTags: ['TCS', 'Infosys'],
      question: "Simplify: 40% of 250 + 25% of 480",
      options: ['200', '220', '240', '260'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: '40% of 250 + 25% of 480.' },
        { label: 'Concept', text: '"of" means multiplication; compute each percentage term separately (against its OWN base), then add.' },
        { label: 'Working', text: '0.40×250=100. 0.25×480=120.' },
        { label: 'Answer', text: '100+120 = 220.' },
      ],
      commonTrap:
        "Adding the percentages first (40%+25%=65%) and applying that to one number — percentages of DIFFERENT base numbers can't be combined that way; each must be computed against its own base first.",
      alternateMethod: {
        label: 'Fraction method',
        steps: [
          '40%=2/5 → 2/5×250=100. 25%=1/4 → 1/4×480=120.',
          'Sum = 220.',
        ],
      },
    },
    {
      id: 'simp-07',
      difficulty: 'Medium',
      patternTags: ['Wipro', 'Zoho'],
      question: "Simplify: (2³ × 3²) ÷ (4 × 3)",
      options: ['4', '5', '6', '8'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: '(2³ × 3²) ÷ (4 × 3).' },
        { label: 'Concept', text: 'Resolve exponents first (Orders in BODMAS), then multiply within each bracket, then divide.' },
        { label: 'Working', text: '2³=8, 3²=9 → 8×9=72. 4×3=12.' },
        { label: 'Answer', text: '72÷12 = 6.' },
      ],
      commonTrap:
        "Combining the base numbers before applying exponents (e.g. treating it as (2×3) raised to some combined power) — exponents apply only to their own base.",
      alternateMethod: {
        label: 'Simplify algebraically first',
        steps: [
          '(2³×3²)/(4×3) = (2³×3²)/(2²×3) = 2^(3−2)×3^(2−1) = 2¹×3¹ = 6.',
        ],
      },
    },
    {
      id: 'simp-08',
      difficulty: 'Medium',
      patternTags: ['TCS', 'Zoho'],
      question: "Simplify: 0.5 + 1/4 × 0.8",
      options: ['0.5', '0.6', '0.65', '0.7'],
      correctIndex: 3,
      steps: [
        { label: 'Given', text: '0.5 + 1/4 × 0.8.' },
        { label: 'Concept', text: 'BODMAS: multiplication before addition, even in mixed fraction/decimal expressions.' },
        { label: 'Working', text: '1/4 × 0.8 = 0.2.' },
        { label: 'Answer', text: '0.5+0.2 = 0.7.' },
      ],
      commonTrap:
        "Adding 0.5 and 1/4 first (treating them as if grouped, getting 0.75) before multiplying by 0.8 — there's no bracket grouping the addition, so multiplication must happen first.",
      alternateMethod: {
        label: 'Decimal conversion',
        steps: [
          'Convert 1/4 to 0.25 throughout: 0.5+0.25×0.8=0.5+0.2=0.7.',
        ],
      },
    },
    {
      id: 'simp-09',
      difficulty: 'Easy',
      patternTags: ['Infosys', 'Wipro'],
      question: "What should come in place of the question mark? 15 × 8 − ? = 100",
      options: ['20', '24', '28', '32'],
      correctIndex: 0,
      steps: [
        { label: 'Given', text: '15 × 8 − ? = 100.' },
        { label: 'Concept', text: 'Compute the known part first, then solve the resulting simple equation for the unknown.' },
        { label: 'Working', text: '15×8=120. 120 − ? = 100.' },
        { label: 'Answer', text: '? = 120−100 = 20.' },
      ],
      commonTrap:
        "Adding instead of subtracting when isolating the unknown (computing ?=120+100=220) — the equation says '120 minus something equals 100', so the unknown is 120−100, not 120+100.",
      alternateMethod: {
        label: 'Verification',
        steps: [
          '15×8−20 = 120−20 = 100 — matches.',
        ],
      },
    },
    {
      id: 'simp-10',
      difficulty: 'Medium',
      patternTags: ['TCS', 'Infosys'],
      question: "Simplify: 0.333... + 0.666...",
      options: ['0.9', '1', '1.1', '1.2'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: '0.333... + 0.666... (repeating decimals).' },
        { label: 'Concept', text: 'Convert each recurring decimal to its exact fraction (0.333...=1/3, 0.666...=2/3) before adding, to avoid rounding errors.' },
        { label: 'Working', text: '1/3 + 2/3 = 3/3.' },
        { label: 'Answer', text: '= 1 (exactly).' },
      ],
      commonTrap:
        "Treating the recurring decimals as rounded approximations (0.33+0.67=1.00) instead of exact fractions — this happens to look fine here, but the distinction matters in problems where rounding would actually shift the answer.",
      alternateMethod: {
        label: 'Direct fraction addition',
        steps: [
          '0.333...=1/3, 0.666...=2/3. 1/3+2/3=1.',
        ],
      },
    },
    {
      id: 'simp-11',
      difficulty: 'Medium',
      patternTags: ['Wipro', 'Zoho'],
      question: "Find the approximate value of: 49.8% of 600 + 24.9 × 4.1",
      options: ['380', '390', '400', '410'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: '49.8% of 600 + 24.9×4.1 (approximate value requested).' },
        { label: 'Concept', text: 'Round each number to the nearest convenient value BEFORE calculating — full precision isn\'t needed for "approximate" questions.' },
        { label: 'Working', text: '49.8%≈50%=0.5; 0.5×600=300. 24.9≈25, 4.1≈4; 25×4=100.' },
        { label: 'Answer', text: '300+100 = 400.' },
      ],
      commonTrap:
        "Computing the exact value with full decimal precision (≈400.89) and then second-guessing which rounded option to pick, or over-rounding too aggressively (e.g. rounding 24.9 down to 20).",
      alternateMethod: {
        label: 'Alternate rounding check',
        steps: [
          '49.8%≈0.5, 600 stays exact; 24.9×4.1≈25×4=100 — same approach, confirming 400 is robust to reasonable rounding choices.',
        ],
      },
    },
    {
      id: 'simp-12',
      difficulty: 'Medium',
      patternTags: ['TCS', 'Zoho'],
      question: "Simplify: √50 + √18 − √8",
      options: ['2√2', '4√2', '5√2', '6√2'],
      correctIndex: 3,
      steps: [
        { label: 'Given', text: '√50 + √18 − √8.' },
        { label: 'Concept', text: 'Factor out the largest perfect-square factor from each surd, expressing each as a multiple of the SAME simplified radical.' },
        { label: 'Working', text: '√50=5√2, √18=3√2, √8=2√2.' },
        { label: 'Answer', text: '5√2+3√2−2√2 = 6√2.' },
      ],
      commonTrap:
        "Adding the numbers UNDER the roots directly (√50+√18−√8 ≠ √(50+18−8)) — square roots don't distribute over addition/subtraction; each term must be simplified to a common radical form first.",
      alternateMethod: {
        label: 'Decimal verification',
        steps: [
          '√50≈7.07, √18≈4.24, √8≈2.83. 7.07+4.24−2.83≈8.48.',
          '6√2≈6×1.414≈8.49 — matches (rounding).',
        ],
      },
    },
    {
      id: 'simp-13',
      difficulty: 'Easy',
      patternTags: ['Infosys', 'Wipro'],
      question: "Simplify: ∛512 + ∛343",
      options: ['13', '14', '15', '16'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: 'Cube root of 512 plus cube root of 343.' },
        { label: 'Concept', text: 'Recognize each as a PERFECT CUBE — find the number that, cubed, gives the value.' },
        { label: 'Working', text: '8³=512, so ∛512=8. 7³=343, so ∛343=7.' },
        { label: 'Answer', text: '8+7 = 15.' },
      ],
      commonTrap:
        "Confusing cube roots with square roots (mistakenly trying √512≈22.6) — always check which root the symbol indicates before computing.",
      alternateMethod: {
        label: 'Verification',
        steps: [
          '8×8×8=512 ✓. 7×7×7=343 ✓ — confirms both cube roots.',
        ],
      },
    },
    {
      id: 'simp-14',
      difficulty: 'Medium',
      patternTags: ['TCS', 'Infosys'],
      question: "Simplify: 2/3 of 3/5 of 45",
      options: ['16', '18', '20', '22'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: '2/3 of 3/5 of 45.' },
        { label: 'Concept', text: '"of" is treated exactly like multiplication, evaluated left to right through the chain.' },
        { label: 'Working', text: '(2/3)×(3/5) = 6/15 = 2/5.' },
        { label: 'Answer', text: '(2/5)×45 = 18.' },
      ],
      commonTrap:
        "Adding the two fractions instead of multiplying them (2/3+3/5=19/15) before applying to 45 — 'of' always means multiply, never add, when chaining fractions of fractions.",
      alternateMethod: {
        label: 'Sequential application',
        steps: [
          '2/3 of 45 = 30. Then 3/5 of 30 = 18 — same final answer, applying the fractions in either order since multiplication is commutative.',
        ],
      },
    },
    {
      id: 'simp-15',
      difficulty: 'Medium',
      patternTags: ['Wipro', 'Zoho'],
      question: "Simplify: 144 ÷ 12 ÷ 4 × 3",
      options: ['6', '8', '9', '12'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: '144 ÷ 12 ÷ 4 × 3.' },
        { label: 'Concept', text: 'Division and multiplication share the SAME precedence level and must be evaluated strictly LEFT TO RIGHT (no implied grouping).' },
        { label: 'Working', text: '144÷12=12. 12÷4=3.' },
        { label: 'Answer', text: '3×3 = 9.' },
      ],
      commonTrap:
        "Grouping all the divisions together as if bracketed (144÷(12÷4)×3=144÷3×3=144) — without explicit brackets, no such grouping exists; strict left-to-right order applies.",
      alternateMethod: {
        label: 'Single-fraction rewrite',
        steps: [
          '144/(12×4)×3 = 144/48×3 = 3×3 = 9 — an equivalent left-to-right result expressed as one combined fraction.',
        ],
      },
    },
    {
      id: 'simp-16',
      difficulty: 'Medium',
      patternTags: ['TCS', 'Zoho'],
      question: "Simplify: −8 − (−15) + (−6) − 4",
      options: ['-9', '-6', '-4', '-3'],
      correctIndex: 3,
      steps: [
        { label: 'Given', text: '−8 − (−15) + (−6) − 4.' },
        { label: 'Concept', text: 'Subtracting a negative is the same as adding its positive value; adding a negative is the same as subtracting its positive value.' },
        { label: 'Working', text: '−8−(−15)=−8+15=7. 7+(−6)=1.' },
        { label: 'Answer', text: '1−4 = −3.' },
      ],
      commonTrap:
        "Mishandling the double-negative sign (treating −(−15) as −15 instead of +15) — a very common sign error with nested negatives.",
      alternateMethod: {
        label: 'Resolve all signs upfront',
        steps: [
          'Rewrite as −8+15−6−4 = 15 − (8+6+4) = 15−18 = −3.',
        ],
      },
    },
    {
      id: 'simp-17',
      difficulty: 'Medium',
      patternTags: ['Infosys', 'Wipro'],
      question: "Simplify: 20% of (3/4 of 800)",
      options: ['120', '130', '140', '150'],
      correctIndex: 0,
      steps: [
        { label: 'Given', text: '20% of (3/4 of 800).' },
        { label: 'Concept', text: 'Resolve the INNERMOST bracket first (3/4 of 800), then apply the outer percentage to that result.' },
        { label: 'Working', text: '3/4 × 800 = 600.' },
        { label: 'Answer', text: '20% of 600 = 0.20×600 = 120.' },
      ],
      commonTrap:
        "Adding the fraction and percentage instead of multiplying them (20%+75%=95%, then 95% of 800=760) — chained 'of' operations must be MULTIPLIED together, never added.",
      alternateMethod: {
        label: 'Combined multiplier',
        steps: [
          '20%×3/4 = 0.20×0.75 = 0.15. Then 0.15×800 = 120 — same answer as one combined factor.',
        ],
      },
    },
    {
      id: 'simp-18',
      difficulty: 'Medium',
      patternTags: ['TCS', 'Zoho'],
      question: "Simplify using a suitable identity: 87 × 93",
      options: ['8,073', '8,091', '8,097', '8,109'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: '87 × 93.' },
        { label: 'Concept', text: 'Both numbers are equidistant from 90 (±3) — this fits the identity (a−b)(a+b) = a²−b².' },
        { label: 'Working', text: '(90−3)(90+3) = 90² − 3² = 8,100 − 9.' },
        { label: 'Answer', text: '= 8,091.' },
      ],
      commonTrap:
        "Forgetting to square the 3 (computing 8,100−3=8,097 instead of 8,100−9) — the identity subtracts b², not b.",
      alternateMethod: {
        label: 'Direct distributive check',
        steps: [
          '87×93 = 87×90+87×3 = 7,830+261 = 8,091 — matches.',
        ],
      },
    },
    {
      id: 'simp-19',
      difficulty: 'Medium',
      patternTags: ['Infosys', 'Wipro'],
      question: "Simplify: 3½ + 2⅓ − 1⅙",
      options: ['4½', '4⅓', '4⅔', '5⅓'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: '3½ + 2⅓ − 1⅙ (mixed numbers).' },
        { label: 'Concept', text: 'Convert each mixed number to an improper fraction first, find a common denominator, then combine.' },
        { label: 'Working', text: '7/2=21/6, 7/3=14/6, 7/6=7/6. 21/6+14/6−7/6=28/6.' },
        { label: 'Answer', text: '= 14/3 = 4⅔.' },
      ],
      commonTrap:
        "Using an incorrect multiplier when converting to the common denominator of 6 (e.g. mistakenly converting 7/3 to 21/6 instead of 14/6) — double-check each fraction's own conversion factor.",
      alternateMethod: {
        label: 'Separate whole and fractional parts',
        steps: [
          'Whole parts: 3+2−1=4. Fractional parts: ½+⅓−⅙ = 3/6+2/6−1/6=4/6=2/3.',
          'Total = 4 + 2/3 = 4⅔ — matches.',
        ],
      },
    },
    {
      id: 'simp-20',
      difficulty: 'Hard',
      patternTags: ['TCS', 'Infosys'],
      question: "Simplify: 1/2 of 40 + 3 × 1/6 of 60",
      options: ['35', '40', '45', '50'],
      correctIndex: 3,
      steps: [
        { label: 'Given', text: '1/2 of 40 + 3 × 1/6 of 60.' },
        { label: 'Concept', text: '"of" is treated exactly like multiplication in precedence — fully resolve all multiplicative terms first, then add.' },
        { label: 'Working', text: 'Term 1: 1/2 of 40 = 20. Term 2: 3×1/6=1/2, then 1/2 of 60=30.' },
        { label: 'Answer', text: '20+30 = 50.' },
      ],
      commonTrap:
        "Performing the addition before fully completing both multiplicative terms (e.g. mistakenly combining numbers from the middle of the expression) — the '+' must wait until BOTH terms are fully resolved.",
      alternateMethod: {
        label: 'Reorder the multiplicative chain',
        steps: [
          'Since multiplication is associative: Term 2 = 3×(1/6 of 60) = 3×10 = 30 — same result computing "of" first, then ×3.',
        ],
      },
    },
    {
      id: 'simp-21',
      difficulty: 'Medium',
      patternTags: ['Wipro', 'Zoho'],
      question: "Simplify: 50% of 40% of 30% of 5000",
      options: ['300', '320', '340', '360'],
      correctIndex: 0,
      steps: [
        { label: 'Given', text: '50% of 40% of 30% of 5000.' },
        { label: 'Concept', text: 'Chain multiple "of" operations by multiplying all the percentage factors together (as decimals), then apply to the base number.' },
        { label: 'Working', text: '0.5 × 0.4 × 0.3 = 0.06.' },
        { label: 'Answer', text: '0.06 × 5000 = 300.' },
      ],
      commonTrap:
        "Adding the percentages instead of multiplying (50%+40%+30%=120%, then 120% of 5000=6000) — chained percentages of percentages compound MULTIPLICATIVELY, never additively.",
      alternateMethod: {
        label: 'Sequential application',
        steps: [
          '30% of 5000=1,500. 40% of 1,500=600. 50% of 600=300 — same answer, applying each percentage one at a time.',
        ],
      },
    },
    {
      id: 'simp-22',
      difficulty: 'Hard',
      patternTags: ['TCS', 'Zoho'],
      question: "Simplify: (2/3 + 1/4) ÷ (5/6 − 1/2)",
      options: ['2.5', '2.75', '3', '3.25'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: '(2/3+1/4) ÷ (5/6−1/2) — a complex fraction.' },
        { label: 'Concept', text: 'Simplify the numerator and denominator SEPARATELY first, then perform the division.' },
        { label: 'Working', text: 'Numerator: 8/12+3/12=11/12. Denominator: 5/6−3/6=1/3.' },
        { label: 'Answer', text: '(11/12) ÷ (1/3) = (11/12)×3 = 11/4 = 2.75.' },
      ],
      commonTrap:
        "Dividing term-by-term (e.g. 2/3÷5/6 and 1/4÷1/2 separately) instead of fully simplifying the numerator and denominator as single combined values first.",
      alternateMethod: {
        label: 'Decimal check',
        steps: [
          '2/3≈0.667, 1/4=0.25 → numerator≈0.917. 5/6≈0.833, 1/2=0.5 → denominator≈0.333.',
          '0.917/0.333≈2.75 — matches 11/4.',
        ],
      },
    },
    {
      id: 'simp-23',
      difficulty: 'Medium',
      patternTags: ['Infosys', 'Wipro'],
      question: "Find the value of 98² using a suitable shortcut.",
      options: ['9,584', '9,600', '9,604', '10,004'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: '98².' },
        { label: 'Concept', text: 'Express 98 as (100−2) and apply the identity (a−b)² = a²−2ab+b².' },
        { label: 'Working', text: '100² − 2(100)(2) + 2² = 10,000 − 400 + 4.' },
        { label: 'Answer', text: '= 9,604.' },
      ],
      commonTrap:
        "Forgetting the MIDDLE term (2ab) in the expansion, computing only a²+b²=10,004 — the identity (a−b)² has three terms, not two.",
      alternateMethod: {
        label: 'Direct distributive check',
        steps: [
          '98×98 = 98×(100−2) = 9,800−196 = 9,604.',
        ],
      },
    },
    {
      id: 'simp-24',
      difficulty: 'Medium',
      patternTags: ['TCS', 'Infosys'],
      question: "Find the value of 999 × 87 using a suitable shortcut.",
      options: ['86,813', '86,863', '86,900', '86,913'],
      correctIndex: 3,
      steps: [
        { label: 'Given', text: '999 × 87.' },
        { label: 'Concept', text: 'Express 999 as (1000−1) and distribute: 999×87 = (1000−1)×87 = 1000×87 − 1×87.' },
        { label: 'Working', text: '87,000 − 87.' },
        { label: 'Answer', text: '= 86,913.' },
      ],
      commonTrap:
        "Multiplying 999×87 via standard long multiplication instead of the (1000−1) shortcut — far more error-prone and slower under time pressure.",
      alternateMethod: {
        label: 'Alternate decomposition',
        steps: [
          '87×999 = 87×900 + 87×99 = 78,300+8,613 = 86,913 — confirms via a different split.',
        ],
      },
    },
    {
      id: 'simp-25',
      difficulty: 'Hard',
      patternTags: ['Wipro', 'Zoho'],
      question: "Simplify: 45 ÷ 9 × [12 − {6 + (8 − 5)}] + 15",
      options: ['30', '33', '36', '39'],
      correctIndex: 0,
      steps: [
        { label: 'Given', text: '45 ÷ 9 × [12 − {6 + (8 − 5)}] + 15.' },
        { label: 'Concept', text: 'Resolve brackets from innermost to outermost first, then apply division/multiplication left to right, and finally addition.' },
        { label: 'Working', text: '(8−5)=3 → {6+3}=9 → [12−9]=3. Then 45÷9×3 = 5×3 = 15.' },
        { label: 'Answer', text: '15+15 = 30.' },
      ],
      commonTrap:
        "Performing the final addition before completing the division/multiplication chain, or resolving brackets out of order (outermost before innermost) — both break the strict BODMAS sequence.",
      alternateMethod: {
        label: 'Full step-by-step rewrite',
        steps: [
          '45÷9×[12−{6+3}]+15 = 45÷9×[12−9]+15 = 45÷9×3+15 = 5×3+15 = 30.',
        ],
      },
    },
  ],
  'data-interpretation': [
    {
      id: 'di-01',
      difficulty: 'Easy',
      patternTags: ['TCS', 'Infosys'],
      question: "Study the table and find the total sales over the 5 years shown (in ₹ lakhs).",
      table: { headers: ['Year', 'Sales (₹ lakhs)'], rows: [['2018', '40'], ['2019', '55'], ['2020', '65'], ['2021', '80'], ['2022', '100']] },
      options: ['320', '340', '350', '360'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: 'Sales for 2018–2022: 40, 55, 65, 80, 100 (₹ lakhs).' },
        { label: 'Concept', text: 'Total = sum of every value in the table.' },
        { label: 'Working', text: '40+55+65+80+100.' },
        { label: 'Answer', text: '= 340.' },
      ],
      commonTrap:
        "Forgetting to include one year while adding quickly — always recount the number of terms added against the number of rows in the table.",
      alternateMethod: {
        label: 'Pair-sum method',
        steps: [
          '(40+100) + (55+80) + 65 = 140+135+65 = 340.',
        ],
      },
    },
    {
      id: 'di-02',
      difficulty: 'Easy',
      patternTags: ['TCS', 'Wipro'],
      question: "The table shows runs scored by 4 batsmen. If the team's total score was 250, what percentage of the team's total did batsman D score?",
      table: { headers: ['Batsman', 'Runs'], rows: [['A', '45'], ['B', '60'], ['C', '30'], ['D', '65']] },
      options: ['20%', '24%', '26%', '30%'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: "D scored 65 runs; team total = 250." },
        { label: 'Concept', text: 'Percentage = (part/whole) × 100 — using the STATED team total, not the sum of the 4 listed batsmen.' },
        { label: 'Working', text: '65/250 × 100.' },
        { label: 'Answer', text: '= 26%.' },
      ],
      commonTrap:
        "Using the sum of the 4 batsmen's runs (45+60+30+65=200) as the 'total' instead of the given team total of 250 — the remaining 50 runs likely came from extras or other batsmen not listed.",
      alternateMethod: {
        label: 'Fraction simplification',
        steps: [
          '65/250 = 13/50 = 0.26 = 26%.',
        ],
      },
    },
    {
      id: 'di-03',
      difficulty: 'Easy',
      patternTags: ['Infosys', 'Zoho'],
      question: "The table shows student enrollment across 4 courses. Find the ratio of students enrolled in Python to those in C++.",
      table: { headers: ['Course', 'Students'], rows: [['Java', '120'], ['Python', '150'], ['C++', '90'], ['JavaScript', '140']] },
      options: ['4:3', '5:3', '3:5', '2:3'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: 'Python=150, C++=90.' },
        { label: 'Concept', text: 'Ratio = first value : second value, simplified by their HCF.' },
        { label: 'Working', text: 'HCF(150,90)=30. 150/30=5, 90/30=3.' },
        { label: 'Answer', text: '5:3.' },
      ],
      commonTrap:
        "Reversing the ratio order (writing C++:Python=3:5 instead of the requested Python:C++) — always match the order to exactly what's asked.",
      alternateMethod: {
        label: 'Decimal check',
        steps: [
          '150/90=1.667=5/3 — matches 5:3.',
        ],
      },
    },
    {
      id: 'di-04',
      difficulty: 'Medium',
      patternTags: ['TCS', 'Zoho'],
      question: "The table shows a family's monthly expenditure. Find the difference between the total groceries expenditure and the total rent expenditure over the three months.",
      table: { headers: ['Month', 'Groceries (₹)', 'Rent (₹)'], rows: [['Jan', '8,000', '12,000'], ['Feb', '8,500', '12,000'], ['Mar', '9,000', '12,000']] },
      options: ['9,000', '9,500', '10,000', '10,500'],
      correctIndex: 3,
      steps: [
        { label: 'Given', text: 'Groceries: 8,000+8,500+9,000. Rent: 12,000×3.' },
        { label: 'Concept', text: 'Sum each column separately first, then find the difference between the two totals.' },
        { label: 'Working', text: 'Groceries total=25,500. Rent total=36,000.' },
        { label: 'Answer', text: '36,000−25,500 = 10,500.' },
      ],
      commonTrap:
        "Comparing only a single month's figures instead of the full 3-month totals — the question asks about TOTALS across all three months.",
      alternateMethod: {
        label: 'Monthly-difference sum',
        steps: [
          'Jan diff=4,000, Feb diff=3,500, Mar diff=3,000.',
          'Sum = 4,000+3,500+3,000 = 10,500 — same answer via a different order.',
        ],
      },
    },
    {
      id: 'di-05',
      difficulty: 'Easy',
      patternTags: ['Wipro', 'Zoho'],
      question: "The table shows a student's marks in 5 subjects. Find the average marks across all 5 subjects.",
      table: { headers: ['Subject', 'Marks'], rows: [['Maths', '85'], ['Science', '78'], ['English', '82'], ['History', '65'], ['Geography', '70']] },
      options: ['76', '78', '80', '82'],
      correctIndex: 0,
      steps: [
        { label: 'Given', text: '85, 78, 82, 65, 70.' },
        { label: 'Concept', text: 'Average = sum of values / number of values.' },
        { label: 'Working', text: 'Sum = 85+78+82+65+70 = 380.' },
        { label: 'Answer', text: '380/5 = 76.' },
      ],
      commonTrap:
        "Dividing by the wrong count (e.g. 4 instead of 5) — always recount the number of subjects listed.",
      alternateMethod: {
        label: 'Deviation check',
        steps: [
          'Deviations from an assumed mean of 76: +9,+2,+6,−11,−6, summing to 0 — confirms 76 is exact.',
        ],
      },
    },
    {
      id: 'di-06',
      difficulty: 'Easy',
      patternTags: ['TCS', 'Infosys'],
      question: "The table shows rainfall (in mm) recorded in 5 cities during a month. Which city recorded the SECOND-highest rainfall?",
      table: { headers: ['City', 'Rainfall (mm)'], rows: [['A', '120'], ['B', '95'], ['C', '150'], ['D', '110'], ['E', '135']] },
      options: ['A', 'B', 'D', 'E'],
      correctIndex: 3,
      steps: [
        { label: 'Given', text: 'A=120, B=95, C=150, D=110, E=135.' },
        { label: 'Concept', text: 'Sort the values to identify rank order, being careful to find the SECOND-highest, not the highest.' },
        { label: 'Working', text: 'Descending: C(150) > E(135) > A(120) > D(110) > B(95).' },
        { label: 'Answer', text: 'E has the second-highest rainfall.' },
      ],
      commonTrap:
        "Identifying the HIGHEST (City C) instead of the SECOND-highest as asked — always re-read exactly which rank is being requested.",
      alternateMethod: {
        label: 'Elimination method',
        steps: [
          'Remove the highest (C=150), then find the highest among the remaining four: A(120), B(95), D(110), E(135) → E is highest among these.',
        ],
      },
    },
    {
      id: 'di-07',
      difficulty: 'Easy',
      patternTags: ['Wipro', 'Zoho'],
      question: "The table shows a company's profit over 3 years. Find the percentage increase in profit from 2021 to 2022.",
      table: { headers: ['Year', 'Profit (₹ lakhs)'], rows: [['2020', '50'], ['2021', '60'], ['2022', '75']] },
      options: ['20%', '25%', '28%', '30%'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: 'Profit 2021=60, 2022=75.' },
        { label: 'Concept', text: '% increase = (new−old)/old × 100 — using the EARLIER year as the base.' },
        { label: 'Working', text: 'Increase = 75−60 = 15.' },
        { label: 'Answer', text: '15/60 × 100 = 25%.' },
      ],
      commonTrap:
        "Dividing by the NEWER value instead of the base (15/75×100=20%) — percentage change is always relative to the STARTING value.",
      alternateMethod: {
        label: 'Ratio method',
        steps: [
          '75/60=1.25, meaning profit became 125% of its 2021 value — a 25% increase.',
        ],
      },
    },
    {
      id: 'di-08',
      difficulty: 'Medium',
      patternTags: ['TCS', 'Zoho'],
      question: "The table shows a household's monthly budget. Find the combined percentage of the budget spent on Food and Rent together.",
      table: { headers: ['Category', 'Amount (₹)'], rows: [['Food', '15,000'], ['Rent', '20,000'], ['Transport', '5,000'], ['Savings', '10,000']] },
      options: ['60%', '65%', '70%', '75%'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: 'Food=15,000, Rent=20,000. Total budget=50,000.' },
        { label: 'Concept', text: "Find the total first, then compute the combined categories' share as a percentage of that total." },
        { label: 'Working', text: 'Food+Rent = 35,000.' },
        { label: 'Answer', text: '35,000/50,000 × 100 = 70%.' },
      ],
      commonTrap:
        "Computing each category's percentage separately and averaging them instead of combining the actual rupee amounts first — the safer method always combines amounts before dividing once.",
      alternateMethod: {
        label: 'Percentage-addition shortcut',
        steps: [
          'Food%=30%, Rent%=40% (both share the SAME base, 50,000), so they can be added directly: 30%+40%=70%.',
        ],
      },
    },
    {
      id: 'di-09',
      difficulty: 'Medium',
      patternTags: ['Infosys', 'Wipro'],
      question: "The table shows the number of employees in a company. Find the percentage decrease in employees from 2019 to 2020.",
      table: { headers: ['Year', 'Employees'], rows: [['2019', '800'], ['2020', '750'], ['2021', '820']] },
      options: ['5%', '5.5%', '6%', '6.25%'],
      correctIndex: 3,
      steps: [
        { label: 'Given', text: 'Employees 2019=800, 2020=750.' },
        { label: 'Concept', text: '% decrease = (old−new)/old × 100 — using the EARLIER year as the base.' },
        { label: 'Working', text: 'Decrease = 800−750 = 50.' },
        { label: 'Answer', text: '50/800 × 100 = 6.25%.' },
      ],
      commonTrap:
        "Using 750 as the base instead of 800 — percentage decrease is always relative to the STARTING (earlier) value.",
      alternateMethod: {
        label: 'Ratio method',
        steps: [
          '750/800=0.9375=93.75% of the original — meaning a 6.25% decrease.',
        ],
      },
    },
    {
      id: 'di-10',
      difficulty: 'Medium',
      patternTags: ['TCS', 'Zoho'],
      question: "The table shows votes received by 4 candidates (total votes = 6,000). Find the combined percentage of total votes received by candidates S and Q (the top two).",
      table: { headers: ['Candidate', 'Votes'], rows: [['P', '900'], ['Q', '1,800'], ['R', '1,200'], ['S', '2,100']] },
      options: ['65%', '68%', '70%', '72%'],
      correctIndex: 0,
      steps: [
        { label: 'Given', text: 'S=2,100, Q=1,800. Total votes=6,000.' },
        { label: 'Concept', text: 'Sum the specified two candidates\' votes, then express as a percentage of the total.' },
        { label: 'Working', text: 'S+Q = 2,100+1,800 = 3,900.' },
        { label: 'Answer', text: '3,900/6,000 × 100 = 65%.' },
      ],
      commonTrap:
        "Computing based on only S's or only Q's individual percentage instead of their COMBINED total — both specified candidates' votes must be summed BEFORE computing the percentage.",
      alternateMethod: {
        label: 'Individual percentages',
        steps: [
          'S=2,100/6,000=35%. Q=1,800/6,000=30%. Since both share the same base, add directly: 35%+30%=65%.',
        ],
      },
    },
    {
      id: 'di-11',
      difficulty: 'Easy',
      patternTags: ['Infosys', 'Wipro'],
      question: "The table shows how a student spends a 24-hour day. What fraction of the day does the student spend studying?",
      table: { headers: ['Activity', 'Hours'], rows: [['Sleep', '8'], ['Study', '6'], ['Leisure', '4'], ['Other', '6']] },
      options: ['1/6', '1/5', '1/4', '1/3'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: 'Study = 6 hours, out of a full 24-hour day.' },
        { label: 'Concept', text: 'Fraction = part / whole, using the FULL 24-hour day as the denominator.' },
        { label: 'Working', text: '6/24.' },
        { label: 'Answer', text: '= 1/4.' },
      ],
      commonTrap:
        "Using the sum of only some listed activities as the denominator instead of the full 24-hour day — the day total is fixed at 24 hours regardless of how the table breaks it down.",
      alternateMethod: {
        label: 'Direct simplification',
        steps: [
          '6/24 = 1/4.',
        ],
      },
    },
    {
      id: 'di-12',
      difficulty: 'Medium',
      patternTags: ['TCS', 'Infosys'],
      question: "The table shows a product's sales (in units) over 6 months. Find the average sales for the LAST 3 months only (Apr, May, Jun).",
      table: { headers: ['Month', 'Sales'], rows: [['Jan', '100'], ['Feb', '120'], ['Mar', '90'], ['Apr', '110'], ['May', '130'], ['Jun', '140']] },
      options: ['115', '126.67', '130', '135'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: 'Apr=110, May=130, Jun=140 (the last 3 months only).' },
        { label: 'Concept', text: 'Use only the relevant SUBSET of data specified in the question, not the full 6-month table.' },
        { label: 'Working', text: 'Sum = 110+130+140 = 380.' },
        { label: 'Answer', text: '380/3 ≈ 126.67.' },
      ],
      commonTrap:
        "Accidentally averaging ALL 6 months (total=690, average=115) instead of restricting to the specified last-3-months subset.",
      alternateMethod: {
        label: 'Deviation check',
        steps: [
          'Deviations from an assumed mean of 126.67: −16.67, +3.33, +13.33, summing to ≈0 — confirms the average.',
        ],
      },
    },
    {
      id: 'di-13',
      difficulty: 'Hard',
      patternTags: ['Wipro', 'Zoho'],
      question: "The table shows a company's revenue over 4 years. In which year did the company see the highest PERCENTAGE growth compared to the previous year?",
      table: { headers: ['Year', 'Revenue (₹ crores)'], rows: [['2019', '50'], ['2020', '100'], ['2021', '150'], ['2022', '220']] },
      options: ['2020', '2021', '2022', 'Cannot be determined'],
      correctIndex: 0,
      steps: [
        { label: 'Given', text: 'Revenue: 50, 100, 150, 220 for 2019–2022.' },
        { label: 'Concept', text: 'Compute YEAR-OVER-YEAR PERCENTAGE growth (not absolute rupee increase) — a smaller base makes even a smaller increase represent a larger percentage.' },
        { label: 'Working', text: '2020: (100−50)/50=100%. 2021: (150−100)/100=50%. 2022: (220−150)/150≈46.67%.' },
        { label: 'Answer', text: '2020 has the highest percentage growth (100%).' },
      ],
      commonTrap:
        "Picking 2022 because it has the LARGEST absolute increase (+70 crores, more than 2020's +50) — but percentage growth is relative to the STARTING value, and 2020's smaller absolute increase is huge relative to its small base.",
      alternateMethod: {
        label: 'Ratio method',
        steps: [
          "2020's revenue is exactly DOUBLE (2×) 2019's — an unambiguous 100% growth, clearly the largest of the three ratios computed.",
        ],
      },
    },
    {
      id: 'di-14',
      difficulty: 'Medium',
      patternTags: ['TCS', 'Zoho'],
      question: "The table shows production at two factories over two quarters. Find the ratio of total production (both factories combined) in Q1 to that in Q2.",
      table: { headers: ['Factory', 'Q1', 'Q2'], rows: [['X', '400', '500'], ['Y', '600', '700']] },
      options: ['4:5', '5:6', '6:7', '3:4'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: 'X: Q1=400,Q2=500. Y: Q1=600,Q2=700.' },
        { label: 'Concept', text: "Sum EACH quarter's total across both factories first, then form the ratio." },
        { label: 'Working', text: 'Q1 total = 400+600=1,000. Q2 total = 500+700=1,200.' },
        { label: 'Answer', text: 'Simplify 1,000:1,200 = 5:6.' },
      ],
      commonTrap:
        "Comparing only ONE factory's Q1-to-Q2 ratio (e.g. just X: 4:5) instead of the COMBINED total for both factories as the question asks.",
      alternateMethod: {
        label: 'Factory-level check',
        steps: [
          'X: 400:500=4:5. Y: 600:700=6:7. The combined ratio must come from actual totals (5:6), not by combining the two individual ratios directly.',
        ],
      },
    },
    {
      id: 'di-15',
      difficulty: 'Medium',
      patternTags: ['Infosys', 'Wipro'],
      question: "The table shows market share of 3 companies. Find the difference (in percentage points) between Company B's and Company A's market share.",
      table: { headers: ['Company', 'Share (%)'], rows: [['A', '35'], ['B', '40'], ['C', '25']] },
      options: ['2', '3', '4', '5'],
      correctIndex: 3,
      steps: [
        { label: 'Given', text: 'A=35%, B=40%.' },
        { label: 'Concept', text: 'When shares are already given as percentages, the difference is a simple subtraction, expressed in PERCENTAGE POINTS.' },
        { label: 'Working', text: '40 − 35.' },
        { label: 'Answer', text: '= 5 percentage points.' },
      ],
      commonTrap:
        "Computing a RELATIVE percentage difference instead ((40−35)/35×100≈14.3%) — when comparing two shares of the same whole, a straightforward point subtraction is what's meant here.",
      alternateMethod: {
        label: 'Verification',
        steps: [
          'Shares sum to 35+40+25=100% (a complete breakdown), and B leads A by exactly 5 points on this shared scale.',
        ],
      },
    },
    {
      id: 'di-16',
      difficulty: 'Easy',
      patternTags: ['TCS', 'Infosys'],
      question: "The table shows the grade distribution of 500 students, with Grade C missing. Find the number of students who got Grade C.",
      table: { headers: ['Grade', 'Students'], rows: [['A', '100'], ['B', '150'], ['C', '?'], ['D', '75']] },
      options: ['175', '180', '185', '190'],
      correctIndex: 0,
      steps: [
        { label: 'Given', text: 'A=100, B=150, D=75. Total students=500.' },
        { label: 'Concept', text: 'Subtract the sum of all KNOWN categories from the grand total to find the missing value.' },
        { label: 'Working', text: '100+150+75 = 325.' },
        { label: 'Answer', text: '500−325 = 175.' },
      ],
      commonTrap:
        "Adding the known values together and mistaking that sum (325) for the answer, forgetting to subtract it from the grand total to find the actual missing value.",
      alternateMethod: {
        label: 'Verification',
        steps: [
          '100+150+175+75 = 500 — matches the given grand total exactly.',
        ],
      },
    },
    {
      id: 'di-17',
      difficulty: 'Easy',
      patternTags: ['Wipro', 'Zoho'],
      question: "The table shows the cost breakdown of manufacturing a product. What percentage of the total manufacturing cost is spent on Labor?",
      table: { headers: ['Component', 'Cost (₹)'], rows: [['Raw Material', '300'], ['Labor', '150'], ['Overhead', '50']] },
      options: ['20%', '30%', '40%', '50%'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: 'Labor=150. Total cost=300+150+50=500.' },
        { label: 'Concept', text: 'Percentage = (part/whole) × 100, using the FULL total cost as the base.' },
        { label: 'Working', text: '150/500 × 100.' },
        { label: 'Answer', text: '= 30%.' },
      ],
      commonTrap:
        "Using an incomplete total (e.g. only Raw Material+Labor=450, excluding Overhead) as the denominator instead of the full manufacturing cost.",
      alternateMethod: {
        label: 'Fraction simplification',
        steps: [
          '150/500 = 3/10 = 30%.',
        ],
      },
    },
    {
      id: 'di-18',
      difficulty: 'Medium',
      patternTags: ['TCS', 'Zoho'],
      question: "The table shows employees by department and gender. Find the total number of female employees across all three departments.",
      table: { headers: ['Department', 'Male', 'Female'], rows: [['Sales', '40', '30'], ['IT', '25', '35'], ['HR', '10', '20']] },
      options: ['75', '80', '85', '90'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: 'Female column: 30, 35, 20.' },
        { label: 'Concept', text: 'Sum only the FEMALE column across all departments — the Male column is irrelevant to this specific question.' },
        { label: 'Working', text: '30+35+20.' },
        { label: 'Answer', text: '= 85.' },
      ],
      commonTrap:
        "Accidentally summing the Male column instead (40+25+10=75), or summing BOTH columns together — carefully read which specific column the question refers to.",
      alternateMethod: {
        label: 'Complementary check',
        steps: [
          'Total employees=160. Male total=75. Female = 160−75 = 85 — cross-checked via the other column.',
        ],
      },
    },
    {
      id: 'di-19',
      difficulty: 'Medium',
      patternTags: ['Infosys', 'Wipro'],
      question: "The table shows weekly sales (in ₹ thousand) of 5 salespersons. Find the average sales, and identify which salesperson's sales is closest to it.",
      table: { headers: ['Salesperson', 'Sales'], rows: [['P', '45'], ['Q', '60'], ['R', '38'], ['S', '52'], ['T', '55']] },
      options: ['P', 'Q', 'S', 'T'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: 'P=45, Q=60, R=38, S=52, T=55.' },
        { label: 'Concept', text: 'Compute the average first, then find which individual value has the SMALLEST absolute difference from it.' },
        { label: 'Working', text: 'Average = (45+60+38+52+55)/5 = 250/5 = 50. Deviations: P=5, Q=10, R=12, S=2, T=5.' },
        { label: 'Answer', text: 'S is closest (deviation of only 2).' },
      ],
      commonTrap:
        "Picking the salesperson with the HIGHEST sales (Q), assuming 'closest to average' means 'highest' — the actual deviation from the average must be computed for each person.",
      alternateMethod: {
        label: 'Verification',
        steps: [
          "S's sales (52) minus the average (50) = 2, the smallest deviation among all 5.",
        ],
      },
    },
    {
      id: 'di-20',
      difficulty: 'Medium',
      patternTags: ['TCS', 'Infosys'],
      question: "The table shows products sold by a shop, with quantity and price per unit. Find the total revenue from selling all three products.",
      table: { headers: ['Product', 'Quantity', 'Price (₹)'], rows: [['Pen', '50', '10'], ['Notebook', '30', '40'], ['Eraser', '100', '5']] },
      options: ['2,000', '2,100', '2,200', '2,300'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: 'Pen: 50×10. Notebook: 30×40. Eraser: 100×5.' },
        { label: 'Concept', text: 'Revenue per product = quantity × price; sum across all products for the total revenue.' },
        { label: 'Working', text: 'Pen=500, Notebook=1,200, Eraser=500.' },
        { label: 'Answer', text: '500+1,200+500 = 2,200.' },
      ],
      commonTrap:
        "Adding the quantities and prices SEPARATELY first (total qty=180, total price=55) and multiplying those sums (180×55=9,900) — each product's own quantity must be multiplied by its OWN price individually.",
      alternateMethod: {
        label: 'Insight check',
        steps: [
          'Notebook alone contributes 1,200 — more than half the total 2,200 — despite having the fewest units sold, because its per-unit price (₹40) is much higher than the others.',
        ],
      },
    },
    {
      id: 'di-21',
      difficulty: 'Hard',
      patternTags: ['Wipro', 'Zoho'],
      question: "The table shows revenue of two companies over two years. Which company had a higher PERCENTAGE growth in revenue from 2021 to 2022?",
      table: { headers: ['Company', '2021', '2022'], rows: [['Alpha', '200', '260'], ['Beta', '150', '200']] },
      options: ['Alpha', 'Beta', 'Both equal', 'Cannot be determined'],
      correctIndex: 1,
      steps: [
        { label: 'Given', text: 'Alpha: 200→260. Beta: 150→200.' },
        { label: 'Concept', text: 'Compute PERCENTAGE growth for each — not just which has the larger absolute rupee increase.' },
        { label: 'Working', text: 'Alpha: (260−200)/200×100=30%. Beta: (200−150)/150×100≈33.33%.' },
        { label: 'Answer', text: 'Beta has the higher percentage growth.' },
      ],
      commonTrap:
        "Comparing absolute rupee increases instead (Alpha:+60, Beta:+50) and concluding Alpha grew 'more' — relative to its smaller starting base, Beta's growth RATE is actually higher.",
      alternateMethod: {
        label: 'Ratio method',
        steps: [
          "Alpha's 2022 revenue is 260/200=1.3× its 2021 value. Beta's is 200/150≈1.333× — since 1.333>1.3, Beta grew at a higher rate.",
        ],
      },
    },
    {
      id: 'di-22',
      difficulty: 'Hard',
      patternTags: ['TCS', 'Zoho'],
      question: "The table shows marks scored and number of students in each score bracket. Find the average marks of all students, weighted by how many students are in each bracket.",
      table: { headers: ['Marks', 'Students'], rows: [['40', '5'], ['60', '10'], ['80', '15'], ['100', '10']] },
      options: ['70', '72', '75', '78'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: 'Marks 40,60,80,100 with 5,10,15,10 students respectively.' },
        { label: 'Concept', text: 'This is a WEIGHTED average — multiply each mark by its student count, sum, then divide by the TOTAL number of students.' },
        { label: 'Working', text: '(40×5)+(60×10)+(80×15)+(100×10) = 200+600+1,200+1,000 = 3,000. Total students=40.' },
        { label: 'Answer', text: '3,000/40 = 75.' },
      ],
      commonTrap:
        "Taking a simple average of just the 4 mark values ((40+60+80+100)/4=70), ignoring that different numbers of students sit in each bracket — brackets with more students should count more heavily.",
      alternateMethod: {
        label: 'Weighted-fraction check',
        steps: [
          '40×(5/40)+60×(10/40)+80×(15/40)+100×(10/40) = 5+15+30+25 = 75 — same answer via weighted fractions.',
        ],
      },
    },
    {
      id: 'di-23',
      difficulty: 'Hard',
      patternTags: ['Infosys', 'Wipro'],
      question: "The table shows total students and pass percentage in 3 sections. Find the total number of students who PASSED across all three sections.",
      table: { headers: ['Section', 'Total Students', 'Pass %'], rows: [['A', '50', '80%'], ['B', '40', '75%'], ['C', '60', '90%']] },
      options: ['118', '120', '124', '128'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: 'A: 50 students, 80% pass. B: 40 students, 75% pass. C: 60 students, 90% pass.' },
        { label: 'Concept', text: "Convert each section's pass PERCENTAGE into an actual number of passing students, then sum across sections." },
        { label: 'Working', text: 'A: 50×0.80=40. B: 40×0.75=30. C: 60×0.90=54.' },
        { label: 'Answer', text: '40+30+54 = 124.' },
      ],
      commonTrap:
        "Averaging the three pass percentages directly ((80+75+90)/3≈81.67%) and applying that to the combined total students — since section sizes differ, a simple percentage average doesn't correctly weight each section.",
      alternateMethod: {
        label: 'Weighted-percentage check',
        steps: [
          "Overall pass% = 124/150×100 ≈ 82.67%, which does NOT equal the simple average of 81.67% — confirming individual computation per section was necessary.",
        ],
      },
    },
    {
      id: 'di-24',
      difficulty: 'Easy',
      patternTags: ['TCS', 'Infosys'],
      question: "The table shows the number of books in a library by genre. Find the ratio of Fiction to Non-fiction to Reference books.",
      table: { headers: ['Genre', 'Books'], rows: [['Fiction', '300'], ['Non-fiction', '200'], ['Reference', '100']] },
      options: ['3:2:1', '2:3:1', '1:2:3', '3:1:2'],
      correctIndex: 0,
      steps: [
        { label: 'Given', text: 'Fiction=300, Non-fiction=200, Reference=100.' },
        { label: 'Concept', text: 'Express as a three-part ratio, then simplify by their common HCF.' },
        { label: 'Working', text: 'HCF(300,200,100)=100. 300/100=3, 200/100=2, 100/100=1.' },
        { label: 'Answer', text: '3:2:1.' },
      ],
      commonTrap:
        "Simplifying only two of the three numbers and leaving the third un-reduced (e.g. 3:2:100) — the SAME divisor must be applied to ALL parts of the ratio.",
      alternateMethod: {
        label: 'Verification',
        steps: [
          '3+2+1=6 parts, total books=600. 1 part=600/6=100, matching Reference\'s actual count directly.',
        ],
      },
    },
    {
      id: 'di-25',
      difficulty: 'Medium',
      patternTags: ['Wipro', 'Zoho'],
      question: "The table shows a town's population over 5 years. Find the overall percentage increase in population from 2018 to 2022.",
      table: { headers: ['Year', 'Population (thousands)'], rows: [['2018', '80'], ['2019', '84'], ['2020', '88'], ['2021', '95'], ['2022', '100']] },
      options: ['20%', '22%', '25%', '28%'],
      correctIndex: 2,
      steps: [
        { label: 'Given', text: 'Population 2018=80, 2022=100.' },
        { label: 'Concept', text: 'For an overall change across multiple years, compare only the FIRST and LAST values directly — intermediate years aren\'t needed for this calculation.' },
        { label: 'Working', text: 'Increase = 100−80 = 20.' },
        { label: 'Answer', text: '20/80 × 100 = 25%.' },
      ],
      commonTrap:
        "Trying to add up each year-over-year percentage change individually — this is NOT how compounding percentage changes work across multiple periods; the overall change must come directly from the first and last values.",
      alternateMethod: {
        label: 'Ratio method',
        steps: [
          '100/80=1.25, meaning the 2022 population is 125% of the 2018 population — a 25% overall increase.',
        ],
      },
    },
  ],
};
